import ProfileSearch from './components/ProfileSearch';
import {publishProfileWithIdentity} from './services/profileIdentity';
import {detectSeriesName} from './services/seriesIdentity';
import {matchesLibraryShortcut} from './services/libraryShortcuts';
import {getCatalogueId,selectCatalogueMatch,toCatalogueGame} from './services/catalogueGame';
import ToolDialog from './components/ToolDialog';
import {hardwareImageForEdition,hardwareBaseImage,hardwareDisplayImage,hardwarePhotoUrl,hardwareEditionChoices} from './services/hardwareEditionPhotos';
import HardwareEditionEditor from './components/HardwareEditionEditor';
import {hardwareEditionLabel,normalizeHardwareEdition,hasHardwareEdition,duplicateHardwareEdition} from './services/hardwareEditions';
import {normalizeEditionLinks} from './services/gameEditions';
import {normalizeDuelHistory} from "./services/soloDuel";
import {THEME_CATALOG} from "./data/themeCatalog";
import { Fragment, Suspense, lazy, useState, useEffect, useMemo, useRef, useCallback } from "react";
import { createPortal } from "react-dom";
import useRendezvousUpdates from './components/useRendezvousUpdates';
import RendezvousHub from './components/RendezvousHub';
import FriendChallenges from './components/FriendChallenges';
import GameRecommendations from './components/GameRecommendations';
import GamingPredictions from './components/GamingPredictions';
import {normalizeEngagement} from './services/engagement';
import QuickCollectionAdd from './components/QuickCollectionAdd';
import useDetailReturn from './components/useDetailReturn';
import {recommendationReason} from './services/recommendationReason';
import "./App.css";
import LibraryGameCover from "./components/LibraryGameCover";
import "./components/LibraryPresentation.css";
import "./polish.css";
import "./components/GameDetailFocus.css";
import "./components/OnePieceTheme.css";
import "./components/KartTheme.css";
import "./components/PopCultureThemes.css";
import { POP_CULTURE_THEMES } from "./data/popCultureThemes";
import "./components/GameUniverseThemes.css";
import { GAME_UNIVERSE_THEMES } from "./data/gameUniverseThemes";
import "./documentScroll.css";
import "./components/CheckpointTheme.css";
import { CYBER2077_THEME, Cyber2077Wordmark } from "./components/Cyber2077Effects";
import CheckpointPremiumChoice from "./components/CheckpointPremiumChoice";
import { CHECKPOINT_PREMIUM_THEME, CHECKPOINT_ULTIMATE_THEME, normalizeThemeUnlocks, mergeThemeUnlocks, resolveThemeRewards, canUseTheme, isCheckpointTheme, getThemeUnlockLevel, getThemeLabel } from "./services/themeRewards";
import { CheckpointMark } from "./components/CheckpointEffects";
import "./components/HomeOverview.css";
import { db, auth } from "./firebase";
import { ownerUid } from "./services/accountConfig";
import { collection, doc, accountsEnabled, accountStorage as localStorage } from "./services/accountData";
import { AccountOptions } from "./components/AccountGate";
import { applySelectedGamePatch } from "./services/gameSelection";
import { HARDWARE_CATALOG } from "./data/hardware";
import { PC_COMPONENT_FIELDS, getPcComponentOptions } from "./data/pcComponents";
import { WEEKLY_QUIZ_XP } from "./data/weeklyQuiz";
import { WEEKLY_QUIZ_SIZE, getWeeklyQuiz, getWeeklyQuizKey, getWeeklyQuizAnswer, submitWeeklyQuizAnswer } from "./services/weeklyQuiz";
import WeeklyQuiz from "./components/WeeklyQuiz";
import { sortSearchResults } from "./services/searchSort";
import { isFanGame, getDiscoveryMetadata, isMainGameResult } from "./services/fanGames";
import "./components/SearchFanFilter.css";
import { compareLatestLibraryAdditions } from "./services/libraryOrder";
import { getWishlistOriginLabel, getDealAlertLabel } from "./services/wishlistLabels";
import ProgressSync, { useProgressSync } from "./components/ProgressSync";
import LibraryBulkEditor from "./components/LibraryBulkEditor";
import {useUsefulUpdates,NotificationPreferences} from "./components/UsefulUpdates";
import {normalizeNotificationPreferences,notificationAllowed} from "./services/usefulUpdates";
import {diffPrivateSettings} from "./services/privateSettings";
import {publicProfileView} from "./services/publicProfilePrivacy";
import Rediscover from "./components/Rediscover";
import {FavoriteReasonEditor,FavoriteReasonPicker,FavoriteReasons} from "./components/FavoriteReasons";
import {favoriteReason,publicFavoriteGames,rediscoveryCandidates} from "./services/rediscovery";
import ArenaTab, {ArenaShortcut} from "./components/ArenaTab";
import SpoilerText from "./components/SpoilerText";
import {spoilerSummary,wrapSpoilerSelection,reviewActivityFields} from "./services/spoilers";
import MonthlyRecap from "./components/MonthlyRecap";
import FriendPolls from "./components/FriendPolls";
import {stampGameMilestones} from "./services/monthlyRecap";
import CheckpointExperiences from './components/CheckpointExperiences';
import CouchMode from './components/CouchMode';
import './components/CheckpointExperiences.css';
import {normalizeExperiences,dueCapsules} from './services/experiences';
import GameHistory from './components/GameHistory';
import GameListMembership from './components/GameListMembership';
import HardwareFilters from './components/HardwareFilters';
import {stampGameHistory} from './services/gameHistory';
import {EMPTY_HARDWARE_FILTERS,matchesHardwareFilters} from './services/hardwareFilters';
import ContentPreferences from './components/ContentPreferences';
import PersonalLists from './components/PersonalLists';
import GameJourneyEditor from './components/GameJourneyEditor';
import {normalizeHiddenTabs} from './services/contentPreferences';
import {normalizePersonalLists} from './services/personalLists';
import {journeyPatch,isStoppedGame} from './services/gameJourney';
import HomeLayout, {HomeBlock} from "./components/HomeLayout";
import QuickGameActions from "./components/QuickGameActions";
import ImportReview from "./components/ImportReview";
import PersonalSearch from "./components/PersonalSearch";
import {normalizeHomeLayout} from "./services/homeLayout";
import {planGameImport} from "./services/importPlan";
import {platformSyncPatch,mergeXboxPages} from './services/platformActivity';
import GamePlatformActivity from './components/GamePlatformActivity';
import FriendComparison from "./components/FriendComparison";
import DataProtection, {AutomaticBackup, ConnectionStatus} from "./components/DataProtection";
import SessionPicker from "./components/SessionPicker";
import {captureBackup} from "./services/deviceStore";
import SaveFeedback, { useSaveFeedback } from "./components/SaveFeedback";
import { FirstSteps, ProfileSection } from "./components/EverydayExperience";
import { TabTutorialEntry, TutorialLibrary } from "./components/TabTutorials";
import ReleaseNotes,{ReleaseNotesDialog,useReleaseProgress} from './components/ReleaseNotes';
import {RELEASE_NOTES} from './data/releaseNotes';
import { TAB_TUTORIALS } from "./data/tabTutorials";
import { buildBulkGamePatch } from "./services/libraryBulk";
import "./components/CollectionImprovements.css";
import "./components/LandscapeLayout.css";
import "./components/GeneralPolish.css";
import "./components/ResponsiveControls.css";
import "./components/HardwareCards.css";
import SeriesCatalogProgress from "./components/SeriesCatalogProgress";
import { loadSeriesCatalog, dedupeSeriesGames, findSeriesLibraryMatch, isSeriesLibraryEpisode, seriesCatalogTitle } from "./services/seriesCatalog";
import "./components/SeriesCatalogProgress.css";
import {
  CHECKPOINT_LEVELS,
  getCheckpointRewardBadges,
  getCheckpointTrial,
} from "./data/checkpointTrials";
import { getOfficialGotyForSeason } from "./data/officialGoty";
import { selectTimelineEvents } from "./services/gamingTimeline";
import useUpcomingReleases from "./components/useUpcomingReleases";
import UpcomingReleasesTab from "./components/UpcomingReleasesTab";
import GameExperiencePanel from "./components/GameExperiencePanel";
import { getGameExperience, getExperienceRatingFields } from "./services/gameExperience";
import RatingSliderControl from "./components/RatingSlider";
import { saveStartupAppearance } from "./services/startupAppearance";
import ThemeTransition, { getThemeTransitionDuration } from "./components/ThemeTransition";
import TransitionSettings from "./components/TransitionSettings";
import SoundSettings from "./components/SoundSettings";
import { DEFAULT_SOUND_SETTINGS, normalizeSoundSettings, readLegacySoundSettings, playSound, applySoundSettings, stopAllSounds } from "./services/soundEffects";
import { SocialNavigation, SocialFriendsDirectory, FriendsActivityFeed } from "./components/SocialHub";
import FriendRequests from "./components/FriendRequests";
import useFriendships from "./components/useFriendships";
import {mergeConfirmedFriends,friendshipNotices} from "./services/friendships";
import useSocialControls from './components/useSocialControls';
import ProfileVisibilityChoices from './components/ProfileVisibilityChoices';
import SocialReportDialog from './components/SocialReportDialog';
import SocialReportsInbox from './components/SocialReportsInbox';
import SocialContactSettings,{BlockPlayerDialog} from './components/SocialContactSettings';
import FriendCommonGames from './components/FriendCommonGames';
import ShareGameToFriend from './components/ShareGameToFriend';
import SocialReactions,{useSocialReactions} from './components/SocialReactions';
import { readPublicSocialProfile } from "./services/socialProfiles";
import { getPlayerNotes, getFounderSignature, getPublicPlayerNotes } from "./services/playerNotes";
import ProfilePhotoEditor, { SocialAvatar } from "./components/ProfilePhoto";
import FavoritePlatforms, { FavoritePlatformTags } from "./components/FavoritePlatforms";
import FounderGames from "./components/FounderGames";
import { toPublicFounderGame } from "./services/gameCovers";
import { restorePublicBadge, toPublicBadge } from "./services/publicBadges";
import TrialRoom from "./components/TrialRoom";
import TesterFeedback from "./components/TesterFeedback";
import FeedbackInbox from "./components/FeedbackInbox";
import DeveloperTools from "./components/DeveloperTools";
import { sendTesterFeedback } from "./services/testerFeedback";
import {
  GameService,
  RawgRequestError,
  buildIgdbApiUrl,
  buildRawgApiUrl,
  fetchJsonWithTimeout,
} from "./services/gameService";
import {
  addDoc,
  onSnapshot,
  deleteDoc,
  getDoc,
  updateDoc,
  setDoc,
  arrayUnion,
  serverTimestamp,
} from "./services/d1Data";

import {
  Home,
  Lock,
  List,
  Play,
  Radio,
  Check,
  Search,
  CalendarDays,
  Library,
  Heart,
  Trophy,
  Swords,
  Medal,
  Moon,
  Settings,
  Puzzle,
  Joystick,
  Users,
  ArrowLeft,
  Newspaper,
  BookmarkPlus,
  Archive,
  BookOpen,
  CheckCircle2,
  Clock3,
  Crown,
  Flag,
  Gamepad2,
  Gem,
  Headphones,
  Landmark,
  Monitor,
  Package,
  PenLine,
  Bell,
  ScrollText,
  Sparkles,
  Star as StarIcon,
  Target,
  Timer,
  Wrench,
} from "lucide-react";

const PhysicalGameScanner=lazy(()=>import('./components/PhysicalGameScanner'));

async function rawgApiRequest(path, params = {}, options = {}) {
  if (typeof path === "string" && path.startsWith("/api/igdb")) {
    return GameService.igdb(path, params, options);
  }

  return GameService.rawg(path, params, options);
}

const WEEKLY_QUIZ_STORAGE_KEY = "checkpoint-weekly-quiz";
const CHECKPOINT_GOALS_STORAGE_KEY = "checkpoint-goals";
const CHECKPOINT_TRIAL_STORAGE_KEY = "checkpoint-trial-progress";
const DEFAULT_WEEKLY_QUIZ_PROGRESS = {
  answers: {},
  totalXP: 0,
  streak: 0,
  bestStreak: 0,
  lastCorrectWeek: "",
};

const DEFAULT_CHECKPOINT_GOAL_PROGRESS = {
  claimed: {},
  totalXP: 0,
};

function hashString(value) {
  return String(value)
    .split("")
    .reduce((hash, char) => ((hash << 5) - hash + char.charCodeAt(0)) | 0, 0);
}

function getStoredWeeklyQuizProgress() {
  try {
    const storedProgress = JSON.parse(
      localStorage.getItem(WEEKLY_QUIZ_STORAGE_KEY) || "{}"
    );

    return {
      ...DEFAULT_WEEKLY_QUIZ_PROGRESS,
      ...storedProgress,
      answers: {
        ...DEFAULT_WEEKLY_QUIZ_PROGRESS.answers,
        ...(storedProgress.answers || {}),
      },
    };
  } catch (error) {
    return DEFAULT_WEEKLY_QUIZ_PROGRESS;
  }
}

function storeWeeklyQuizProgress(progress) {
  localStorage.setItem(WEEKLY_QUIZ_STORAGE_KEY, JSON.stringify(progress));
}

function getStoredCheckpointGoalProgress() {
  try {
    const storedProgress = JSON.parse(
      localStorage.getItem(CHECKPOINT_GOALS_STORAGE_KEY) || "{}"
    );

    return {
      ...DEFAULT_CHECKPOINT_GOAL_PROGRESS,
      ...storedProgress,
      claimed: {
        ...DEFAULT_CHECKPOINT_GOAL_PROGRESS.claimed,
        ...(storedProgress.claimed || {}),
      },
    };
  } catch (error) {
    return DEFAULT_CHECKPOINT_GOAL_PROGRESS;
  }
}

function storeCheckpointGoalProgress(progress) {
  localStorage.setItem(CHECKPOINT_GOALS_STORAGE_KEY, JSON.stringify(progress));
}

function isAbortError(error) {
  const message = String(
    error?.message || error?.reason || error || ""
  ).toLowerCase();

  return (
    error?.name === "AbortError" ||
    message.includes("aborted") ||
    message.includes("user aborted") ||
    message.includes("the user aborted a request")
  );
}

/* ==================== STATS AVANCÉES & PROFIL JOUEUR ==================== */

const APP_RATING_MAX = 10;
const APP_TAB_LABELS = {
  home:"Accueil",arena:"L’Arène",news:"Actu",search:"Recherche",upcoming:"Sorties",deals:"Promos",live:"Live",social:"Social",
  library:"Bibliothèque",series:"Séries",hardware:"Matériel",favorites:"Sanctuaire",top5:"Top 5",profile:"Profil",options:"Options",
};
const APP_TAB_IDS = [
  "home",
  "arena",
  "news",
  "search",
  "upcoming",
  "deals",
  "live",
  "social",
  "library",
  "series",
  "hardware",
  "favorites",
  "top5",
  "profile",
  "options",
];

const DEFAULT_PUBLIC_SECTIONS = {
  photos: true,
  essential: true,
  identityGames: true,
  hardware: true,
  activity: true,
};

const DEFAULT_APP_OPTIONS = {
  searchIncludeFanGames: false,
  sound: DEFAULT_SOUND_SETTINGS,
  startTab: "home",
  visualEffects: "balanced",
  transitionMotion: "full",
  animatedBackground: true,
  appIcon: "theme",
  ratingDisplay: "number",
  headerMode: "standard",
  mobileNavMode: "standard",
  rememberLastTab: true,
  confirmDangerActions: true,
  afterAddAction: "stay",
  afterRatingAction: "stay",
  dealRegion: "FR",
  dealSources: {
    steam: true,
    epic: true,
    psn: true,
    xbox: true,
  },
  steamProfile: {
    profileInput: "",
    steamId: "",
    lastSyncedAt: "",
    lastGameCount: 0,
    lastImportedCount: 0,
    lastWishlistSyncedAt: "",
    lastWishlistCount: 0,
    lastWishlistImportedCount: 0,
  },
  xboxProfile: {
    connected: false,
    gamertag: "",
    xuid: "",
    avatar: "",
    connectedAt: "",
    lastCheckedAt: "",
    lastSyncedAt: "",
    lastGameCount: 0,
    lastImportedCount: 0,
  },
};

const APP_OPTIONS_STORAGE_KEY = "checkpoint-app-options";
const APP_OPTIONS_SCHEMA_VERSION = 2;
const PRIVATE_SETTINGS_COLLECTION = "playerPrivateSettings";
const PRIVATE_SETTINGS_DOC_ID = "main";

function normalizeConnectedProfile(profile = {}, defaults = {}) {
  return {
    ...defaults,
    ...normalizeObject(profile),
  };
}

function normalizeAppOptions(options = {}) {
  const source = normalizeObject(options);
  const startTab = APP_TAB_IDS.includes(source.startTab)
    ? source.startTab
    : DEFAULT_APP_OPTIONS.startTab;
  const visualEffects = ["calm", "balanced", "boost"].includes(source.visualEffects)
    ? source.visualEffects
    : DEFAULT_APP_OPTIONS.visualEffects;
  const ratingDisplay = ["number", "stars", "compact"].includes(source.ratingDisplay)
    ? source.ratingDisplay
    : DEFAULT_APP_OPTIONS.ratingDisplay;
  const afterRatingAction = ["stay", "list"].includes(source.afterRatingAction)
    ? source.afterRatingAction
    : DEFAULT_APP_OPTIONS.afterRatingAction;

  return {
    ...DEFAULT_APP_OPTIONS,
    ...source,
    searchIncludeFanGames: source.searchIncludeFanGames === true,
    homeLayout:normalizeHomeLayout(source.homeLayout),
    hiddenTabs:normalizeHiddenTabs(source.hiddenTabs),
    personalLists:normalizePersonalLists(source.personalLists),
    experiences:normalizeExperiences(source.experiences),
    engagement:normalizeEngagement(source.engagement),
    duelPalmares:normalizeDuelHistory(source.duelPalmares),
    editionLinks:normalizeEditionLinks(source.editionLinks),
    notifications:normalizeNotificationPreferences(source.notifications),
    schemaVersion: APP_OPTIONS_SCHEMA_VERSION,
    sound: normalizeSoundSettings(source.sound, readLegacySoundSettings(localStorage)),
    startTab,
    visualEffects,
    transitionMotion: ["light", "system"].includes(source.transitionMotion) ? "light" : "full",
    headerMode: ["standard", "compact", "minimal"].includes(source.headerMode) ? source.headerMode : DEFAULT_APP_OPTIONS.headerMode,
    animatedBackground:
      typeof source.animatedBackground === "boolean"
        ? source.animatedBackground
        : DEFAULT_APP_OPTIONS.animatedBackground,
    ratingDisplay,
    afterRatingAction,
    rememberLastTab:
      typeof source.rememberLastTab === "boolean"
        ? source.rememberLastTab
        : DEFAULT_APP_OPTIONS.rememberLastTab,
    confirmDangerActions:
      typeof source.confirmDangerActions === "boolean"
        ? source.confirmDangerActions
        : DEFAULT_APP_OPTIONS.confirmDangerActions,
    dealSources: {
      ...DEFAULT_APP_OPTIONS.dealSources,
      ...normalizeObject(source.dealSources),
    },
    steamProfile: normalizeConnectedProfile(
      source.steamProfile,
      DEFAULT_APP_OPTIONS.steamProfile
    ),
    xboxProfile: normalizeConnectedProfile(
      source.xboxProfile,
      DEFAULT_APP_OPTIONS.xboxProfile
    ),
  };
}

function storeAppOptions(options) {
  const normalizedOptions = normalizeAppOptions(options);
  localStorage.setItem(APP_OPTIONS_STORAGE_KEY, JSON.stringify(normalizedOptions));
  return normalizedOptions;
}

const GAME_RATING_KEYS = [
  "rating",
  "ratingGraphics",
  "ratingGameplay",
  "ratingStory",
  "ratingSound",
  "ratingLongevity",
  "ostRating",
  "ratingOpenWorld",
  "ratingGunplay",
  "ratingDriving",
  "ratingCombat",
  "ratingExploration",
  "ratingChallenge",
  "ratingMultiplayer",
  "ratingStealth",
  "ratingPuzzle",
  "ratingPlatforming",
  "ratingHorror",
];

function clampRating(value, max = APP_RATING_MAX) {
  const rating = Number(value) || 0;
  return Math.max(0, Math.min(max, Math.round(rating * 10) / 10));
}

function getRatingDisplayMode() {
  try {
    const saved = JSON.parse(localStorage.getItem(APP_OPTIONS_STORAGE_KEY) || "{}");
    return normalizeAppOptions(saved).ratingDisplay;
  } catch (error) {
    return DEFAULT_APP_OPTIONS.ratingDisplay;
  }
}

function getStoredAppOptions() {
  try {
    const saved = JSON.parse(localStorage.getItem(APP_OPTIONS_STORAGE_KEY) || "{}");
    return storeAppOptions(saved);
  } catch (error) {
    return storeAppOptions(DEFAULT_APP_OPTIONS);
  }
}

function formatRating10(value, emptyLabel = "Pas note") {
  const rating = clampRating(value);
  if (!rating) return emptyLabel;

  const displayMode = getRatingDisplayMode();

  if (displayMode === "stars") {
    const stars = Math.max(1, Math.round(rating / 2));
    return `${"★".repeat(stars)}${"☆".repeat(5 - stars)}`;
  }

  if (displayMode === "compact") {
    return String(rating);
  }

  return `${rating}/10`;
}

function getGameRating(game) {
  return clampRating(game?.rating);
}

function normalizeGameRatings(game = {}) {
  const normalizedGame = { ...game };

  GAME_RATING_KEYS.forEach((key) => {
    if (normalizedGame[key] !== undefined) {
      normalizedGame[key] = clampRating(normalizedGame[key]);
    }
  });

  return {
    ...normalizedGame,
    name: normalizedGame.name || "Jeu sans titre",
    status: normalizedGame.status || "wishlist",
    completed: Boolean(normalizedGame.completed),
    favorite: Boolean(normalizedGame.favorite),
    sanctuary: Boolean(normalizedGame.sanctuary),
    physicalOwned: Boolean(normalizedGame.physicalOwned),
    physicalEdition: normalizedGame.physicalEdition || "",
    physicalCondition: normalizedGame.physicalCondition || "",
    physicalRarity: normalizedGame.physicalRarity || "",
    physicalEstimatedValue: Number(normalizedGame.physicalEstimatedValue) || 0,
    physicalPhoto: normalizedGame.physicalPhoto || "",
    physicalNotes: normalizedGame.physicalNotes || "",
    physicalUpdatedAt: normalizedGame.physicalUpdatedAt || "",
    platforms: normalizeArray(normalizedGame.platforms).map(String),
    genreNames: normalizeArray(normalizedGame.genreNames).map(String),
    tags: normalizeArray(normalizedGame.tags),
    dlcs: normalizeArray(normalizedGame.dlcs),
    image: getGameCoverImage(normalizedGame),
    source: normalizedGame.source || "manual",
  };
}

const GAME_GENRE_SIGNAL_RULES = [
  { label: "RPG", weight: 1.35, keywords: ["rpg", "role playing", "role-playing", "jrpg", "arpg", "soulslike"] },
  { label: "Shooter", weight: 1.35, keywords: ["shooter", "fps", "tps", "first person", "third person", "gunplay"] },
  { label: "Course", weight: 1.3, keywords: ["racing", "driving", "course", "pilotage", "motorsport", "rally"] },
  { label: "Horreur", weight: 1.28, keywords: ["horror", "survival horror", "survie", "terror", "scary"] },
  { label: "Stratégie", weight: 1.25, keywords: ["strategy", "tactical", "tactics", "rts", "4x", "gestion"] },
  { label: "Simulation", weight: 1.18, keywords: ["simulation", "simulator", "management", "builder"] },
  { label: "Plateforme", weight: 1.18, keywords: ["platformer", "platform", "metroidvania"] },
  { label: "Puzzle", weight: 1.16, keywords: ["puzzle", "logic", "escape"] },
  { label: "Sport", weight: 1.12, keywords: ["sports", "sport", "football", "basketball", "tennis"] },
  { label: "Combat", weight: 1.08, keywords: ["fighting", "fight", "brawler", "beat em up", "beat-em-up"] },
  { label: "Aventure", weight: 0.9, keywords: ["adventure", "action-adventure", "narrative", "story rich"] },
  { label: "Indé", weight: 0.82, keywords: ["indie", "independent"] },
  { label: "Action", weight: 0.58, keywords: ["action", "arcade"] },
];

function getGameGenreSignals(game = {}) {
  const text = [
    game.name,
    game.slug,
    ...(game.genreNames || []),
    ...(game.tags || []).map((tag) => tag?.name || tag),
  ]
    .filter(Boolean)
    .map((value) => normalizeIdentityText(String(value)))
    .join(" ");

  const scores = {};

  GAME_GENRE_SIGNAL_RULES.forEach((rule) => {
    const matched = rule.keywords.some((keyword) =>
      text.includes(normalizeIdentityText(keyword))
    );

    if (matched) {
      scores[rule.label] = Math.max(scores[rule.label] || 0, rule.weight);
    }
  });

  const hasSpecificGenre = Object.entries(scores).some(
    ([label, score]) => label !== "Action" && score >= 1
  );

  if (hasSpecificGenre && scores.Action) {
    scores.Action = Math.min(scores.Action, 0.25);
  }

  if (!Object.keys(scores).length && game.genreNames?.[0]) {
    scores[game.genreNames[0]] = 0.75;
  }

  return Object.entries(scores)
    .sort((a, b) => b[1] - a[1])
    .map(([label, weight]) => ({ label, weight }));
}

function getAdvancedStats(games) {
  const finished = games.filter(isGameFinishedStatus);

  const totalPlaytime = games.reduce((acc, g) => acc + (g.playtime || 0), 0);

  const ratedGames = games.filter((g) => getGameRating(g) > 0);
  const avgRating =
    ratedGames.reduce((acc, g) => acc + getGameRating(g), 0) /
    (ratedGames.length || 1);

  const avgDifficulty = (() => {
    const map = { casual: 1, normal: 2, hard: 3, hardcore: 4 };
    const values = games.map(g => map[g.difficulty]).filter(Boolean);
    if (!values.length) return 0;
    return values.reduce((a, b) => a + b, 0) / values.length;
  })();

  const genres = {};
  const platforms = {};

  games.forEach(g => {
    getGameGenreSignals(g).forEach(({ label, weight }) => {
      genres[label] = (genres[label] || 0) + weight;
    });

    g.platformNames?.forEach(p => {
      platforms[p] = (platforms[p] || 0) + 1;
    });
  });

  const topGenres = Object.entries(genres)
    .sort((a, b) => b[1] - a[1])
    .map(([label, score]) => [label, Math.max(1, Math.round(score))])
    .slice(0, 3);

  const topPlatforms = Object.entries(platforms)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 3);

  return {
  totalPlaytime,
  avgRating,
  avgDifficulty,
  topGenres,
  topPlatforms,
  finishedCount: finished.length,
  avgXpPerFinished:
    finished.length > 0
      ? Math.round(
          finished.reduce((acc, game) => acc + calculateXP(game), 0) / finished.length
        )
      : 0,
};
}

function formatFullDate(date) {
  if (!date) return "Date inconnue";

  return new Date(date).toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
}

function getReleaseCountdown(date) {
  if (!date) return "";

  const today = new Date();
  const releaseDate = new Date(date);

  today.setHours(0, 0, 0, 0);
  releaseDate.setHours(0, 0, 0, 0);

  const diffTime = releaseDate - today;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (Number.isNaN(diffDays)) return "";
  if (diffDays > 1) return `Sort dans ${diffDays} jours`;
  if (diffDays === 1) return "Sort demain";
  if (diffDays === 0) return "Sort aujourd’hui";
  if (diffDays < 0) return "Déjà sorti";

  return "";
}

function isFutureReleaseDate(date, referenceDate = new Date()) {
  if (!date) return false;

  const releaseDate = new Date(date);
  if (Number.isNaN(releaseDate.getTime())) return false;

  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  releaseDate.setHours(0, 0, 0, 0);

  return releaseDate >= today;
}

function getDaysUntil(date, referenceDate = new Date()) {
  if (!date) return null;

  const target = new Date(date);
  if (Number.isNaN(target.getTime())) return null;

  const today = new Date(referenceDate);
  today.setHours(0, 0, 0, 0);
  target.setHours(0, 0, 0, 0);

  return Math.ceil((target - today) / (1000 * 60 * 60 * 24));
}

function getUsefulNotifications({
  games = [],
  upcomingGames = [],
  pendingCheckpointLevel = null,
  upcomingSourceStatus = "idle",
} = {}) {
  const notifications = [];
  const add = (notification) => {
    if (!notification?.id || notifications.some((item) => item.id === notification.id)) return;
    notifications.push(notification);
  };

  if (pendingCheckpointLevel) {
    add({
      id: `checkpoint-${pendingCheckpointLevel}`,
      type: "trial",
      priority: 100,
      title: "Nouvelle epreuve disponible",
      message: `Le Checkpoint ${pendingCheckpointLevel} t'attend dans la Salle des Epreuves.`,
      cta: "Entrer",
      tab: "profile",
      tone: "legendary",
    });
  }

  const unratedGames = games
    .filter((game) => game.status !== "wishlist" && getGameRating(game) <= 0)
    .sort((a, b) => {
      const dateA = parseActivityDate(a.updatedAt || a.createdAt || a.released)?.getTime() || 0;
      const dateB = parseActivityDate(b.updatedAt || b.createdAt || b.released)?.getTime() || 0;
      return dateB - dateA;
    });

  if (unratedGames.length >= 3) {
    add({
      id: `unrated-${unratedGames.slice(0, 3).map((game) => game.id || game.name).join("-")}`,
      type: "rating",
      priority: 72,
      title: `${Math.min(unratedGames.length, 9)} jeux sans note`,
      message: `${unratedGames[0].name} et quelques jeux recents attendent une note.`,
      cta: "Noter",
      tab: "library",
      tone: "focus",
    });
  }

  const weekReleases = upcomingGames
    .map((game) => ({ game, days: getDaysUntil(game.released) }))
    .filter(({ days }) => days !== null && days >= 0 && days <= 7)
    .sort((a, b) => a.days - b.days);

  if (weekReleases.length > 0) {
    add({
      id: `release-week-${weekReleases[0].game.id || weekReleases[0].game.name}`,
      type: "release",
      priority: 66,
      title: `${weekReleases.length} sortie${weekReleases.length > 1 ? "s" : ""} cette semaine`,
      message: `${weekReleases[0].game.name} - ${getReleaseCountdown(weekReleases[0].game.released) || formatFullDate(weekReleases[0].game.released)}.`,
      cta: "Voir",
      tab: "upcoming",
      tone: "release",
    });
  }

  const wishlistSoon = games
    .filter((game) => game.status === "wishlist")
    .map((game) => ({ game, days: getDaysUntil(game.released) }))
    .filter(({ days }) => days !== null && days >= 0 && days <= 30)
    .sort((a, b) => a.days - b.days);

  if (wishlistSoon.length > 0) {
    add({
      id: `wishlist-soon-${wishlistSoon[0].game.id || wishlistSoon[0].game.name}`,
      type: "wishlist",
      priority: 58,
      title: "Wishlist a surveiller",
      message: `${wishlistSoon[0].game.name} approche : ${getReleaseCountdown(wishlistSoon[0].game.released) || formatFullDate(wishlistSoon[0].game.released)}.`,
      cta: "Ouvrir",
      tab: "library",
      tone: "wishlist",
    });
  }

  if (upcomingSourceStatus === "unavailable") {
    add({
      id: "source-upcoming-unavailable",
      type: "source",
      priority: 20,
      title: "Source sorties temporairement indisponible",
      message: "Les donnees deja chargees restent utilisables, mais la mise a jour externe est en attente.",
      cta: "Sorties",
      tab: "upcoming",
      tone: "muted",
    });
  }

  return notifications.sort((a, b) => b.priority - a.priority).slice(0, 8);
}

function getGenreChartData(games) {
  const genres = {};

  games.forEach((game) => {
    getGameGenreSignals(game).forEach(({ label, weight }) => {
      genres[label] = (genres[label] || 0) + weight;
    });
  });

  return Object.entries(genres)
    .sort((a, b) => b[1] - a[1])
    .map(([label, score]) => [label, Math.max(1, Math.round(score))])
    .slice(0, 5);
}


function AdvancedStats({ games }) {
  const stats = getAdvancedStats(games);
  const profile = getPlayerProfile(games);

  return (
    <div className="advanced-stats">
      <div className="stat-card wide profile-card">
        <div className="stat-value">{profile.title}</div>
        <div className="stat-label">{profile.subtitle}</div>
      </div>

      <div className="stat-card">
        <div className="stat-value">{stats.totalPlaytime}h</div>
        <div className="stat-label">Temps total</div>
      </div>

      <div className="stat-card">
        <div className="stat-value">
          {stats.avgRating ? stats.avgRating.toFixed(1) : "-"}
        </div>
        <div className="stat-label">Note moyenne</div>
      </div>

      <div className="stat-card">
        <div className={`difficulty-badge difficulty-${Math.round(stats.avgDifficulty || 0)}`}>
          {stats.avgDifficulty ? difficultyLabel(stats.avgDifficulty) : "-"}
        </div>
        <div className="stat-label">Difficulté moyenne</div>
      </div>

      <div className="stat-card">
        <div className="stat-value">
          {stats.avgXpPerFinished ? `${stats.avgXpPerFinished} XP` : "-"}
        </div>
        <div className="stat-label">XP moyenne / jeu</div>
      </div>

      <div className="stat-card wide">
        <div className="stat-value">
          {stats.topGenres.map((g) => g[0]).join(" • ") || "-"}
        </div>
        <div className="stat-label">Genres favoris</div>
      </div>

      <div className="stat-card wide">
        <div className="stat-value">
          {stats.topPlatforms.map((p) => p[0]).join(" • ") || "-"}
        </div>
        <div className="stat-label">Plateformes principales</div>
      </div>
    </div>
  );
}

function SearchGameDetailModal({ game, onClose, onWishlist, onCollection, libraryReturn = false }) {
  useDetailReturn(Boolean(game));
  const [details, setDetails] = useState(null);
  const [screenshots, setScreenshots] = useState([]);
  const [movies, setMovies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [detailError,setDetailError]=useState(false);
  const [detailRetry,setDetailRetry]=useState(0);

  useEffect(() => {
  if (!game) return;

  document.body.classList.add("modal-open");
  const escape=event=>{if(event.key==='Escape')onClose();};
  window.addEventListener('keydown',escape);

  return () => {
    window.removeEventListener('keydown',escape);
    if (!document.querySelector('.modal-backdrop')) document.body.classList.remove("modal-open");
  };
}, [game?.id, game?.review, game?.ostRating]);

  useEffect(() => {
    if (!game) return;
    let active = true;
    setDetails(null);setScreenshots([]);setMovies([]);setDetailError(false);
    const fetchDetails = async () => {
      try {
        setLoading(true);

        const [detailsData, screenshotsData, moviesData] = await Promise.all([
          rawgApiRequest(`/games/${encodeURIComponent(game.id)}`, { lang: "fr" }),
          rawgApiRequest(`/games/${encodeURIComponent(game.id)}/screenshots`),
          rawgApiRequest(`/games/${encodeURIComponent(game.id)}/movies`),
        ]);

        if (!active) return;
        setDetails(detailsData);
        setScreenshots(screenshotsData.results || []);
        setMovies(moviesData.results || []);
      } catch (error) {
        if (isAbortError(error)) return;
        console.warn("Erreur détail RAWG ignorée :", error);
        if(active)setDetailError(true);
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchDetails();
    return () => { active = false; };
  }, [game,detailRetry]);

  if (!game) return null;

  const buildPayload = (status) => ({
    discoveryMetadata: getDiscoveryMetadata(game, details),
    name: game.name,
    rating: 0,
    favorite: false,
    sanctuary: false,
    image: game.background_image || details?.background_image || "",
    status,
    released: game.released || details?.released || "",
    platformNames:
      game.platforms?.map((p) => p.platform.name) ||
      details?.platforms?.map((p) => p.platform.name) ||
      [],
    genreNames:
      game.genres?.map((g) => g.name) ||
      details?.genres?.map((g) => g.name) ||
      [],
    playtime: game.playtime || details?.playtime || null,
    difficulty: getSuggestedDifficulty(details || game),
    progressStatus: "not_started",
    playtimeRange: "none",
    review: "",
    ratingGraphics: 0,
    ratingGameplay: 0,
    ratingStory: 0,
    ratingSound: 0,
    ostRating: 0,
    ratingLongevity: 0,
    rawgId: game.id,
  });

  const trailer = movies[0]?.data?.max || movies[0]?.data?.["480"];
  const youtubeMatch = String(trailer || "").match(
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&?/]+)/
  );
  const youtubeEmbedUrl = youtubeMatch
    ? `https://www.youtube.com/embed/${youtubeMatch[1]}`
    : "";

  return (
    <div className={`search-detail-backdrop ${libraryReturn ? 'library-catalogue-backdrop' : ''}`} onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={`Fiche complète · ${game.name}`} className="search-detail-modal" onKeyDown={event=>{if(event.key==='Escape'){event.stopPropagation();onClose();}}} onClick={(e) => e.stopPropagation()}>
        <button className="modal-close-btn" type="button" aria-label="Fermer la fiche du jeu" onClick={onClose}>
          ✕
        </button>

        {loading ? (
          <Loader text="Chargement des infos..." />
        ) : (
          <>
            <div
              className="search-detail-hero premium"
              style={{
                backgroundImage: `url(${details?.background_image || game.background_image})`,
              }}
            >
              <div className="search-detail-hero-blur" />

              <img
                src={details?.background_image || game.background_image || undefined}
                alt={game.name}
                className="search-detail-cover"
              />

              <div className="search-detail-hero-overlay">
                <h2>{details?.name || game.name}</h2>
                <div className="search-detail-meta">
                  {details?.released || game.released || "Date inconnue"} • ⭐{" "}
                  {details?.rating || game.rating || "-"}
                </div>
              </div>
            </div>

            <div className="search-detail-content">
              {detailError&&<div role="status"><p>Les informations du catalogue n’ont pas pu être chargées.</p><button type="button" onClick={()=>setDetailRetry(value=>value+1)}>Réessayer</button></div>}
              {libraryReturn ? <div className="search-detail-actions"><button type="button" autoFocus onClick={onClose}>← Retour à ma fiche</button></div> : <div className="search-detail-actions">
                <button
                  type="button"
                  onClick={() => onWishlist(buildPayload("wishlist"))}
                >
                  <BookmarkPlus size={18} />
                  Wishlist
                </button>

                <button
                  type="button"
                  onClick={() => onCollection(buildPayload("collection"))}
                >
                  <Library size={18} />
                  Collection
                </button>
              </div>}

              <div className="search-detail-section">
                <h3>Synopsis</h3>
                <p>
                  {details?.description_raw ||
                    "Aucun synopsis disponible en français pour ce jeu."}
                </p>
              </div>

              {screenshots.length > 0 && (
                <div className="search-detail-section">
                  <h3>Images</h3>
                  <div className="screenshots-carousel">
                    {screenshots.slice(0, 6).map((shot) => (
                      <img key={shot.id} src={shot.image} alt="" />
                    ))}
                  </div>
                </div>
              )}

              {(youtubeEmbedUrl || trailer) && (
                <div className="search-detail-section">
                  <h3>Bande-annonce</h3>
                  {youtubeEmbedUrl ? (
                    <iframe
                      className="search-detail-video"
                      src={youtubeEmbedUrl}
                      title={`Bande-annonce ${details?.name || game.name}`}
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                      allowFullScreen
                    />
                  ) : (
                    <video controls className="search-detail-video">
                      <source src={trailer} />
                    </video>
                  )}
                </div>
              )}

              <div className="search-detail-section">
                <h3>Infos</h3>
                <div className="search-detail-info-grid">
                  <div>
                    <strong>Genres</strong>
                    <span>{details?.genres?.map((g) => g.name).join(" • ") || "—"}</span>
                  </div>

                  <div>
                    <strong>Plateformes</strong>
                    <span>
                      {details?.platforms?.map((p) => p.platform.name).slice(0, 6).join(" • ") ||
                        "—"}
                    </span>
                  </div>

                  <div>
                    <strong>Développeur</strong>
                    <span>{details?.developers?.map((d) => d.name).join(" • ") || "—"}</span>
                  </div>

                  <div>
                    <strong>Éditeur</strong>
                    <span>{details?.publishers?.map((p) => p.name).join(" • ") || "—"}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GenreStatsChart({ games }) {
  const data = getGenreChartData(games);

  if (!data.length) {
    return (
      <div className="search-panel">
        <h2 className="panel-title">Genres les plus joués</h2>
        <div className="option-value">Pas assez de données pour afficher le graphique.</div>
      </div>
    );
  }

  const maxValue = data[0][1];

  return (
    <div className="search-panel">
      <h2 className="panel-title">Genres les plus joués</h2>

      <div className="genre-chart">
        {data.map(([genre, count]) => (
          <div key={genre} className="genre-chart-row">
            <div className="genre-chart-label">{genre}</div>

            <div className="genre-chart-bar-wrap">
              <div
                className="genre-chart-bar"
                style={{ width: `${(count / maxValue) * 100}%` }}
              />
            </div>

            <div className="genre-chart-value">{count}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function getPlayerProfile(games) {
  const { title, subtitle } = getPlayerNotes(games);
  return { title, subtitle };
}

function getPlayerAnalysis(games = [], hardware = []) {
  return getPlayerNotes(games, hardware);
}

function getTimelineYear(value) {
  if (!value) return null;

  const text = String(value);
  const match = text.match(/\b(19|20)\d{2}\b/);

  if (match) return Number(match[0]);

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date.getFullYear();
}

function getGameTimelineYear(game = {}) {
  return (
    getTimelineYear(game.completedAt) ||
    getTimelineYear(game.finishedAt) ||
    getTimelineYear(game.updatedAt) ||
    getTimelineYear(game.createdAt) ||
    getTimelineYear(game.released) ||
    getTimelineYear(game.year)
  );
}

function getHardwareTimelineYear(item = {}) {
  return (
    getTimelineYear(item.acquiredYear) ||
    getTimelineYear(item.acquiredAt) ||
    getTimelineYear(item.obtainedAt) ||
    getTimelineYear(item.updatedAt) ||
    getTimelineYear(item.createdAt) ||
    getTimelineYear(item.releaseDate) ||
    getTimelineYear(item.releaseYear) ||
    getTimelineYear(item.year)
  );
}

function getGamingTimeline(games = [], hardware = [], socialProfile = {}) {
  const events = [];
  const seen = new Set();
  const addEvent = (event) => {
    const year = Number(event.year) || null;
    const id = event.id || `${event.type}-${year || "now"}-${event.title}`;

    if (!event.title || seen.has(id)) return;
    seen.add(id);
    events.push({
      ...event,
      id,
      year,
      yearLabel: event.yearLabel || (year ? String(year) : "Maintenant"),
      sortYear: year || 9999,
    });
  };

  const identityGameIds = Array.isArray(socialProfile.identityGameIds)
    ? socialProfile.identityGameIds.slice(0, 3).map(String)
    : [];
  const identityGames = identityGameIds
    .map((id) => games.find((game) => String(game.id) === id))
    .filter(Boolean);

  identityGames.forEach((game, index) => {
    addEvent({
      id: `identity-${game.id || game.name}`,
      type: "identity",
      icon: "CP",
      kicker: `Jeu fondateur ${index + 1}`,
      title: game.name,
      detail:
        game.platforms?.length > 0
          ? game.platforms.slice(0, 2).join(" / ")
          : formatRating10(getGameRating(game), "Jeu marquant"),
      image: game.image || "",
      year: getGameTimelineYear(game),
      tone: "legendary",
    });
  });

  games
    .filter((game) => Number(game.gotyYear) > 0)
    .sort((a, b) => Number(a.gotyYear) - Number(b.gotyYear))
    .forEach((game) => {
      addEvent({
        id: `goty-${game.gotyYear}-${game.id || game.name}`,
        type: "goty",
        icon: "G",
        kicker: `GOTY perso ${game.gotyYear}`,
        title: game.name,
        detail: formatRating10(getGameRating(game), "Choix personnel"),
        image: game.image || "",
        year: Number(game.gotyYear),
        tone: "gold",
      });
    });

  const markedGames = games
    .filter((game) => !identityGameIds.includes(String(game.id)) && !Number(game.gotyYear))
    .filter((game) => game.favorite || getGameRating(game) >= 9 || isGameFinishedStatus(game))
    .map((game) => ({
      game,
      year: getGameTimelineYear(game),
      score:
        getGameRating(game) * 12 +
        (game.favorite ? 22 : 0) +
        (isGameFinishedStatus(game) ? 8 : 0),
    }))
    .filter((item) => item.year)
    .sort((a, b) => b.score - a.score)
    .slice(0, 8);

  markedGames.forEach(({ game, year }) => {
    addEvent({
      id: `marked-${year}-${game.id || game.name}`,
      type: "game",
      icon: "J",
      kicker: game.favorite ? "Coup de cœur" : isGameFinishedStatus(game) ? "Aventure terminée" : "Belle découverte",
      title: game.name,
      detail: `${formatRating10(getGameRating(game), "non note")} - ${
        game.genres?.[0]?.name || game.genres?.[0] || game.platforms?.[0] || "bibliotheque"
      }`,
      image: game.image || "",
      year,
      tone: game.favorite ? "favorite" : "standard",
    });
  });

  hardware
    .filter((item) => {
      const status = normalizeIdentityText(item.status || "");
      const type = normalizeIdentityText(item.type || "");
      return (
        type.includes("console") ||
        type.includes("vr") ||
        type.includes("display") ||
        status.includes("poss") ||
        status.includes("hist")
      );
    })
    .map((item) => ({
      item,
      year: getHardwareTimelineYear(item),
      score:
        (getHardwareAverageRating(item) || 0) * 10 +
        (normalizeIdentityText(item.status || "").includes("poss") ? 14 : 0),
    }))
    .filter((entry) => entry.year)
    .sort((a, b) => b.score - a.score)
    .slice(0, 6)
    .forEach(({ item, year }) => {
      addEvent({
        id: `hardware-${year}-${item.id || item.name}`,
        type: "hardware",
        icon: "M",
        kicker: "Materiel repere",
        title: item.name,
        detail: [item.brand, item.category || item.type].filter(Boolean).join(" - "),
        image: item.image || "",
        year,
        tone: "hardware",
      });
    });

  const finishedCount = games.filter(isGameFinishedStatus).length;
  const ratedCount = games.filter((game) => getGameRating(game) > 0).length;
  const currentHardware = getCurrentOwnedHardware(hardware);

  if (games.length || currentHardware.length) {
    addEvent({
      id: "timeline-current-checkpoint",
      type: "checkpoint",
      icon: "XP",
      kicker: "Checkpoint actuel",
      title: "Univers en construction",
      detail: `${games.length} jeux, ${finishedCount} termines, ${ratedCount} notes, ${currentHardware.length} materiels actuels`,
      year: null,
      yearLabel: "Aujourd'hui",
      tone: "current",
    });
  }

  return events
    .sort((a, b) => {
      if (a.sortYear !== b.sortYear) return a.sortYear - b.sortYear;
      const typeOrder = { hardware: 0, identity: 1, goty: 2, game: 3, checkpoint: 4 };
      return (typeOrder[a.type] ?? 9) - (typeOrder[b.type] ?? 9);
    });
}



function normalizeIdentityText(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "");
}

function getIdentityPlayerTitle(identityGames = []) {
  return getFounderSignature(identityGames);
}

function formatGameRating10(rating) {
  return formatRating10(rating, "");
}

const createPixelEffect = (x, y) => {
  const pixel = document.createElement("div");
  pixel.className = "pixel-click";

  pixel.style.left = `${x}px`;
  pixel.style.top = `${y}px`;

  document.body.appendChild(pixel);

  setTimeout(() => {
    pixel.remove();
  }, 400);
};

/* -------------------- HELPERS -------------------- */

/* ==================== HELPERS / FORMATAGE ==================== */

function formatReleaseDate(dateStr) {
  if (!dateStr) return "Date inconnue";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("fr-FR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

function getMonthKey(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return "";
  const month = String(d.getMonth() + 1).padStart(2, "0");
  return `${d.getFullYear()}-${month}`;
}

function formatMonthLabel(monthKey) {
  if (!monthKey) return "Tous les mois";
  const [year, month] = monthKey.split("-");
  const d = new Date(Number(year), Number(month) - 1, 1);
  return d.toLocaleDateString("fr-FR", {
    month: "long",
    year: "numeric",
  });
}

function averageDetailedRating(game) {
  const values = getGameRatingBreakdown(game).map((field) => field.value).filter((v) => v > 0);

  if (values.length === 0) return 0;
  const avg = values.reduce((sum, v) => sum + v, 0) / values.length;
  return Math.round(avg * 10) / 10;
}

const BASE_GAME_RATING_FIELDS = [
  { key: "ratingGraphics", label: "Graphismes", hint: "Direction visuelle, technique, lisibilité" },
  { key: "ratingGameplay", label: "Gameplay", hint: "Feeling, rythme, plaisir manette en main" },
  { key: "ratingStory", label: "Histoire", hint: "Écriture, personnages, mise en scène" },
  { key: "ratingSound", label: "Audio", hint: "Sound design, doublage, ambiance sonore" },
  { key: "ratingLongevity", label: "Durée de vie", hint: "Contenu, rejouabilité, rythme global" },
];

function getGameRatingBreakdown(game = {}) {
  return getExperienceRatingFields(game, BASE_GAME_RATING_FIELDS).map((field) => ({
    ...field,
    value: clampRating(game[field.key]),
  }));
}

function getGameDetailedRatingSummary(game = {}) {
  const baseFields = getGameRatingBreakdown(game);
  const ratedBaseFields = baseFields.filter((field) => field.value > 0);
  const contextualFields = (getGameExperience(game) === "completed" ? getContextualRatingFields(game) : []).map((field) => ({
    ...field,
    value: clampRating(game[field.key]),
  }));
  const ratedContextualFields = contextualFields.filter((field) => field.value > 0);
  const rankedFields = [...ratedBaseFields, ...ratedContextualFields].sort(
    (a, b) => b.value - a.value
  );

  return {
    baseFields,
    contextualFields,
    ratedBaseFields,
    ratedContextualFields,
    bestField: rankedFields[0],
    completion: baseFields.length
      ? Math.round((ratedBaseFields.length / baseFields.length) * 100)
      : 0,
  };
}

  /* -------------------- BADGES V2 -------------------- */

/* ==================== BADGES ==================== */

const BADGES = [
  { id: "first_game", icon: "🎮", name: "Premier pas", desc: "Ajouter 1 jeu", rarity: "common", condition: (s) => s.total >= 1 },
  { id: "collector_10", icon: "📚", name: "Collectionneur", desc: "Avoir 10 jeux", rarity: "common", condition: (s) => s.total >= 10 },
  { id: "collector_25", icon: "🗂️", name: "Archiviste", desc: "Avoir 25 jeux", rarity: "rare", condition: (s) => s.total >= 25 },
  { id: "collector_50", icon: "🏛️", name: "Bibliothèque vivante", desc: "Avoir 50 jeux", rarity: "epic", condition: (s) => s.total >= 50 },
  { id: "collector_250", icon: "🏰", name: "Grande bibliothèque", desc: "Avoir 250 jeux", rarity: "legendary", condition: (s) => s.total >= 250 },
  { id: "collector_500", icon: "🌌", name: "Univers personnel", desc: "Avoir 500 jeux", rarity: "mythic", condition: (s) => s.total >= 500 },

  { id: "finisher_5", icon: "🏁", name: "Finisseur", desc: "Terminer 5 jeux", rarity: "common", condition: (s) => s.finished >= 5 },
  { id: "finisher_15", icon: "🔥", name: "Joueur sérieux", desc: "Terminer 15 jeux", rarity: "rare", condition: (s) => s.finished >= 15 },
  { id: "finisher_30", icon: "⚔️", name: "Machine à finir", desc: "Terminer 30 jeux", rarity: "epic", condition: (s) => s.finished >= 30 },
  { id: "finisher_250", icon: "🏆", name: "Backlog dompté", desc: "Terminer 250 jeux", rarity: "legendary", condition: (s) => s.finished >= 250 },
  { id: "finisher_500", icon: "⚡", name: "Archive absolue", desc: "Terminer 500 jeux", rarity: "mythic", condition: (s) => s.finished >= 500 },

  { id: "favorites_3", icon: "💖", name: "Coup de cœur", desc: "Avoir 3 favoris", rarity: "common", condition: (s) => s.favorites >= 3 },
  { id: "favorites_8", icon: "💜", name: "Passionné", desc: "Avoir 8 favoris", rarity: "rare", condition: (s) => s.favorites >= 8 },
  { id: "favorites_15", icon: "🌟", name: "Fan ultime", desc: "Avoir 15 favoris", rarity: "epic", condition: (s) => s.favorites >= 15 },
  { id: "favorites_25", icon: "💫", name: "Panthéon personnel", desc: "Avoir 25 favoris", rarity: "legendary", condition: (s) => s.favorites >= 25 },

  { id: "reviews_3", icon: "✍️", name: "Critique", desc: "Écrire 3 avis", rarity: "common", condition: (s) => s.reviews >= 3 },
  { id: "reviews_10", icon: "📝", name: "Testeur pro", desc: "Écrire 10 avis", rarity: "rare", condition: (s) => s.reviews >= 10 },
  { id: "reviews_25", icon: "📜", name: "Plume légendaire", desc: "Écrire 25 avis", rarity: "legendary", condition: (s) => s.reviews >= 25 },
  { id: "rated_100", icon: "⭐", name: "Œil critique", desc: "Noter 100 jeux", rarity: "legendary", condition: (s) => s.ratedGames >= 100 },
  { id: "rated_250", icon: "💎", name: "Juge du Panthéon", desc: "Noter 250 jeux", rarity: "mythic", condition: (s) => s.ratedGames >= 250 },

  { id: "hours_50", icon: "⏱️", name: "Session longue", desc: "Cumuler 50 h", rarity: "common", condition: (s) => s.hours >= 50 },
  { id: "hours_100", icon: "⏳", name: "Marathon", desc: "Cumuler 100 h", rarity: "rare", condition: (s) => s.hours >= 100 },
  { id: "hours_250", icon: "🌙", name: "No-life", desc: "Cumuler 250 h", rarity: "epic", condition: (s) => s.hours >= 250 },
  { id: "hours_500", icon: "👑", name: "Légende vivante", desc: "Cumuler 500 h", rarity: "legendary", condition: (s) => s.hours >= 500 },

  { id: "level_10", icon: "⭐", name: "Joueur confirmé", desc: "Atteindre le niveau 10", rarity: "common", condition: (s) => s.level >= 10 },
  { id: "level_25", icon: "🥈", name: "Expert", desc: "Atteindre le niveau 25", rarity: "rare", condition: (s) => s.level >= 25 },
  { id: "level_50", icon: "🥇", name: "Légende", desc: "Atteindre le niveau 50", rarity: "epic", condition: (s) => s.level >= 50 },
  { id: "level_100", icon: "💎", name: "Suprême", desc: "Atteindre le niveau 100", rarity: "legendary", condition: (s) => s.level >= 100 },
  { id: "hardware_3", icon: "\uD83E\uDDF0", name: "Setup lance", desc: "Posseder 3 materiels", rarity: "common", condition: (s) => s.hardware >= 3 },
  { id: "hardware_8", icon: "\uD83D\uDEE0\uFE0F", name: "Coin gaming", desc: "Posseder 8 materiels", rarity: "rare", condition: (s) => s.hardware >= 8 },
  { id: "hardware_15", icon: "\uD83C\uDFC6", name: "Salle d'arcade", desc: "Posseder 15 materiels", rarity: "epic", condition: (s) => s.hardware >= 15 },
  { id: "hardware_30", icon: "🛡️", name: "Setup signature", desc: "Posseder 30 materiels", rarity: "legendary", condition: (s) => s.hardware >= 30 },
  { id: "hardware_60", icon: "🔱", name: "Forge personnelle", desc: "Posseder 60 materiels", rarity: "mythic", condition: (s) => s.hardware >= 60 },

  { id: "consoles_2", icon: "\uD83D\uDDA5\uFE0F", name: "Multi-console", desc: "Posseder 2 consoles", rarity: "common", condition: (s) => s.consoles >= 2 },
  { id: "consoles_5", icon: "\uD83C\uDF10", name: "Generation ouverte", desc: "Posseder 5 consoles", rarity: "rare", condition: (s) => s.consoles >= 5 },
  { id: "consoles_10", icon: "\uD83D\uDE80", name: "Musee personnel", desc: "Posseder 10 consoles", rarity: "legendary", condition: (s) => s.consoles >= 10 },

  { id: "controllers_3", icon: "\uD83D\uDD79\uFE0F", name: "Pret pour le multi", desc: "Posseder 3 manettes", rarity: "common", condition: (s) => s.controllers >= 3 },
  { id: "controllers_6", icon: "\uD83C\uDFAF", name: "Maitre des commandes", desc: "Posseder 6 manettes", rarity: "rare", condition: (s) => s.controllers >= 6 },

  { id: "audio_2", icon: "\uD83C\uDFA7", name: "Immersion", desc: "Posseder 2 equipements audio", rarity: "common", condition: (s) => s.audio >= 2 },
  { id: "top_console", icon: "\u2728", name: "Console coup de coeur", desc: "Noter une console a 9/10", rarity: "epic", condition: (s) => s.topConsoleRating >= 9 },

  { id: "quiz_first", icon: "?", name: "Premier checkpoint", desc: "Repondre a 1 quiz hebdo", rarity: "common", condition: (s) => s.quizAnswered >= 1 },
  { id: "quiz_5", icon: "5", name: "Memoire active", desc: "Repondre a 5 quiz hebdo", rarity: "rare", condition: (s) => s.quizAnswered >= 5 },
  { id: "quiz_streak_4", icon: "4", name: "Serie parfaite", desc: "Enchainer 4 bonnes reponses hebdomadaires", rarity: "epic", condition: (s) => s.quizBestStreak >= 4 },

  { id: "creator_checkpoint", icon: "C", name: "Createur de Checkpoint", desc: "Badge unique du createur de l'application", rarity: "creator", special: "creator", hiddenWhenLocked: false, condition: (s) => s.isCreator },

  { id: "brand_playstation_50", icon: "PS", platformFamily: "playstation", name: "Explorateur PlayStation", desc: "Jouer 50 jeux sur PlayStation", rarity: "rare", condition: (s) => s.platformFamilies.playstation >= 50 },
  { id: "brand_playstation_100", icon: "PS", platformFamily: "playstation", name: "Veteran PlayStation", desc: "Jouer 100 jeux sur PlayStation", rarity: "epic", condition: (s) => s.platformFamilies.playstation >= 100 },
  { id: "brand_playstation_200", icon: "PS", platformFamily: "playstation", name: "Heros PlayStation", desc: "Jouer 200 jeux sur PlayStation", rarity: "legendary", condition: (s) => s.platformFamilies.playstation >= 200 },
  { id: "brand_playstation_400", icon: "PS", platformFamily: "playstation", name: "Legende PlayStation", desc: "Jouer 400 jeux sur PlayStation", rarity: "mythic", condition: (s) => s.platformFamilies.playstation >= 400 },
  { id: "brand_playstation_600", icon: "PS", platformFamily: "playstation", name: "Immortel PlayStation", desc: "Jouer 600 jeux sur PlayStation", rarity: "mythic", condition: (s) => s.platformFamilies.playstation >= 600 },
  { id: "brand_playstation_700", icon: "PS", platformFamily: "playstation", name: "Legende absolue PlayStation", desc: "Jouer 700 jeux sur PlayStation", rarity: "mythic", condition: (s) => s.platformFamilies.playstation >= 700 },
  { id: "brand_playstation_800", icon: "PS", platformFamily: "playstation", name: "Divin PlayStation", desc: "Jouer 800 jeux sur PlayStation", rarity: "mythic", condition: (s) => s.platformFamilies.playstation >= 800 },
  { id: "brand_playstation_900", icon: "PS", platformFamily: "playstation", name: "Eternel PlayStation", desc: "Jouer 900 jeux sur PlayStation", rarity: "mythic", condition: (s) => s.platformFamilies.playstation >= 900 },
  { id: "brand_playstation_1000", icon: "PS", platformFamily: "playstation", name: "Omega PlayStation", desc: "Jouer 1000 jeux sur PlayStation", rarity: "mythic", condition: (s) => s.platformFamilies.playstation >= 1000 },

  { id: "brand_xbox_50", icon: "X", platformFamily: "xbox", name: "Explorateur Xbox", desc: "Jouer 50 jeux sur Xbox", rarity: "rare", condition: (s) => s.platformFamilies.xbox >= 50 },
  { id: "brand_xbox_100", icon: "X", platformFamily: "xbox", name: "Veteran Xbox", desc: "Jouer 100 jeux sur Xbox", rarity: "epic", condition: (s) => s.platformFamilies.xbox >= 100 },
  { id: "brand_xbox_200", icon: "X", platformFamily: "xbox", name: "Heros Xbox", desc: "Jouer 200 jeux sur Xbox", rarity: "legendary", condition: (s) => s.platformFamilies.xbox >= 200 },
  { id: "brand_xbox_400", icon: "X", platformFamily: "xbox", name: "Legende Xbox", desc: "Jouer 400 jeux sur Xbox", rarity: "mythic", condition: (s) => s.platformFamilies.xbox >= 400 },
  { id: "brand_xbox_600", icon: "X", platformFamily: "xbox", name: "Immortel Xbox", desc: "Jouer 600 jeux sur Xbox", rarity: "mythic", condition: (s) => s.platformFamilies.xbox >= 600 },
  { id: "brand_xbox_700", icon: "X", platformFamily: "xbox", name: "Legende absolue Xbox", desc: "Jouer 700 jeux sur Xbox", rarity: "mythic", condition: (s) => s.platformFamilies.xbox >= 700 },
  { id: "brand_xbox_800", icon: "X", platformFamily: "xbox", name: "Divin Xbox", desc: "Jouer 800 jeux sur Xbox", rarity: "mythic", condition: (s) => s.platformFamilies.xbox >= 800 },
  { id: "brand_xbox_900", icon: "X", platformFamily: "xbox", name: "Eternel Xbox", desc: "Jouer 900 jeux sur Xbox", rarity: "mythic", condition: (s) => s.platformFamilies.xbox >= 900 },
  { id: "brand_xbox_1000", icon: "X", platformFamily: "xbox", name: "Omega Xbox", desc: "Jouer 1000 jeux sur Xbox", rarity: "mythic", condition: (s) => s.platformFamilies.xbox >= 1000 },

  { id: "brand_nintendo_50", icon: "N", platformFamily: "nintendo", name: "Explorateur Nintendo", desc: "Jouer 50 jeux sur Nintendo", rarity: "rare", condition: (s) => s.platformFamilies.nintendo >= 50 },
  { id: "brand_nintendo_100", icon: "N", platformFamily: "nintendo", name: "Veteran Nintendo", desc: "Jouer 100 jeux sur Nintendo", rarity: "epic", condition: (s) => s.platformFamilies.nintendo >= 100 },
  { id: "brand_nintendo_200", icon: "N", platformFamily: "nintendo", name: "Heros Nintendo", desc: "Jouer 200 jeux sur Nintendo", rarity: "legendary", condition: (s) => s.platformFamilies.nintendo >= 200 },
  { id: "brand_nintendo_400", icon: "N", platformFamily: "nintendo", name: "Legende Nintendo", desc: "Jouer 400 jeux sur Nintendo", rarity: "mythic", condition: (s) => s.platformFamilies.nintendo >= 400 },
  { id: "brand_nintendo_600", icon: "N", platformFamily: "nintendo", name: "Immortel Nintendo", desc: "Jouer 600 jeux sur Nintendo", rarity: "mythic", condition: (s) => s.platformFamilies.nintendo >= 600 },
  { id: "brand_nintendo_700", icon: "N", platformFamily: "nintendo", name: "Legende absolue Nintendo", desc: "Jouer 700 jeux sur Nintendo", rarity: "mythic", condition: (s) => s.platformFamilies.nintendo >= 700 },
  { id: "brand_nintendo_800", icon: "N", platformFamily: "nintendo", name: "Divin Nintendo", desc: "Jouer 800 jeux sur Nintendo", rarity: "mythic", condition: (s) => s.platformFamilies.nintendo >= 800 },
  { id: "brand_nintendo_900", icon: "N", platformFamily: "nintendo", name: "Eternel Nintendo", desc: "Jouer 900 jeux sur Nintendo", rarity: "mythic", condition: (s) => s.platformFamilies.nintendo >= 900 },
  { id: "brand_nintendo_1000", icon: "N", platformFamily: "nintendo", name: "Omega Nintendo", desc: "Jouer 1000 jeux sur Nintendo", rarity: "mythic", condition: (s) => s.platformFamilies.nintendo >= 1000 },

  { id: "platform_ps5_25", icon: "PS5", platformFamily: "playstation", platformKey: "ps5", name: "Explorateur PS5", desc: "Jouer 25 jeux sur PS5", rarity: "rare", condition: (s) => s.platformCounts.ps5 >= 25 },
  { id: "platform_ps5_50", icon: "PS5", platformFamily: "playstation", platformKey: "ps5", name: "Veteran PS5", desc: "Jouer 50 jeux sur PS5", rarity: "epic", condition: (s) => s.platformCounts.ps5 >= 50 },
  { id: "platform_ps5_100", icon: "PS5", platformFamily: "playstation", platformKey: "ps5", name: "Legende PS5", desc: "Jouer 100 jeux sur PS5", rarity: "legendary", condition: (s) => s.platformCounts.ps5 >= 100 },
  { id: "platform_ps5_200", icon: "PS5", platformFamily: "playstation", platformKey: "ps5", name: "Icone PS5", desc: "Jouer 200 jeux sur PS5", rarity: "mythic", condition: (s) => s.platformCounts.ps5 >= 200 },

  { id: "platform_ps4_25", icon: "PS4", platformFamily: "playstation", platformKey: "ps4", name: "Explorateur PS4", desc: "Jouer 25 jeux sur PS4", rarity: "rare", condition: (s) => s.platformCounts.ps4 >= 25 },
  { id: "platform_ps4_50", icon: "PS4", platformFamily: "playstation", platformKey: "ps4", name: "Veteran PS4", desc: "Jouer 50 jeux sur PS4", rarity: "epic", condition: (s) => s.platformCounts.ps4 >= 50 },
  { id: "platform_ps4_100", icon: "PS4", platformFamily: "playstation", platformKey: "ps4", name: "Legende PS4", desc: "Jouer 100 jeux sur PS4", rarity: "legendary", condition: (s) => s.platformCounts.ps4 >= 100 },
  { id: "platform_ps4_200", icon: "PS4", platformFamily: "playstation", platformKey: "ps4", name: "Icone PS4", desc: "Jouer 200 jeux sur PS4", rarity: "mythic", condition: (s) => s.platformCounts.ps4 >= 200 },

  { id: "platform_ps3_25", icon: "PS3", platformFamily: "playstation", platformKey: "ps3", name: "Explorateur PS3", desc: "Jouer 25 jeux sur PS3", rarity: "rare", condition: (s) => s.platformCounts.ps3 >= 25 },
  { id: "platform_ps3_50", icon: "PS3", platformFamily: "playstation", platformKey: "ps3", name: "Veteran PS3", desc: "Jouer 50 jeux sur PS3", rarity: "epic", condition: (s) => s.platformCounts.ps3 >= 50 },
  { id: "platform_ps3_100", icon: "PS3", platformFamily: "playstation", platformKey: "ps3", name: "Legende PS3", desc: "Jouer 100 jeux sur PS3", rarity: "legendary", condition: (s) => s.platformCounts.ps3 >= 100 },
  { id: "platform_ps3_200", icon: "PS3", platformFamily: "playstation", platformKey: "ps3", name: "Icone PS3", desc: "Jouer 200 jeux sur PS3", rarity: "mythic", condition: (s) => s.platformCounts.ps3 >= 200 },

  { id: "platform_xbox_series_25", icon: "XS", platformFamily: "xbox", platformKey: "xboxSeries", name: "Explorateur Xbox Series", desc: "Jouer 25 jeux sur Xbox Series", rarity: "rare", condition: (s) => s.platformCounts.xboxSeries >= 25 },
  { id: "platform_xbox_series_50", icon: "XS", platformFamily: "xbox", platformKey: "xboxSeries", name: "Veteran Xbox Series", desc: "Jouer 50 jeux sur Xbox Series", rarity: "epic", condition: (s) => s.platformCounts.xboxSeries >= 50 },
  { id: "platform_xbox_series_100", icon: "XS", platformFamily: "xbox", platformKey: "xboxSeries", name: "Legende Xbox Series", desc: "Jouer 100 jeux sur Xbox Series", rarity: "legendary", condition: (s) => s.platformCounts.xboxSeries >= 100 },
  { id: "platform_xbox_series_200", icon: "XS", platformFamily: "xbox", platformKey: "xboxSeries", name: "Icone Series", desc: "Jouer 200 jeux sur Xbox Series", rarity: "mythic", condition: (s) => s.platformCounts.xboxSeries >= 200 },

  { id: "platform_xbox_one_25", icon: "XO", platformFamily: "xbox", platformKey: "xboxOne", name: "Explorateur Xbox One", desc: "Jouer 25 jeux sur Xbox One", rarity: "rare", condition: (s) => s.platformCounts.xboxOne >= 25 },
  { id: "platform_xbox_one_50", icon: "XO", platformFamily: "xbox", platformKey: "xboxOne", name: "Veteran Xbox One", desc: "Jouer 50 jeux sur Xbox One", rarity: "epic", condition: (s) => s.platformCounts.xboxOne >= 50 },
  { id: "platform_xbox_one_100", icon: "XO", platformFamily: "xbox", platformKey: "xboxOne", name: "Legende Xbox One", desc: "Jouer 100 jeux sur Xbox One", rarity: "legendary", condition: (s) => s.platformCounts.xboxOne >= 100 },
  { id: "platform_xbox_one_200", icon: "XO", platformFamily: "xbox", platformKey: "xboxOne", name: "Icone Xbox One", desc: "Jouer 200 jeux sur Xbox One", rarity: "mythic", condition: (s) => s.platformCounts.xboxOne >= 200 },

  { id: "platform_switch_25", icon: "SW", platformFamily: "nintendo", platformKey: "switch", name: "Explorateur Switch", desc: "Jouer 25 jeux sur Switch", rarity: "rare", condition: (s) => s.platformCounts.switch >= 25 },
  { id: "platform_switch_50", icon: "SW", platformFamily: "nintendo", platformKey: "switch", name: "Veteran Switch", desc: "Jouer 50 jeux sur Switch", rarity: "epic", condition: (s) => s.platformCounts.switch >= 50 },
  { id: "platform_switch_100", icon: "SW", platformFamily: "nintendo", platformKey: "switch", name: "Legende Switch", desc: "Jouer 100 jeux sur Switch", rarity: "legendary", condition: (s) => s.platformCounts.switch >= 100 },
  { id: "platform_switch_200", icon: "SW", platformFamily: "nintendo", platformKey: "switch", name: "Icone Switch", desc: "Jouer 200 jeux sur Switch", rarity: "mythic", condition: (s) => s.platformCounts.switch >= 200 },

  { id: "platform_pc_25", icon: "PC", platformFamily: "pc", platformKey: "pc", name: "Explorateur PC", desc: "Jouer 25 jeux sur PC", rarity: "rare", condition: (s) => s.platformCounts.pc >= 25 },
  { id: "platform_pc_50", icon: "PC", platformFamily: "pc", platformKey: "pc", name: "Veteran PC", desc: "Jouer 50 jeux sur PC", rarity: "epic", condition: (s) => s.platformCounts.pc >= 50 },
  { id: "platform_pc_100", icon: "PC", platformFamily: "pc", platformKey: "pc", name: "Legende PC", desc: "Jouer 100 jeux sur PC", rarity: "legendary", condition: (s) => s.platformCounts.pc >= 100 },
  { id: "platform_pc_200", icon: "PC", platformFamily: "pc", platformKey: "pc", name: "Machine de guerre", desc: "Jouer 200 jeux sur PC", rarity: "mythic", condition: (s) => s.platformCounts.pc >= 200 },
];

const BADGE_ICON_COMPONENTS = {
  first_game: Gamepad2,
  collector_10: BookOpen,
  collector_25: Archive,
  collector_50: Landmark,
  finisher_5: CheckCircle2,
  finisher_15: Flag,
  finisher_30: Trophy,
  favorites_3: Heart,
  favorites_8: Heart,
  favorites_15: StarIcon,
  reviews_3: PenLine,
  reviews_10: Newspaper,
  reviews_25: ScrollText,
  hours_50: Timer,
  hours_100: Clock3,
  hours_250: Moon,
  hours_500: Crown,
  level_10: StarIcon,
  level_25: Medal,
  level_50: Trophy,
  level_100: Gem,
  hardware_3: Package,
  hardware_8: Wrench,
  hardware_15: Joystick,
  consoles_2: Monitor,
  consoles_5: Puzzle,
  consoles_10: Landmark,
  controllers_3: Gamepad2,
  controllers_6: Target,
  audio_2: Headphones,
  top_console: Sparkles,
  quiz_first: Target,
  quiz_5: Puzzle,
  quiz_streak_4: Trophy,
};

const BADGE_BRAND_LOGOS = {
  playstation: "/images/brands/sony.png",
  xbox: "/images/brands/xbox.png",
  nintendo: "/images/brands/nintendo.png",
  pc: "/images/brands/pc.png",
};

const CREATOR_HANDLES = ["checkpoint", "irondev44", "bodyjordan", "bodyj"];

function isCreatorProfile(profile = {}) {
  if (accountsEnabled) return (profile.ownerUid || auth.currentUser?.uid) === ownerUid;
  const handle = normalizeHandle(profile.handle || "");
  const displayName = normalizeHandle(profile.displayName || "");

  return (
    profile.isCreator === true ||
    profile.creatorBadgeEnabled === true ||
    profile.featuredBadgeId === "creator_checkpoint" ||
    CREATOR_HANDLES.includes(handle) ||
    CREATOR_HANDLES.includes(displayName)
  );
}

function BadgeVisualIcon({ badge, size = 20 }) {
  const rawIcon = badge?.icon || "";
  const compactIcon = String(rawIcon).replace(/\s+/g, "").trim();
  const isNumericSeal = /^\d{2,3}$/.test(compactIcon);
  const sealSize = size >= 30 ? 56 : size <= 16 ? 30 : 46;
  const platformSize = size >= 30 ? 54 : size <= 16 ? 30 : 44;

  if (badge?.special === "creator") {
    return <span className="badge-creator-mark">C</span>;
  }

  if (badge?.id?.startsWith("brand_") && BADGE_BRAND_LOGOS[badge.platformFamily]) {
    return (
      <img
        src={BADGE_BRAND_LOGOS[badge.platformFamily]}
        alt=""
        className="badge-brand-logo"
      />
    );
  }

  if (isNumericSeal) {
    return (
      <span
        className={`checkpoint-seal-badge checkpoint-seal-badge-${badge?.rarity || "epic"} checkpoint-seal-badge-numeric`}
        aria-label={compactIcon}
        style={{ "--badge-visual-size": `${sealSize}px` }}
      >
        <span className="checkpoint-seal-value">{compactIcon}</span>
      </span>
    );
  }

  if (badge?.platformFamily) {
    const playstationMark = compactIcon.match(/^PS(\d+)$/i);

    if (playstationMark) {
      const psLabel = `PS${playstationMark[1]}`;

      return (
        <span
          className="checkpoint-platform-badge checkpoint-platform-badge-playstation"
          aria-label={psLabel}
          style={{ "--badge-visual-size": `${platformSize}px` }}
        >
          <span className="checkpoint-platform-shine" />
          <span className="checkpoint-platform-label">{psLabel}</span>
        </span>
      );
    }

    return <span className={`badge-platform-mark ${badge.platformFamily}`}>{compactIcon || rawIcon}</span>;
  }

  const Icon = BADGE_ICON_COMPONENTS[badge?.id];

  if (Icon) {
    return <Icon size={size} strokeWidth={2.35} />;
  }

  return <span>{badge?.icon}</span>;
}

function isBadgeNumericSeal(badge) {
  return /^\d{2,3}$/.test(String(badge?.icon || "").replace(/\s+/g, "").trim());
}

function getBadgeRarityLabel(badge) {
  if (!badge) return "Badge";
  if (badge.special === "creator") return "Créateur";

  const labels = {
    mythic: "Mythique",
    legendary: "Légendaire",
    epic: "Épique",
    rare: "Rare",
    common: "Commun",
  };

  return labels[badge.rarity] || "Badge";
}

function getLibraryGamePlatforms(game = {}) {
  if (Array.isArray(game.playedPlatforms) && game.playedPlatforms.length > 0) {
    return game.playedPlatforms.filter(Boolean);
  }

  if (Array.isArray(game.platformNames) && game.platformNames.length > 0) {
    return game.platformNames.filter(Boolean);
  }

  if (Array.isArray(game.platforms) && game.platforms.length > 0) {
    return game.platforms
      .map((platform) =>
        typeof platform === "string"
          ? platform
          : platform?.name || platform?.platform?.name
      )
      .filter(Boolean);
  }

  return [];
}

function getPrimaryLibraryGamePlatform(game = {}) {
  return getLibraryGamePlatforms(game)[0] || "Plateforme inconnue";
}

function getFeaturedBadgeFromSelection(badges = [], featuredBadgeId = "") {
  const unlockedBadges = badges.filter((badge) => badge.unlocked);
  const exactBadge = unlockedBadges.find((badge) => badge.id === featuredBadgeId);

  if (exactBadge) return exactBadge;

  const legacyBrandMatch = featuredBadgeId.match(/^platform_(playstation|xbox|nintendo)_(\d+)$/);

  if (legacyBrandMatch) {
    const [, family, target] = legacyBrandMatch;
    const directBrandBadge = unlockedBadges.find(
      (badge) => badge.id === `brand_${family}_${target}`
    );

    if (directBrandBadge) return directBrandBadge;

    return unlockedBadges
      .filter((badge) => badge.id.startsWith(`brand_${family}_`))
      .sort((a, b) => Number(b.id.split("_").at(-1)) - Number(a.id.split("_").at(-1)))[0];
  }

  return null;
}

function getPlatformFamily(platform = "") {
  const normalized = platform.toLowerCase();

  if (
    normalized.includes("playstation") ||
    normalized.includes("ps vita") ||
    normalized.includes("psp") ||
    /\bps[1-5]\b/.test(normalized)
  ) {
    return "playstation";
  }

  if (normalized.includes("xbox")) {
    return "xbox";
  }

  if (
    normalized.includes("nintendo") ||
    normalized.includes("switch") ||
    normalized.includes("wii") ||
    normalized.includes("gamecube") ||
    normalized.includes("game boy") ||
    normalized.includes("gameboy") ||
    normalized.includes("ds") ||
    normalized.includes("3ds")
  ) {
    return "nintendo";
  }

  if (
    normalized === "pc" ||
    normalized.includes("windows") ||
    normalized.includes("macos") ||
    normalized.includes("linux")
  ) {
    return "pc";
  }

  return "";
}

function getPlatformKey(platform = "") {
  const normalized = platform.toLowerCase();

  if (normalized.includes("playstation 5") || /\bps5\b/.test(normalized)) {
    return "ps5";
  }

  if (normalized.includes("playstation 4") || /\bps4\b/.test(normalized)) {
    return "ps4";
  }

  if (normalized.includes("playstation 3") || /\bps3\b/.test(normalized)) {
    return "ps3";
  }

  if (normalized.includes("xbox series")) {
    return "xboxSeries";
  }

  if (normalized.includes("xbox one")) {
    return "xboxOne";
  }

  if (normalized.includes("switch")) {
    return "switch";
  }

  if (
    normalized === "pc" ||
    normalized.includes("windows") ||
    normalized.includes("macos") ||
    normalized.includes("linux")
  ) {
    return "pc";
  }

  return "";
}

function isGameEligibleForPlatformBadge(game = {}) {
  const status = String(game.status || "").toLowerCase();

  return !status.includes("wishlist");
}

function getPlatformFamilyCounts(games = []) {
  const counts = {
    playstation: 0,
    xbox: 0,
    nintendo: 0,
    pc: 0,
  };

  games
    .filter(isGameFinishedStatus)
    .forEach((game) => {
      const platforms =
        game.playedPlatforms?.length > 0
          ? game.playedPlatforms
          : game.platformNames || [];

      const families = new Set(
        platforms
          .map(getPlatformFamily)
          .filter(Boolean)
      );

      families.forEach((family) => {
        counts[family] += 1;
      });
    });

  return counts;
}

function getPlatformFamilyBadgeCounts(games = []) {
  const counts = {
    playstation: 0,
    xbox: 0,
    nintendo: 0,
    pc: 0,
  };

  games
    .filter(isGameEligibleForPlatformBadge)
    .forEach((game) => {
      const platforms =
        game.playedPlatforms?.length > 0
          ? game.playedPlatforms
          : game.platformNames || [];

      const families = new Set(
        platforms
          .map(getPlatformFamily)
          .filter(Boolean)
      );

      families.forEach((family) => {
        counts[family] += 1;
      });
    });

  return counts;
}

function getPlatformBadgeCounts(games = []) {
  const counts = {
    ps5: 0,
    ps4: 0,
    ps3: 0,
    xboxSeries: 0,
    xboxOne: 0,
    switch: 0,
    pc: 0,
  };

  games
    .filter(isGameEligibleForPlatformBadge)
    .forEach((game) => {
      const platforms =
        game.playedPlatforms?.length > 0
          ? game.playedPlatforms
          : game.platformNames || [];

      const platformKeys = new Set(
        platforms
          .map(getPlatformKey)
          .filter(Boolean)
      );

      platformKeys.forEach((platformKey) => {
        counts[platformKey] += 1;
      });
    });

  return counts;
}

function calculateBadgeStats(
  games,
  level,
  hardware = [],
  socialProfile = DEFAULT_SOCIAL_PROFILE,
  quizProgress = DEFAULT_WEEKLY_QUIZ_PROGRESS
) {
  const currentHardware = hardware.filter((item) => {
    const status = getNormalizedStatus(item?.status || "");
    return status.includes("poss") || status.includes("reparer");
  });

  return {
    total: games.length,
    finished: games.filter(isGameFinishedStatus).length,
    favorites: games.filter(g => g.favorite).length,
    ratedGames: games.filter((g) => clampRating(g.rating) > 0).length,
    reviews: games.filter(g => g.review && g.review.length > 10).length,
    hours: games.reduce((sum, g) => sum + (Number(g.playtime) || 0), 0),
    level,
    hardware: currentHardware.length,
    consoles: currentHardware.filter((item) => item.type === "console").length,
    controllers: currentHardware.filter((item) => item.type === "controller").length,
    audio: currentHardware.filter((item) => item.type === "audio").length,
    topConsoleRating: Math.max(
      0,
      ...currentHardware
        .filter((item) => item.type === "console")
        .map((item) => Number(item.rating) || 0)
    ),
    platformFamilies: getPlatformFamilyBadgeCounts(games),
    platformCounts: getPlatformBadgeCounts(games),
    quizAnswered: Object.keys(quizProgress.answers || {}).length,
    quizBestStreak: quizProgress.bestStreak || 0,
    isCreator: isCreatorProfile(socialProfile),
  };
}

function getUnlockedBadgesV2(
  games,
  level,
  hardware = [],
  socialProfile = DEFAULT_SOCIAL_PROFILE,
  quizProgress = DEFAULT_WEEKLY_QUIZ_PROGRESS
) {
  const stats = calculateBadgeStats(games, level, hardware, socialProfile, quizProgress);
  return BADGES.map(b => ({
    ...b,
    unlocked: b.condition(stats)
  }));
}

const UNLOCKED_BADGES_STORAGE_KEY = "checkpoint-unlocked-badges";

function readStoredUnlockedBadgeIds() {
  try {
    const parsed = JSON.parse(
      localStorage.getItem(UNLOCKED_BADGES_STORAGE_KEY) || "[]"
    );

    return Array.isArray(parsed) ? parsed.filter(Boolean).map(String) : [];
  } catch (error) {
    console.warn("Badges sauvegardes illisibles, reinitialisation.", error);
    return [];
  }
}

function storeUnlockedBadgeIds(ids) {
  localStorage.setItem(
    UNLOCKED_BADGES_STORAGE_KEY,
    JSON.stringify([...new Set(ids.filter(Boolean).map(String))])
  );
}

function getBadgeProgress(badge, stats) {
  if (badge.id.startsWith("collector_")) {
    return { current: stats.total, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("finisher_")) {
    return { current: stats.finished, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("favorites_")) {
    return { current: stats.favorites, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("reviews_")) {
    return { current: stats.reviews, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("rated_")) {
    return { current: stats.ratedGames, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id === "quiz_first") {
    return { current: stats.quizAnswered, target: 1 };
  }

  if (badge.id === "quiz_5") {
    return { current: stats.quizAnswered, target: 5 };
  }

  if (badge.id === "quiz_streak_4") {
    return { current: stats.quizBestStreak, target: 4 };
  }

  if (badge.id.startsWith("hours_")) {
    return { current: stats.hours, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("level_")) {
    return { current: stats.level, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("hardware_")) {
    return { current: stats.hardware, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("consoles_")) {
    return { current: stats.consoles, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("controllers_")) {
    return { current: stats.controllers, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id.startsWith("audio_")) {
    return { current: stats.audio, target: Number(badge.id.split("_")[1]) };
  }

  if (badge.id === "top_console") {
    return { current: stats.topConsoleRating, target: 9 };
  }

  if (badge.id.startsWith("brand_")) {
    const family = badge.platformFamily || badge.id.split("_")[1];
    const target = Number(badge.id.split("_").at(-1));
    return { current: stats.platformFamilies[family] || 0, target };
  }

  if (badge.id.startsWith("platform_")) {
    if (badge.platformKey) {
      const target = Number(badge.id.split("_").at(-1));
      return { current: stats.platformCounts[badge.platformKey] || 0, target };
    }

    const family = badge.platformFamily || badge.id.split("_")[1];
    const target = Number(badge.id.split("_").at(-1));
    return { current: stats.platformFamilies[family] || 0, target };
  }

  return null;
}

function FeaturedBadgePill({ badge, compact = false }) {
  if (!badge) return null;

  return (
    <span
      className={`featured-badge-pill ${badge.rarity} ${
        badge.platformFamily ? `platform-${badge.platformFamily}` : ""
      } ${compact ? "compact" : ""}`}
    >
      <span className="featured-badge-gem">
        <BadgeVisualIcon badge={badge} size={15} />
      </span>
      {!compact && <span>{badge.name}</span>}
    </span>
  );
}

/* -------------------- XP SYSTEM -------------------- */

/* ==================== SYSTÈME XP & NIVEAUX ==================== */

const BASE_XP = 100;

const PROGRESS_OPTIONS = [
  { id: "not_started", label: "Non commencé", icon: "⬜", xp: 0 },
  { id: "tried", label: "Juste essayé", icon: "🎮", xp: 25 },
  { id: "in_progress", label: "En cours", icon: "▶️", xp: 60 },
  { id: "playing", label: "En cours", icon: "▶️", xp: 60 },
  { id: "paused", label: "En pause", icon: "⏸", xp: 25 },
  { id: "abandoned", label: "Abandonné", icon: "⏹", xp: 25 },
  { id: "deep_play", label: "Bien avancé", icon: "🔥", xp: 100 },
  { id: "completed", label: "Terminé", icon: "🏆", xp: 150 },
];

const PLAYTIME_RANGE_OPTIONS = [
  { id: "none", label: "Non renseigné", xp: 0 },
  { id: "under_2", label: "Moins de 2h", xp: 5 },
  { id: "2_5", label: "2 à 5h", xp: 10 },
  { id: "5_15", label: "5 à 15h", xp: 20 },
  { id: "15_40", label: "15 à 40h", xp: 35 },
  { id: "40_80", label: "40 à 80h", xp: 50 },
  { id: "80_150", label: "80 à 150h", xp: 70 },
  { id: "150_plus", label: "150h+", xp: 90 },
  { id: "too_much", label: "Beaucoup trop joué 😅", xp: 110 },
];

function getProgressLabel(progressStatus) {
  return (
    PROGRESS_OPTIONS.find((option) => option.id === progressStatus)?.label ||
    "Non commencé"
  );
}

function getPlaytimeRangeLabel(playtimeRange) {
  return (
    PLAYTIME_RANGE_OPTIONS.find((option) => option.id === playtimeRange)?.label ||
    "Non renseigné"
  );
}

function getProgressXP(progressStatus) {
  return (
    PROGRESS_OPTIONS.find((option) => option.id === progressStatus)?.xp || 0
  );
}

function getPlaytimeRangeXP(playtimeRange) {
  return (
    PLAYTIME_RANGE_OPTIONS.find((option) => option.id === playtimeRange)?.xp || 0
  );
}

function getBonusXP(game) {
  let bonus = 0;
  if (game.favorite) bonus += 25;
  if (getGameRating(game) >= 9) bonus += 15;
  if (game.review && game.review.trim().length > 20) bonus += 20;
  if (averageDetailedRating(game) >= 9) bonus += 20;
  return bonus;
}

function calculateXP(game) {
  return (
    BASE_XP +
    getProgressXP(
      game.progressStatus || (isGameFinishedStatus(game) ? "completed" : "not_started")
    ) +
    getPlaytimeRangeXP(game.playtimeRange || "none") +
    0 +
    getBonusXP(game)
  );
}

function calculateHardwareXP(item) {
  if (!String(item.status || "").toLowerCase().includes("poss")) return 0;
  return Number(item.rating) > 0 ? 20 : 0;
}

function getConsoleXPStats(games) {
  const stats = {};

  games
    .filter(isGameFinishedStatus)
    .forEach((game) => {
      const playedPlatforms =
        game.playedPlatforms?.length > 0
          ? game.playedPlatforms
          : [];

      playedPlatforms.forEach((platform) => {
        if (!stats[platform]) {
          stats[platform] = {
            platform,
            games: 0,
            xp: 0,
          };
        }

        stats[platform].games += 1;
        stats[platform].xp += calculateXP(game);
      });
    });

  return Object.values(stats).sort((a, b) => b.xp - a.xp);
}

function getLevel(totalXP) {
  let level = 1;
  let xpNeeded = 180;
  let xp = totalXP;

  while (xp >= xpNeeded && level < 100) {
    xp -= xpNeeded;
    level++;
    xpNeeded = Math.floor(xpNeeded * 1.08);
  }

  if (level >= 100) return 100;
  return level;
}

function getProgress(totalXP) {
  let level = 1;
  let xpNeeded = 180;
  let xp = totalXP;

  while (xp >= xpNeeded && level < 100) {
    xp -= xpNeeded;
    level++;
    xpNeeded = Math.floor(xpNeeded * 1.08);
  }

  if (level >= 100) {
    return {
      currentXP: xpNeeded,
      xpToNext: xpNeeded,
      percent: 100,
    };
  }

  return {
    currentXP: xp,
    xpToNext: xpNeeded,
    percent: Math.floor((xp / xpNeeded) * 100),
  };
}

const DEFAULT_CHECKPOINT_TRIAL_PROGRESS = {
  completed: {},
  attempts: {},
  exclusiveBadges: {},
  history: [],
  lastUnlockedLevel: null,
};

function getXPForLevel(targetLevel) {
  let xp = 0;
  let xpNeeded = 180;

  for (let level = 1; level < targetLevel && level < 100; level++) {
    xp += xpNeeded;
    xpNeeded = Math.floor(xpNeeded * 1.08);
  }

  return xp;
}

function getStoredCheckpointTrialProgress() {
  try {
    const raw = localStorage.getItem(CHECKPOINT_TRIAL_STORAGE_KEY);
    if (!raw) return DEFAULT_CHECKPOINT_TRIAL_PROGRESS;
    const parsed = JSON.parse(raw);

    return {
      ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS,
      ...parsed,
      completed: {
        ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS.completed,
        ...(parsed.completed || {}),
      },
      attempts: {
        ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS.attempts,
        ...(parsed.attempts || {}),
      },
      exclusiveBadges: {
        ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS.exclusiveBadges,
        ...(parsed.exclusiveBadges || {}),
      },
      history: Array.isArray(parsed.history) ? parsed.history : [],
    };
  } catch (error) {
    console.error("Erreur lecture progression Salle des Epreuves :", error);
    return DEFAULT_CHECKPOINT_TRIAL_PROGRESS;
  }
}

function storeCheckpointTrialProgress(progress) {
  try {
    localStorage.setItem(CHECKPOINT_TRIAL_STORAGE_KEY, JSON.stringify(progress));
  } catch (error) {
    console.error("Erreur sauvegarde progression Salle des Epreuves :", error);
  }
}

function getPendingCheckpoint(rawXP, completed = {}) {
  const rawLevel = getLevel(rawXP);

  return (
    CHECKPOINT_LEVELS.find(
      (checkpointLevel) =>
        rawLevel >= checkpointLevel && !completed[String(checkpointLevel)]
    ) || null
  );
}

function getEffectiveXP(rawXP, completed = {}) {
  const pendingCheckpoint = getPendingCheckpoint(rawXP, completed);

  if (!pendingCheckpoint) return rawXP;

  return getXPForLevel(pendingCheckpoint);
}

function getRankTitle(level) {
  if (level >= 100) return "Suprême";
  if (level >= 90) return "Légende V";
  if (level >= 80) return "Légende IV";
  if (level >= 70) return "Légende III";
  if (level >= 60) return "Légende II";
  if (level >= 50) return "Légende I";
  if (level >= 40) return "Maître";
  if (level >= 30) return "Expert";
  if (level >= 20) return "Confirmé";
  if (level >= 10) return "Joueur";
  return "Débutant";
}

const RANKS = [
  { min: 1, max: 9, title: "Débutant" },
  { min: 10, max: 19, title: "Joueur" },
  { min: 20, max: 29, title: "Confirmé" },
  { min: 30, max: 39, title: "Expert" },
  { min: 40, max: 49, title: "Maître" },
  { min: 50, max: 59, title: "Légende I" },
  { min: 60, max: 69, title: "Légende II" },
  { min: 70, max: 79, title: "Légende III" },
  { min: 80, max: 89, title: "Légende IV" },
  { min: 90, max: 99, title: "Légende V" },
  { min: 100, max: 100, title: "Suprême" },
];

function getCheckpointRoadmap(
  level,
  rawTotalXP,
  checkpointTrialProgress = DEFAULT_CHECKPOINT_TRIAL_PROGRESS
) {
  const completed = checkpointTrialProgress.completed || {};
  const attempts = checkpointTrialProgress.attempts || {};

  return CHECKPOINT_LEVELS.map((checkpointLevel, index) => {
    const trial = getCheckpointTrial(checkpointLevel);
    const checkpointXP = getXPForLevel(checkpointLevel);
    const previousXP =
      index === 0 ? 0 : getXPForLevel(CHECKPOINT_LEVELS[index - 1]);
    const segmentXP = Math.max(checkpointXP - previousXP, 1);
    const segmentProgress = Math.min(
      Math.max(((rawTotalXP - previousXP) / segmentXP) * 100, 0),
      100
    );
    const isCompleted = Boolean(completed[String(checkpointLevel)]);
    const isReached = rawTotalXP >= checkpointXP || level >= checkpointLevel;
    const isPending = isReached && !isCompleted;

    return {
      level: checkpointLevel,
      title: trial?.title || `Checkpoint ${checkpointLevel}`,
      rewardRank: trial?.rewardRank || "Rang a debloquer",
      reward: trial?.reward || null,
      guardian: trial?.guardian || "Gardien du Checkpoint",
      passScore: trial?.passScore || 0,
      totalQuestions: trial?.questions?.length || 0,
      attempts: attempts[String(checkpointLevel)] || 0,
      isCompleted,
      isReached,
      isPending,
      segmentProgress,
      xpRemaining: Math.max(checkpointXP - rawTotalXP, 0),
    };
  });
}

function getSuggestedDifficulty(game) {
  const genres = (game.genres || []).map((g) => g.name.toLowerCase());
  const tags = (game.tags || []).map((t) => t.name.toLowerCase());
  const text = [...genres, ...tags].join(" ");

  if (
    text.includes("souls-like") ||
    text.includes("soulslike") ||
    text.includes("difficult") ||
    text.includes("hardcore")
  ) {
    return "hardcore";
  }

  if (
    text.includes("roguelike") ||
    text.includes("rogue-lite") ||
    text.includes("roguelite") ||
    text.includes("strategy") ||
    text.includes("tactical") ||
    text.includes("survival horror")
  ) {
    return "hard";
  }

  if (
    text.includes("casual") ||
    text.includes("family") ||
    text.includes("party") ||
    text.includes("simulation") ||
    text.includes("life sim")
  ) {
    return "casual";
  }

  return "normal";
}

function difficultyLabel(value) {
  switch (value) {
    case "casual":
      return "Casual";
    case "normal":
      return "Normal";
    case "hard":
      return "Exigeant";
    case "hardcore":
      return "Hardcore";
    default:
      return "Normal";
  }
}

/* -------------------- SMALL UI -------------------- */

/* ==================== COMPOSANTS UI DE BASE ==================== */

function Toast({ message }) {
  if (!message) return null;
  return <div className="toast" role="status" aria-live="polite">{message}</div>;
}

function BadgeUnlockToast({ badge }) {
  if (!badge) return null;

  return (
    <div className={`badge-unlock-toast ${badge.rarity}`}>
      <div className="badge-unlock-icon">
        <BadgeVisualIcon badge={badge} size={30} />
      </div>
      <div>
        <div className="badge-unlock-kicker">Badge débloqué</div>
        <div className="badge-unlock-name">{badge.name}</div>
        <div className="badge-unlock-desc">{badge.desc}</div>
      </div>
    </div>
  );
}

function Loader({ text = "Chargement..." }) {
  return (
    <div className="loader-wrap" role="status" aria-live="polite">
      <div className="loader-spinner" aria-hidden="true" />
      <div className="loader-text">{text}</div>
    </div>
  );
}

function EmptyState({ title, subtitle, message, icon = "✦" }) {
  return (
    <div className="empty-state">
      <div className="empty-state-icon" aria-hidden="true">{icon}</div>
      <div className="empty-state-title">{title}</div>
      {(subtitle || message) && <div className="empty-state-subtitle">{subtitle || message}</div>}
    </div>
  );
}

function NotificationCenter({
  notifications = [],
  open,
  onToggle,
  onOpenNotification,
  onDismiss,
}) {
  const count = notifications.length;

  return (
    <div className={`notification-center ${open ? "open" : ""}`}>
      <button
        type="button"
        className={`notification-trigger ${count > 0 ? "has-items" : ""}`}
        onClick={onToggle}
        aria-label="Ouvrir les notifications utiles"
        aria-expanded={open}
      >
        <Bell size={19} strokeWidth={2.5} />
        {count > 0 && <span className="notification-count">{count > 9 ? "9+" : count}</span>}
      </button>

      {open && (
        <div className="notification-panel">
          <div className="notification-panel-header">
            <div>
              <strong>Notifications utiles</strong>
              <small>Ce qui merite ton attention maintenant.</small>
            </div>
            <span>{count}</span>
          </div>

          {count === 0 ? (
            <div className="notification-empty">
              Rien d'urgent. Ton hub est tranquille.
            </div>
          ) : (
            <div className="notification-list">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={`notification-item tone-${notification.tone || "default"}`}
                >
                  <button
                    type="button"
                    className="notification-item-main"
                    onClick={() => onOpenNotification(notification)}
                  >
                    <span>{notification.title}</span>
                    <small>{notification.message}</small>
                    <em>{notification.cta || "Ouvrir"}</em>
                  </button>
                  <button
                    type="button"
                    className="notification-dismiss"
                    onClick={(event) => {
                      event.stopPropagation();
                      onDismiss(notification.id);
                    }}
                    aria-label="Masquer cette notification"
                  >
                    x
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const playClick = () => {
  playSound("click");
};

const APP_TAB_DESCRIPTIONS = {
  home: "Retrouve ta progression, ta collection et tes prochaines parties.",
  arena: "Fais s’affronter tes jeux, reprends ton Grand Tournoi et retrouve tes champions.",
  news: "Les dernières nouvelles du jeu vidéo, à lire quand tu veux.",
  search: "Trouve un jeu et ajoute-le à ta collection ou à ta wishlist.",
  upcoming: "Repère les prochaines sorties selon tes plateformes et tes envies.",
  deals: "Les offres Steam et Epic, et les vitrines officielles PlayStation et Xbox.",
  live: "Retrouve les prochains lives et les rendez-vous gaming.",
  social: "Retrouve tes amis, découvre leurs profils et partage tes jeux du moment.",
  library: "Tous tes jeux au même endroit : collection, parties en cours et wishlist.",
  series: "Retrouve tes sagas et les jeux qu’il te reste à découvrir.",
  hardware: "Garde une trace de ton matériel, d’hier et d’aujourd’hui.",
  favorites: "Rassemble les jeux, les objets et le matériel qui comptent pour toi.",
  top5: "Classe tes jeux, ton matériel et tes bandes-son, et choisis tes jeux de l’année.",
  profile: "Ton parcours de joueur : tes jeux marquants, tes badges et ta progression.",
  options: "Règle l'app sans toucher à tes jeux ni à ton matériel.",
};

export function AppHeaderBrand({theme}) {
  return <div className="app-brand">
    <h1 className="title" data-title={theme === CYBER2077_THEME ? "Checkpoint 2077" : "Checkpoint"}>{theme === CYBER2077_THEME ? <Cyber2077Wordmark/> : <><CheckpointMark className="checkpoint-header-mark" variant={theme === CHECKPOINT_ULTIMATE_THEME ? "diamond" : theme === CHECKPOINT_PREMIUM_THEME ? "platinum" : "gold"}/><span className="checkpoint-wordmark">Checkpoint</span></>}</h1>
  </div>;
}

export function TabIntroduction({activeTab, tutorialsReady = true}) {
  const tab = APP_TAB_LABELS[activeTab] ? activeTab : "home";
  return <section className="search-panel tab-introduction" aria-labelledby="tab-introduction-title">
    <h2 className="panel-title" id="tab-introduction-title">{APP_TAB_LABELS[tab]}</h2>
    <p className="option-value">{APP_TAB_DESCRIPTIONS[tab]}</p>
    <TabTutorialEntry key={tab} tab={tab} enabled={tutorialsReady}/>
  </section>;
}

export function BottomTabs({ activeTab, setActiveTab, soundStyle, hiddenTabs=[] }) {
  const tabs = [
  { id: "home", Icon: Home, label: APP_TAB_LABELS.home },
  { id: "library", Icon: Library, label: APP_TAB_LABELS.library },
  { id: "search", Icon: Search, label: APP_TAB_LABELS.search },
  { id: "arena", Icon: Swords, label: APP_TAB_LABELS.arena },
  { id: "profile", Icon: Medal, label: APP_TAB_LABELS.profile },
  { id: "hardware", Icon: Joystick, label: APP_TAB_LABELS.hardware },
  { id: "social", Icon: Users, label: APP_TAB_LABELS.social },
  { id: "series", Icon: Puzzle, label: APP_TAB_LABELS.series },
  { id: "favorites", Icon: Landmark, label: APP_TAB_LABELS.favorites },
  { id: "top5", Icon: Trophy, label: APP_TAB_LABELS.top5 },
  { id: "upcoming", Icon: CalendarDays, label: APP_TAB_LABELS.upcoming },
  { id: "deals", Icon: Sparkles, label: APP_TAB_LABELS.deals },
  { id: "news", Icon: Newspaper, label: APP_TAB_LABELS.news },
  { id: "live", Icon: Radio, label: APP_TAB_LABELS.live },
  { id: "options", Icon: Settings, label: APP_TAB_LABELS.options },
  ];

  return (
    <div className="bottom-tabs">
      {tabs.filter(tab=>!hiddenTabs.includes(tab.id)).map((tab, index) => (
        <button
          key={tab.id}
          className={`tab-btn ${activeTab === tab.id ? "active" : ""}`}
          onClick={() => {
              playSound("click", soundStyle);
              setActiveTab(tab.id);
            }}
          type="button"
        >
          <span className="tab-icon">
            <tab.Icon size={20} strokeWidth={2.4} />
          </span>
          <span className="tab-label">{tab.label}</span>
        </button>
      ))}
    </div>
  );
}

function XPCard({ totalXP, level, title, progress }) {
  return (
    <div className="xp-card">
      <div className="xp-top">
        <div>
          <div className="xp-level">Niveau {level}</div>
          <div className="xp-title">{title}</div>
        </div>
        <div className="xp-total">{totalXP} XP</div>
      </div>

      <div className="xp-bar">
        <div className="xp-fill" style={{ width: `${progress.percent}%` }} />
      </div>

      <div className="xp-bottom">
        {level >= 100
          ? "Rang maximal atteint"
          : `${progress.currentXP} / ${progress.xpToNext} XP avant le niveau suivant`}
      </div>
    </div>
  );
}

function XPCardCompact({ totalXP, level, title, progress }) {
  return (
    <div className="xp-card compact">
      <div className="xp-top compact">
        <div className="xp-compact-left">
          <span className="xp-level compact">Nv {level}</span>
          <span className="xp-title compact">{title}</span>
        </div>
        <div className="xp-total compact">{totalXP} XP</div>
      </div>

      <div className="xp-bar compact">
        <div className="xp-fill" style={{ width: `${progress.percent}%` }} />
      </div>
    </div>
  );
}

function StatsBarCompact({ total, finished, favorites }) {
  return (
    <div className="stats-grid compact">
      <div className="stat-card compact">
        <div className="stat-value">{total}</div>
        <div className="stat-label">Jeux</div>
      </div>

      <div className="stat-card compact">
        <div className="stat-value">{finished}</div>
        <div className="stat-label">Terminés</div>
      </div>

      <div className="stat-card compact">
        <div className="stat-value">{favorites}</div>
        <div className="stat-label">Favoris</div>
      </div>
    </div>
  );
}

/* -------------------- SEARCH CARD -------------------- */

/* ==================== RECHERCHE ==================== */

function SearchResultCard({ game, games, onAdd, onWishlist, isOwned, onOpenSearchDetail }) {

  const [startX, setStartX] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [swipeState, setSwipeState] = useState("");

  const buildGamePayload = (status) => ({
    discoveryMetadata: getDiscoveryMetadata(game),
    rawgId: game.id,
    name: game.name,
    rating: 0,
    favorite: false,
    sanctuary: false,
    image: game.background_image || "",
    status,
    released: game.released || "",
    platformNames: game.platforms?.map((p) => p.platform.name) || [],
    genreNames: game.genres?.map((g) => g.name) || [],
    playtime: game.playtime || null,
    difficulty: getSuggestedDifficulty(game),
    progressStatus: status === "wishlist" ? "not_started" : "not_started",
    playtimeRange: "none",
    review: "",
    ratingGraphics: 0,
    ratingGameplay: 0,
    ratingStory: 0,
    ratingSound: 0,
    ostRating: 0,
    ratingLongevity: 0,
  });

  const handleTouchStart = (e) => {
    setStartX(e.touches[0].clientX);
    setIsDragging(true);
    setSwipeState("");
  };

  const handleTouchMove = (e) => {
    const currentX = e.touches[0].clientX;
    const deltaX = currentX - startX;
    const nextX = Math.max(Math.min(deltaX, 140), -140);
    setTranslateX(nextX);

    if (nextX > 70) setSwipeState("right-ready");
    else if (nextX < -70) setSwipeState("left-ready");
    else setSwipeState("");
  };

  const handleTouchEnd = async () => {
    setIsDragging(false);

    if (translateX > 100) {
      if (navigator.vibrate) navigator.vibrate(25);
      setTranslateX(140);
      setSwipeState("success-right");
      await onWishlist(buildGamePayload("wishlist"));
      setTimeout(() => {
        setTranslateX(0);
        setSwipeState("");
      }, 220);
      return;
    }

    if (translateX < -100) {
      if (navigator.vibrate) navigator.vibrate(25);
      setTranslateX(-140);
      setSwipeState("success-left");
      await onAdd(buildGamePayload("collection"));
      setTimeout(() => {
        setTranslateX(0);
        setSwipeState("");
      }, 220);
      return;
    }

    setTranslateX(0);
    setSwipeState("");
  };

  const existingGame = games.find(
  (g) =>
    g.rawgId === game.id ||
    g.name.toLowerCase() === game.name.toLowerCase()
  );
  const isIgdbResult = game.source === "igdb";
  const displayImage =
    game.cover_image ||
    game.image ||
    game.background_image ||
    game.short_screenshots?.[0]?.image ||
    "";
  const backdropImage =
    game.background_image ||
    game.short_screenshots?.[0]?.image ||
    displayImage;

  return (
    <div className="swipe-wrapper">
      <div
        className={`swipe-bg swipe-bg-wishlist ${
          translateX > 60 ? "visible" : ""
        } ${swipeState === "success-right" ? "confirmed" : ""}`}
      >
        <div className="swipe-big-icon">
          <BookmarkPlus size={58} />
        </div>
      </div>

      <div
        className={`swipe-bg-left swipe-bg-collection ${
          translateX < -60 ? "visible" : ""
        } ${swipeState === "success-left" ? "confirmed" : ""}`}
      >
        <div className="swipe-big-icon">
          <Library size={58} />
        </div>
      </div>

      {game.status && (
        <div className={`status-badge ${game.status}`}>
          {game.status === "wishlist" ? (
            <List size={14} />
          ) : game.status === "en cours" ? (
            <Play size={14} />
          ) : (
            <Check size={14} />
          )}
        </div>
      )}

      <div
        className={`game-card swipe-card ${isIgdbResult ? "igdb-result-card" : ""} ${isDragging ? "dragging" : ""} ${
          translateX > 40 ? "swiping-wishlist" : ""
        } ${translateX < -40 ? "swiping-collection" : ""}`}
        onClick={() => {
          if (onOpenSearchDetail) {
            onOpenSearchDetail(game);
          }
        }}
                onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={(e) => {
          e.stopPropagation();
          handleTouchEnd();
        }}
        style={{
          transform: `translateX(${translateX}px)`,
          transition: isDragging
          ? "none"
          : "transform 0.28s cubic-bezier(0.34, 1.56, 0.64, 1)",
        }}
      >
        {displayImage ? (
          <>
            <img
              src={backdropImage}
              alt=""
              className="game-image-backdrop"
              aria-hidden="true"
            />
            <img
              src={displayImage}
              alt={game.name}
              className="game-image"
            />
          </>
        ) : (
          <div className="game-image placeholder">🎮</div>
        )}

        <div className="game-overlay">
          <div className="game-title">{game.name}</div>
          {isFanGame(game) && <span className="search-fan-badge">Jeu de fans</span>}

          <div className="game-meta">
            <span>
              {game.released?.split("-")[0] || "—"} • ⭐{" "}
              {Math.round((game.rating || 0) * 10) / 10}
              {game.genres?.length > 0 &&
                ` • ${game.genres.slice(0, 2).map((g) => g.name).join(", ")}`}
            </span>
          </div>

          {isOwned && (
            <div className="already-owned-badge">
              <Check size={13} />
              Déjà ajouté
            </div>
          )}

          <div className="swipe-hints">
            <span className="hint-left">
              <BookmarkPlus size={15} /> Wishlist →
            </span>

            <span className="hint-right">
              ← <Library size={15} /> Collection
            </span>
          </div>
        </div>
      </div>
    </div>

  );
}

/* -------------------- UPCOMING CARD -------------------- */

/* ==================== SORTIES À VENIR ==================== */

function UpcomingGameCard({ game, onWishlist }) {
  const isIgdbResult = game.source === "igdb";
  const displayImage =
    game.cover_image ||
    game.image ||
    game.background_image ||
    game.short_screenshots?.[0]?.image ||
    "";
  const backdropImage =
    game.background_image ||
    game.short_screenshots?.[0]?.image ||
    displayImage;
  const isFallbackArt = String(displayImage).startsWith("data:image/svg+xml");
  const payload = {
    name: game.name,
    rating: 0,
    favorite: false,
    sanctuary: false,
    image: displayImage || "",
    status: "wishlist",
    released: game.released || "",
    platformNames: game.platforms?.map((p) => p.platform.name) || [],
    genreNames: game.genres?.map((g) => g.name) || [],
    playtime: game.playtime || null,
    difficulty: getSuggestedDifficulty(game),
    review: "",
    ratingGraphics: 0,
    ratingGameplay: 0,
    ratingStory: 0,
    ratingSound: 0,
    ostRating: 0,
    ratingLongevity: 0,
  };

  return (
    <div className={`upcoming-card ${isIgdbResult ? "igdb-upcoming-card" : ""} ${isFallbackArt ? "fallback-art" : ""}`}>
      {displayImage ? (
        <div className="upcoming-image-frame">
          <img src={backdropImage} alt="" className="upcoming-image-backdrop" aria-hidden="true" />
          <img src={displayImage} alt={game.name} className="upcoming-image" />
        </div>
      ) : (
        <div className="upcoming-image-frame placeholder">🎮</div>
      )}

      <div className="upcoming-content">
        <div className="upcoming-title">{game.name}</div>
        <div className="upcoming-date">{formatReleaseDate(game.released)}</div>
        <div className="grid-countdown">
          {getReleaseCountdown(game.released)}
        </div>

        {game.platforms?.length > 0 && (
          <div className="upcoming-meta">
            {game.platforms.slice(0, 3).map((p) => p.platform.name).join(" • ")}
          </div>
        )}

        <button
          className="wishlist-upcoming-btn"
          type="button"
          onClick={() => onWishlist(payload)}
        >
          Ajouter à la wishlist
        </button>
      </div>
    </div>
  );
}

/* -------------------- RATING -------------------- */

/* ==================== SYSTÈME DE NOTATION ==================== */

function Star({ fill = 0, onHalfClick, onFullClick }) {
  return (
    <div className="star-hitbox">
      <button
        type="button"
        className="star-click left"
        onClick={onHalfClick}
        aria-label="Demi-étoile"
      />
      <button
        type="button"
        className="star-click right"
        onClick={onFullClick}
        aria-label="Étoile entière"
      />
      <span className="star-base">★</span>
      <span className="star-fill" style={{ width: `${fill * 100}%` }}>
        ★
      </span>
    </div>
  );
}

function RatingSlider(props) {
  return <RatingSliderControl {...props} formatValue={formatRating10} />;
}

const CONTEXTUAL_GAME_RATING_FIELDS = [
  {
    key: "ratingOpenWorld",
    label: "Open world",
    hint: "Monde ouvert, densité, exploration libre",
    topLabel: "Top open worlds",
    keywords: ["open world", "sandbox", "action-adventure", "rpg"],
  },
  {
    key: "ratingGunplay",
    label: "Sensation de tir",
    hint: "Impact, feeling des armes, lisibilité des affrontements",
    topLabel: "Top shooters",
    keywords: ["shooter", "fps", "tps", "first-person", "third-person", "gunplay"],
  },
  {
    key: "ratingDriving",
    label: "Conduite",
    hint: "Feeling, vitesse, précision, plaisir au volant",
    topLabel: "Top pilotage",
    keywords: ["racing", "driving", "course", "pilotage", "motorsport", "rally"],
  },
  {
    key: "ratingCombat",
    label: "Combat",
    hint: "Rythme, précision, profondeur des affrontements",
    topLabel: "Top combats",
    keywords: ["fighting", "soulslike", "hack and slash", "beat em up", "beat-em-up", "brawler", "action rpg"],
  },
  {
    key: "ratingExploration",
    label: "Exploration",
    hint: "Curiosité, récompenses, envie de fouiller",
    topLabel: "Top exploration",
    keywords: ["adventure", "open world", "metroidvania", "platformer", "rpg"],
  },
  {
    key: "ratingChallenge",
    label: "Challenge / boss",
    hint: "Difficulté, boss, tension et satisfaction",
    topLabel: "Top challenge",
    keywords: ["soulslike", "fighting", "platformer", "roguelike", "boss", "hardcore"],
  },
  {
    key: "ratingMultiplayer",
    label: "Coop / multi",
    hint: "Plaisir à plusieurs, équilibre, rejouabilité",
    topLabel: "Top coop / multi",
    keywords: ["multiplayer", "co-op", "coop", "online", "mmo"],
  },
  {
    key: "ratingStealth",
    label: "Infiltration",
    hint: "Discrétion, level design, tension et outils",
    topLabel: "Top infiltration",
    keywords: ["stealth", "infiltration", "tactical espionage", "assassin"],
  },
  {
    key: "ratingPuzzle",
    label: "Puzzle / réflexion",
    hint: "Logique, lisibilité, satisfaction des énigmes",
    topLabel: "Top puzzle",
    keywords: ["puzzle", "logic", "escape", "detective"],
  },
  {
    key: "ratingPlatforming",
    label: "Plateforme",
    hint: "Précision, rythme, level design et flow",
    topLabel: "Top plateforme",
    keywords: ["platformer", "platform", "metroidvania"],
  },
  {
    key: "ratingHorror",
    label: "Tension / survie",
    hint: "Ambiance, vulnérabilité, peur et gestion des ressources",
    topLabel: "Top horreur",
    keywords: ["horror", "survival horror", "survie", "terror"],
  },
];

function getContextualRatingFields(game) {
  const text = getTopGameSearchText(game);
  const genreLabels = getGameGenreSignals(game).map((genre) =>
    normalizeIdentityText(genre.label)
  );

  return CONTEXTUAL_GAME_RATING_FIELDS.filter((field) =>
    field.keywords.some((keyword) => {
      const normalizedKeyword = normalizeIdentityText(keyword);
      return text.includes(normalizedKeyword) || genreLabels.includes(normalizedKeyword);
    })
  );
}

function DetailedRatingsBlock({ game, onSetDetailedRating }) {
  const summary = getGameDetailedRatingSummary(game);

  return (
    <div className="game-ratings-panel">
      <div className="game-ratings-summary">
        <div>
          <span>Moyenne détaillée</span>
          <strong>{formatRating10(averageDetailedRating(game), "En attente de la note complète")}</strong>
        </div>

        <div>
          <span>Critères essentiels</span>
          <strong>
            {summary.ratedBaseFields.length}/{summary.baseFields.length}
          </strong>
        </div>
      </div>

      {summary.bestField && (
        <div className="game-ratings-insights">
          <div className="game-ratings-insight strong">
            <span>Point fort</span>
            <strong>{summary.bestField.label}</strong>
            <small>{formatRating10(summary.bestField.value, "-")}</small>
          </div>
        </div>
      )}

      <div className="game-ratings-section-head">
        <div>
          <strong>Critères essentiels</strong>
          <span>La base commune à tous les jeux pour garder des notes comparables.</span>
        </div>
        <small>{summary.completion}%</small>
      </div>

      <div className="detailed-ratings-grid">
        {summary.baseFields.map((item) => (
          <div
            key={item.key}
            className={`detailed-rating-card ${item.value > 0 ? "rated" : ""}`}
          >
            <div className="detailed-rating-title">
              <span>{item.label}</span>
              <em>{formatRating10(item.value, "À noter")}</em>
            </div>
            <small className="detailed-rating-hint">{item.hint}</small>
            <RatingSlider
              rating={item.value}
              onRate={(value) => onSetDetailedRating(game.id, item.key, value)}
            />
          </div>
        ))}
      </div>

      {summary.contextualFields.length > 0 && (
        <div className="contextual-ratings-block">
          <div className="contextual-ratings-head">
            <div>
              <strong>Critères spécifiques</strong>
              <span>
                Ces notes servent aux Tops spécialisés quand le jeu s'y prête.
              </span>
            </div>
            <small>
              {summary.ratedContextualFields.length}/{summary.contextualFields.length} notés
            </small>
          </div>

          <div className="detailed-ratings-grid contextual">
            {summary.contextualFields.map((item) => (
              <div
                key={item.key}
                className={`detailed-rating-card contextual ${
                  item.value > 0 ? "rated" : ""
                }`}
              >
                <div className="detailed-rating-title">
                  <span>
                    {item.label}
                    <em>{item.topLabel}</em>
                  </span>
                  <small>{item.hint}</small>
                </div>
                <RatingSlider
                  rating={item.value}
                  onRate={(value) => onSetDetailedRating(game.id, item.key, value)}
                />
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* -------------------- STATS -------------------- */

function StatsBar({ total, wishlist, inProgress, finished, favorites }) {
  return (
    <div className="stats-grid">
      <div className="stat-card">
        <div className="stat-value">{total}</div>
        <div className="stat-label">Total</div>
      </div>

      <div className="stat-card">
        <div className="stat-value">{wishlist}</div>
        <div className="stat-label">Wishlist</div>
      </div>

      <div className="stat-card">
        <div className="stat-value">{inProgress}</div>
        <div className="stat-label">En cours</div>
      </div>

      <div className="stat-card">
        <div className="stat-value">{finished}</div>
        <div className="stat-label">Terminé</div>
      </div>

      <div className="stat-card favorite">
        <div className="stat-value">{favorites}</div>
        <div className="stat-label">Coups de cœur</div>
      </div>
    </div>
  );
}

/* -------------------- TOP 5 TAB -------------------- */

function Top5Section({ title, games, scoreKey }) {
  const sorted = [...games]
    .filter((g) => getGameScore(g, scoreKey) > 0)
    .sort((a, b) => {
      const aScore = getGameScore(a, scoreKey);
      const bScore = getGameScore(b, scoreKey);
      return bScore - aScore;
    })
    .slice(0, 5);

  return (
    <div className="search-panel">
      <h2 className="panel-title">{title}</h2>

      {sorted.length === 0 ? (
        <EmptyState
          title="Pas assez de notes"
          subtitle="Ajoute des notes pour voir ce top 5."
        />
      ) : (
        <div className="top5-list">
          {sorted.map((game, index) => {
            const score = getGameScore(game, scoreKey);
            return (
              <div key={`${title}-${game.id}`} className="top5-item">
                <div className="top5-rank">#{index + 1}</div>
                {game.image ? (
                  <img src={game.image} alt={game.name} className="top5-thumb" />
                ) : (
                  <div className="top5-thumb placeholder">🎮</div>
                )}
                <div className="top5-main">
                  <div className="top5-name">{game.name}</div>
                  <div className="top5-meta">
                    {formatTopScore(score, scoreKey)} • {game.released ? game.released.split("-")[0] : "—"}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Top5Tab({ games }) {
  const scopedGames = games.filter(isTopEligibleGame);

  return (
    <div className="progression-stack">
      <Top5Section title="Top 5 global" games={scopedGames} scoreKey="rating" />
      <Top5Section title="Top 5 gameplay" games={scopedGames} scoreKey="ratingGameplay" />
      <Top5Section title="Top 5 histoire" games={scopedGames} scoreKey="ratingStory" />
      <Top5Section title="Top 5 graphismes" games={scopedGames} scoreKey="ratingGraphics" />
      <Top5Section title="Top 5 audio" games={scopedGames} scoreKey="ratingSound" />
    </div>
  );
}

const TOP5_SCORE_OPTIONS = [
  { id: "rating", label: "Global", title: "Top 5 global" },
  { id: "ratingGameplay", label: "Gameplay", title: "Top 5 gameplay" },
  { id: "ratingGraphics", label: "Graphismes", title: "Top 5 graphismes" },
  { id: "ratingStory", label: "Histoire", title: "Top 5 histoire" },
  { id: "ratingSound", label: "Audio", title: "Top 5 audio" },
  { id: "ratingLongevity", label: "Durée de vie", title: "Top 5 durée de vie" },
];

const TOP5_HARDWARE_GROUPS = [
  { id: "all", label: "Tout", title: "Top 5 matériel" },
  { id: "console", label: "Consoles", title: "Top 5 consoles" },
  { id: "controller", label: "Manettes", title: "Top 5 manettes" },
  { id: "audio", label: "Casques", title: "Top 5 casques" },
  { id: "speaker", label: "Enceintes", title: "Top 5 enceintes" },
  { id: "vr", label: "VR", title: "Top 5 VR" },
  { id: "mouse", label: "Souris", title: "Top 5 souris" },
  { id: "keyboard", label: "Claviers", title: "Top 5 claviers" },
  { id: "display", label: "Écrans / TV", title: "Top 5 écrans / TV" },
];

const TOP5_ADVANCED_LISTS = [
  {
    id: "open-world",
    label: "Open worlds",
    title: "Top open worlds",
    criterionLabel: "Open world",
    description: "Densité du monde, exploration libre, envie de s'y perdre.",
    scoreKey: "ratingOpenWorld",
    keywords: ["open world", "adventure", "action-adventure", "rpg"],
  },
  {
    id: "rpg",
    label: "RPG",
    title: "Top RPG",
    criterionLabel: "Histoire",
    description: "Aventure, progression, écriture et attachement au voyage.",
    scoreKey: "ratingStory",
    keywords: ["rpg", "role-playing", "jrpg", "soulslike"],
  },
  {
    id: "shooters",
    label: "Shooters",
    title: "Top shooters",
    criterionLabel: "Sensation de tir",
    description: "Impact des armes, précision, rythme des affrontements.",
    scoreKey: "ratingGunplay",
    keywords: ["shooter", "fps", "tps", "first-person", "third-person"],
  },
  {
    id: "pilotage",
    label: "Pilotage",
    title: "Top pilotage",
    criterionLabel: "Conduite",
    description: "Feeling, vitesse, maîtrise, plaisir manette en main.",
    scoreKey: "ratingDriving",
    keywords: ["racing", "driving", "simulation", "sports"],
  },
  {
    id: "ambiance",
    label: "Ambiance",
    title: "Top ambiance",
    criterionLabel: "Audio",
    description: "Immersion sonore, tension, identité et atmosphère.",
    scoreKey: "ratingSound",
    keywords: ["horror", "survival", "atmospheric", "adventure"],
  },
  {
    id: "combat",
    label: "Combat",
    title: "Top combats",
    criterionLabel: "Combat",
    description: "Rythme, profondeur, lisibilité et satisfaction des duels.",
    scoreKey: "ratingCombat",
    keywords: ["fighting", "soulslike", "hack and slash", "beat em up", "beat-em-up", "brawler", "action rpg"],
  },
  {
    id: "exploration",
    label: "Exploration",
    title: "Top exploration",
    criterionLabel: "Exploration",
    description: "Curiosité, secrets, récompenses et envie de fouiller.",
    scoreKey: "ratingExploration",
    keywords: ["adventure", "open world", "metroidvania", "platformer", "rpg"],
  },
  {
    id: "challenge",
    label: "Challenge",
    title: "Top challenge",
    criterionLabel: "Challenge / boss",
    description: "Difficulté, boss, tension et satisfaction après l'effort.",
    scoreKey: "ratingChallenge",
    keywords: ["soulslike", "fighting", "platformer", "roguelike", "boss", "hardcore"],
  },
  {
    id: "multi",
    label: "Coop / multi",
    title: "Top coop / multi",
    criterionLabel: "Coop / multi",
    description: "Plaisir à plusieurs, équilibre, rejouabilité et moments partagés.",
    scoreKey: "ratingMultiplayer",
    keywords: ["multiplayer", "co-op", "coop", "online", "mmo"],
  },
  {
    id: "indes",
    label: "Indes",
    title: "Top jeux indes",
    criterionLabel: "Global",
    description: "Les jeux plus personnels, inventifs ou surprenants de ta bibliothèque.",
    scoreKey: "rating",
    keywords: ["indie", "platformer", "puzzle", "metroidvania"],
  },
  {
    id: "infiltration",
    label: "Infiltration",
    title: "Top infiltration",
    criterionLabel: "Infiltration",
    description: "Discrétion, outils, tension et plaisir de passer sans bruit.",
    scoreKey: "ratingStealth",
    keywords: ["stealth", "infiltration", "tactical espionage", "assassin"],
  },
  {
    id: "puzzle",
    label: "Puzzle",
    title: "Top puzzle",
    criterionLabel: "Puzzle / réflexion",
    description: "Logique, élégance des énigmes et satisfaction quand tout s'emboîte.",
    scoreKey: "ratingPuzzle",
    keywords: ["puzzle", "logic", "escape", "detective"],
  },
  {
    id: "plateforme",
    label: "Plateforme",
    title: "Top plateforme",
    criterionLabel: "Plateforme",
    description: "Précision, rythme, level design et flow.",
    scoreKey: "ratingPlatforming",
    keywords: ["platformer", "platform", "metroidvania"],
  },
  {
    id: "horreur",
    label: "Horreur",
    title: "Top horreur",
    criterionLabel: "Tension / survie",
    description: "Ambiance, vulnérabilité, peur et gestion des ressources.",
    scoreKey: "ratingHorror",
    keywords: ["horror", "survival horror", "survie", "terror"],
  },
];

const TOP5_CONTEXT_SCORE_FALLBACKS = {
  ratingOpenWorld: "rating",
  ratingGunplay: "ratingGameplay",
  ratingDriving: "ratingGameplay",
  ratingCombat: "ratingGameplay",
  ratingExploration: "rating",
  ratingChallenge: "ratingGameplay",
  ratingMultiplayer: "ratingLongevity",
  ratingStealth: "ratingGameplay",
  ratingPuzzle: "ratingGameplay",
  ratingPlatforming: "ratingGameplay",
  ratingHorror: "ratingSound",
};

function getGameScore(game, scoreKey) {
  const contextualScore = clampRating(game?.[scoreKey]);
  if (contextualScore > 0) return contextualScore;

  const fallbackKey = TOP5_CONTEXT_SCORE_FALLBACKS[scoreKey];
  return fallbackKey ? clampRating(game?.[fallbackKey]) : contextualScore;
}

function getTopScoreSource(game, scoreKey) {
  if (clampRating(game?.[scoreKey]) > 0) return "Note spécialisée";

  const fallbackKey = TOP5_CONTEXT_SCORE_FALLBACKS[scoreKey];
  if (fallbackKey && clampRating(game?.[fallbackKey]) > 0) {
    return "Note approchée";
  }

  return "";
}

function getNormalizedStatus(status = "") {
  return normalizeIdentityText(status).replace(/\s+/g, " ").trim();
}

function isGameFinishedStatus(game = {}) {
  const status = getNormalizedStatus(game?.status || "");
  return (
    status.includes("termin") ||
    game?.completed === true ||
    game?.progressStatus === "completed"
  );
}

function isGameInCollection(game = {}) {
  const status = getNormalizedStatus(game?.status || "");
  return status === "collection" || isGameFinishedStatus(game);
}

function getNormalizedGameProgressPatch(game = {}) {
  const status = getNormalizedStatus(game.status || "");
  const isFinished = isGameFinishedStatus(game);
  const isInProgress = status.includes("cours") || game.progressStatus === "in_progress";
  const isWishlist = status === "wishlist";

  const nextState = isFinished
    ? { status: "collection", progressStatus: "completed", completed: true }
    : isInProgress
      ? { status: "en cours", progressStatus: "in_progress", completed: false }
      : isWishlist
        ? { status: "wishlist", progressStatus: "not_started", completed: false }
        : { status: "collection", progressStatus: isStoppedGame(game)?game.progressStatus:"not_started", completed: false };

  const patch = {};
  if (game.status !== nextState.status) patch.status = nextState.status;
  if ((game.progressStatus || "not_started") !== nextState.progressStatus) {
    patch.progressStatus = nextState.progressStatus;
  }
  if (Boolean(game.completed) !== nextState.completed) {
    patch.completed = nextState.completed;
  }

  return Object.keys(patch).length ? patch : null;
}

function getGameProgressState(game = {}) {
  const status = getNormalizedStatus(game.status || "");
  if (isGameFinishedStatus(game)) return "finished";
  if(isStoppedGame(game))return game.progressStatus;
  if (status.includes("cours") || game.progressStatus === "in_progress") return "inProgress";
  if (status === "wishlist") return "wishlist";
  return "collection";
}

function getGameProgressStateLabel(state) {
  return (
    {
      paused: "En pause",
      abandoned: "Abandonné",
      finished: "Terminé",
      inProgress: "En cours",
      wishlist: "Wishlist",
      collection: "Collection",
    }[state] || "Collection"
  );
}

function getGameIntegrityTargetLabel(game = {}, patch = {}) {
  return getGameProgressStateLabel(
    getGameProgressState({
      ...game,
      ...patch,
    })
  );
}

function getGameIntegrityChangeSummary(game = {}, patch = {}) {
  const changes = [];
  if (Object.prototype.hasOwnProperty.call(patch, "status")) {
    changes.push(`statut ${game.status || "-"} -> ${patch.status}`);
  }
  if (Object.prototype.hasOwnProperty.call(patch, "progressStatus")) {
    changes.push(`progression ${game.progressStatus || "not_started"} -> ${patch.progressStatus}`);
  }
  if (Object.prototype.hasOwnProperty.call(patch, "completed")) {
    changes.push(`terminé ${game.completed ? "oui" : "non"} -> ${patch.completed ? "oui" : "non"}`);
  }
  return changes.join(" · ");
}

function getGameDataIntegrityIssues(games = []) {
  return games
    .map((game) => {
      const patch = getNormalizedGameProgressPatch(game);
      if (!patch) return null;

      const reasons = [];
      const status = getNormalizedStatus(game.status || "");

      if (status.includes("termin")) {
        reasons.push("ancien statut terminé");
      }
      if (game.completed === true && game.progressStatus !== "completed") {
        reasons.push("terminé sans progression terminée");
      }
      if (game.progressStatus === "completed" && game.completed !== true) {
        reasons.push("progression terminée sans validation");
      }
      if (status.includes("cours") && game.progressStatus !== "in_progress") {
        reasons.push("en cours sans progression en cours");
      }
      if (game.progressStatus === "in_progress" && !status.includes("cours")) {
        reasons.push("progression en cours hors onglet en cours");
      }

      return {
        game,
        patch,
        reasons: reasons.length ? reasons : ["statut à normaliser"],
      };
    })
    .filter(Boolean);
}

function isTopFinishedGame(game) {
  return isGameFinishedStatus(game);
}

function isTopEligibleGame(game) {
  const status = getNormalizedStatus(game?.status);
  return ["completed", "partial"].includes(getGameExperience(game));
}

function formatTopScore(score, scoreKey = "rating") {
  const value = Number(score) || 0;
  if (!value) return "-";

  return formatRating10(value, "-");
}

function getTopGameMeta(game) {
  const year = game.released ? game.released.split("-")[0] : "";
  const platforms = game.playedPlatforms?.length
    ? game.playedPlatforms
    : game.platformNames || [];
  const genres = game.genreNames || [];

  return [getGameExperience(game) === "partial" ? "Avis partiel" : "", year, platforms[0], genres[0]].filter(Boolean).join(" - ") || "Jeu classe";
}

function getTopGroups(games, field) {
  const counts = {};

  games.forEach((game) => {
    const values =
      field === "platform"
        ? game.playedPlatforms?.length
          ? game.playedPlatforms
          : game.platformNames || []
        : game.genreNames || [];

    values.forEach((value) => {
      if (!value) return;
      counts[value] = (counts[value] || 0) + 1;
    });
  });

  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    .map(([name, count]) => ({ name, count }));
}

function filterGamesForTop(games, mode, selectedGroup) {
  if (!selectedGroup) return games;

  if (mode === "platform") {
    return games.filter((game) => {
      const platforms = game.playedPlatforms?.length
        ? game.playedPlatforms
        : game.platformNames || [];

      return platforms.includes(selectedGroup);
    });
  }

  if (mode === "genre") {
    return games.filter((game) => (game.genreNames || []).includes(selectedGroup));
  }

  return games;
}

function getTopGamesForScore(games, scoreKey, limit = 3) {
  return [...games]
    .filter((game) => isTopEligibleGame(game) && getGameScore(game, scoreKey) > 0)
    .sort((a, b) => {
      const statusDiff = Number(isTopFinishedGame(b)) - Number(isTopFinishedGame(a));
      if (statusDiff !== 0) return statusDiff;
      const scoreDiff = getGameScore(b, scoreKey) - getGameScore(a, scoreKey);
      if (scoreDiff !== 0) return scoreDiff;
      return (a.name || "").localeCompare(b.name || "");
    })
    .slice(0, limit);
}

function getTopGameSearchText(game) {
  return [
    game.name,
    game.slug,
    ...(game.genreNames || []),
    ...(game.platformNames || []),
    ...(game.playedPlatforms || []),
    ...(game.tags || []).map((tag) => tag?.name || tag),
  ]
    .filter(Boolean)
    .map((value) => normalizeIdentityText(String(value)))
    .join(" ");
}

function getTopAdvancedGames(games, list) {
  const keywords = list.keywords.map((keyword) => normalizeIdentityText(keyword));
  const filtered = games.filter((game) => {
    const text = getTopGameSearchText(game);
    return keywords.some((keyword) => text.includes(keyword));
  });

  return getTopGamesForScore(filtered, list.scoreKey, 5);
}

function getTopOstGames(games, limit = 5) {
  return [...games]
    .filter((game) => isTopEligibleGame(game) && getGameScore(game, "ostRating") > 0)
    .sort((a, b) => {
      const scoreDiff =
        getGameScore(b, "ostRating") - getGameScore(a, "ostRating");
      if (scoreDiff !== 0) return scoreDiff;

      return (a.name || "").localeCompare(b.name || "");
    })
    .slice(0, limit);
}

function isTopEligibleHardware(item) {
  const status = getNormalizedStatus(item?.status);
  return (
    getHardwareAverageRating(item) > 0 &&
    !status.includes("wishlist") &&
    !status.includes("souhait")
  );
}

function getHardwareTypeLabel(type = "") {
  return (
    TOP5_HARDWARE_GROUPS.find((group) => group.id === type)?.label ||
    "Matériel"
  );
}

function getTopHardwareMeta(item) {
  return [getHardwareTypeLabel(item.type), item.brand, item.status]
    .filter(Boolean)
    .join(" - ");
}

function getTopHardwareItems(hardware, type = "all", limit = 5) {
  return [...hardware]
    .filter(
      (item) =>
        isTopEligibleHardware(item) && (type === "all" || item.type === type)
    )
    .sort((a, b) => {
      const ratingDiff = getHardwareAverageRating(b) - getHardwareAverageRating(a);
      if (ratingDiff !== 0) return ratingDiff;
      return (a.name || "").localeCompare(b.name || "");
    })
    .slice(0, limit);
}

function getHardwareCriterionScore(item, criterionKey = "average") {
  if (criterionKey === "average") return getHardwareAverageRating(item);
  return clampRating(item?.ratings?.[criterionKey]);
}

function getTopHardwareItemsForCriterion(
  hardware,
  type = "all",
  criterionKey = "average",
  limit = 5
) {
  return [...hardware]
    .filter(
      (item) =>
        isTopEligibleHardware(item) &&
        (type === "all" || item.type === type) &&
        getHardwareCriterionScore(item, criterionKey) > 0
    )
    .sort((a, b) => {
      const ratingDiff =
        getHardwareCriterionScore(b, criterionKey) -
        getHardwareCriterionScore(a, criterionKey);
      if (ratingDiff !== 0) return ratingDiff;
      return (a.name || "").localeCompare(b.name || "");
    })
    .slice(0, limit);
}

function getHardwareCriterionOptions(type = "all") {
  if (type === "all") {
    return [{ key: "average", label: "Moyenne globale", icon: "TOP" }];
  }

  return [
    { key: "average", label: "Moyenne globale", icon: "TOP" },
    ...getHardwareRatingFields(type),
  ];
}

function getGameYear(game) {
  const year = Number(String(game?.released || "").slice(0, 4));
  return Number.isFinite(year) && year > 1970 ? year : null;
}

function isGameOfYearEligible(game) {
  const status = getNormalizedStatus(game?.status);
  return (
    getGameYear(game) &&
    !status.includes("wishlist") &&
    !status.includes("souhait")
  );
}

function getGameOfYearYears(games) {
  return [...new Set(games.filter(isGameOfYearEligible).map(getGameYear))]
    .filter(Boolean)
    .sort((a, b) => b - a);
}

function serializeShowcaseGame(game, scoreKey = "rating") {
  if (!game) return null;

  return {
    id: game.id,
    name: game.name,
    image: game.image || "",
    score: getGameScore(game, scoreKey),
    meta: getTopGameMeta(game),
  };
}

function getProfileShowcase(games = [], hardware = []) {
  const topScores = TOP5_SCORE_OPTIONS.map((option) => {
    const game = getTopGamesForScore(games, option.id, 1)[0];

    return game
      ? {
          key: option.id,
          label: option.label,
          game: serializeShowcaseGame(game, option.id),
        }
      : null;
  })
    .filter(Boolean)
    .slice(0, 6);

  const specializedTops = TOP5_ADVANCED_LISTS.map((list) => {
    const game = getTopAdvancedGames(games, list)[0];

    return game
      ? {
          key: list.scoreKey,
          label: list.label,
          criterion: list.criterionLabel,
          game: serializeShowcaseGame(game, list.scoreKey),
        }
      : null;
  })
    .filter(Boolean)
    .slice(0, 4);

  const gotyHighlights = games
    .filter((game) => Number(game.gotyYear) > 0)
    .sort((a, b) => Number(b.gotyYear) - Number(a.gotyYear))
    .slice(0, 3)
    .map((game) => ({
      year: Number(game.gotyYear),
      game: serializeShowcaseGame(game, "rating"),
    }));

  const hardwareHighlights = getTopHardwareItems(
    getCurrentOwnedHardware(hardware),
    "all",
    3
  ).map((item) => ({
    id: item.id,
    name: item.name,
    image: item.image || "",
    type: item.type || "",
    label: getHardwareTypeLabel(item.type),
    score: getHardwareAverageRating(item),
  }));

  return {
    topScores,
    specializedTops,
    gotyHighlights,
    hardwareHighlights,
  };
}

function getTop5OverviewData(games = [], hardware = []) {
  const eligibleGames = games.filter(isTopEligibleGame);
  const ratedGames = eligibleGames.filter((game) => getGameRating(game) > 0);
  const bestGame = getTopGamesForScore(eligibleGames, "rating", 1)[0] || null;
  const bestHardware = getTopHardwareItems(hardware, "all", 1)[0] || null;
  const bestOst = getTopOstGames(eligibleGames, 1)[0] || null;
  const gotyYears = getGameOfYearYears(games);
  const latestGotyYear = gotyYears[0] || null;
  const latestGoty =
    latestGotyYear &&
    games.find((game) => Number(game.gotyYear) === Number(latestGotyYear));
  const specializedFilled = TOP5_ADVANCED_LISTS.filter(
    (list) => getTopAdvancedGames(eligibleGames, list).length > 0
  ).length;
  const scoreFilled = TOP5_SCORE_OPTIONS.filter(
    (option) => getTopGamesForScore(eligibleGames, option.id, 5).length > 0
  ).length;
  const platformGroups = getTopGroups(eligibleGames, "platform");
  const genreGroups = getTopGroups(eligibleGames, "genre");

  return {
    bestGame,
    bestHardware,
    bestOst,
    latestGoty,
    latestGotyYear,
    ratedGamesCount: ratedGames.length,
    eligibleGamesCount: eligibleGames.length,
    hardwareRatedCount: getTopHardwareItems(hardware, "all", 999).length,
    ostRatedCount: getTopOstGames(eligibleGames, 999).length,
    gotyCount: games.filter((game) => Number(game.gotyYear) > 0).length,
    specializedFilled,
    scoreFilled,
    platformGroupsCount: platformGroups.length,
    genreGroupsCount: genreGroups.length,
  };
}

function Top5OverviewPanel({ games = [], hardware = [] }) {
  const overview = getTop5OverviewData(games, hardware);
  const gameCoverage = overview.eligibleGamesCount
    ? Math.round((overview.ratedGamesCount / overview.eligibleGamesCount) * 100)
    : 0;
  const specializedCoverage = Math.round(
    (overview.specializedFilled / TOP5_ADVANCED_LISTS.length) * 100
  );
  const criteriaCoverage = Math.round(
    (overview.scoreFilled / TOP5_SCORE_OPTIONS.length) * 100
  );
  const cards = [
    {
      label: "Champion global",
      value: overview.bestGame?.name || "A definir",
      detail: overview.bestGame
        ? formatRating10(getGameRating(overview.bestGame), "-")
        : "Note quelques jeux pour lancer le classement.",
      image: overview.bestGame?.image,
    },
    {
      label: "Materiel repere",
      value: overview.bestHardware?.name || "A calibrer",
      detail: overview.bestHardware
        ? formatRating10(getHardwareAverageRating(overview.bestHardware), "-")
        : "Ajoute des notes materiel pour faire sortir un favori.",
      image: overview.bestHardware?.image,
    },
    {
      label: "OST en tete",
      value: overview.bestOst?.name || "Pas encore d'OST",
      detail: overview.bestOst
        ? formatRating10(getGameScore(overview.bestOst, "ostRating"), "-")
        : "Note les bandes-son dans les fiches jeux.",
      image: overview.bestOst?.image,
    },
    {
      label: overview.latestGotyYear ? `GOTY ${overview.latestGotyYear}` : "GOTY",
      value: overview.latestGoty?.name || "A choisir",
      detail: overview.latestGoty
        ? formatRating10(getGameRating(overview.latestGoty), "-")
        : "Choisis tes jeux de l'annee.",
      image: overview.latestGoty?.image,
    },
  ];
  const gauges = [
    {
      label: "Notes jeux",
      value: `${overview.ratedGamesCount}/${overview.eligibleGamesCount}`,
      percent: gameCoverage,
    },
    {
      label: "Criteres",
      value: `${overview.scoreFilled}/${TOP5_SCORE_OPTIONS.length}`,
      percent: criteriaCoverage,
    },
    {
      label: "Specialises",
      value: `${overview.specializedFilled}/${TOP5_ADVANCED_LISTS.length}`,
      percent: specializedCoverage,
    },
  ];

  return (
    <div className="search-panel top5-overview-panel">
      <div className="profile-section-header">
        <div>
          <h2 className="panel-title">Tableau de bord Top 5</h2>
          <div className="option-value">
            Un resume rapide de tes classements les plus solides et de ce qu'il reste a completer.
          </div>
        </div>
      </div>

      <div className="top5-overview-grid">
        {cards.map((card) => (
          <div key={card.label} className="top5-overview-card">
            {card.image ? (
              <img src={hardwarePhotoUrl(card.image)} alt={card.value} />
            ) : (
              <div className="top5-overview-placeholder">{card.label.slice(0, 3)}</div>
            )}
            <div>
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <small>{card.detail}</small>
            </div>
          </div>
        ))}
      </div>

      <div className="top5-overview-gauges">
        {gauges.map((gauge) => (
          <div key={gauge.label} className="top5-overview-gauge">
            <div>
              <span>{gauge.label}</span>
              <strong>{gauge.value}</strong>
            </div>
            <div className="top5-overview-bar">
              <div style={{ width: `${gauge.percent}%` }} />
            </div>
            <small>{gauge.percent}% complet</small>
          </div>
        ))}
      </div>
    </div>
  );
}

function Top5RankingPanel({
  title,
  games,
  scoreKey,
  contextLabel = "",
  compact = false,
  advancedList = null,
}) {
  const sorted = [...games]
    .filter((game) => getGameScore(game, scoreKey) > 0)
    .sort((a, b) => {
      const statusDiff = Number(isTopFinishedGame(b)) - Number(isTopFinishedGame(a));
      if (statusDiff !== 0) return statusDiff;
      const scoreDiff = getGameScore(b, scoreKey) - getGameScore(a, scoreKey);
      if (scoreDiff !== 0) return scoreDiff;
      return (a.name || "").localeCompare(b.name || "");
    })
    .slice(0, 5);

  return (
    <div
      className={`search-panel top5-panel ${compact ? "compact" : ""} ${
        advancedList ? "advanced" : ""
      }`}
    >
      <div className="top5-section-head">
        <div>
          <h2 className="panel-title">{title}</h2>
          {contextLabel && <div className="option-value">{contextLabel}</div>}
          {advancedList && (
            <div className="top5-criterion-chip">
              Basé sur {advancedList.criterionLabel}
            </div>
          )}
        </div>
        <span className="top5-count">{sorted.length}/5</span>
      </div>

      {sorted.length === 0 ? (
        <EmptyState
          title="Pas assez de notes"
          subtitle="Ajoute des notes sur les jeux de cette sélection."
        />
      ) : (
        <div className="top5-list">
          {sorted.map((game, index) => {
            const scoreSource = advancedList
              ? getTopScoreSource(game, scoreKey)
              : "";

            return (
              <div key={`${title}-${game.id}`} className="top5-item">
                <div className="top5-rank">{index + 1}</div>
                {game.image ? (
                  <img src={game.image} alt={game.name} className="top5-thumb" />
                ) : (
                  <div className="top5-thumb placeholder">Jeu</div>
                )}
                <div className="top5-main">
                  <div className="top5-name">{game.name}</div>
                  <div className="top5-meta">{getTopGameMeta(game)}</div>
                  {scoreSource && (
                    <div className="top5-score-source">{scoreSource}</div>
                  )}
                </div>
                <div className="top5-score">
                  {formatTopScore(getGameScore(game, scoreKey), scoreKey)}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Top5HardwarePanel({
  title,
  items,
  contextLabel = "",
  compact = false,
  criterion = null,
}) {
  const criterionKey = criterion?.key || "average";

  return (
    <div
      className={`search-panel top5-panel ${compact ? "compact" : ""} ${
        criterion ? "advanced" : ""
      }`}
    >
      <div className="top5-section-head">
        <div>
          <h2 className="panel-title">{title}</h2>
          {contextLabel && <div className="option-value">{contextLabel}</div>}
          {criterion && (
            <div className="top5-criterion-chip">
              Basé sur {criterion.label}
            </div>
          )}
        </div>
        <span className="top5-count">{items.length}/5</span>
      </div>

      {items.length === 0 ? (
        <EmptyState
          title="Pas assez de notes"
          subtitle="Ajoute des notes sur ton matériel pour afficher ce classement."
        />
      ) : (
        <div className="top5-list">
          {items.map((item, index) => (
            <div key={`${title}-${item.id}`} className="top5-item">
              <div className="top5-rank">{index + 1}</div>
              {item.image ? (
                <img src={hardwarePhotoUrl(item.image)} alt={item.name} className="top5-thumb" />
              ) : (
                <div className="top5-thumb placeholder">Mat</div>
              )}
              <div className="top5-main">
                <div className="top5-name">{item.name}</div>
                <div className="top5-meta">{getTopHardwareMeta(item)}</div>
                {criterion && criterionKey !== "average" && (
                  <div className="top5-score-source">Critère matériel</div>
                )}
              </div>
              <div className="top5-score">
                {formatRating10(getHardwareCriterionScore(item, criterionKey), "-")}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Top5OstPanel({ games }) {
  const ostGames = getTopOstGames(games, 5);
  const featured = ostGames[0];

  return (
    <div className="progression-stack">
      <div className="search-panel top5-panel ost-top-panel">
        <div className="top5-section-head">
          <div>
            <h2 className="panel-title">Top 5 OST</h2>
            <div className="option-value">
              Classement basé sur ta note OST, enrichi avec tes morceaux favoris.
            </div>
          </div>
          <span className="top5-count">{ostGames.length}/5</span>
        </div>

        {ostGames.length === 0 ? (
          <EmptyState
            title="Pas encore d'OST classée"
            subtitle="Note l'OST d'un jeu pour alimenter ce Top 5."
          />
        ) : (
          <div className="top5-list ost-top-list">
            {ostGames.map((game, index) => (
              <div key={`ost-${game.id}`} className="top5-item ost-top-item">
                <div className="top5-rank">{index + 1}</div>
                {game.image ? (
                  <img src={game.image} alt={game.name} className="top5-thumb" />
                ) : (
                  <div className="top5-thumb placeholder">OST</div>
                )}
                <div className="top5-main">
                  <div className="top5-name">{game.name}</div>
                  <div className="top5-meta">{getTopGameMeta(game)}</div>
                </div>
                <div className="ost-top-actions">
                  <div className="top5-score">
                    {formatTopScore(getGameScore(game, "ostRating"), "ostRating")}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {featured && (
        <div className="search-panel ost-feature-card">
          <div>
            <span className="goty-kicker">OST favorite</span>
            <h2 className="panel-title">{featured.name}</h2>
            <div className="option-value">Ta meilleure OST notée.</div>
          </div>
          {featured.image ? (
            <img src={featured.image} alt={featured.name} />
          ) : (
            <div className="goty-empty-cover">OST</div>
          )}
        </div>
      )}
    </div>
  );
}

function GameOfYearPanel({ games, onSetGameOfYear }) {
  const years = getGameOfYearYears(games);
  const [selectedYear, setSelectedYear] = useState(years[0] || "");

  useEffect(() => {
    if (!years.length) {
      setSelectedYear("");
      return;
    }

    if (!years.includes(Number(selectedYear))) {
      setSelectedYear(years[0]);
    }
  }, [years, selectedYear]);

  const year = Number(selectedYear);
  const candidates = games
    .filter((game) => isGameOfYearEligible(game) && getGameYear(game) === year)
    .sort((a, b) => {
      const ratingDiff = getGameRating(b) - getGameRating(a);
      if (ratingDiff !== 0) return ratingDiff;
      return (a.name || "").localeCompare(b.name || "");
    });
  const selectedGame = candidates.find((game) => Number(game.gotyYear) === year);
  const officialGoty = getOfficialGotyForSeason(year);
  const normalizeGotyName = (value = "") =>
    value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const selectedOfficialMatch = selectedGame
    ? officialGoty?.awards.find(
        (award) => normalizeGotyName(award.winner) === normalizeGotyName(selectedGame.name)
      )
    : null;

  return (
    <div className="progression-stack">
      <div className="search-panel goty-hero">
        <div>
          <h2 className="panel-title">Game of the Year</h2>
          <div className="option-value">
            Choisis ton jeu marquant pour chaque année parmi les jeux joués.
          </div>
        </div>

        {years.length > 0 && (
          <div className="goty-year-picker" role="group" aria-label="Choisir une annee GOTY">
            <span>Année</span>
            <div className="goty-year-scroll">
              {years.map((item) => (
                <button
                  key={item}
                  type="button"
                  className={Number(selectedYear) === item ? "active" : ""}
                  onClick={() => setSelectedYear(item)}
                >
                  {item}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      {!years.length ? (
        <EmptyState
          title="Aucune année disponible"
          subtitle="Ajoute des jeux joués avec une date de sortie pour créer tes GOTY."
        />
      ) : (
        <>
          <div className="search-panel goty-current-card">
            <div>
              <span className="goty-kicker">Mon GOTY {year}</span>
              <h2 className="panel-title">
                {selectedGame ? selectedGame.name : "Aucun jeu sélectionné"}
              </h2>
              <div className="option-value">
                {selectedGame
                  ? `${formatRating10(getGameRating(selectedGame), "Non noté")} - ${getTopGameMeta(selectedGame)}`
                  : "Sélectionne un jeu dans la liste ci-dessous."}
              </div>
              {selectedOfficialMatch && (
                <div className="goty-official-match">
                  Aussi sacré par {selectedOfficialMatch.ceremony} {selectedOfficialMatch.awardYear}.
                </div>
              )}
            </div>
            {selectedGame?.image ? (
              <img src={selectedGame.image} alt={selectedGame.name} />
            ) : (
              <div className="goty-empty-cover">GOTY</div>
            )}
          </div>

          <div className="search-panel goty-official-panel">
            <div className="top5-section-head">
              <div>
                <h2 className="panel-title">Palmarès officiel</h2>
                <div className="option-value">
                  Le GOTY The Game Awards de la saison {year}, pour comparer avec ton choix.
                </div>
              </div>
              <span className="top5-count">
                {officialGoty ? officialGoty.awards.length : 0}
              </span>
            </div>

            {officialGoty ? (
              <div className="goty-official-grid">
                {officialGoty.awards.map((award) => {
                  const isPersonalPick =
                    selectedGame &&
                    normalizeGotyName(award.winner) === normalizeGotyName(selectedGame.name);

                  return (
                    <div
                      key={`${award.ceremony}-${award.awardYear}`}
                      className={`goty-official-award ${isPersonalPick ? "match" : ""}`}
                    >
                      <div>
                        <span>{award.ceremony}</span>
                        <small>{award.awardYear}</small>
                      </div>
                      <strong>{award.winner}</strong>
                      {isPersonalPick && <em>Ton choix aussi</em>}
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="goty-official-empty">
                Aucun palmarès officiel enregistré pour cette année.
              </div>
            )}
          </div>

          <div className="search-panel top5-panel">
            <div className="top5-section-head">
              <div>
                <h2 className="panel-title">Candidats {year}</h2>
                <div className="option-value">
                  {candidates.length} jeux joués cette année-là.
                </div>
              </div>
              <span className="top5-count">{selectedGame ? "1/1" : "0/1"}</span>
            </div>

            <div className="top5-list">
              {candidates.map((game, index) => {
                const isSelected = Number(game.gotyYear) === year;

                return (
                  <button
                    key={game.id}
                    type="button"
                    className={`top5-item goty-candidate ${isSelected ? "active" : ""}`}
                    onClick={() => onSetGameOfYear(year, game.id)}
                  >
                    <div className="top5-rank">{index + 1}</div>
                    {game.image ? (
                      <img src={game.image} alt={game.name} className="top5-thumb" />
                    ) : (
                      <div className="top5-thumb placeholder">Jeu</div>
                    )}
                    <div className="top5-main">
                      <div className="top5-name">{game.name}</div>
                      <div className="top5-meta">{getTopGameMeta(game)}</div>
                    </div>
                    <div className="top5-score">
                      {isSelected ? "GOTY" : formatRating10(getGameRating(game), "-")}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Top5TabV2({ games, hardware = [], onSetGameOfYear }) {
  const [includePartial, setIncludePartial] = useState(false);
  const scopedGames = games.filter((game) => getGameExperience(game) === "completed" || (includePartial && getGameExperience(game) === "partial"));
  const scopedHardware = hardware.filter(isTopEligibleHardware);
  const [contentMode, setContentMode] = useState("games");
  const [mode, setMode] = useState("criteria");
  const [scoreKey, setScoreKey] = useState("rating");
  const [hardwareType, setHardwareType] = useState("all");
  const [selectedPlatform, setSelectedPlatform] = useState("");
  const [selectedGenre, setSelectedGenre] = useState("");
  const [hardwareRankingMode, setHardwareRankingMode] = useState("global");
  const [hardwareCriterionKey, setHardwareCriterionKey] = useState("average");
  const [selectedAdvancedTop, setSelectedAdvancedTop] = useState(
    TOP5_ADVANCED_LISTS[0].id
  );

  const selectedScore =
    TOP5_SCORE_OPTIONS.find((option) => option.id === scoreKey) ||
    TOP5_SCORE_OPTIONS[0];
  const selectedAdvancedList =
    TOP5_ADVANCED_LISTS.find((list) => list.id === selectedAdvancedTop) ||
    TOP5_ADVANCED_LISTS[0];
  const platformGroups = getTopGroups(scopedGames, "platform");
  const genreGroups = getTopGroups(scopedGames, "genre");
  const selectedGroup =
    mode === "platform" ? selectedPlatform : mode === "genre" ? selectedGenre : "";
  const visibleGames = filterGamesForTop(scopedGames, mode, selectedGroup);
  const visibleAdvancedGames = getTopAdvancedGames(
    scopedGames,
    selectedAdvancedList
  );
  const selectedHardwareGroup =
    TOP5_HARDWARE_GROUPS.find((group) => group.id === hardwareType) ||
    TOP5_HARDWARE_GROUPS[0];
  const hardwareCriterionOptions = getHardwareCriterionOptions(hardwareType);
  const selectedHardwareCriterion =
    hardwareCriterionOptions.find((option) => option.key === hardwareCriterionKey) ||
    hardwareCriterionOptions[0];
  const safeHardwareCriterionKey = selectedHardwareCriterion.key;
  const visibleHardware =
    hardwareRankingMode === "criteria"
      ? getTopHardwareItemsForCriterion(
          scopedHardware,
          hardwareType,
          safeHardwareCriterionKey,
          5
        )
      : getTopHardwareItems(scopedHardware, hardwareType, 5);
  const contextLabel =
    mode === "advanced"
      ? `${selectedAdvancedList.label} - ${visibleAdvancedGames.length} jeux classés`
      : mode === "criteria"
      ? "Jeux terminés en priorité, puis jeux en cours notés si besoin."
      : selectedGroup
        ? `${selectedGroup} - ${visibleGames.length} jeux`
        : "Choisis une catégorie pour afficher son classement.";
  const hardwareContextLabel =
    hardwareRankingMode === "criteria"
      ? `${selectedHardwareGroup.label} - ${selectedHardwareCriterion.label}`
      : hardwareType === "all"
      ? "Matériel noté, hors wishlist."
      : `${selectedHardwareGroup.label} - ${visibleHardware.length} matériel noté`;
  useEffect(() => {
    if (
      !hardwareCriterionOptions.some(
        (option) => option.key === hardwareCriterionKey
      )
    ) {
      setHardwareCriterionKey("average");
    }
  }, [hardwareCriterionOptions, hardwareCriterionKey]);

  useEffect(() => {
    if (
      mode === "platform" &&
      platformGroups[0] &&
      !platformGroups.some((group) => group.name === selectedPlatform)
    ) {
      setSelectedPlatform(platformGroups[0].name);
    }

    if (
      mode === "genre" &&
      genreGroups[0] &&
      !genreGroups.some((group) => group.name === selectedGenre)
    ) {
      setSelectedGenre(genreGroups[0].name);
    }
  }, [mode, selectedPlatform, selectedGenre, platformGroups, genreGroups]);

  return (
    <div className="progression-stack top5-page">
      <div className="search-panel top5-dashboard">
        {contentMode !== "hardware" && (
          <div className="top5-mode-tabs">
            <button
              type="button"
              className={includePartial ? "active" : ""}
              aria-pressed={includePartial}
              onClick={() => setIncludePartial((previous) => !previous)}
            >
              Inclure les avis partiels
            </button>
          </div>
        )}
        <div className="top5-control-block">
          <span>Section</span>
          <div className="top5-content-tabs">
          {[
            { id: "games", label: "Jeux" },
            { id: "hardware", label: "Matériel" },
            { id: "ost", label: "OST" },
            { id: "goty", label: "GOTY" },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              className={contentMode === item.id ? "active" : ""}
              onClick={() => setContentMode(item.id)}
            >
              {item.label}
            </button>
          ))}
          </div>
        </div>

        {contentMode === "games" ? (
          <>
        <div className="top5-control-block">
          <span>Classement</span>
        <div className="top5-mode-tabs">
          {[
            { id: "criteria", label: "Notes" },
            { id: "advanced", label: "Spécialisés" },
            { id: "platform", label: "Plateformes" },
            { id: "genre", label: "Genres" },
          ].map((item) => (
            <button
              key={item.id}
              type="button"
              className={mode === item.id ? "active" : ""}
              onClick={() => setMode(item.id)}
            >
              {item.label}
            </button>
          ))}
        </div>
        </div>

        {mode !== "advanced" && (
          <div className="top5-control-block">
            <span>Critère</span>
          <div className="top5-score-tabs">
            {TOP5_SCORE_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className={scoreKey === option.id ? "active" : ""}
                onClick={() => setScoreKey(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          </div>
        )}

        {mode === "advanced" && (
          <div className="top5-control-block">
            <span>Top spécialisé</span>
          <div className="top5-group-picker top5-advanced-picker">
            {TOP5_ADVANCED_LISTS.map((list) => (
              <button
                key={list.id}
                type="button"
                className={selectedAdvancedTop === list.id ? "active" : ""}
                onClick={() => setSelectedAdvancedTop(list.id)}
              >
                <span>{list.label}</span>
                <small>
                  {list.criterionLabel} · {getTopAdvancedGames(scopedGames, list).length}
                </small>
              </button>
            ))}
          </div>
          </div>
        )}

        {mode !== "criteria" && mode !== "advanced" && (
          <div className="top5-control-block">
            <span>{mode === "platform" ? "Plateforme" : "Genre"}</span>
          <div className="top5-group-picker">
            {(mode === "platform" ? platformGroups : genreGroups).map((group) => (
              <button
                key={group.name}
                type="button"
                className={selectedGroup === group.name ? "active" : ""}
                onClick={() =>
                  mode === "platform"
                    ? setSelectedPlatform(group.name)
                    : setSelectedGenre(group.name)
                }
              >
                <span>{group.name}</span>
                <small>{group.count}</small>
              </button>
            ))}
          </div>
          </div>
        )}
          </>
        ) : contentMode === "hardware" ? (
          <>
            <div className="top5-control-block">
              <span>Classement</span>
            <div className="top5-mode-tabs hardware">
              {[
                { id: "global", label: "Global" },
                { id: "criteria", label: "Critères" },
              ].map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={hardwareRankingMode === item.id ? "active" : ""}
                  onClick={() => setHardwareRankingMode(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            </div>

            <div className="top5-control-block">
              <span>Type</span>
            <div className="top5-group-picker hardware">
              {TOP5_HARDWARE_GROUPS.map((group) => (
                <button
                  key={group.id}
                  type="button"
                  className={hardwareType === group.id ? "active" : ""}
                  onClick={() => {
                    setHardwareType(group.id);
                    setHardwareCriterionKey("average");
                  }}
                >
                  <span>{group.label}</span>
                  <small>
                    {group.id === "all"
                      ? scopedHardware.length
                      : scopedHardware.filter((item) => item.type === group.id).length}
                  </small>
                </button>
              ))}
            </div>
            </div>

            {hardwareRankingMode === "criteria" && hardwareType !== "all" && (
              <div className="top5-control-block">
                <span>Critère matériel</span>
              <div className="top5-group-picker top5-hardware-criteria-picker">
                {hardwareCriterionOptions.map((criterion) => (
                  <button
                    key={criterion.key}
                    type="button"
                    className={safeHardwareCriterionKey === criterion.key ? "active" : ""}
                    onClick={() => setHardwareCriterionKey(criterion.key)}
                  >
                    <span>{criterion.label}</span>
                    <small>
                      {getTopHardwareItemsForCriterion(
                        scopedHardware,
                        hardwareType,
                        criterion.key,
                        5
                      ).length}
                    </small>
                  </button>
                ))}
              </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      <Top5OverviewPanel games={scopedGames} hardware={scopedHardware} />

      {contentMode === "games" ? (
        <>
          <Top5RankingPanel
            title={
              mode === "advanced"
                ? selectedAdvancedList.title
                : mode === "criteria"
                ? selectedScore.title
                : `${selectedScore.title}${selectedGroup ? ` - ${selectedGroup}` : ""}`
            }
            games={mode === "advanced" ? visibleAdvancedGames : visibleGames}
            scoreKey={mode === "advanced" ? selectedAdvancedList.scoreKey : scoreKey}
            contextLabel={contextLabel}
            advancedList={mode === "advanced" ? selectedAdvancedList : null}
          />

          {mode === "criteria" && (
            <>
              <div className="top5-mini-grid">
                {TOP5_SCORE_OPTIONS.filter((option) => option.id !== scoreKey)
                  .slice(0, 4)
                  .map((option) => (
                    <Top5RankingPanel
                      key={option.id}
                      title={option.title}
                      games={scopedGames}
                      scoreKey={option.id}
                      compact
                    />
                  ))}
              </div>

            </>
          )}
        </>
      ) : contentMode === "hardware" ? (
        <>
          {hardwareRankingMode === "criteria" && hardwareType === "all" && (
            <div className="search-panel top5-inline-note">
              <div>
                <h2 className="panel-title">Choisis un type de matériel</h2>
                <div className="option-value">
                  Les critères dépendent du type : manettes, casques, écrans, VR,
                  souris, claviers...
                </div>
              </div>
            </div>
          )}

          <Top5HardwarePanel
            title={
              hardwareRankingMode === "criteria" && hardwareType !== "all"
                ? `Top ${selectedHardwareCriterion.label}`
                : selectedHardwareGroup.title
            }
            items={visibleHardware}
            contextLabel={hardwareContextLabel}
            criterion={
              hardwareRankingMode === "criteria" && hardwareType !== "all"
                ? selectedHardwareCriterion
                : null
            }
          />

          {hardwareType === "all" && hardwareRankingMode === "global" && (
            <div className="top5-mini-grid">
              {TOP5_HARDWARE_GROUPS.filter((group) => group.id !== "all")
                .slice(0, 4)
                .map((group) => (
                  <Top5HardwarePanel
                    key={group.id}
                    title={group.title}
                    items={getTopHardwareItems(scopedHardware, group.id, 5)}
                    compact
                  />
                ))}
            </div>
          )}
        </>
      ) : contentMode === "ost" ? (
        <Top5OstPanel games={scopedGames} />
      ) : (
        <GameOfYearPanel games={games} onSetGameOfYear={onSetGameOfYear} />
      )}
    </div>
  );
}

/* -------------------- DETAIL MODAL -------------------- */

/* ==================== MODAL DÉTAIL JEU ==================== */

export function GameDetailModal({
  socialFriends=[],
  personalLists=[],
  onPersonalListsChange,
  game,
  onSetExperienceInfo,
  onSavePlatformActivity,
  onClose,
  onDelete,
  onSetStatus,
  onSetRating,
  onRatingCommit,
  onToggleFavorite,
  onSetGameSanctuary,
  onSetSanctuaryFigures,
  onSetDifficulty,
  onSetReview,
  onSetFavoriteReason,
  onSetOstInfo,
  onSetDetailedRating,
  onToggleCompleted,
  onSetProgressStatus,
  onSetPlaytimeRange,
  onSetPlayedPlatforms,
  onAddGame,
  games = [],
  onNavigateGame,
  canGoPrevious,
  canGoNext,
  closeAfterRating = false,
}) {
  useDetailReturn(Boolean(game));
  const [detailTab,setDetailTab]=useState("partie");
  const [catalogueGame,setCatalogueGame]=useState(null);
  const [catalogueLoading,setCatalogueLoading]=useState(false);
  const [catalogueError,setCatalogueError]=useState('');
  const catalogueRequest=useRef(0);
  useEffect(()=>{catalogueRequest.current+=1;setCatalogueGame(null);setCatalogueLoading(false);setCatalogueError('');return()=>{catalogueRequest.current+=1;};},[game?.id]);
  const openCatalogue = async () => {
    const request = ++catalogueRequest.current;
    setCatalogueError('');
    const id=getCatalogueId(game);
    if(id){setCatalogueGame(toCatalogueGame(game,id));return;}
    setCatalogueLoading(true);
    try {
      const payload=await GameService.igdb('/games',{search:game.name,page_size:20});
      if(request!==catalogueRequest.current)return;
      const match=selectCatalogueMatch(game,payload.results || []);
      if(!match){setCatalogueError('Aucune fiche identifiée avec certitude pour cette édition.');return;}
      setCatalogueGame(toCatalogueGame(match));
    } catch {if(request===catalogueRequest.current)setCatalogueError('La fiche complète est indisponible. Réessaie dans un instant.');}
    finally {if(request===catalogueRequest.current)setCatalogueLoading(false);}
  };
  const detailModalRef=useRef(null);
  useEffect(()=>{setDetailTab("partie");if(detailModalRef.current)detailModalRef.current.scrollTop=0;},[game?.id]);
  const [localReview, setLocalReview] = useState("");
  const [savingReview, setSavingReview] = useState(false);
  const [localReviewSpoiler,setLocalReviewSpoiler]=useState(false);
  const [localReviewShared,setLocalReviewShared]=useState(false);
  const [reviewSelection,setReviewSelection]=useState({start:0,end:0});
  const reviewTextareaRef=useRef(null);
  const [localOstRating, setLocalOstRating] = useState(0);
  const [dlcs, setDlcs] = useState([]);
  const [loadingDlcs, setLoadingDlcs] = useState(false);
  const [dlcStatus,setDlcStatus]=useState("unavailable");
  const [dlcRetry,setDlcRetry]=useState(0);
  const swipeStartRef = useRef(null);

  useEffect(() => {
    setLocalReview(game?.review || "");
    setLocalReviewSpoiler(Boolean(game?.reviewSpoiler));
    setLocalReviewShared(game?.reviewShared===true||(game?.reviewShared!==false&&getGameRating(game)<=0));
    setReviewSelection({start:0,end:0});
  }, [game?.id]);
  useEffect(() => {
    setLocalOstRating(clampRating(game?.ostRating));
  }, [game?.id, game?.ostRating]);

  useEffect(() => {
    if (!game) return;

    document.body.classList.add("modal-open");

    const handleEsc = (e) => {
      if(catalogueGame)return;
      if (e.key === "Escape") {
        onClose();
      }

      if (e.target?.closest?.("input, textarea, select, [role=tablist]")) return;

      if (e.key === "ArrowLeft" && canGoPrevious) {
        onNavigateGame?.(-1);
      }

      if (e.key === "ArrowRight" && canGoNext) {
        onNavigateGame?.(1);
      }
    };

    window.addEventListener("keydown", handleEsc);

    return () => {
      document.body.classList.remove("modal-open");
      window.removeEventListener("keydown", handleEsc);
    };
  }, [game, onClose, onNavigateGame, canGoPrevious, canGoNext, catalogueGame]);

  useEffect(() => {
    if(detailTab!=="infos")return;
    let active=true;
    setDlcs([]);
    const rawgId=String(game?.rawgId||game?.rawg_id||game?.rawgID||'');
    if(!/^[1-9]\d*$/.test(rawgId)){setDlcStatus('unavailable');setLoadingDlcs(false);return;}
    setLoadingDlcs(true);setDlcStatus('loading');
    // Only the catalogue's explicit parent/extension relationship is reliable.
    // A title search also returns unrelated games from the same franchise.
    rawgApiRequest(`/games/${encodeURIComponent(rawgId)}/additions`,{page_size:'20'})
      .then(data=>{
        if(!active)return;
        if(!Array.isArray(data?.results))throw Error('Invalid additions response');
        setDlcs(data.results.filter((item,index,items)=>item?.id&&item?.name&&String(item.id)!==rawgId&&items.findIndex(other=>String(other.id)===String(item.id))===index));
        setDlcStatus('ready');
      }).catch(()=>{if(active)setDlcStatus('error');})
      .finally(()=>{if(active)setLoadingDlcs(false);});
    return()=>{active=false;};
  },[game?.id,detailTab,dlcRetry]);

  if (!game) return null;

  const handleSwipeStart = (event) => {
    const target = event.target;

    if (
      target.closest("button, input, textarea, select") ||
      target.closest(".rating-slider")
    ) {
      swipeStartRef.current = null;
      return;
    }

    const touch = event.touches[0];
    swipeStartRef.current = {
      x: touch.clientX,
      y: touch.clientY,
    };
  };

  const handleSwipeEnd = (event) => {
    if (!swipeStartRef.current) return;

    const touch = event.changedTouches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = touch.clientY - swipeStartRef.current.y;
    swipeStartRef.current = null;

    if (Math.abs(deltaX) < 70 || Math.abs(deltaY) > 60) return;

    if (deltaX > 0 && canGoPrevious) {
      onNavigateGame?.(-1);
    }

    if (deltaX < 0 && canGoNext) {
      onNavigateGame?.(1);
    }
  };

  const experience = getGameExperience(game);
  const progressStatus =
    game.progressStatus || (isGameFinishedStatus(game) ? "completed" : "not_started");
  const progressLabel = getProgressLabel(progressStatus);
  const selectedPlatforms = game.playedPlatforms?.length
    ? game.playedPlatforms
    : game.platformNames || [];
  const primaryPlatform = selectedPlatforms[0] || game.platformNames?.[0] || "Plateforme à choisir";
  const genresLabel = game.genreNames?.length
    ? game.genreNames.slice(0, 3).join(" • ")
    : "Genre à compléter";
  const releaseLabel = game.released ? formatReleaseDate(game.released) : "Date inconnue";
  const seriesLabel = detectSeriesName(game) || "Série non détectée";
  const editionLabel = game.isDLC
    ? "DLC / Extension"
    : game.name.match(
        /(standard|digital|deluxe|ultimate|complete|definitive|collector|goty|game of the year|remastered|remake)/i
      )?.[0] || "Édition standard";
  const captureImages = (game.screenshots || []).filter(Boolean).slice(0, 4);
  const formatGameHistoryDate = (value) => {
    if (!value) return "Non renseigné";
    const dateValue =
      typeof value.toDate === "function"
        ? value.toDate()
        : value.seconds
          ? new Date(value.seconds * 1000)
          : new Date(value);

    return Number.isNaN(dateValue.getTime())
      ? "Non renseigné"
      : formatFullDate(dateValue);
  };
  const historyItems = [
    { label: "Ajout", value: formatGameHistoryDate(game.createdAt) },
    { label: "Dernière mise à jour", value: formatGameHistoryDate(game.updatedAt) },
    { label: "Statut actuel", value: getGameProgressStateLabel(getGameProgressState(game)) },
  ];
  const figureOptions = getGameFigureOptions(game);
  const selectedFigureIds = getSelectedGameFigureIds(game);
  const toggleSanctuaryFigure = (figureId) => {
    const nextIds = selectedFigureIds.includes(figureId)
      ? selectedFigureIds.filter((id) => id !== figureId)
      : [...selectedFigureIds, figureId];

    onSetSanctuaryFigures?.(game.id, nextIds);
  };

  return (
    <div
      className="modal-backdrop"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        className="game-modal game-detail-focused"
        inert={catalogueGame ? true : undefined}
        ref={detailModalRef}
        role="dialog"
        aria-modal="true"
        aria-label={game.name}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={handleSwipeStart}
        onTouchEnd={handleSwipeEnd}
      >
        <div className="game-detail-toolbar" aria-label="Navigation de la fiche">
        <button
          className="modal-close-btn"
          aria-label="Fermer la fiche du jeu"
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onClose();
          }}
          type="button"
        >
          ✕
        </button>

        <button
          className="game-detail-nav-btn prev"
          type="button"
          disabled={!canGoPrevious}
          onClick={() => onNavigateGame?.(-1)}
          aria-label="Jeu précédent"
        >
          ‹
        </button>

        <button
          className="game-detail-nav-btn next"
          type="button"
          disabled={!canGoNext}
          onClick={() => onNavigateGame?.(1)}
          aria-label="Jeu suivant"
        >
          ›
        </button>

        </div>

        <div
          className={`game-detail-premium-hero ${game.image ? "" : "empty"}`}
          style={game.image ? { "--hero-image": `url(${game.image})` } : undefined}
        >
          <div className="game-detail-premium-cover">
            {game.image ? (
              <img src={game.image} alt={game.name} />
            ) : (
              <span>🎮</span>
            )}
          </div>

          <div className="game-detail-premium-identity">
            <div className="game-detail-kicker">
              <span>{progressLabel}</span>
              <span>{primaryPlatform}</span>
            </div>
            <h2>{game.name}</h2>
            <p>{releaseLabel} • {genresLabel}</p>

            <div className="game-detail-premium-actions">
              <button
                className={`heart-btn ${game.favorite ? "active" : ""}`}
                aria-label={game.favorite ? "Retirer des favoris" : "Ajouter aux favoris"}
                aria-pressed={Boolean(game.favorite)}
                onClick={() => onToggleFavorite(game.id, game.favorite)}
                type="button"
              >
                <Heart size={18} fill={game.favorite ? "currentColor" : "none"} />
              </button>
              <span>{getGameRating(game)>0 ? `Ma note : ${getGameRating(game)}/10` : "Pas encore noté"}</span>
            </div>
          </div>
        </div>

        <div className="modal-content game-detail-premium-content">
          <div className="game-detail-tabs" role="tablist" aria-label="Rubriques de la fiche">
            {[['partie','Ma partie'],['avis','Mon avis'],['collection','Collection'],['infos','Infos']].map(([id,label],index)=><button type="button" role="tab" key={id} id={`game-detail-tab-${id}`} aria-selected={detailTab===id} aria-controls={`game-detail-panel-${id}`} tabIndex={detailTab===id?0:-1} onClick={()=>setDetailTab(id)} onKeyDown={event=>{const items=['partie','avis','collection','infos'];let next;if(event.key==='ArrowRight')next=(index+1)%4;if(event.key==='ArrowLeft')next=(index+3)%4;if(event.key==='Home')next=0;if(event.key==='End')next=3;if(next!==undefined){event.preventDefault();event.stopPropagation();setDetailTab(items[next]);event.currentTarget.parentElement.querySelectorAll('[role=tab]')[next].focus();}}}>{label}</button>)}
          </div>
          <section role="tabpanel" id="game-detail-panel-partie" aria-labelledby="game-detail-tab-partie" hidden={detailTab!=='partie'} key={`partie-${game.id}`}>
            <p className="game-detail-panel-intro">Ta progression, ta plateforme et ton temps de jeu.</p>
            <GameJourneyEditor key={`${game.id}-${game.progressStatus}`} game={game} onSave={onSetProgressStatus}/>
          <details className="game-detail-organized-group">
            <summary>Ta session de jeu</summary>
            <div className="game-detail-group-content">
<div className="game-detail-section">
            <div className="modal-block-title">Plateforme utilisée</div>

            <div className="choice-grid platform-choice-grid">
              {(game.platformNames || []).map((platform) => {
                const selected = (game.playedPlatforms || []).includes(platform);

                return (
                  <button
                    key={platform}
                    type="button"
                    className={`choice-pill small ${selected ? "active" : ""}`}
                    onClick={() => {
                      const current = game.playedPlatforms || [];
                      const next = selected
                        ? current.filter((p) => p !== platform)
                        : [...current, platform];

                      onSetPlayedPlatforms(game.id, next);
                    }}
                  >
                    {selected ? "✓ " : ""}
                    {platform}
                  </button>
                );
              })}
            </div>

            {(!game.platformNames || game.platformNames.length === 0) && (
              <div className="option-value">Aucune plateforme détectée pour ce jeu.</div>
            )}
          </div>

          <div className="game-detail-section">
            <div className="modal-block-title">Temps joué estimé</div>

            <div className="choice-grid time-choice-grid">
              {PLAYTIME_RANGE_OPTIONS.map((option) => (
                <button
                  key={option.id}
                  className={`choice-pill small ${
                    (game.playtimeRange || "none") === option.id ? "active" : ""
                  }`}
                  onClick={() => onSetPlaytimeRange(game.id, option.id)}
                  type="button"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div className="game-detail-section">
            <div className="modal-block-title">Difficulté</div>

            <div className="status-row">
              <button
                className={`status-btn ${game.difficulty === "casual" ? "active" : ""}`}
                onClick={() => onSetDifficulty(game.id, "casual")}
                type="button"
              >
                Casual
              </button>

              <button
                className={`status-btn ${game.difficulty === "normal" ? "active" : ""}`}
                onClick={() => onSetDifficulty(game.id, "normal")}
                type="button"
              >
                Normal
              </button>

              <button
                className={`status-btn ${game.difficulty === "hard" ? "active" : ""}`}
                onClick={() => onSetDifficulty(game.id, "hard")}
                type="button"
              >
                Hard
              </button>

              <button
                className={`status-btn ${game.difficulty === "hardcore" ? "active" : ""}`}
                onClick={() => onSetDifficulty(game.id, "hardcore")}
                type="button"
              >
                Hardcore
              </button>
            </div>
          </div>
            </div>
          </details>

          <GamePlatformActivity key={game.id} game={game} onSave={onSavePlatformActivity}/>
          </section>
          <section role="tabpanel" id="game-detail-panel-avis" aria-labelledby="game-detail-tab-avis" hidden={detailTab!=='avis'} key={`avis-${game.id}`}>
            <p className="game-detail-panel-intro">Ta note et ce que tu retiens de ce jeu.</p>
          {!experience&&<p className="game-detail-rating-context">Pour noter ce jeu, indique où tu en es dans « Ma partie ». <button type="button" className="option-pill" onClick={()=>setDetailTab('partie')}>Renseigner ma partie</button></p>}
          {experience==='partial'&&<p className="game-detail-rating-context">Note ce que tu as découvert jusqu’ici. Tu pourras compléter ton avis au fil de ta partie.</p>}
          {experience==='tried'&&<details className="game-detail-organized-group"><summary>Mes impressions (facultatif)</summary><GameExperiencePanel key={game.id} game={game} onChange={onSetExperienceInfo}/></details>}

          {experience && <div className="game-detail-section">
            <div className="modal-block-title">{experience === "completed" ? "Note globale" : experience === "tried" ? "Ton ressenti (facultatif)" : "Ressenti général · Avis partiel"}</div>

            <RatingSlider
              rating={getGameRating(game)}
              onRate={(value) => onSetRating(game.id, value)}
            />

            {closeAfterRating && (
              <button
                className="save-review-btn game-rating-save-btn"
                type="button"
                onClick={() => onRatingCommit?.()}
              >
                Enregistrer la note
              </button>
            )}
          </div>}

          {(experience === "completed" || experience === "partial") && <details className="game-detail-organized-group">
            <summary>Notes détaillées par critère</summary>
            <div className="game-detail-section game-detail-ratings-section">

            <DetailedRatingsBlock
              game={game}
              onSetDetailedRating={onSetDetailedRating}
            />
          </div></details>}

          {experience === "completed" && <details className="game-detail-organized-group"><summary>OST / Musique</summary><div className="game-detail-section ost-block">

            <RatingSlider
              rating={localOstRating}
              onRate={(value) => setLocalOstRating(value)}
              onCommit={(value) => {
                onSetOstInfo(game.id, { ostRating: value });
              }}
            />
          </div></details>

          }

          {game.favorite&&<div className="game-detail-section"><FavoriteReasonEditor key={game.id} game={game} onSave={onSetFavoriteReason}/></div>}

          <details className="game-detail-organized-group"><summary>Mon avis et son partage</summary><div className="game-detail-section">
            <div className="modal-block-title">{experience === "tried" ? "Un petit mot sur ton expérience ?" : "Avis personnel"}</div>

            <textarea
              className="review-textarea"
              ref={reviewTextareaRef}
              aria-label="Ton avis personnel"
              value={localReview}
              onSelect={e=>setReviewSelection({start:e.target.selectionStart,end:e.target.selectionEnd})}
              onChange={e=>{setLocalReview(e.target.value);setReviewSelection({start:e.target.selectionStart,end:e.target.selectionEnd});}}
              placeholder={experience === "tried" ? "Une impression, un souvenir… si tu en as envie." : "Ton avis sur ce jeu..."}
            />

            <div className="review-spoiler-tools"><button type="button" className="option-pill" disabled={savingReview||reviewSelection.start===reviewSelection.end} onClick={()=>{setLocalReview(text=>wrapSpoilerSelection(text,reviewSelection.start,reviewSelection.end));setReviewSelection({start:0,end:0});reviewTextareaRef.current?.focus();}}>Masquer la sélection</button><label><input type="checkbox" checked={localReviewSpoiler} disabled={savingReview} onChange={e=>setLocalReviewSpoiler(e.target.checked)}/>Tout mon avis contient des spoilers</label><label><input type="checkbox" checked={localReviewShared} disabled={savingReview} onChange={e=>setLocalReviewShared(e.target.checked)}/>Afficher cet avis dans mon activité publique</label></div>
            <p className="option-value">Sélectionne un passage puis masque-le, ou protège l’avis entier. Les lecteurs devront choisir de le révéler. Le partage dépend aussi de la visibilité de ton profil et de sa rubrique Activité.</p>
            {localReview&&<div className="review-spoiler-preview"><small>Aperçu pour les lecteurs</small><p><SpoilerText text={localReview} whole={localReviewSpoiler}/></p></div>}
            <button
              className="save-review-btn"
              type="button"
              disabled={savingReview}
              onClick={async () => {
                setSavingReview(true);
                try {
                  const saved = await onSetReview(game.id, localReview, {reviewSpoiler:localReviewSpoiler,reviewShared:localReviewShared});
                  if (saved !== false) onClose();
                } finally { setSavingReview(false); }
              }}
            >
              {savingReview ? "Enregistrement…" : "Enregistrer l’avis"}
            </button>
          </div></details>

            <ShareGameToFriend key={game.id} game={game} friends={socialFriends}/>
          </section>
          <section role="tabpanel" id="game-detail-panel-collection" aria-labelledby="game-detail-tab-collection" hidden={detailTab!=='collection'} key={`collection-${game.id}`}>
            <p className="game-detail-panel-intro">Range ce jeu dans tes listes ou mets-le en avant dans ton Sanctuaire.</p>
          {game.physicalOwned&&(game.physicalBarcode||game.physicalPlatform)&&<details className="game-detail-organized-group"><summary>Mon exemplaire physique</summary><p>{game.physicalPlatform||'Plateforme non renseignée'}{game.physicalEdition?` · ${game.physicalEdition}`:''}</p>{game.physicalBarcode&&<p>Code-barres : {game.physicalBarcode}</p>}</details>}
          <div className="game-detail-collection-content">
          <div className="game-detail-section">
            {onPersonalListsChange&&games.some(item=>String(item.id)===String(game.id))&&<GameListMembership key={game.id} game={game} value={personalLists} onChange={onPersonalListsChange}/>}
            <GameHistory key={`history-${game.id}`} game={game}/>
            <div className="modal-block-title">Rangement</div>

            <div className="status-row">
              <button
                className={`status-btn ${game.status === "wishlist" ? "active" : ""}`}
                onClick={() => onSetStatus(game.id, "wishlist")}
                type="button"
              >
                Wishlist
              </button>

              <button
                className={`status-btn ${game.status === "en cours" ? "active" : ""}`}
                onClick={() => onSetStatus(game.id, "en cours")}
                type="button"
              >
                En cours
              </button>

              <button
                className={`status-btn ${isGameInCollection(game) ? "active" : ""}`}
                onClick={() => onSetStatus(game.id, "collection")}
                type="button"
              >
                Dans la collection
              </button>
            </div>
          </div>

          <div className="game-detail-section sanctuary-picker-section">
            <div className="modal-block-title">Sanctuaire</div>

            <label className="sanctuary-select-row">
              <span>Vitrine personnelle</span>
              <select
                value={game.sanctuary ? "in" : "out"}
                onChange={(e) => onSetGameSanctuary?.(game.id, e.target.value === "in")}
              >
                <option value="out">Ne pas afficher</option>
                <option value="in">Dans le Sanctuaire</option>
              </select>
            </label>

            <div className="sanctuary-figure-picker">
              <div>
                <strong>Figures cultes</strong>
                <p>
                  Tu peux mettre un personnage en avant sans ajouter le jeu entier au Sanctuaire.
                </p>
              </div>

              {figureOptions.length ? (
                <div className="sanctuary-figure-chip-grid">
                  {figureOptions.map((figure) => {
                    const selected = selectedFigureIds.includes(figure.id);

                    return (
                      <button
                        key={figure.id}
                        type="button"
                        className={`sanctuary-figure-chip ${figure.role} ${selected ? "active" : ""}`}
                        onClick={() => toggleSanctuaryFigure(figure.id)}
                      >
                        <span>{figure.emblem}</span>
                        <strong>{figure.name}</strong>
                        <small>{figure.role === "hero" ? "Héros" : "Antagoniste"}</small>
                      </button>
                    );
                  })}
                </div>
              ) : (
                <p className="sanctuary-figure-picker-empty">
                  Aucune figure culte reliée automatiquement à ce jeu pour l'instant.
                </p>
              )}
            </div>
          </div>
          </div>
            <details className="game-detail-remove"><summary>Retirer ce jeu de Checkpoint</summary><p>Le jeu sera placé dans la corbeille.</p>          <button
            className="delete-large-btn"
            onClick={() => onDelete(game.id)}
            type="button"
          >
            Supprimer ce jeu
          </button></details>
          </section>
          <section role="tabpanel" id="game-detail-panel-infos" aria-labelledby="game-detail-tab-infos" hidden={detailTab!=='infos'} key={`infos-${game.id}`}>
            <p className="game-detail-panel-intro">La série, l’édition, les images et les extensions.</p>
            <div className="game-detail-catalogue-link">
              <button type="button" className="option-pill" disabled={catalogueLoading} onClick={openCatalogue}>{catalogueLoading?'Recherche de la fiche…':'Voir la fiche complète du jeu'}</button>
              <p>Synopsis, vidéos et informations du catalogue, comme dans la recherche.</p>
              {catalogueError&&<p role="status">{catalogueError}</p>}
            </div>
          <div className="game-detail-info-content">
          <div className="game-detail-meta-grid">
            <div>
              <span>Série</span>
              <strong>{seriesLabel}</strong>
            </div>
            <div>
              <span>Édition</span>
              <strong>{editionLabel}</strong>
            </div>
          </div>


          {captureImages.length > 0 && (
            <div className="game-detail-section game-detail-media-section">
              <div className="modal-block-title">Captures</div>
              <div className="game-detail-capture-grid">
                {captureImages.map((image, index) => (
                  <img
                    key={`${game.id}-capture-${index}`}
                    src={image}
                    alt={`${game.name} capture ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          )}

          <div className="game-detail-section">
            <div className="modal-block-title">DLC & Extensions</div>

            {loadingDlcs && <div className="option-value" role="status">Chargement des extensions…</div>}

            {!loadingDlcs&&dlcStatus==='unavailable'&&<p className="option-value">Les extensions ne sont pas renseignées pour ce jeu.</p>}
            {!loadingDlcs&&dlcStatus==='error'&&<div><p role="status">Impossible de charger les extensions pour le moment.</p><button type="button" className="option-pill" onClick={()=>setDlcRetry(value=>value+1)}>Réessayer</button></div>}
            {!loadingDlcs&&dlcStatus==='ready'&&!dlcs.length&&<p className="option-value">Aucune extension répertoriée.</p>}

            {!loadingDlcs && dlcs.length > 0 && (
              <div className="library-dlc-list">
                {dlcs.map((dlc) => {
                  const alreadyAdded = games.some(
                    (g) =>
                      g.rawgId === dlc.id ||
                      (g.name || "").toLowerCase() === (dlc.name || "").toLowerCase()
                  );

                  return (
                    <div key={dlc.id} className="library-dlc-card">
                      {dlc.background_image ? (
                        <img src={dlc.background_image} alt={dlc.name} />
                      ) : (
                        <div className="library-dlc-placeholder">🎮</div>
                      )}

                      <div className="library-dlc-content">
                        <div className="library-dlc-title">{dlc.name}</div>

                        <div className="library-dlc-meta">
                          {dlc.released ? formatReleaseDate(dlc.released) : "Date inconnue"}
                        </div>

                        {alreadyAdded ? (
                          <div className="already-added">✔ Déjà ajouté</div>
                        ) : (
                          <button
                            type="button"
                            className="library-dlc-btn"
                            onClick={() =>
                              onAddGame({
                                name: dlc.name,
                                rating: 0,
                                favorite: false,
                                sanctuary: false,
                                image: dlc.background_image || "",
                                status: "collection",
                                released: dlc.released || "",
                                platformNames: game.platformNames || [],
                                genreNames: game.genreNames || [],
                                playtime: dlc.playtime || null,
                                difficulty: "normal",
                                progressStatus: "not_started",
                                playtimeRange: "none",
                                review: "",
                                ratingGraphics: 0,
                                ratingGameplay: 0,
                                ratingStory: 0,
                                ratingSound: 0,
                                ostRating: 0,
                                ratingLongevity: 0,
                                rawgId: dlc.id,
                                isDLC: true,
                                parentGame: game.name,
                              })
                            }
                          >
                            Ajouter à la collection
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="game-detail-section game-detail-history-section">
            <div className="modal-block-title">Dates de ta collection</div>
            {historyItems.filter(item=>item.label!=="Statut actuel"&&item.value!=="Non renseigné").length===0&&<p className="option-value">Aucune date enregistrée pour ce jeu.</p>}
            <div className="game-detail-history-list">
              {historyItems.filter(item=>item.label!=="Statut actuel"&&item.value!=="Non renseigné").map((item) => (
                <div key={item.label}>
                  <span>{item.label}</span>
                  <strong>{item.value}</strong>
                </div>
              ))}
            </div>
          </div>
          </div>
          </section>
        </div>
      </div>
      {catalogueGame&&createPortal(<SearchGameDetailModal game={catalogueGame} libraryReturn onClose={()=>setCatalogueGame(null)}/>,document.body)}
    </div>
  );
}

/* -------------------- LIBRARY SECTION -------------------- */

/* ==================== BIBLIOTHÈQUE ==================== */

function LibraryShelf3D({ games, onOpenDetail }) {
  const displayedGames = games;
  const [gamesPerShelf, setGamesPerShelf] = useState(14);
  const shelfRef = useRef(null);

  useEffect(() => {
    const shelf = shelfRef.current;
    if (!shelf) return;
    const resize = () => {
      const row = shelf.querySelector(".shelf3d-level-games");
      if (!row) return;
      const style = getComputedStyle(row);
      const width = row.clientWidth - parseFloat(style.paddingLeft || 0) - parseFloat(style.paddingRight || 0);
      if (width <= 0) return;
      const gap = parseFloat(style.columnGap) || 0;
      const spine = parseFloat(style.getPropertyValue("--shelf-spine-width")) || 20;
      setGamesPerShelf(Math.max(1, Math.floor((width + gap) / (spine + gap))));
    };
    resize();
    const observer = typeof ResizeObserver !== "undefined" ? new ResizeObserver(resize) : null;
    observer?.observe(shelf);
    window.addEventListener("resize", resize);
    return () => { observer?.disconnect(); window.removeEventListener("resize", resize); };
  }, [games.length]);

  const groupedByPlatform = displayedGames.reduce((acc, game) => {
    const platforms =
      game.playedPlatforms?.length > 0
        ? game.playedPlatforms
        : game.platformNames?.length > 0
        ? [game.platformNames[0]]
        : ["Autre"];

    platforms.forEach((platform) => {
      if (!acc[platform]) acc[platform] = [];
      acc[platform].push(game);
    });

    return acc;
  }, {});

  const platformGroups = Object.entries(groupedByPlatform).sort(
    ([a], [b]) => a.localeCompare(b)
  );

  if (!displayedGames.length) {
    return (
      <EmptyState
        title="Tes étagères attendent leurs premiers jeux"
        subtitle="Ajoute quelques titres à ta bibliothèque et cette vue prendra vie."
      />
    );
  }

  return (
    <div ref={shelfRef} className="shelf3d-scene shelf3d-multi shelf3d-platforms" style={{"--shelf-capacity": gamesPerShelf}}>
      <div className="shelf3d-title-block">
        <h2>Bibliothèque 3D</h2>
        <p>
          {displayedGames.length} jeux rangés par console sur tes étagères.
        </p>
      </div>

      <div className="shelf3d-platform-list">
        {platformGroups.map(([platform, platformGames]) => {
          const shelves = [];

          for (let i = 0; i < platformGames.length; i += gamesPerShelf) {
            shelves.push(platformGames.slice(i, i + gamesPerShelf));
          }

          return (
            <section key={platform} className="shelf3d-platform-section">
              <div className="shelf3d-platform-header">
                <div>
                  <h3>{platform}</h3>
                  <p>{platformGames.length} jeux</p>
                </div>
              </div>

              <div className="shelf3d-cabinet">
                {shelves.map((shelfGames, shelfIndex) => (
                  <div key={`${platform}-${shelfIndex}`} className="shelf3d-level">
                    <div className="shelf3d-level-back" />

                    <div className="shelf3d-level-games">
                      {shelfGames.map((game) => (
                        <button
                          key={`${platform}-${game.id}`}
                          type="button"
                          className="shelf3d-case"
                          onClick={() => onOpenDetail(game)}
                        >
                          <LibraryGameCover game={game} fallback={game.image} className="shelf3d-cover" alt=""/>
                          <span className="shelf3d-label">{game.name}</span>
                        </button>
                      ))}
                    </div>

                    <div className="shelf3d-wood" />
                  </div>
                ))}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
}

const PHYSICAL_CONDITION_OPTIONS = [
  "Neuf scellé",
  "Excellent",
  "Très bon",
  "Bon",
  "Usé",
  "À restaurer",
];

const PHYSICAL_RARITY_OPTIONS = [
  "Commune",
  "Peu commune",
  "Rare",
  "Très rare",
  "Collector",
  "Graal",
];

function PhysicalCollectionPanel({ games, onOpenDetail, onUpdatePhysicalGame }) {
  const [selectedGameId, setSelectedGameId] = useState("");
  const selectedGame = games.find((game) => game.id === selectedGameId) || null;
  const alphabeticGames = useMemo(
    () =>
      [...games].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "fr", {
          numeric: true,
          sensitivity: "base",
        })
      ),
    [games]
  );
  const physicalGames = games.filter((game) => game.physicalOwned);
  const totalEstimatedValue = physicalGames.reduce(
    (total, game) => total + (Number(game.physicalEstimatedValue) || 0),
    0
  );
  const collectorCount = physicalGames.filter((game) =>
    ["Collector", "Graal"].includes(game.physicalRarity)
  ).length;

  useEffect(() => {
    if (!games.length) {
      setSelectedGameId("");
      return;
    }

    if (selectedGameId && !games.some((game) => game.id === selectedGameId)) {
      setSelectedGameId("");
    }
  }, [games, selectedGameId]);

  const updateSelectedGame = (patch) => {
    if (!selectedGame) return;
    onUpdatePhysicalGame(selectedGame.id, {
      physicalOwned: true,
      ...patch,
    });
  };

  const handlePhotoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file || !selectedGame) return;

    try {
      const photo = await resizeSocialPhoto(file);
      updateSelectedGame({ physicalPhoto: photo.src });
    } catch (error) {
      console.error("Erreur photo collection physique :", error);
    }
  };

  if (!games.length) {
    return (
      <EmptyState
        title="Aucune base pour la collection physique"
        subtitle="Ajoute d'abord quelques jeux à ta collection numérique pour créer leurs versions boîte."
      />
    );
  }

  return (
    <div className="physical-collection-panel">
      <div className="physical-hero">
        <div>
          <span>Inventaire physique</span>
          <h2>Jeux boîte, collectors et pièces rares</h2>
          <p>
            Sépare ta bibliothèque jouée de ta vraie collection : état, rareté,
            édition, photo et valeur estimée manuelle.
          </p>
        </div>
        <div className="physical-hero-score">
          <strong>{physicalGames.length}</strong>
          <span>pièces</span>
        </div>
      </div>

      <div className="physical-stats-grid">
        <div>
          <strong>{physicalGames.length}</strong>
          <span>Jeux boîte</span>
        </div>
        <div>
          <strong>{collectorCount}</strong>
          <span>Collectors / graals</span>
        </div>
        <div>
          <strong>{totalEstimatedValue ? `${totalEstimatedValue.toLocaleString("fr-FR")} €` : "-"}</strong>
          <span>Valeur estimée</span>
        </div>
      </div>

      <div className="physical-editor">
        <div className="physical-editor-head">
          <div>
            <span>Ajouter ou modifier</span>
            <strong>{selectedGame?.name || "Choisir un jeu"}</strong>
          </div>
          {selectedGame && (
            <button type="button" onClick={() => onOpenDetail(selectedGame)}>
              Fiche jeu
            </button>
          )}
        </div>

        <label className="physical-field full">
          <span>Jeu concerné</span>
          <select
            value={selectedGameId}
            onChange={(event) => setSelectedGameId(event.target.value)}
          >
            <option value="">Choisir un jeu à renseigner</option>
            {alphabeticGames.map((game) => (
              <option key={game.id} value={game.id}>
                {game.name}
              </option>
            ))}
          </select>
        </label>

        {selectedGame && (
          <>
            <div className="physical-editor-grid">
              <label className="physical-field">
                <span>Édition</span>
                <input
                  value={selectedGame.physicalEdition || ""}
                  placeholder="Standard, Collector, Steelbook..."
                  onChange={(event) =>
                    updateSelectedGame({ physicalEdition: event.target.value })
                  }
                />
              </label>

              <label className="physical-field">
                <span>État</span>
                <select
                  value={selectedGame.physicalCondition || "Très bon"}
                  onChange={(event) =>
                    updateSelectedGame({ physicalCondition: event.target.value })
                  }
                >
                  {PHYSICAL_CONDITION_OPTIONS.map((condition) => (
                    <option key={condition} value={condition}>
                      {condition}
                    </option>
                  ))}
                </select>
              </label>

              <label className="physical-field">
                <span>Rareté</span>
                <select
                  value={selectedGame.physicalRarity || "Commune"}
                  onChange={(event) =>
                    updateSelectedGame({ physicalRarity: event.target.value })
                  }
                >
                  {PHYSICAL_RARITY_OPTIONS.map((rarity) => (
                    <option key={rarity} value={rarity}>
                      {rarity}
                    </option>
                  ))}
                </select>
              </label>

              <label className="physical-field">
                <span>Valeur estimée</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={selectedGame.physicalEstimatedValue || ""}
                  placeholder="0"
                  onChange={(event) =>
                    updateSelectedGame({
                      physicalEstimatedValue: Number(event.target.value) || 0,
                    })
                  }
                />
              </label>
            </div>

            <label className="physical-field full">
              <span>Notes collection</span>
              <textarea
                value={selectedGame.physicalNotes || ""}
                placeholder="État de la boîte, notice, fourreau, souvenir d'achat..."
                onChange={(event) =>
                  updateSelectedGame({ physicalNotes: event.target.value })
                }
              />
            </label>

            <div className="physical-photo-row">
              <div className="physical-photo-preview">
                {selectedGame.physicalPhoto ? (
                  <img
                    src={selectedGame.physicalPhoto}
                    alt={selectedGame.name}
                  />
                ) : (
                  <span>Photo</span>
                )}
              </div>

              <div>
                <label className="physical-photo-add">
                  Ajouter une photo
                  <input type="file" accept="image/*" onChange={handlePhotoUpload} />
                </label>
                {selectedGame.physicalOwned && (
                  <button
                    type="button"
                    className="physical-remove-btn"
                    onClick={() =>
                      onUpdatePhysicalGame(selectedGame.id, {
                        physicalOwned: false,
                        physicalPhoto: "",
                      })
                    }
                  >
                    Retirer de la collection physique
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>

      <div className="physical-list">
        <div className="physical-list-head">
          <strong>Vitrine physique</strong>
          <span>{physicalGames.length} pièces renseignées</span>
        </div>

        {physicalGames.length === 0 ? (
          <div className="physical-empty">
            Aucun jeu boîte renseigné pour le moment.
          </div>
        ) : (
          physicalGames.map((game) => (
            <button
              key={`physical-${game.id}`}
              type="button"
              className="physical-card"
              onClick={() => {
                setSelectedGameId(game.id);
                onOpenDetail(game);
              }}
            >
              <div className="physical-card-image">
                {game.physicalPhoto || game.image ? (
                  <img src={game.physicalPhoto || game.image} alt={game.name} />
                ) : (
                  <span>CP</span>
                )}
              </div>
              <div className="physical-card-main">
                <span>{game.physicalRarity || "Commune"}</span>
                <strong>{game.name}</strong>
                <small>
                  {game.physicalEdition || "Édition standard"} • {game.physicalCondition || "Très bon"}
                </small>
              </div>
              <em>
                {Number(game.physicalEstimatedValue)
                  ? `${Number(game.physicalEstimatedValue).toLocaleString("fr-FR")} €`
                  : "Valeur libre"}
              </em>
            </button>
          ))
        )}
      </div>
    </div>
  );
}

const SANCTUARY_FIGURE_CATALOG = [
  { id: "mario", name: "Mario", role: "hero", universe: "Super Mario", emblem: "M", keywords: ["mario", "super mario", "mario kart"] },
  { id: "luigi", name: "Luigi", role: "hero", universe: "Super Mario", emblem: "L", keywords: ["mario", "super mario", "luigi", "luigi's mansion"] },
  { id: "peach", name: "Princesse Peach", role: "hero", universe: "Super Mario", emblem: "P", keywords: ["mario", "super mario", "peach", "mario kart"] },
  { id: "yoshi", name: "Yoshi", role: "hero", universe: "Super Mario", emblem: "Y", keywords: ["mario", "super mario", "yoshi", "mario kart"] },
  { id: "link", name: "Link", role: "hero", universe: "The Legend of Zelda", emblem: "L", keywords: ["zelda", "link", "ocarina of time", "breath of the wild", "tears of the kingdom"] },
  { id: "zelda", name: "Zelda", role: "hero", universe: "The Legend of Zelda", emblem: "Z", keywords: ["zelda", "princess zelda", "ocarina of time", "breath of the wild", "tears of the kingdom"] },
  { id: "samus", name: "Samus Aran", role: "hero", universe: "Metroid", emblem: "S", keywords: ["metroid", "samus"] },
  { id: "sonic", name: "Sonic", role: "hero", universe: "Sonic", emblem: "S", keywords: ["sonic"] },
  { id: "tails", name: "Tails", role: "hero", universe: "Sonic", emblem: "T", keywords: ["sonic", "tails"] },
  { id: "knuckles", name: "Knuckles", role: "hero", universe: "Sonic", emblem: "K", keywords: ["sonic", "knuckles"] },
  { id: "lara", name: "Lara Croft", role: "hero", universe: "Tomb Raider", emblem: "L", keywords: ["tomb raider", "lara croft"] },
  { id: "master-chief", name: "Master Chief", role: "hero", universe: "Halo", emblem: "117", keywords: ["halo"] },
  { id: "cortana", name: "Cortana", role: "hero", universe: "Halo", emblem: "AI", keywords: ["halo", "cortana"] },
  { id: "kratos", name: "Kratos", role: "hero", universe: "God of War", emblem: "K", keywords: ["god of war", "kratos"] },
  { id: "atreus", name: "Atreus", role: "hero", universe: "God of War", emblem: "A", keywords: ["god of war", "atreus", "ragnarok"] },
  { id: "snake", name: "Solid Snake", role: "hero", universe: "Metal Gear", emblem: "S", keywords: ["metal gear", "solid snake"] },
  { id: "geralt", name: "Geralt de Riv", role: "hero", universe: "The Witcher", emblem: "G", keywords: ["witcher", "geralt"] },
  { id: "ciri", name: "Ciri", role: "hero", universe: "The Witcher", emblem: "C", keywords: ["witcher", "ciri"] },
  { id: "aloy", name: "Aloy", role: "hero", universe: "Horizon", emblem: "A", keywords: ["horizon zero dawn", "horizon forbidden west", "aloy"] },
  { id: "nathan-drake", name: "Nathan Drake", role: "hero", universe: "Uncharted", emblem: "ND", keywords: ["uncharted", "nathan drake"] },
  { id: "chloe-frazer", name: "Chloe Frazer", role: "hero", universe: "Uncharted", emblem: "CF", keywords: ["uncharted", "chloe frazer", "lost legacy"] },
  { id: "ellie", name: "Ellie", role: "hero", universe: "The Last of Us", emblem: "E", keywords: ["the last of us", "last of us"] },
  { id: "joel", name: "Joel Miller", role: "hero", universe: "The Last of Us", emblem: "J", keywords: ["the last of us", "last of us", "joel"] },
  { id: "arthur", name: "Arthur Morgan", role: "hero", universe: "Red Dead Redemption", emblem: "A", keywords: ["red dead redemption"] },
  { id: "john-marston", name: "John Marston", role: "hero", universe: "Red Dead Redemption", emblem: "JM", keywords: ["red dead redemption", "john marston"] },
  { id: "jin", name: "Jin Sakai", role: "hero", universe: "Ghost", emblem: "J", keywords: ["ghost of tsushima", "ghost of yotei"] },
  { id: "atsus", name: "Atsu", role: "hero", universe: "Ghost", emblem: "A", keywords: ["ghost of yotei", "yotei", "atsu"] },
  { id: "ezio", name: "Ezio Auditore", role: "hero", universe: "Assassin's Creed", emblem: "E", keywords: ["assassin's creed", "assassins creed", "ezio", "brotherhood", "revelations"] },
  { id: "bayek", name: "Bayek", role: "hero", universe: "Assassin's Creed", emblem: "B", keywords: ["assassin's creed origins", "assassins creed origins", "bayek"] },
  { id: "kassandra", name: "Kassandra", role: "hero", universe: "Assassin's Creed", emblem: "K", keywords: ["assassin's creed odyssey", "assassins creed odyssey", "kassandra"] },
  { id: "commander-shepard", name: "Commandant Shepard", role: "hero", universe: "Mass Effect", emblem: "N7", keywords: ["mass effect", "shepard"] },
  { id: "garrus", name: "Garrus Vakarian", role: "hero", universe: "Mass Effect", emblem: "GV", keywords: ["mass effect", "garrus"] },
  { id: "commander-shepard-fem", name: "FemShep", role: "hero", universe: "Mass Effect", emblem: "N7", keywords: ["mass effect", "femshep"] },
  { id: "doom-slayer", name: "Doom Slayer", role: "hero", universe: "DOOM", emblem: "D", keywords: ["doom", "doom eternal", "doom slayer"] },
  { id: "dante", name: "Dante", role: "hero", universe: "Devil May Cry", emblem: "D", keywords: ["devil may cry", "dante"] },
  { id: "vergil", name: "Vergil", role: "villain", universe: "Devil May Cry", emblem: "V", keywords: ["devil may cry", "vergil"] },
  { id: "leon-kennedy", name: "Leon S. Kennedy", role: "hero", universe: "Resident Evil", emblem: "L", keywords: ["resident evil", "leon kennedy", "leon s kennedy"] },
  { id: "jill-valentine", name: "Jill Valentine", role: "hero", universe: "Resident Evil", emblem: "J", keywords: ["resident evil", "jill valentine"] },
  { id: "chris-redfield", name: "Chris Redfield", role: "hero", universe: "Resident Evil", emblem: "C", keywords: ["resident evil", "chris redfield"] },
  { id: "isaac-clarke", name: "Isaac Clarke", role: "hero", universe: "Dead Space", emblem: "I", keywords: ["dead space", "isaac clarke"] },
  { id: "alan-wake", name: "Alan Wake", role: "hero", universe: "Alan Wake", emblem: "AW", keywords: ["alan wake"] },
  { id: "saga-anderson", name: "Saga Anderson", role: "hero", universe: "Alan Wake", emblem: "SA", keywords: ["alan wake 2", "saga anderson"] },
  { id: "max-payne", name: "Max Payne", role: "hero", universe: "Max Payne", emblem: "MP", keywords: ["max payne"] },
  { id: "agent-47", name: "Agent 47", role: "hero", universe: "Hitman", emblem: "47", keywords: ["hitman", "agent 47"] },
  { id: "sam-fisher", name: "Sam Fisher", role: "hero", universe: "Splinter Cell", emblem: "SF", keywords: ["splinter cell", "sam fisher"] },
  { id: "cj", name: "CJ", role: "hero", universe: "Grand Theft Auto", emblem: "CJ", keywords: ["grand theft auto san andreas", "gta san andreas", "san andreas"] },
  { id: "niko-bellic", name: "Niko Bellic", role: "hero", universe: "Grand Theft Auto", emblem: "NB", keywords: ["grand theft auto iv", "gta iv", "gta 4", "niko bellic"] },
  { id: "trevor", name: "Trevor Philips", role: "hero", universe: "Grand Theft Auto", emblem: "T", keywords: ["grand theft auto v", "gta v", "gta 5", "trevor"] },
  { id: "cloud", name: "Cloud Strife", role: "hero", universe: "Final Fantasy VII", emblem: "C", keywords: ["final fantasy vii", "final fantasy 7", "cloud strife"] },
  { id: "tifa", name: "Tifa Lockhart", role: "hero", universe: "Final Fantasy VII", emblem: "T", keywords: ["final fantasy vii", "final fantasy 7", "tifa"] },
  { id: "aerith", name: "Aerith Gainsborough", role: "hero", universe: "Final Fantasy VII", emblem: "A", keywords: ["final fantasy vii", "final fantasy 7", "aerith"] },
  { id: "tidus", name: "Tidus", role: "hero", universe: "Final Fantasy X", emblem: "T", keywords: ["final fantasy x", "final fantasy 10", "tidus"] },
  { id: "yuna", name: "Yuna", role: "hero", universe: "Final Fantasy X", emblem: "Y", keywords: ["final fantasy x", "final fantasy 10", "yuna"] },
  { id: "sora", name: "Sora", role: "hero", universe: "Kingdom Hearts", emblem: "S", keywords: ["kingdom hearts", "sora"] },
  { id: "roxas", name: "Roxas", role: "hero", universe: "Kingdom Hearts", emblem: "R", keywords: ["kingdom hearts", "roxas"] },
  { id: "ryu", name: "Ryu", role: "hero", universe: "Street Fighter", emblem: "R", keywords: ["street fighter", "ryu"] },
  { id: "chun-li", name: "Chun-Li", role: "hero", universe: "Street Fighter", emblem: "CL", keywords: ["street fighter", "chun-li", "chun li"] },
  { id: "scorpion", name: "Scorpion", role: "hero", universe: "Mortal Kombat", emblem: "S", keywords: ["mortal kombat", "scorpion"] },
  { id: "sub-zero", name: "Sub-Zero", role: "hero", universe: "Mortal Kombat", emblem: "SZ", keywords: ["mortal kombat", "sub-zero", "sub zero"] },
  { id: "kiryu", name: "Kazuma Kiryu", role: "hero", universe: "Yakuza", emblem: "K", keywords: ["yakuza", "like a dragon", "kazuma kiryu"] },
  { id: "ichiban", name: "Ichiban Kasuga", role: "hero", universe: "Like a Dragon", emblem: "I", keywords: ["like a dragon", "ichiban", "yakuza like a dragon"] },
  { id: "joker-persona", name: "Joker", role: "hero", universe: "Persona 5", emblem: "J", keywords: ["persona 5", "persona 5 royal", "joker"] },
  { id: "yu-narukami", name: "Yu Narukami", role: "hero", universe: "Persona 4", emblem: "Y", keywords: ["persona 4", "yu narukami"] },
  { id: "makoto-yuki", name: "Makoto Yuki", role: "hero", universe: "Persona 3", emblem: "M", keywords: ["persona 3", "makoto yuki"] },
  { id: "2b", name: "2B", role: "hero", universe: "NieR", emblem: "2B", keywords: ["nier automata", "nier: automata", "2b"] },
  { id: "a2", name: "A2", role: "hero", universe: "NieR", emblem: "A2", keywords: ["nier automata", "nier: automata", "a2"] },
  { id: "amicia", name: "Amicia de Rune", role: "hero", universe: "A Plague Tale", emblem: "A", keywords: ["a plague tale", "amicia"] },
  { id: "cal-kestis", name: "Cal Kestis", role: "hero", universe: "Star Wars Jedi", emblem: "CK", keywords: ["star wars jedi", "fallen order", "survivor", "cal kestis"] },
  { id: "kay-vess", name: "Kay Vess", role: "hero", universe: "Star Wars Outlaws", emblem: "KV", keywords: ["star wars outlaws", "kay vess"] },
  { id: "clive", name: "Clive Rosfield", role: "hero", universe: "Final Fantasy XVI", emblem: "C", keywords: ["final fantasy xvi", "final fantasy 16", "clive"] },
  { id: "jill-warrick", name: "Jill Warrick", role: "hero", universe: "Final Fantasy XVI", emblem: "JW", keywords: ["final fantasy xvi", "final fantasy 16", "jill warrick"] },
  { id: "kirby", name: "Kirby", role: "hero", universe: "Kirby", emblem: "K", keywords: ["kirby"] },
  { id: "donkey-kong", name: "Donkey Kong", role: "hero", universe: "Donkey Kong", emblem: "DK", keywords: ["donkey kong"] },
  { id: "fox-mccloud", name: "Fox McCloud", role: "hero", universe: "Star Fox", emblem: "F", keywords: ["star fox", "fox mccloud"] },
  { id: "mega-man", name: "Mega Man", role: "hero", universe: "Mega Man", emblem: "MM", keywords: ["mega man", "megaman"] },
  { id: "pac-man", name: "Pac-Man", role: "hero", universe: "Pac-Man", emblem: "PM", keywords: ["pac-man", "pac man"] },
  { id: "crash", name: "Crash Bandicoot", role: "hero", universe: "Crash Bandicoot", emblem: "C", keywords: ["crash bandicoot"] },
  { id: "spyro", name: "Spyro", role: "hero", universe: "Spyro", emblem: "S", keywords: ["spyro"] },
  { id: "ratchet", name: "Ratchet", role: "hero", universe: "Ratchet & Clank", emblem: "R", keywords: ["ratchet", "ratchet & clank", "ratchet and clank"] },
  { id: "clank", name: "Clank", role: "hero", universe: "Ratchet & Clank", emblem: "C", keywords: ["ratchet", "clank", "ratchet & clank", "ratchet and clank"] },
  { id: "sly-cooper", name: "Sly Cooper", role: "hero", universe: "Sly Cooper", emblem: "S", keywords: ["sly cooper"] },
  { id: "jak", name: "Jak", role: "hero", universe: "Jak and Daxter", emblem: "J", keywords: ["jak and daxter", "jak & daxter"] },
  { id: "daxter", name: "Daxter", role: "hero", universe: "Jak and Daxter", emblem: "D", keywords: ["jak and daxter", "jak & daxter", "daxter"] },
  { id: "commander-keen", name: "Commander Keen", role: "hero", universe: "Commander Keen", emblem: "CK", keywords: ["commander keen"] },
  { id: "gordon-freeman", name: "Gordon Freeman", role: "hero", universe: "Half-Life", emblem: "GF", keywords: ["half-life", "half life", "gordon freeman"] },
  { id: "alyx-vance", name: "Alyx Vance", role: "hero", universe: "Half-Life", emblem: "AV", keywords: ["half-life", "half life", "alyx"] },
  { id: "chell", name: "Chell", role: "hero", universe: "Portal", emblem: "C", keywords: ["portal", "chell"] },
  { id: "elizabeth", name: "Elizabeth", role: "hero", universe: "BioShock", emblem: "E", keywords: ["bioshock infinite", "elizabeth"] },
  { id: "booker", name: "Booker DeWitt", role: "hero", universe: "BioShock", emblem: "BD", keywords: ["bioshock infinite", "booker dewitt"] },
  { id: "big-daddy", name: "Big Daddy", role: "hero", universe: "BioShock", emblem: "BD", keywords: ["bioshock", "big daddy"] },
  { id: "corvo", name: "Corvo Attano", role: "hero", universe: "Dishonored", emblem: "C", keywords: ["dishonored", "corvo"] },
  { id: "emily-kaldwin", name: "Emily Kaldwin", role: "hero", universe: "Dishonored", emblem: "E", keywords: ["dishonored 2", "emily kaldwin"] },
  { id: "alexios", name: "Alexios", role: "hero", universe: "Assassin's Creed", emblem: "A", keywords: ["assassin's creed odyssey", "assassins creed odyssey", "alexios"] },
  { id: "bowser", name: "Bowser", role: "villain", universe: "Super Mario", emblem: "B", keywords: ["mario", "super mario", "mario kart", "bowser"] },
  { id: "wario", name: "Wario", role: "villain", universe: "Super Mario", emblem: "W", keywords: ["mario", "wario", "mario kart"] },
  { id: "ganondorf", name: "Ganondorf", role: "villain", universe: "The Legend of Zelda", emblem: "G", keywords: ["zelda", "ganon", "ganondorf", "ocarina of time", "tears of the kingdom"] },
  { id: "dr-eggman", name: "Dr Eggman", role: "villain", universe: "Sonic", emblem: "E", keywords: ["sonic", "eggman", "robotnik"] },
  { id: "sephiroth", name: "Sephiroth", role: "villain", universe: "Final Fantasy VII", emblem: "S", keywords: ["final fantasy vii", "final fantasy 7", "sephiroth"] },
  { id: "vader", name: "Dark Vador", role: "villain", universe: "Star Wars", emblem: "DV", keywords: ["star wars", "vador", "vader", "lego star wars"] },
  { id: "darth-maul", name: "Dark Maul", role: "villain", universe: "Star Wars", emblem: "DM", keywords: ["star wars", "darth maul", "dark maul", "lego star wars"] },
  { id: "wesker", name: "Albert Wesker", role: "villain", universe: "Resident Evil", emblem: "W", keywords: ["resident evil", "wesker"] },
  { id: "nemesis", name: "Nemesis", role: "villain", universe: "Resident Evil", emblem: "N", keywords: ["resident evil 3", "nemesis"] },
  { id: "lady-dimitrescu", name: "Lady Dimitrescu", role: "villain", universe: "Resident Evil", emblem: "LD", keywords: ["resident evil village", "dimitrescu"] },
  { id: "vaas", name: "Vaas Montenegro", role: "villain", universe: "Far Cry", emblem: "V", keywords: ["far cry 3", "vaas"] },
  { id: "pagan-min", name: "Pagan Min", role: "villain", universe: "Far Cry", emblem: "PM", keywords: ["far cry 4", "pagan min"] },
  { id: "joseph-seed", name: "Joseph Seed", role: "villain", universe: "Far Cry", emblem: "JS", keywords: ["far cry 5", "joseph seed"] },
  { id: "glados", name: "GLaDOS", role: "villain", universe: "Portal", emblem: "G", keywords: ["portal", "glados"] },
  { id: "pyramid-head", name: "Pyramid Head", role: "villain", universe: "Silent Hill", emblem: "PH", keywords: ["silent hill", "pyramid head"] },
  { id: "big-boss", name: "Big Boss", role: "villain", universe: "Metal Gear", emblem: "BB", keywords: ["metal gear", "big boss", "phantom pain"] },
  { id: "skull-face", name: "Skull Face", role: "villain", universe: "Metal Gear", emblem: "SF", keywords: ["metal gear solid v", "phantom pain", "skull face"] },
  { id: "the-illusive-man", name: "L'Homme Trouble", role: "villain", universe: "Mass Effect", emblem: "IM", keywords: ["mass effect", "illusive man", "homme trouble"] },
  { id: "saren", name: "Saren Arterius", role: "villain", universe: "Mass Effect", emblem: "SA", keywords: ["mass effect", "saren"] },
  { id: "handsome-jack", name: "Handsome Jack", role: "villain", universe: "Borderlands", emblem: "HJ", keywords: ["borderlands 2", "handsome jack"] },
  { id: "andrew-ryan", name: "Andrew Ryan", role: "villain", universe: "BioShock", emblem: "AR", keywords: ["bioshock", "andrew ryan"] },
  { id: "fontaine", name: "Frank Fontaine", role: "villain", universe: "BioShock", emblem: "FF", keywords: ["bioshock", "fontaine"] },
  { id: "hades", name: "Hadès", role: "villain", universe: "Hades", emblem: "H", keywords: ["hades"] },
  { id: "senator-armstrong", name: "Senator Armstrong", role: "villain", universe: "Metal Gear", emblem: "SA", keywords: ["metal gear rising", "revengeance", "armstrong"] },
  { id: "micah-bell", name: "Micah Bell", role: "villain", universe: "Red Dead Redemption", emblem: "MB", keywords: ["red dead redemption 2", "micah"] },
  { id: "m-bison", name: "M. Bison", role: "villain", universe: "Street Fighter", emblem: "MB", keywords: ["street fighter", "m bison", "bison"] },
  { id: "shao-kahn", name: "Shao Kahn", role: "villain", universe: "Mortal Kombat", emblem: "SK", keywords: ["mortal kombat", "shao kahn"] },
  { id: "kefka", name: "Kefka Palazzo", role: "villain", universe: "Final Fantasy VI", emblem: "K", keywords: ["final fantasy vi", "final fantasy 6", "kefka"] },
  { id: "ardyn", name: "Ardyn Izunia", role: "villain", universe: "Final Fantasy XV", emblem: "A", keywords: ["final fantasy xv", "final fantasy 15", "ardyn"] },
  { id: "xehanort", name: "Xehanort", role: "villain", universe: "Kingdom Hearts", emblem: "X", keywords: ["kingdom hearts", "xehanort"] },
  { id: "majima", name: "Goro Majima", role: "villain", universe: "Yakuza", emblem: "GM", keywords: ["yakuza", "like a dragon", "majima"] },
  { id: "tyr", name: "Tyran", role: "villain", universe: "Gaming", emblem: "!", keywords: ["boss final", "final boss"] },
];

function getGameFigureOptions(game = {}) {
  const haystack = normalizeSearchText(
    [game.name, game.series, game.genre, ...(game.genreNames || []), ...(game.platformNames || [])]
      .filter(Boolean)
      .join(" ")
  );

  if (!haystack) return [];

  return SANCTUARY_FIGURE_CATALOG.filter((figure) =>
    figure.keywords.some((keyword) => haystack.includes(normalizeSearchText(keyword)))
  );
}

function getSelectedGameFigureIds(game = {}) {
  if (Array.isArray(game.sanctuaryFigureIds)) return game.sanctuaryFigureIds;
  if (Array.isArray(game.cultFigureIds)) return game.cultFigureIds;
  return [];
}

function getSanctuaryFigureMatches(games = [], sanctuaryGames = []) {
  const sourceGames = sanctuaryGames.length
    ? sanctuaryGames
    : games.filter((game) => game.favorite || game.sanctuary || getGameRating(game) >= 8 || isGameFinishedStatus(game));

  const rankedGames = sourceGames
    .map((game) => ({
      game,
      haystack: normalizeSearchText(
        [game.name, game.series, game.genre, ...(game.genreNames || []), ...(game.platformNames || [])]
          .filter(Boolean)
          .join(" ")
      ),
      score:
        getGameRating(game) * 10 +
        (game.sanctuary ? 30 : 0) +
        (getSelectedGameFigureIds(game).length ? 22 : 0) +
        (game.favorite ? 18 : 0) +
        (isGameFinishedStatus(game) ? 8 : 0),
    }))
    .sort((a, b) => b.score - a.score);

  const automaticMatches = SANCTUARY_FIGURE_CATALOG.map((figure) => {
    const match = rankedGames.find(({ haystack }) =>
      figure.keywords.some((keyword) => haystack.includes(normalizeSearchText(keyword)))
    );

    if (!match) return null;
    return { ...figure, sourceGame: match.game, score: match.score, manual: false };
  })
    .filter(Boolean);

  const manualMatches = games.flatMap((game) =>
    getSelectedGameFigureIds(game)
      .map((figureId) => {
        const figure = SANCTUARY_FIGURE_CATALOG.find((entry) => entry.id === figureId);
        if (!figure) return null;

        return {
          ...figure,
          sourceGame: game,
          score:
            200 +
            getGameRating(game) * 10 +
            (game.favorite ? 8 : 0) +
            (isGameFinishedStatus(game) ? 4 : 0),
          manual: true,
        };
      })
      .filter(Boolean)
  );

  const merged = new Map();

  [...automaticMatches, ...manualMatches].forEach((match) => {
    const current = merged.get(match.id);

    if (!current || (match.manual && !current.manual) || match.score > current.score) {
      merged.set(match.id, match);
    }
  });

  return Array.from(merged.values()).sort(
    (a, b) => Number(b.manual) - Number(a.manual) || b.score - a.score
  );
}

function SanctuaryTab({
  games = [],
  hardware = [],
  onOpenGameDetail,
  onGoLibrary,
  onGoHardware,
  onSetSanctuaryFigures,
}) {
  const [figureSearch, setFigureSearch] = useState("");
  const [figureRoleFilter, setFigureRoleFilter] = useState("all");
  const sanctuaryGames = games.filter((game) => game.sanctuary);
  const sanctuaryPhysicalGames = sanctuaryGames.filter((game) => game.physicalOwned);
  const sanctuaryHardware = hardware.filter((item) => item.sanctuary);
  const finishedSanctuaryGames = sanctuaryGames.filter(isGameFinishedStatus);
  const sanctuaryFigures = getSanctuaryFigureMatches(games, sanctuaryGames);
  const heroFigures = sanctuaryFigures.filter((figure) => figure.role === "hero").slice(0, 4);
  const villainFigures = sanctuaryFigures.filter((figure) => figure.role === "villain").slice(0, 4);
  const figureCandidates = useMemo(() => {
    const query = normalizeSearchText(figureSearch);
    const byFigure = new Map();

    games.forEach((game) => {
      const selectedIds = getSelectedGameFigureIds(game);
      const optionsById = new Map(getGameFigureOptions(game).map((figure) => [figure.id, figure]));

      selectedIds.forEach((figureId) => {
        const manualFigure =
          optionsById.get(figureId) ||
          SANCTUARY_FIGURE_CATALOG.find((figure) => figure.id === figureId);
        if (manualFigure) optionsById.set(figureId, manualFigure);
      });

      optionsById.forEach((figure) => {
        const selected = selectedIds.includes(figure.id);
        const score =
          (selected ? 1000 : 0) +
          getGameRating(game) * 10 +
          (game.sanctuary ? 30 : 0) +
          (game.favorite ? 14 : 0) +
          (isGameFinishedStatus(game) ? 8 : 0);
        const entry = {
          ...figure,
          sourceGame: game,
          selected,
          score,
        };
        const current = byFigure.get(figure.id);

        if (!current || entry.score > current.score) {
          byFigure.set(figure.id, entry);
        }
      });
    });

    return Array.from(byFigure.values())
      .filter((figure) => figureRoleFilter === "all" || figure.role === figureRoleFilter)
      .filter((figure) => {
        if (!query) return true;
        return normalizeSearchText(
          [figure.name, figure.universe, figure.sourceGame?.name].filter(Boolean).join(" ")
        ).includes(query);
      })
      .sort(
        (a, b) =>
          Number(b.selected) - Number(a.selected) ||
          a.name.localeCompare(b.name, "fr", { sensitivity: "base" }) ||
          a.universe.localeCompare(b.universe, "fr", { sensitivity: "base" })
      );
  }, [figureSearch, figureRoleFilter, games]);

  const toggleSanctuaryFigure = (figure) => {
    if (!figure?.sourceGame?.id || !onSetSanctuaryFigures) return;

    const currentIds = getSelectedGameFigureIds(figure.sourceGame);
    const nextIds = currentIds.includes(figure.id)
      ? currentIds.filter((figureId) => figureId !== figure.id)
      : [...currentIds, figure.id];

    onSetSanctuaryFigures(figure.sourceGame.id, nextIds);
  };

  const hallOfFameGames = [...sanctuaryGames]
    .sort((a, b) => {
      const scoreA =
        getGameRating(a) * 10 +
        (isGameFinishedStatus(a) ? 8 : 0) +
        (a.physicalOwned ? 5 : 0);
      const scoreB =
        getGameRating(b) * 10 +
        (isGameFinishedStatus(b) ? 8 : 0) +
        (b.physicalOwned ? 5 : 0);

      return scoreB - scoreA;
    })
    .slice(0, 4);

  const recentSanctuaryGames = [...sanctuaryGames]
    .sort((a, b) => (b.createdAt || b.updatedAt || "").localeCompare(a.createdAt || a.updatedAt || ""))
    .slice(0, 4);

  const relics = [
    ...sanctuaryPhysicalGames.map((game) => ({
      id: `game-${game.id}`,
      type: "Jeu physique",
      title: game.name,
      subtitle: game.physicalEdition || game.physicalCondition || "Pièce de collection",
      image: game.physicalPhoto || game.image,
      onClick: () => onOpenGameDetail(game, sanctuaryGames),
    })),
    ...sanctuaryHardware.map((item) => ({
      id: `hardware-${item.id}`,
      type: "Materiel choisi",
      title: item.name,
      subtitle: item.brand || getHardwareTypeLabel(item.type),
      image: item.image,
      onClick: onGoHardware,
    })),
  ].slice(0, 6);

  const signatureGame = hallOfFameGames[0] || sanctuaryGames[0];
  const completionIdeas = [
    {
      title: sanctuaryGames.length ? "Raconter pourquoi" : "Choisir ton premier jeu",
      text: sanctuaryGames.length
        ? "Ajoute une note perso dans tes fiches pour donner une vraie mémoire au Sanctuaire."
        : "Ouvre une fiche jeu et place-la volontairement dans le Sanctuaire.",
      action: sanctuaryGames.length ? "Ouvrir la bibliothèque" : "Choisir un jeu",
      onClick: onGoLibrary,
    },
    {
      title: sanctuaryPhysicalGames.length ? "Mettre les reliques en valeur" : "Ajouter une relique",
      text: sanctuaryPhysicalGames.length
        ? "Photos, édition et état transforment ta collection physique en vraie vitrine."
        : "Les jeux boîte et collectors choisis depuis leur fiche auront leur place ici.",
      action: "Collection physique",
      onClick: onGoLibrary,
    },
    {
      title: sanctuaryHardware.length ? "Compléter le setup" : "Choisir ton matériel culte",
      text: sanctuaryHardware.length
        ? "Ton matériel choisi donne une signature plus personnelle à ton profil."
        : "Ouvre une fiche matériel et ajoute-la au Sanctuaire.",
      action: "Voir le matériel",
      onClick: onGoHardware,
    },
  ];

  const renderGameTile = (game, index) => (
    <button
      type="button"
      className="sanctuary-game-tile"
      key={game.id}
      onClick={() => onOpenGameDetail(game, sanctuaryGames)}
    >
      <span className="sanctuary-rank">{index + 1}</span>
      <span className="sanctuary-cover">
        {game.image ? <img src={game.image} alt={game.name} /> : <span>CP</span>}
      </span>
      <span className="sanctuary-game-copy">
        <strong>{game.name}</strong>
        <small>
          {game.released?.split("-")[0] || "Annee inconnue"} · ⭐{" "}
          {formatRating10(getGameRating(game), "-")}
        </small>
      </span>
      <span className="sanctuary-heart">
        <Landmark size={16} />
      </span>
    </button>
  );

  return (
    <div className="progression-stack sanctuary-tab">
      <div className="sanctuary-stat-grid">
        <div>
          <strong>{sanctuaryGames.length}</strong>
          <span>jeux choisis</span>
        </div>
        <div>
          <strong>{finishedSanctuaryGames.length}</strong>
          <span>terminés</span>
        </div>
        <div>
          <strong>{sanctuaryPhysicalGames.length}</strong>
          <span>reliques</span>
        </div>
        <div>
          <strong>{sanctuaryHardware.length}</strong>
          <span>matériels</span>
        </div>
      </div>

      <section className="sanctuary-feature">
        <div className="sanctuary-feature-media">
          {signatureGame?.image ? (
            <img src={signatureGame.image} alt={signatureGame.name} />
          ) : (
            <span>CP</span>
          )}
        </div>
        <div>
          <span>Hall of Fame</span>
          <h2>{signatureGame?.name || "Ton premier jeu culte"}</h2>
          <p>
            {signatureGame
              ? `${formatRating10(getGameRating(signatureGame), "Non note")} · ${
                  signatureGame.genreNames?.[0] || "jeu marquant"
                }`
              : "Ajoute un jeu au Sanctuaire depuis sa fiche pour commencer cette vitrine."}
          </p>
          <button type="button" onClick={signatureGame ? () => onOpenGameDetail(signatureGame, sanctuaryGames) : onGoLibrary}>
            {signatureGame ? "Ouvrir la fiche" : "Choisir un jeu"}
          </button>
        </div>
      </section>

      <section className="sanctuary-section">
        <div className="sanctuary-section-head">
          <div>
            <span>Hall of Fame</span>
            <h2>Jeux intouchables</h2>
          </div>
          <small>{hallOfFameGames.length}/4</small>
        </div>

        {hallOfFameGames.length ? (
          <div className="sanctuary-game-list">
            {hallOfFameGames.map(renderGameTile)}
          </div>
        ) : (
          <div className="sanctuary-empty">
            <strong>Aucun jeu sacralisé pour l'instant.</strong>
            <span>Ajoute un jeu au Sanctuaire depuis sa fiche pour remplir cette zone.</span>
          </div>
        )}
      </section>

      <section className="sanctuary-section sanctuary-figures-section">
        <div className="sanctuary-section-head">
          <div>
            <span>Figures cultes</span>
            <h2>Héros et antagonistes</h2>
          </div>
          <small>{heroFigures.length + villainFigures.length}</small>
        </div>
        <p className="sanctuary-note">
          Mets en avant les personnages qui t'ont marqué, même si leur jeu
          n'est pas dans ton Hall of Fame.
        </p>

        <div className="sanctuary-figure-manager">
          <div className="sanctuary-figure-manager-head">
            <div>
              <strong>Ajouter une figure</strong>
              <span>
                Choisis directement parmi les personnages repérés dans ta bibliothèque.
              </span>
            </div>
            <small>{figureCandidates.length}</small>
          </div>

          <input
            type="search"
            value={figureSearch}
            onChange={(event) => setFigureSearch(event.target.value)}
            placeholder="Chercher un personnage ou un jeu..."
            className="sanctuary-figure-search"
          />

          <div className="sanctuary-figure-filter-row">
            {[
              ["all", "Tout"],
              ["hero", "Héros"],
              ["villain", "Antagonistes"],
            ].map(([value, label]) => (
              <button
                type="button"
                key={value}
                className={figureRoleFilter === value ? "active" : ""}
                onClick={() => setFigureRoleFilter(value)}
              >
                {label}
              </button>
            ))}
          </div>

          <div className="sanctuary-figure-candidate-list">
            {figureCandidates.length ? (
              figureCandidates.map((figure) => (
                <button
                  type="button"
                  key={`${figure.id}-${figure.sourceGame?.id}`}
                  className={`sanctuary-figure-candidate ${figure.role} ${
                    figure.selected ? "selected" : ""
                  }`}
                  onClick={() => toggleSanctuaryFigure(figure)}
                >
                  <span className="sanctuary-figure-emblem">{figure.emblem}</span>
                  <span className="sanctuary-figure-copy">
                    <strong>{figure.name}</strong>
                    <small>{figure.universe}</small>
                    <em>{figure.sourceGame?.name}</em>
                  </span>
                  <span className="sanctuary-figure-candidate-action">
                    {figure.selected ? "Retirer" : "Ajouter"}
                  </span>
                </button>
              ))
            ) : (
              <div className="sanctuary-empty compact">
                <span>Aucune figure trouvée dans ta bibliothèque.</span>
              </div>
            )}
          </div>
        </div>

        {heroFigures.length || villainFigures.length ? (
          <div className="sanctuary-figure-columns">
            <div className="sanctuary-figure-column">
              <h3>Héros</h3>
              <div className="sanctuary-figure-list">
                {heroFigures.map((figure) => (
                  <button
                    type="button"
                    className="sanctuary-figure-card hero"
                    key={figure.id}
                    onClick={() => onOpenGameDetail(figure.sourceGame, sanctuaryGames)}
                  >
                    <span className="sanctuary-figure-emblem">{figure.emblem}</span>
                    <span className="sanctuary-figure-copy">
                      <strong>{figure.name}</strong>
                      <small>{figure.universe}</small>
                      <em>{figure.sourceGame?.name}</em>
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <div className="sanctuary-figure-column">
              <h3>Antagonistes</h3>
              <div className="sanctuary-figure-list">
                {villainFigures.map((figure) => (
                  <button
                    type="button"
                    className="sanctuary-figure-card villain"
                    key={figure.id}
                    onClick={() => onOpenGameDetail(figure.sourceGame, sanctuaryGames)}
                  >
                    <span className="sanctuary-figure-emblem">{figure.emblem}</span>
                    <span className="sanctuary-figure-copy">
                      <strong>{figure.name}</strong>
                      <small>{figure.universe}</small>
                      <em>{figure.sourceGame?.name}</em>
                    </span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="sanctuary-empty">
            <strong>Aucune figure repérée pour l'instant.</strong>
            <span>
              Ouvre une fiche jeu et choisis ses figures cultes pour les faire
              apparaître ici.
            </span>
          </div>
        )}
      </section>

      <section className="sanctuary-section">
        <div className="sanctuary-section-head">
          <div>
            <span>Reliques</span>
            <h2>Objets et souvenirs</h2>
          </div>
          <small>{relics.length}</small>
        </div>

        {relics.length ? (
          <div className="sanctuary-relic-grid">
            {relics.map((item) => (
              <button type="button" key={item.id} onClick={item.onClick}>
                <span className="sanctuary-relic-image">
                  {item.image ? <img src={hardwarePhotoUrl(item.image)} alt={item.title} /> : <span>CP</span>}
                </span>
                <span>{item.type}</span>
                <strong>{item.title}</strong>
                <small>{item.subtitle}</small>
              </button>
            ))}
          </div>
        ) : (
          <div className="sanctuary-empty">
            <strong>Pas encore de relique.</strong>
            <span>Les jeux physiques et le matériel choisis apparaîtront ici.</span>
          </div>
        )}
      </section>

      <section className="sanctuary-section">
        <div className="sanctuary-section-head">
          <div>
            <span>Sélection</span>
            <h2>Derniers ajouts</h2>
          </div>
          <small>{recentSanctuaryGames.length}</small>
        </div>

        {recentSanctuaryGames.length ? (
          <div className="sanctuary-chip-list">
            {recentSanctuaryGames.map((game) => (
              <button
                type="button"
                key={`recent-${game.id}`}
                onClick={() => onOpenGameDetail(game, sanctuaryGames)}
              >
                <Landmark size={14} />
                <span>{game.name}</span>
              </button>
            ))}
          </div>
        ) : (
          <div className="sanctuary-empty compact">
            <span>Tes derniers choix apparaîtront ici.</span>
          </div>
        )}
      </section>

      <section className="sanctuary-section">
        <div className="sanctuary-section-head">
          <div>
            <span>Vitrine</span>
            <h2>À compléter</h2>
          </div>
        </div>
        <div className="sanctuary-todo-list">
          {completionIdeas.map((item) => (
            <button type="button" key={item.title} onClick={item.onClick}>
              <strong>{item.title}</strong>
              <span>{item.text}</span>
              <small>{item.action}</small>
            </button>
          ))}
        </div>
      </section>
    </div>
  );
}

export function LibrarySection({
  title,
  filterKey = "",
  onBulkUpdate,
  games,
  onDelete,
  onSetStatus,
  onSetRating,
  onQuickProgress,
  onToggleFavorite,
  onOpenDetail,
  libraryCardMode,
  setLibraryCardMode,
}) {
  const [bulkMode,setBulkMode]=useState(false);
  const [quickFilter,setQuickFilter]=useState('all');
  const [platformChoice,setPlatformChoice]=useState('');
  const [genreChoice,setGenreChoice]=useState('');
  const platformChoices=useMemo(()=>[...new Set(games.flatMap(game=>game.playedPlatforms?.length?game.playedPlatforms:game.platformNames || []))].sort((a,b)=>a.localeCompare(b,'fr')),[games]);
  const genreChoices=useMemo(()=>[...new Set(games.flatMap(game=>game.genreNames || []))].sort((a,b)=>a.localeCompare(b,'fr')),[games]);
  const activeFilterCount=Number(Boolean(platformChoice))+Number(Boolean(genreChoice))+Number(quickFilter!=='all'&&title==='Collection');
  const activeShortcut=title==='Collection'?quickFilter:'all';
  const shortcutOptions=[['all','Tous'],['recent','Ajoutés · 30 jours'],['unrated','Sans note'],['paused','En pause']];
  const shortcutGames=useMemo(()=>games.filter(game=>matchesLibraryShortcut(game,activeShortcut,getGameRating)&&(!platformChoice||(game.playedPlatforms?.length?game.playedPlatforms:game.platformNames || []).includes(platformChoice))&&(!genreChoice||(game.genreNames || []).includes(genreChoice))),[games,activeShortcut,platformChoice,genreChoice]);
  const [sortBy, setSortBy] = useState("recent");
  const [visibleLimit,setVisibleLimit]=useState(48);
  // Live saves can reorder or remove records without changing the user's view.
  // Keep expanded pages until the user changes the search, sorting or layout.
  useEffect(()=>setVisibleLimit(48),[sortBy,title,libraryCardMode,filterKey,quickFilter,platformChoice,genreChoice]);

  const sortedGames = useMemo(()=>[...shortcutGames].sort((a, b) => {
    if (sortBy === "alphabetical") {
      return (a.name || "").localeCompare(b.name || "", "fr", {
        numeric: true,
        sensitivity: "base",
      });
    }

    if (sortBy === "year") {
      return (b.released || "").localeCompare(a.released || "");
    }

    if (sortBy === "platform") {
      return getPrimaryLibraryGamePlatform(a).localeCompare(
        getPrimaryLibraryGamePlatform(b),
        "fr",
        {
          numeric: true,
          sensitivity: "base",
        }
      );
    }

    if (sortBy === "genre") {
      return (a.genreNames?.[0] || "Genre inconnu").localeCompare(
        b.genreNames?.[0] || "Genre inconnu"
      );
    }

    return compareLatestLibraryAdditions(a, b);
  }),[shortcutGames,sortBy]);
  const visibleGames=sortedGames.slice(0,visibleLimit);

  const groupedGames = visibleGames.reduce((acc, game) => {
    let group = "Autres";

    if (sortBy === "year") {
      group = game.released?.split("-")[0] || "Année inconnue";
    }

    if (sortBy === "platform") {
      group = getPrimaryLibraryGamePlatform(game);
    }

    if (sortBy === "genre") {
      group = game.genreNames?.[0] || "Genre inconnu";
    }

    if (sortBy === "alphabetical") {
      group = (game.name || "#").trim().charAt(0).toUpperCase() || "#";
    }

    if (!acc[group]) acc[group] = [];
    acc[group].push(game);

    return acc;
  }, {});

  const renderGameCard = (game) => {
    const gameVisual = getLibraryFallbackVisual(game);
    const progressLabel = getProgressLabel(
      game.progressStatus ||
        (isGameFinishedStatus(game) ? "completed" : "not_started")
    );

    const playtimeLabel = getPlaytimeRangeLabel(game.playtimeRange);

    if (libraryCardMode === "compact") {
      return (
        <div
          key={game.id}
          className="game-grid-item"
          onClick={() => onOpenDetail(game)}
        >
          <LibraryGameCover game={game} fallback={gameVisual} className="library-card-art"/>

          {onQuickProgress&&<QuickGameActions game={game} rating={getGameRating(game)} onProgress={onQuickProgress} onRate={onSetRating}/>}
          <div className="grid-top-actions">
            <button
              className={`heart-btn grid-heart ${game.favorite ? "active" : ""}`}
              aria-pressed={Boolean(game.favorite)}
              aria-label={game.favorite ? `Retirer ${game.name} des favoris` : `Ajouter ${game.name} aux favoris`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(game.id, game.favorite);
              }}
              type="button"
            >
              <Heart size={18} fill={game.favorite ? "currentColor" : "none"} />
            </button>
          </div>

          <div className="grid-overlay">
            <div className="grid-title">{game.name}</div>

            <div className="grid-meta">
              <span>
                {game.status === "wishlist"
                  ? formatFullDate(game.released)
                  : game.released?.split("-")[0] || "—"}
              </span>
              <span>⭐ {formatRating10(getGameRating(game), "-")}</span>
            </div>

            <div className={`grid-status ${game.status.replace(" ", "-")}`}>
              {game.status === "wishlist"
                ? "Wishlist"
                : isStoppedGame(game)
                ? getProgressLabel(game.progressStatus)
                : game.status === "en cours"
                ? "En cours"
                : "Collection"}
            </div>

            {game.status === "wishlist" && (
              <div className="grid-countdown">
                {getReleaseCountdown(game.released)}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (libraryCardMode === "detailed") {
      return (
        <div
          key={game.id}
          className="game-item clickable library-simple-card"
          onClick={() => onOpenDetail(game)}
        >
          {onQuickProgress&&<QuickGameActions game={game} rating={getGameRating(game)} onProgress={onQuickProgress} onRate={onSetRating}/>}
          <LibraryGameCover game={game} fallback={gameVisual} className="library-game-cover library-card-art"/>

          <div className="game-item-content">
            <div className="game-name">{game.name}</div>

            <div className="library-meta">
              {game.released?.split("-")[0] || "Année inconnue"}
              {game.genreNames?.length > 0 &&
                ` • ${game.genreNames.slice(0, 2).join(" • ")}`}
            </div>

            <div className="library-meta">
              {progressLabel} • {playtimeLabel}
            </div>
            <div className="library-meta">
              ⭐ {formatRating10(getGameRating(game), "Pas encore noté")}
            </div>
          </div>

          <button
            className={`heart-btn ${game.favorite ? "active" : ""}`}
            onClick={(e) => {
              e.stopPropagation();
              onToggleFavorite(game.id, game.favorite);
            }}
            type="button"
          >
            <Heart size={18} fill={game.favorite ? "currentColor" : "none"} />
          </button>
        </div>
      );
    }

    return (
      <div
        key={game.id}
        className="game-item clickable detailed-card"
        onClick={() => onOpenDetail(game)}
      >
        {onQuickProgress&&<QuickGameActions game={game} rating={getGameRating(game)} onProgress={onQuickProgress} onRate={onSetRating}/>}
        <div className={`status-badge ${game.status.replace(" ", "-")}`}>
          {game.status === "wishlist" ? (
            <List size={14} />
          ) : game.status === "en cours" ? (
            <Play size={14} />
          ) : (
            <Check size={14} />
          )}
        </div>

        <div className="game-card-head">
          <div className="game-item-left">
            <LibraryGameCover game={game} fallback={gameVisual} className="game-thumb library-card-art"/>
          </div>

          <div className="game-head-main">
            <div className="game-head-top">
              <div className="game-main-info">
                <div className="game-name">{game.name}</div>

                {game.released && (
                  <div className="library-meta">
                    <strong>Sortie :</strong> {game.released}
                  </div>
                )}

                {game.genreNames?.length > 0 && (
                  <div className="library-meta genre-text">
                    <strong>Genres :</strong>{" "}
                    {game.genreNames.slice(0, 2).join(" • ")}
                  </div>
                )}

                <div className="library-meta">
                  <strong>Progression :</strong> {progressLabel}
                </div>

                <div className="library-meta">
                  <strong>Temps joué :</strong> {playtimeLabel}
                </div>

                <div className="library-meta">
                  <strong>XP :</strong> {calculateXP(game)} XP
                </div>

                <div className="library-meta">
                  <strong>Moyenne détaillée :</strong>{" "}
                  {formatRating10(averageDetailedRating(game), "Pas encore notée")}
                </div>

                {game.platformNames?.length > 0 && (
                  <div className="library-meta platforms-text">
                    <strong>Plateformes :</strong>{" "}
                    {game.platformNames.slice(0, 2).join(" • ")}
                  </div>
                )}
              </div>

              <button
                className="delete-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(game.id);
                }}
                type="button"
              >
                X
              </button>
            </div>
          </div>
        </div>

        <div className="game-card-actions">
          <div className="status-row">
            <button
              className={`status-btn ${game.status === "wishlist" ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onSetStatus(game.id, "wishlist");
              }}
              type="button"
            >
              Wishlist
            </button>

            <button
              className={`status-btn ${game.status === "en cours" ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onSetStatus(game.id, "en cours");
              }}
              type="button"
            >
              En cours
            </button>

            <button
              className={`status-btn ${isGameInCollection(game) ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onSetStatus(game.id, "collection");
              }}
              type="button"
            >
              Collection
            </button>
          </div>

          <div className="detailed-bottom-row">
            <RatingSlider
              rating={getGameRating(game)}
              onRate={(value) => onSetRating(game.id, value)}
            />

            <button
              className={`heart-btn ${game.favorite ? "active" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                onToggleFavorite(game.id, game.favorite);
              }}
              type="button"
            >
              <Heart size={18} fill={game.favorite ? "currentColor" : "none"} />
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={`library-section ${libraryCardMode === "shelf" ? "library-section-shelf" : ""}`}>
      <div className="section-header">
        <h2>{title}</h2>
        <span className="section-count">{sortedGames.length} jeux{activeShortcut!=='all'?` / ${games.length}`:''}</span>
      </div>

      <div className="library-controls">
      {onBulkUpdate && <LibraryBulkEditor games={shortcutGames} onApply={onBulkUpdate} active={bulkMode} onActiveChange={setBulkMode}/>}
      {!bulkMode && <>
      <div className="library-view-toggle">
        <button
          type="button"
          className={libraryCardMode === "compact" ? "active" : ""}
          onClick={() => setLibraryCardMode("compact")}
        >
          Compact
        </button>

        <button
          type="button"
          className={libraryCardMode === "complete" ? "active" : ""}
          onClick={() => setLibraryCardMode("complete")}
        >
          Complet
        </button>

        <button
          type="button"
          className={`library-switch-btn ${libraryCardMode === "shelf" ? "active" : ""}`}
          onClick={() => setLibraryCardMode("shelf")}
        >
          Étagère 3D
        </button>
      </div>

      <label className="library-sort-select">Trier
        <select aria-label="Trier la bibliothèque" value={sortBy} onChange={event=>setSortBy(event.target.value)}>
          <option value="recent">Ajouts récents</option>
          <option value="year">Année de sortie</option>
          <option value="alphabetical">Titre A–Z</option>
          <option value="platform">Plateforme</option>
          <option value="genre">Genre</option>
        </select>
      </label>
      <details className="library-filter-panel">
        <summary>Filtrer les jeux <span>{activeFilterCount?`${activeFilterCount} actif${activeFilterCount>1?'s':''}`:'Plateforme, genre et suivi'}</span></summary>
        <div className="library-filter-fields">
          <label>Plateforme<select value={platformChoice} onChange={event=>setPlatformChoice(event.target.value)}><option value="">Toutes les plateformes</option>{platformChoices.map(name=><option key={name} value={name}>{name}</option>)}</select></label>
          <label>Genre<select value={genreChoice} onChange={event=>setGenreChoice(event.target.value)}><option value="">Tous les genres</option>{genreChoices.map(name=><option key={name} value={name}>{name}</option>)}</select></label>
          {title==='Collection'&&<label>Suivi<select value={quickFilter} onChange={event=>setQuickFilter(event.target.value)}>{shortcutOptions.map(([key,label])=><option value={key} key={key}>{label}</option>)}</select></label>}
          {activeFilterCount>0&&<button type="button" className="option-pill" onClick={()=>{setQuickFilter('all');setPlatformChoice('');setGenreChoice('');}}>Réinitialiser les filtres</button>}
        </div>
        <p role="status">{shortcutGames.length} / {games.length} jeux correspondent</p>
      </details>
      </>}
      </div>
      {!bulkMode && <>
      {sortedGames.length === 0 ? (
        <EmptyState
          title={activeFilterCount?'Aucun jeu ne correspond aux filtres':"Cette section est encore calme"}
          subtitle={activeFilterCount?'Modifie ou réinitialise les filtres pour retrouver les autres jeux.':"Ajoute un jeu, change un statut ou lance une recherche pour lui donner du relief."}
        />
      ) : libraryCardMode === "shelf" ? (
        <LibraryShelf3D
          games={sortedGames}
          onOpenDetail={onOpenDetail}
        />
      ) : (
        <div
          className={
            sortBy !== "recent"
              ? "library-grouped-list"
              : libraryCardMode === "compact"
              ? "games-grid library-compact-grid"
              : "games-list"
          }
        >
          {sortBy === "recent"
            ? visibleGames.map((game) => renderGameCard(game))
            : Object.entries(groupedGames).map(([groupName, groupGames]) => (
                <div key={groupName} className="library-group">
                  <div className="library-group-title">
                    <span>{groupName}</span>
                    <span>{groupGames.length}</span>
                  </div>

                  <div
                    className={
                      libraryCardMode === "compact"
                        ? "games-grid library-compact-grid"
                        : "games-list"
                    }
                  >
                    {groupGames.map((game) => renderGameCard(game))}
                  </div>
                </div>
              ))}
        </div>
      )}
      {libraryCardMode!=="shelf"&&sortedGames.length>48&&<div className="library-pagination"><span>{visibleGames.length} / {sortedGames.length} jeux affichés</span>{visibleGames.length<sortedGames.length&&<button type="button" className="option-pill" onClick={()=>setVisibleLimit(count=>count+48)}>Afficher les jeux suivants</button>}</div>}
      </>}
    </div>
  );
}

function LibrarySmartPanel({
  games = [],
  collectionGames = [],
  inProgressGames = [],
  wishlistGames = [],
  onOpenDetail,
  onSetLibraryView,
  onSetLibrarySearch,
}) {
  const ratedCollection = collectionGames
    .map((game) => ({ ...game, checkpointRating: getGameRating(game) }))
    .filter((game) => game.checkpointRating > 0)
    .sort((a, b) => b.checkpointRating - a.checkpointRating);
  const unfinishedCollection = collectionGames.filter(
    (game) => !isGameFinishedStatus(game)&&!isStoppedGame(game)
  );
  const continueGame = [...inProgressGames]
    .map((game) => ({ ...game, checkpointRating: getGameRating(game) }))
    .sort((a, b) => b.checkpointRating - a.checkpointRating)[0];
  const launchCandidate =
    unfinishedCollection
      .map((game) => ({
        ...game,
        checkpointRating: getGameRating(game),
      }))
      .filter((game) => game.status !== "en cours")
      .sort((a, b) => {
        const ratingGap = b.checkpointRating - a.checkpointRating;
        if (ratingGap !== 0) return ratingGap;
        return String(b.released || "").localeCompare(String(a.released || ""));
      })[0] || ratedCollection.find(game=>!isStoppedGame(game));
  const unratedCandidate =
    collectionGames.find((game) => getGameRating(game) <= 0) ||
    games.find((game) => getGameRating(game) <= 0 && game.status !== "wishlist");
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const wishlistCandidate =
    wishlistGames
      .map((game) => ({
        ...game,
        releaseDate: game.released ? new Date(game.released) : null,
      }))
      .sort((a, b) => {
        const futureA = a.releaseDate && a.releaseDate >= today;
        const futureB = b.releaseDate && b.releaseDate >= today;
        if (futureA && !futureB) return -1;
        if (!futureA && futureB) return 1;
        if (a.releaseDate && b.releaseDate) return a.releaseDate - b.releaseDate;
        return 0;
      })[0];
  const ratedPercent = collectionGames.length
    ? Math.round(
        (collectionGames.filter((game) => getGameRating(game) > 0).length /
          collectionGames.length) *
          100
      )
    : 0;
  const finishedPercent = collectionGames.length
    ? Math.round(
        (collectionGames.filter(isGameFinishedStatus).length /
          collectionGames.length) *
          100
      )
    : 0;
  const libraryPulse = Math.min(
    100,
    Math.round(
      ratedPercent * 0.42 +
        finishedPercent * 0.38 +
        Math.min(inProgressGames.length * 5, 12) +
        Math.min(wishlistGames.length * 0.4, 8)
    )
  );
  const libraryMood =
    libraryPulse >= 80
      ? "Ta bibliotheque tourne bien"
      : libraryPulse >= 55
      ? "Tu as de quoi jouer"
      : "Quelques jeux t'attendent";
  const collectionPlatforms = new Set(
    collectionGames.flatMap((game) =>
      game.playedPlatforms?.length
        ? game.playedPlatforms
        : game.platformNames?.length
        ? [game.platformNames[0]]
        : []
    )
  ).size;
  const premiumMetrics = [
    {
      label: "Collection",
      value: collectionGames.length,
      detail: `${collectionPlatforms || 0} plateformes suivies`,
    },
    {
      label: "En cours",
      value: inProgressGames.length,
      detail: inProgressGames.length ? "parties actives" : "aucune partie",
    },
    {
      label: "Notes faites",
      value: `${ratedPercent}%`,
      detail: "jeux deja notes",
    },
    {
      label: "Termines",
      value: `${finishedPercent}%`,
      detail: "dans la collection",
    },
  ];
  const smartCards = [
    continueGame && {
      label: "Reprendre",
      title: continueGame.name,
      detail: `${formatRating10(getGameRating(continueGame), "non note")} - en cours`,
      action: "Ouvrir",
      view: "en cours",
      game: continueGame,
    },
    launchCandidate && {
      label: "Lancer ensuite",
      title: launchCandidate.name,
      detail: `${formatRating10(getGameRating(launchCandidate), "non note")} - dans ta collection`,
      action: "Voir",
      view: "collection",
      game: launchCandidate,
    },
    unratedCandidate && {
      label: "Note rapide",
      title: unratedCandidate.name,
      detail: "Une note suffit pour affiner tes tops.",
      action: "Noter",
      view: "collection",
      game: unratedCandidate,
    },
    wishlistCandidate && {
      label: "Sortie a suivre",
      title: wishlistCandidate.name,
      detail: getReleaseCountdown(wishlistCandidate.released),
      action: "Suivre",
      view: "wishlist",
      game: wishlistCandidate,
    },
  ].filter(Boolean);

  if (!games.length) return null;

  return (
    <div className="search-panel library-smart-panel">
      <div className="library-smart-head">
        <div>
          <span>Point bibliotheque</span>
          <h2>{libraryMood}</h2>
          <p>
            Un résumé simple de ce qui mérite ton attention dans ta collection.
          </p>
        </div>

        <div className="library-smart-score">
          <span>Suivi</span>
          <strong>{libraryPulse}%</strong>
        </div>
      </div>

      <div className="library-smart-metrics">
        {premiumMetrics.map((metric) => (
          <div key={metric.label}>
            <strong>{metric.value}</strong>
            <span>{metric.label}</span>
            <small>{metric.detail}</small>
          </div>
        ))}
      </div>

      {smartCards.length > 0 && (
        <div className="library-smart-grid">
          {smartCards.map((card) => (
            <button
              key={`${card.label}-${card.game.id || card.title}`}
              type="button"
              className="library-smart-card"
              onClick={() => {
                onSetLibraryView?.(card.view);
                onSetLibrarySearch?.("");
                onOpenDetail?.(card.game, card.view === "wishlist" ? wishlistGames : games);
              }}
            >
              {card.game.image ? (
                <img src={card.game.image} alt={card.title} loading="lazy" decoding="async" />
              ) : (
                <div className="library-smart-card-art">CP</div>
              )}
              <div>
                <span>{card.label}</span>
                <strong>{card.title}</strong>
                <small>{card.detail}</small>
                <em>{card.action}</em>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* -------------------- OPTIONS -------------------- */

const FALLBACK_GAMING_EVENTS = [
  {
    id: "summer-game-fest",
    name: "Summer Game Fest",
    date: "2026-06-06T21:00:00",
    durationHours: 2,
    youtubeId: "VIDEO_ID_ICI",
    type: "Showcase",
    description: "Conférence gaming avec annonces, trailers et nouveautés.",
  },
  {
    id: "game-awards",
    name: "The Game Awards",
    date: "2026-12-12T01:30:00",
    durationHours: 3,
    youtubeId: "VIDEO_ID_ICI",
    type: "Cérémonie",
    description: "Récompenses, annonces mondiales et gros trailers.",
  },
];

function getEventState(event) {
  const now = new Date();
  const start = new Date(event.date);
  const end = new Date(start);

  end.setHours(end.getHours() + (event.durationHours || 2));

  if (now >= start && now <= end) return "live";
  if (now < start) return "upcoming";
  return "ended";
}

function getSortedEvents(events = FALLBACK_GAMING_EVENTS) {
  return [...events].sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  );
}

function getUpcomingEvents(events = FALLBACK_GAMING_EVENTS) {
  return getSortedEvents(events).filter(
    (event) => getEventState(event) !== "ended"
  );
}

function formatEventDate(date) {
  return new Date(date).toLocaleDateString("fr-FR", {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const DEFAULT_SOCIAL_PROFILE = {
  photoURL: "",
  displayName: "Player One",
  handle: accountsEnabled ? "" : "checkpoint",
  bio: "Je construis mon univers gaming, une partie après l'autre.",
  platform: "Multi-plateforme",
  visibility: "prive",
  featuredBadgeId: "",
  selectedBadgeIds: [],
  isCreator: !accountsEnabled,
  creatorBadgeEnabled: !accountsEnabled,
  identityGameIds: [],
  setupPhotos: [],
  collectionPhotos: [],
  publicSections: DEFAULT_PUBLIC_SECTIONS,
  showShowcasePreview: true,
  activityLikes: {},
  activityComments: {},
  posts: [],
};

const SOCIAL_PROFILE_STORAGE_KEY = "checkpoint-social-profile";
const SOCIAL_PROFILE_SCHEMA_VERSION = 2;

function normalizeArray(value, limit = Infinity) {
  return Array.isArray(value) ? value.filter(Boolean).slice(0, limit) : [];
}

function normalizeObject(value) {
  return value && typeof value === "object" && !Array.isArray(value) ? value : {};
}

function normalizeSocialProfile(profile = {}) {
  const source = normalizeObject(profile);
  const hasDisplayName = Object.prototype.hasOwnProperty.call(
    source,
    "displayName"
  );
  const hasHandle = Object.prototype.hasOwnProperty.call(source, "handle");
  const displayName =
    (hasDisplayName
      ? String(source.displayName).trim()
      : DEFAULT_SOCIAL_PROFILE.displayName) || "";
  const handle = hasHandle
    ? normalizeHandle(source.handle)
    : DEFAULT_SOCIAL_PROFILE.handle;
  const creatorEnabled =
    DEFAULT_SOCIAL_PROFILE.creatorBadgeEnabled ||
    isCreatorProfile({
      ...DEFAULT_SOCIAL_PROFILE,
      ...source,
      displayName,
      handle,
    });

  return {
    ...DEFAULT_SOCIAL_PROFILE,
    ...source,
    schemaVersion: SOCIAL_PROFILE_SCHEMA_VERSION,
    photoURL: typeof source.photoURL === "string" ? source.photoURL : "",
    displayName,
    handle,
    bio:
      typeof source.bio === "string"
        ? source.bio
        : DEFAULT_SOCIAL_PROFILE.bio,
    platform:
      typeof source.platform === "string"
        ? source.platform
        : DEFAULT_SOCIAL_PROFILE.platform,
    visibility: ["public","friends"].includes(source.visibility) ? source.visibility : "prive",
    featuredBadgeId:
      source.featuredBadgeId || (creatorEnabled ? "creator_checkpoint" : ""),
    selectedBadgeIds: normalizeArray(source.selectedBadgeIds, 4).map(String),
    isCreator: creatorEnabled,
    creatorBadgeEnabled: creatorEnabled,
    identityGameIds: normalizeArray(source.identityGameIds, 3).map(String),
    setupPhotos: normalizeArray(source.setupPhotos, 9),
    collectionPhotos: normalizeArray(source.collectionPhotos, 9),
    publicSections: {
      ...DEFAULT_PUBLIC_SECTIONS,
      ...normalizeObject(source.publicSections),
    },
    showShowcasePreview: source.showShowcasePreview !== false,
    activityLikes: normalizeObject(source.activityLikes),
    activityComments: normalizeObject(source.activityComments),
    posts: normalizeArray(source.posts, 50),
  };
}

function storeSocialProfile(profile) {
  const normalizedProfile = normalizeSocialProfile(profile);
  localStorage.setItem(
    SOCIAL_PROFILE_STORAGE_KEY,
    JSON.stringify(normalizedProfile)
  );

  return normalizedProfile;
}

const SOCIAL_FRIENDS_STORAGE_KEY = "checkpoint-social-friends";

function normalizeSocialFriends(friends = []) {
  const byHandle = new Map();

  normalizeArray(friends).forEach((friend) => {
    const item = normalizeObject(friend);
    const handle = normalizeHandle(item.handle || "");
    if (!handle) return;

    byHandle.set(handle, {
      ...item,
      handle,
      displayName: item.displayName || handle,
      bio: item.bio || "",
      addedAt: item.addedAt || new Date().toISOString(),
    });
  });

  return [...byHandle.values()];
}

function storeSocialFriends(friends) {
  const normalizedFriends = normalizeSocialFriends(friends);
  localStorage.setItem(
    SOCIAL_FRIENDS_STORAGE_KEY,
    JSON.stringify(normalizedFriends)
  );

  return normalizedFriends;
}

function getCloudSafeSocialProfile(profile = {}) {
  const normalizedProfile = normalizeSocialProfile(profile);
  const { setupPhotos, collectionPhotos, ...cloudProfile } = normalizedProfile;

  return {
    ...cloudProfile,
    photoCounts: {
      setup: setupPhotos.length,
      collection: collectionPhotos.length,
    },
  };
}

function mergeCloudSocialProfile(remoteProfile = {}, localProfile = {}) {
  const local = normalizeSocialProfile(localProfile);
  const remote = normalizeSocialProfile(remoteProfile);

  return {
    ...remote,
    setupPhotos: local.setupPhotos,
    collectionPhotos: local.collectionPhotos,
  };
}

function getActivityLikeState(activityLikes = {}, activityId = "") {
  const entry = activityLikes?.[activityId];

  if (typeof entry === "number") {
    return { count: Math.max(0, entry), liked: false };
  }

  return {
    count: Math.max(0, Number(entry?.count || 0)),
    liked: Boolean(entry?.liked),
  };
}

function getInitials(name = "") {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (!words.length) return "C";

  return words
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
}

function parseActivityDate(value) {
  if (!value) return null;
  if (typeof value.toDate === "function") return value.toDate();
  if (typeof value.seconds === "number") return new Date(value.seconds * 1000);

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatActivityTime(value) {
  const date = parseActivityDate(value);
  if (!date) return "Récemment";

  return date.toLocaleDateString("fr-FR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

function getGameCoverImage(game = {}) {
  return (
    game.image ||
    game.background_image ||
    game.cover ||
    game.coverUrl ||
    game.imageUrl ||
    game.thumbnail ||
    ""
  );
}

function getSocialActivityFeed(games = [], hardware = [], badges = []) {
  const activities = [];

  games.forEach((game, index) => {
    const date = parseActivityDate(game.updatedAt || game.createdAt);
    const base = {
      id: `game-${game.id || index}`,
      image: getGameCoverImage(game),
      title: game.name,
      date: game.updatedAt || game.createdAt,
      sortTime: date?.getTime() || 0,
      priority: 20 - index,
      source: "games",
      ...reviewActivityFields(game,getGameRating(game)),
    };
    const finishedDate=parseActivityDate(game.completedAt);
    if(isGameFinishedStatus(game)&&finishedDate){
      activities.push({id:`finished-${game.id||index}`,image:base.image,title:game.name,date:game.completedAt,sortTime:finishedDate.getTime(),priority:75,source:'games',type:'Terminé',kind:'completed',text:`a terminé ${game.name}`,detail:''});
    }

    if (getGameRating(game) > 0) {
      activities.push({
        ...base,
        type: "Note",
        text: `a noté ${game.name}`,
        detail: formatRating10(getGameRating(game)),
        priority: base.priority + 50,
      });
      return;
    }

    if (game.review && game.review.trim().length > 10) {
      activities.push({
        ...base,
        type: "Avis",
        text: `a publié un avis sur ${game.name}`,
        detail: "",
        priority: base.priority + 45,
      });
      return;
    }

    if (game.status === "wishlist") {
      activities.push({
        ...base,
        type: "Wishlist",
        text: `${game.name} passe dans la wishlist`,
        detail: game.released?.split("-")[0] || "Jeu à surveiller",
        priority: base.priority + 10,
      });
      return;
    }

    if (game.status === "en cours") {
      activities.push({
        ...base,
        type: "En cours",
        text: `a commence ${game.name}`,
        detail: "Partie en cours",
        priority: base.priority + 30,
      });
      return;
    }

    activities.push({
      ...base,
      type: "Collection",
      text: `${game.name} rejoint la collection`,
      detail: game.released?.split("-")[0] || "Nouvelle entrée",
      priority: base.priority + 15,
    });
  });

  hardware.forEach((item, index) => {
    const itemRating = clampRating(item.rating);
    const date = parseActivityDate(item.updatedAt || item.createdAt);
    activities.push({
      id: `hardware-${item.id || index}`,
      image: item.image || "",
      title: item.name,
      type: itemRating ? "Matériel noté" : "Matériel",
      text: itemRating
        ? `a noté ${item.name}`
        : `${item.name} rejoint le setup`,
      detail: itemRating ? formatRating10(itemRating) : item.status || "Nouveau matériel",
      date: item.updatedAt || item.createdAt,
      sortTime: date?.getTime() || 0,
      priority: 8 - index,
      source: "hardware",
    });
  });

  badges
    .filter((badge) => badge.unlocked)
    .slice(0, 5)
    .forEach((badge, index) => {
      activities.push({
        id: `badge-${badge.id}`,
        image: "",
        title: badge.name,
        type: "Badge",
        text: `a débloqué le badge ${badge.name}`,
        detail: badge.desc,
        date: null,
        sortTime: 0,
        priority: 35 - index,
        source: "badges",
      });
    });

  return activities
    .sort((a, b) => {
      if (b.sortTime !== a.sortTime) return b.sortTime - a.sortTime;
      return b.priority - a.priority;
    })
    .slice(0, 20);
}

function normalizeHandle(handle = "") {
  return handle.toLowerCase().trim().replace(/[^a-z0-9_-]/g, "");
}

function getProfileShareUrl(handle) {
  if (!handle) return "";

  return `${window.location.origin}${window.location.pathname}?profile=${encodeURIComponent(
    handle
  )}`;
}

function getCurrentOwnedHardware(hardware = []) {
  return hardware.filter((item) => {
    const status = getNormalizedStatus(item?.status || "");
    return status.includes("poss") || status.includes("reparer");
  });
}

function getHardwareConsoleGameStats(hardwareItem, games = []) {
  if (hardwareItem?.type !== "console") {
    return {
      games: 0,
      xp: 0,
      level: 1,
      progressPercent: 0,
    };
  }

  const consoleName = hardwareItem.name?.toLowerCase() || "";
  const parentName = hardwareItem.parentName?.toLowerCase() || "";
  const brand = hardwareItem.brand?.toLowerCase() || "";

  const matchedGames = games.filter((game) => {
    const platforms =
      game.playedPlatforms?.length > 0
        ? game.playedPlatforms
        : game.platformNames || [];

    return platforms.some((platform) => {
      const p = platform.toLowerCase();

      return (
        p.includes(consoleName) ||
        consoleName.includes(p) ||
        p.includes(parentName) ||
        parentName.includes(p) ||
        p.includes(brand)
      );
    });
  });

  const xp = matchedGames.reduce((sum, game) => sum + calculateXP(game), 0);

  return {
    games: matchedGames.length,
    xp,
    level: Math.max(1, Math.floor(xp / 500) + 1),
    progressPercent: Math.min(100, (xp % 500) / 5),
  };
}

const PUBLIC_HARDWARE_GROUPS = [
  { id: "console", label: "Consoles" },
  { id: "controller", label: "Manettes" },
  { id: "audio", label: "Audio" },
  { id: "speaker", label: "Enceintes" },
  { id: "vr", label: "VR" },
  { id: "display", label: "Ecrans & TV" },
  { id: "mouse", label: "Souris" },
  { id: "keyboard", label: "Claviers" },
  { id: "other", label: "Autre matériel" },
];

function getPublicHardwareGroup(type) {
  if (["console", "controller", "audio", "speaker", "vr", "display", "mouse", "keyboard"].includes(type)) {
    return type;
  }

  return "other";
}

function resizeSocialPhoto(file) {
  return new Promise((resolve, reject) => {
    if (!file?.type?.startsWith("image/")) {
      reject(new Error("Format image invalide"));
      return;
    }

    const reader = new FileReader();

    reader.onload = () => {
      const image = new Image();

      image.onload = () => {
        const maxSize = 1100;
        const scale = Math.min(1, maxSize / Math.max(image.width, image.height));
        const canvas = document.createElement("canvas");

        canvas.width = Math.round(image.width * scale);
        canvas.height = Math.round(image.height * scale);

        const ctx = canvas.getContext("2d");
        ctx.drawImage(image, 0, 0, canvas.width, canvas.height);

        resolve({
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          src: canvas.toDataURL("image/jpeg", 0.78),
          name: file.name,
          addedAt: new Date().toISOString(),
        });
      };

      image.onerror = reject;
      image.src = reader.result;
    };

    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function ActivityFeed({
  reactionResource,
  reactionHandle,
  activities = [],
  compact = false,
  activityLikes = {},
  onToggleLike,
  activityComments = {},
  onAddComment,
  onDeletePost,
}) {
  const [commentDrafts, setCommentDrafts] = useState({});

  if (!activities.length) {
    return (
      <EmptyState
        title="Aucune activité"
        subtitle="Ajoute des jeux, du matériel ou des notes pour alimenter ton feed."
      />
    );
  }

  return (
    <div className={`social-feed ${compact ? "compact" : ""}`}>
      {activities.map((activity) => {
        const storedLikeState = getActivityLikeState(activityLikes, activity.id);
        const comments = Array.isArray(activityComments[activity.id])
          ? activityComments[activity.id]
          : Array.isArray(activity.comments)
          ? activity.comments
          : [];
        const likeCount = Math.max(
          storedLikeState.count,
          Number(activity.likes || 0)
        );
        const isLiked = storedLikeState.liked || Boolean(activity.liked);
        const canLike = typeof onToggleLike === "function";
        const canComment = typeof onAddComment === "function";
        const commentDraft = commentDrafts[activity.id] || "";

        const submitComment = (event) => {
          event.preventDefault();
          const text = commentDraft.trim();

          if (!text) return;

          onAddComment(activity.id, text);
          setCommentDrafts((current) => ({
            ...current,
            [activity.id]: "",
          }));
        };

        return (
          <div
            key={activity.id}
            className={`social-feed-item source-${activity.source || "generic"} ${activity.image ? "" : "no-media"}`}
          >
            {activity.image ? (
              <img
                className="social-feed-media"
                src={hardwarePhotoUrl(activity.image)}
                alt={activity.title}
                loading="lazy"
                decoding="async"
              />
            ) : null}

            <div className="social-feed-main">
              <div className="social-feed-top">
                <span>{activity.type}</span>
                <div className="social-feed-top-actions">
                  <small>{formatActivityTime(activity.date)}</small>
                  {activity.source === "posts" && typeof onDeletePost === "function" && (
                    <button
                      type="button"
                      onClick={() => onDeletePost(activity.postId)}
                    >
                      Retirer
                    </button>
                  )}
                </div>
              </div>
              {activity.author && (
                <div className="social-feed-author">@{activity.author}</div>
              )}
              <strong>{activity.text}</strong>
              {activity.detail && <p><SpoilerText text={activity.detail}/></p>}
              {activity.reviewText&&<p><SpoilerText text={activity.reviewText} whole={activity.reviewSpoiler}/></p>}

              {reactionResource&&<SocialReactions handle={reactionHandle} activityId={activity.id} resource={reactionResource}/>}
              {!reactionResource&&(canLike || canComment || likeCount > 0 || comments.length > 0) && (
                <div className="social-feed-actions">
                  <button
                    type="button"
                    className={`social-like-btn ${isLiked ? "liked" : ""}`}
                    onClick={() => onToggleLike?.(activity.id)}
                    aria-pressed={isLiked}
                    disabled={!canLike}
                  >
                    <span className="social-like-icon" aria-hidden="true">
                      ♥
                    </span>
                    <span>{isLiked ? "Aimé" : "J'aime"}</span>
                    <strong>{likeCount}</strong>
                  </button>
                  {comments.length > 0 && (
                    <span className="social-comment-count">
                      {comments.length} commentaire{comments.length > 1 ? "s" : ""}
                    </span>
                  )}
                </div>
              )}

              {comments.length > 0 && (
                <div className="social-comments">
                  {comments.slice(0, 2).map((comment) => (
                    <div
                      key={comment.id || `${activity.id}-${comment.createdAt}`}
                      className="social-comment"
                    >
                      <strong>{comment.author || "Checkpoint"}</strong>
                      <span>{comment.text}</span>
                    </div>
                  ))}
                </div>
              )}

            </div>
            {canComment && (
              <form className="social-comment-form" onSubmit={submitComment}>
                <input
                  value={commentDraft}
                  onChange={(event) =>
                    setCommentDrafts((current) => ({
                      ...current,
                      [activity.id]: event.target.value,
                    }))
                  }
                  placeholder="Ajouter un commentaire"
                  maxLength={160}
                />
                {commentDraft.trim() && (
                  <button type="submit">
                    Envoyer
                  </button>
                )}
              </form>
            )}
          </div>
        );
      })}
    </div>
  );
}

function GamingTimeline({ timeline = [], compact = false }) {
  const [filter, setFilter] = useState("all");
  const [newestFirst, setNewestFirst] = useState(false);
  const [showAllYears, setShowAllYears] = useState(false);
  if (!timeline.length) {
    return (
      <EmptyState
        title="Ton histoire gaming commence ici"
        message="Tes jeux marquants, tes GOTY et ton matériel dessinent ton parcours, à ton rythme."
      />
    );
  }

  const knownYears = timeline
    .map((event) => event.year)
    .filter(Boolean)
    .sort((a, b) => a - b);
  const firstYear = knownYears[0];
  const lastYear = knownYears[knownYears.length - 1];
  const filteredTimeline = selectTimelineEvents(timeline, filter, newestFirst);
  const recentYears = [...new Set(filteredTimeline.map((event) => event.year).filter(Boolean))].sort((a, b) => b - a).slice(0, 6);
  const visibleTimeline = compact ? timeline.slice(-6) : showAllYears ? filteredTimeline : filteredTimeline.filter((event) => !event.year || recentYears.includes(event.year));

  return (
    <div className={`gaming-timeline ${compact ? "compact" : ""}`}>
      {!compact && (
        <div className="gaming-timeline-overview">
          <div>
            <span>Tes repères gaming</span>
            <strong>
              {firstYear && lastYear
                ? `${firstYear} - ${lastYear}`
                : "À ton rythme"}
            </strong>
            <small>{timeline.length} moments retenus dans ta frise.</small>
          </div>
          <div>
            <span>Tes coups de cœur de l’année</span>
            <strong>{timeline.filter((event) => event.type === "goty").length}</strong>
            <small>GOTY personnels épinglés.</small>
          </div>
        </div>
      )}

      {!compact && <div className="gaming-timeline-controls">
        <div className="chips-group" role="group" aria-label="Filtrer ton parcours">
          {[["all", "Tout"], ["games", "Jeux"], ["goty", "GOTY"], ["hardware", "Matériel"]].map(([value, label]) => (
            <button key={value} type="button" className={`chip ${filter === value ? "active" : ""}`} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
          ))}
        </div>
        <button type="button" className="profile-toggle-btn" aria-pressed={newestFirst} onClick={() => setNewestFirst((value) => !value)}>{newestFirst ? "Du plus récent au plus ancien" : "Du plus ancien au plus récent"}</button>
        <button type="button" className="profile-toggle-btn" aria-pressed={showAllYears} onClick={() => setShowAllYears((value) => !value)}>{showAllYears ? "Voir les 6 dernières années" : "Voir toutes les années"}</button>
        <span className="option-value">{visibleTimeline.length} {visibleTimeline.length > 1 ? "moments" : "moment"}</span>
      </div>}
      {!visibleTimeline.length && <p className="option-value">Pas encore de moment dans cette catégorie. Ton parcours continue de se dessiner.</p>}

      <div className="gaming-timeline-track">
        {visibleTimeline.map((event) => (
          <article
            key={event.id}
            className={`gaming-timeline-event tone-${event.tone || event.type}`}
          >
            <div className="gaming-timeline-year">{event.yearLabel}</div>
            <div className="gaming-timeline-node">
              {event.image ? <img src={hardwarePhotoUrl(event.image)} alt={event.title} /> : <span>{event.icon}</span>}
            </div>
            <div className="gaming-timeline-card">
              <span>{event.kicker}</span>
              <strong>{event.title}</strong>
              <small>{event.detail}</small>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
}

export function PublicProfilePreview({
  profile,
  title = "Profil public",
  actionLabel,
  onAction,
  onBlock,
  onReport,
  onClose,
  closeLabel = "Fermer",
}) {
  const [selectedHardware, setSelectedHardware] = useState(null);

  if (!profile) return null;
  profile=publicProfileView(profile);
  const publicSections = {
    ...DEFAULT_PUBLIC_SECTIONS,
    ...(profile.publicSections || {}),
  };
  const profileShowcase = profile.showcase || {
    topScores: (profile.essentialTops || []).map((section) => ({
      key: section.key,
      label: section.label,
      game: section.game,
    })),
    specializedTops: [],
    gotyHighlights: [],
    hardwareHighlights: [],
  };
  const playerAnalysis = getPublicPlayerNotes(profile);
  const identityTitle = publicSections.identityGames && profile.identityGames?.length
    ? getFounderSignature(profile.identityGames, profile.displayName || "Ce joueur")
    : null;
  const publicFeaturedBadge = restorePublicBadge(profile.featuredBadge, BADGES);
  const publicBadges = [
    publicFeaturedBadge,
    ...(profile.selectedBadges || []),
  ]
    .filter(Boolean)
    .filter((badge, index, list) =>
      list.findIndex((item) => String(item.id) === String(badge.id)) === index
    )
    .slice(0, 5)
    .map(badge => restorePublicBadge(badge, BADGES));
  const platformStrengths = (profile.platformStrengths || []).slice(0, 4);
  const identityGames = publicSections.identityGames ? (profile.identityGames || []).slice(0, 3) : [];
  const sanctuaryGames = (profile.sanctuaryGames || []).slice(0, 4);
  const sanctuaryHardware = (profile.sanctuaryHardware || []).slice(0, 3);
  const gamingTimeline = (profile.gamingTimeline || []).slice(0, 8);
  const heroGame =
    identityGames[0] ||
    sanctuaryGames[0] ||
    profileShowcase.topScores?.[0]?.game ||
    profile.favoriteGames?.[0] ||
    null;

  return (
    <div className="search-panel social-public-profile social-public-profile-v2">
      <div className="public-vitrine-hero">
        <div className="public-vitrine-hero-bg" aria-hidden="true" />
        <div className="public-vitrine-topbar">
          <div>
            <span>{title}</span>
            <strong>@{profile.handle}</strong>
          </div>
          {onClose && (
            <button type="button" onClick={onClose}>
              {closeLabel}
            </button>
          )}
        </div>

        <div className="public-vitrine-main">
          <div className="public-vitrine-avatar">
            {profile.photoURL ? <SocialAvatar name={profile.displayName} src={profile.photoURL} className="public-profile-photo" zoomable /> : heroGame?.image ? (
              <img src={heroGame.image} alt={heroGame.name} />
            ) : (
              <span>{getInitials(profile.displayName)}</span>
            )}
          </div>
          <div className="public-vitrine-identity">
            <div className="public-profile-name-row">
              <h3>{profile.displayName}</h3>
              <FeaturedBadgePill badge={publicFeaturedBadge} />
            </div>
            <p>{profile.bio || "Profil Checkpoint"}</p>
            <FavoritePlatformTags value={profile.platform} />
            {identityTitle && (
              <div className="public-vitrine-signature">
                <span>Les jeux qui comptent</span>
                <strong>{identityTitle.title}</strong>
                <small>{identityTitle.subtitle}</small>
              </div>
            )}
          </div>
        </div>

        <div className="public-vitrine-stats">
          <div>
            <strong>{profile.level || 1}</strong>
            <span>Niveau</span>
          </div>
          <div>
            <strong>{profile.totalGames || 0}</strong>
            <span>Jeux</span>
          </div>
          <div>
            <strong>{profile.finishedGames || 0}</strong>
            <span>Termines</span>
          </div>
          <div>
            <strong>{profile.averageRating || "-"}</strong>
            <span>Note moy.</span>
          </div>
        </div>
      </div>

      {publicBadges.length > 0 && (
        <section className="public-vitrine-section" aria-label="Badges à la une">
          <div className="public-vitrine-section-head">
            <span>Ses récompenses</span>
            <strong>Badges à la une</strong>
          </div>
          <div className="public-profile-badges profile-tab">
            <div className="public-badge-strip">
              {publicBadges.map(badge => (
                <article key={badge.id} className={`badge-card unlocked ${badge.rarity}${
                  badge.platformFamily ? ` platform-${badge.platformFamily}` : ""
                }${badge.platformKey ? " platform-specific" : " brand-family"}${
                  isBadgeNumericSeal(badge) ? " badge-numeric-seal" : ""
                }${badge.special ? ` badge-special-${badge.special}` : ""}`}>
                  <span className="badge-sheen" aria-hidden="true" />
                  <span className="badge-sparkles" aria-hidden="true" />
                  <div className="badge-topline">
                    <div className="badge-emblem"><span className="badge-icon"><BadgeVisualIcon badge={badge} /></span></div>
                    <span className="badge-rarity-chip">{getBadgeRarityLabel(badge)}</span>
                  </div>
                  <div className="badge-name">{badge.name}</div>
                </article>
              ))}
            </div>
          </div>
        </section>
      )}

      {playerAnalysis && (
        <div className="public-vitrine-section public-player-analysis">
          <div className="public-vitrine-section-head">
            <span>Ses habitudes de jeu</span>
            <strong>{playerAnalysis.maturity}</strong>
          </div>
          <div className="public-analysis-card">
            <div className="public-analysis-icon">{playerAnalysis.icon}</div>
            <div>
              <strong>{playerAnalysis.title}</strong>
              <p>{playerAnalysis.subtitle}</p>
              {playerAnalysis.vibe && <span>{playerAnalysis.vibe}</span>}
            </div>
          </div>
        </div>
      )}

      <div className="home-section-head">
        <div>
          <h2 className="panel-title">{title}</h2>
          <div className="option-value">@{profile.handle}</div>
        </div>
        {onClose && (
          <button type="button" onClick={onClose}>
            Fermer
          </button>
        )}
      </div>

      <div className="social-public-profile-body">
        <div className="social-avatar">{getInitials(profile.displayName)}</div>
        <div>
          <div className="public-profile-name-row">
            <h3>{profile.displayName}</h3>
            <FeaturedBadgePill badge={publicFeaturedBadge} />
          </div>
          <p>{profile.bio || "Profil Checkpoint"}</p>
          <div className="social-profile-tags">
            <span>Niveau {profile.level || 1}</span>
            <span>{profile.platform || "Multi-plateforme"}</span>
            <span>{profile.finishedGames || 0} terminés</span>
            <span>{profile.averageRating || "-"} note moy.</span>
            <span>{profile.hardwareCount || 0} matériel</span>
          </div>
        </div>
      </div>

      {publicSections.photos && profile.setupPhotos?.length > 0 && (
        <div className="social-public-mini-section">
          <strong>Setup</strong>
          <div className="social-photo-gallery">
            {profile.setupPhotos.map((photo) => (
              <img key={photo.id || photo.src} src={photo.src} alt="Setup" />
            ))}
          </div>
        </div>
      )}

      {publicSections.photos && profile.collectionPhotos?.length > 0 && (
        <div className="social-public-mini-section">
          <strong>Collection physique</strong>
          <div className="social-photo-gallery">
            {profile.collectionPhotos.map((photo) => (
              <img
                key={photo.id || photo.src}
                src={photo.src}
                alt="Collection physique"
              />
            ))}
          </div>
        </div>
      )}

      {publicSections.essential&&<FavoriteReasons games={profile.favoriteGames||[]}/>}

      {publicSections.essential &&
        (profileShowcase.topScores?.length > 0 ||
          profileShowcase.specializedTops?.length > 0 ||
          profileShowcase.gotyHighlights?.length > 0 ||
          profileShowcase.hardwareHighlights?.length > 0) && (
        <div className="social-public-mini-section">
          <strong>Vitrine Checkpoint</strong>
          <ProfileShowcase showcase={profileShowcase} />
        </div>
      )}

      {publicSections.identityGames && profile.identityGames?.length > 0 && (
        <div className="social-public-mini-section">
          <strong>Ses jeux fondateurs</strong>
          <FounderGames games={identityGames} />
        </div>
      )}

      {publicSections.essential &&
        (sanctuaryGames.length > 0 || sanctuaryHardware.length > 0) && (
        <div className="public-vitrine-section public-sanctuary-section">
          <div className="public-vitrine-section-head">
            <span>Sanctuaire</span>
            <strong>Ce qui compte vraiment</strong>
          </div>
          <div className="public-sanctuary-grid">
            {sanctuaryGames.map((game) => (
              <div key={`sanctuary-game-${game.id || game.name}`} className="public-sanctuary-item">
                {game.image ? <img src={game.image} alt={game.name} /> : <span>Jeu</span>}
                <div>
                  <strong>{game.name}</strong>
                  <small>
                    {game.rating ? formatGameRating10(game.rating) : "Jeu marquant"}
                  </small>
                </div>
              </div>
            ))}
            {sanctuaryHardware.map((item) => (
              <div key={`sanctuary-hardware-${item.id || item.name}`} className="public-sanctuary-item hardware">
                {item.image ? <img src={hardwarePhotoUrl(item.image)} alt={item.name} /> : <span>Set</span>}
                <div>
                  <strong>{item.name}</strong>
                  <small>{item.type || "Matériel culte"}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {gamingTimeline.length > 0 && (
        <div className="public-vitrine-section public-gaming-timeline">
          <div className="public-vitrine-section-head">
            <span>Timeline gaming</span>
            <strong>Parcours joueur</strong>
          </div>
          <GamingTimeline timeline={gamingTimeline} compact />
        </div>
      )}

      {platformStrengths.length > 0 && (
        <div className="public-vitrine-section">
          <div className="public-vitrine-section-head">
            <span>Plateformes fortes</span>
            <strong>Terrain de jeu principal</strong>
          </div>
          <div className="public-platform-strip">
            {platformStrengths.map(([platform, count]) => (
              <div key={platform}>
                <strong>{platform}</strong>
                <span>{count} jeux</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {publicSections.hardware && profile.currentHardware?.length > 0 && (
        <div className="social-public-mini-section">
          <strong>Matériel actuel</strong>
          <div className="social-public-hardware-groups">
            {PUBLIC_HARDWARE_GROUPS.map((group) => {
              const items = profile.currentHardware.filter(
                (item) => getPublicHardwareGroup(item.type) === group.id
              );

              if (!items.length) return null;

              return (
                <div key={group.id} className="social-public-hardware-group">
                  <div className="social-public-hardware-group-title">
                    {group.label}
                  </div>
                  <div className="social-public-favorites">
                    {items.map((item) => (
                      <button
                        key={item.id || item.name}
                        type="button"
                        className="social-public-favorite hardware"
                        onClick={() =>
                          setSelectedHardware((current) =>
                            current?.id === item.id ? null : item
                          )
                        }
                      >
                        {item.image ? (
                          <img src={hardwarePhotoUrl(item.image)} alt={item.name} />
                        ) : null}
                        <span>{item.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {selectedHardware && (
        <div className="social-public-hardware-detail">
          <div>
            <strong>{selectedHardware.name}</strong>
            <span>
              {selectedHardware.type === "console"
                ? "Console"
                : selectedHardware.type === "controller"
                ? "Manette"
                : selectedHardware.type === "audio"
                ? "Audio"
                : selectedHardware.type === "display"
                ? "Ecran / TV"
                : "Matériel"}
              {" • "}
              {selectedHardware.status || "Possédé"}
            </span>
          </div>

          <div className="social-public-hardware-stats">
            <span>{formatRating10(selectedHardware.rating, "-")}</span>
            {selectedHardware.type === "console" && (
              <span>{selectedHardware.gameCount || 0} jeux joués</span>
            )}
          </div>

          {selectedHardware.ratings &&
            Object.keys(selectedHardware.ratings).length > 0 && (
              <div className="social-public-rating-grid">
                {getHardwareRatingFields(selectedHardware.type)
                  .filter((field) => selectedHardware.ratings[field.key])
                  .map((field) => (
                    <div key={field.key}>
                      <span>{field.label}</span>
                      <strong>{formatRating10(selectedHardware.ratings[field.key], "-")}</strong>
                    </div>
                  ))}
              </div>
            )}
        </div>
      )}

      {publicSections.activity && profile.recentActivity?.length > 0 && (
        <div className="social-public-mini-section">
          <strong>Activité récente</strong>
          <ActivityFeed activities={profile.recentActivity.slice(0, 4)} compact />
        </div>
      )}

      {actionLabel && onAction && (
        <button type="button" className="social-public-add" onClick={onAction}>
          {actionLabel}
        </button>
      )}
      {onBlock&&<button type="button" className="option-pill" onClick={()=>onBlock(profile)}>Bloquer ce joueur</button>}
      {onReport&&<button type="button" className="option-pill" onClick={()=>onReport({type:'profile',handle:profile.handle})}>Signaler ce profil</button>}
    </div>
  );
}

function ProfileShowcase({ showcase, identityTitle = null }) {
  const hasTopScores = showcase?.topScores?.length > 0;
  const hasSpecialized = showcase?.specializedTops?.length > 0;
  const hasGoty = showcase?.gotyHighlights?.length > 0;
  const hasHardware = showcase?.hardwareHighlights?.length > 0;

  if (!identityTitle && !hasTopScores && !hasSpecialized && !hasGoty && !hasHardware) {
    return null;
  }

  return (
    <div className="profile-showcase">
      {identityTitle && (
        <div className="profile-showcase-signature">
          <span>Les jeux qui comptent</span>
          <strong>{identityTitle.title}</strong>
          <p>{identityTitle.subtitle}</p>
        </div>
      )}

      {hasTopScores && (
        <div className="profile-showcase-section">
          <div className="profile-showcase-section-title">Tops jeux</div>
          <div className="profile-showcase-grid">
            {showcase.topScores.map((item) => (
              <div
                key={item.key}
                className={`profile-showcase-card ${item.game.image ? "" : "no-image"}`}
              >
                {item.game.image ? <img src={item.game.image} alt={item.game.name} /> : null}
                <div>
                  <span>{item.label}</span>
                  <strong>{item.game.name}</strong>
                  <small>{formatTopScore(item.game.score, item.key)}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasSpecialized && (
        <div className="profile-showcase-section">
          <div className="profile-showcase-section-title">Tops spécialisés</div>
          <div className="profile-showcase-grid">
            {showcase.specializedTops.map((item) => (
              <div
                key={`${item.label}-${item.game.id}`}
                className={`profile-showcase-card ${item.game.image ? "" : "no-image"}`}
              >
                {item.game.image ? <img src={item.game.image} alt={item.game.name} /> : null}
                <div>
                  <span>{item.label}</span>
                  <strong>{item.game.name}</strong>
                  <small>{item.criterion} · {formatTopScore(item.game.score, item.key)}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasGoty && (
        <div className="profile-showcase-section">
          <div className="profile-showcase-section-title">GOTY personnels</div>
          <div className="profile-showcase-grid compact">
            {showcase.gotyHighlights.map((item) => (
              <div
                key={`${item.year}-${item.game.id}`}
                className={`profile-showcase-card ${item.game.image ? "" : "no-image"}`}
              >
                {item.game.image ? <img src={item.game.image} alt={item.game.name} /> : null}
                <div>
                  <span>GOTY {item.year}</span>
                  <strong>{item.game.name}</strong>
                  <small>{formatTopScore(item.game.score, "rating")}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {hasHardware && (
        <div className="profile-showcase-section">
          <div className="profile-showcase-section-title">Matériel préféré</div>
          <div className="profile-showcase-grid compact">
            {showcase.hardwareHighlights.map((item) => (
              <div
                key={item.id || item.name}
                className={`profile-showcase-card hardware ${item.image ? "" : "no-image"}`}
              >
                {item.image ? <img src={hardwarePhotoUrl(item.image)} alt={item.name} /> : null}
                <div>
                  <span>{item.label}</span>
                  <strong>{item.name}</strong>
                  <small>{formatRating10(item.score, "-")}</small>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const loadCheckpointSocialProfile = handle => readPublicSocialProfile(handle, value => getDoc(doc(db, "publicProfiles", value)));

export function SocialTab({
  games,
  hardware,
  badges,
  level,
  socialProfile,
  socialFriends,
  sharedProfile,
  onCloseSharedProfile,
  onProfileChange,
  onSetProfileVisibility,
  onCopyProfileLink,
  onAddProfilePhotos,
  onRemoveProfilePhoto,
  onAddFriend,
  onRemoveFriend,
  onOpenFriend,
  initialView = "feed",
  targetPollId,
  targetChallengeId,
  targetRecommendationId,
  onSetFavoriteReason,
  onOpenComparisonGame,
  onDiscoverGame,
  friendInvitations,
  socialControls,
  onBlockFriend,
  onCommonGames,
  onMuteFriend,
  onReportSocial,
  navigationToken,
  loadFriendProfile = loadCheckpointSocialProfile,
}) {
  const [friendHandle, setFriendHandle] = useState("");
  const [socialMessage, setSocialMessage] = useState("");
  const [showPublicPreview, setShowPublicPreview] = useState(false);
  const [socialView, setSocialView] = useState(initialView);
  useEffect(()=>setSocialView(initialView),[initialView,navigationToken]);
  const [socialFeedSource, setSocialFeedSource] = useState("friends");
  const [socialFeedFilter, setSocialFeedFilter] = useState("all");
  const [postDraft, setPostDraft] = useState("");
  const [composerOpen, setComposerOpen] = useState(false);
  const activities = getSocialActivityFeed(games, hardware, badges);
  const stats = getAdvancedStats(games);
  const favorites = games.filter((game) => game.favorite).slice(0, 3);
  const finishedGames = games.filter(isGameFinishedStatus);
  const favoriteGames = games.filter((game) => game.favorite);
  const alphabeticGames = useMemo(
    () =>
      [...games].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", "fr", {
          numeric: true,
          sensitivity: "base",
        })
      ),
    [games]
  );
  const identityGameIds = Array.isArray(socialProfile.identityGameIds)
    ? socialProfile.identityGameIds.slice(0, 3).map(String)
    : [];
  const identityGames = identityGameIds
    .map((id) => games.find((game) => String(game.id) === id))
    .filter(Boolean);
  const identityTitle = getIdentityPlayerTitle(identityGames);
  const playerAnalysis = getPlayerAnalysis(games, hardware, badges);
  const essentialTopSections = [
    { key: "rating", label: "Global", games: getTopGamesForScore(games, "rating", 1) },
    { key: "ratingGameplay", label: "Gameplay", games: getTopGamesForScore(games, "ratingGameplay", 1) },
    { key: "ratingGraphics", label: "Graphismes", games: getTopGamesForScore(games, "ratingGraphics", 1) },
  ].filter((section) => section.games.length > 0);
  const currentHardware = getCurrentOwnedHardware(hardware);
  const profileShowcase = getProfileShowcase(games, hardware);
  const gamingTimeline = getGamingTimeline(games, hardware, socialProfile);
  const sanctuaryGames = games.filter((game) => game.sanctuary);
  const sanctuaryHardware = hardware.filter((item) => item.sanctuary);
  const missingIdentitySlots = Math.max(0, 3 - identityGames.length);
  const sanctuaryFounderSuggestions = sanctuaryGames
    .filter((game) => !identityGameIds.includes(String(game.id)))
    .slice(0, missingIdentitySlots);
  const activityLikes = socialProfile.activityLikes || {};
  const activityComments = socialProfile.activityComments || {};
  const socialPosts = Array.isArray(socialProfile.posts)
    ? socialProfile.posts
    : [];
  const socialPostActivities = socialPosts.map((post, index) => {
    const date = parseActivityDate(post.createdAt);

    return {
      id: `post-${post.id || index}`,
      type: "Post",
      title: "Publication",
      text: post.text,
      detail: post.mood || "",
      image: post.image || "",
      author: normalizeHandle(socialProfile.handle),
      date: post.createdAt,
      sortTime: date?.getTime() || 0,
      priority: 90 - index,
      source: "posts",
      postId: post.id,
    };
  });
  const allSocialFeedItems = [...socialPostActivities, ...activities]
    .sort((a, b) => {
      if ((b.sortTime || 0) !== (a.sortTime || 0)) {
        return (b.sortTime || 0) - (a.sortTime || 0);
      }

      return (b.priority || 0) - (a.priority || 0);
    })
    .slice(0, 16);
  const socialFeedItems = allSocialFeedItems.filter((activity) => {
    if (socialFeedFilter === "all") return true;
    return activity.source === socialFeedFilter;
  });
  const ownReactions=useSocialReactions(socialView==='feed'&&socialFeedSource==='mine'&&['public','friends'].includes(socialProfile.visibility)?[{...socialProfile,recentActivity:allSocialFeedItems}]:[]);
  const socialFeedFilters = [
    { key: "all", label: "Tout", count: allSocialFeedItems.length },
    { key: "posts", label: "Posts", count: socialPostActivities.length },
    {
      key: "games",
      label: "Jeux",
      count: allSocialFeedItems.filter((item) => item.source === "games").length,
    },
    {
      key: "hardware",
      label: "Materiel",
      count: allSocialFeedItems.filter((item) => item.source === "hardware").length,
    },
    {
      key: "badges",
      label: "Badges",
      count: allSocialFeedItems.filter((item) => item.source === "badges").length,
    },
  ];
  const profileCompletionItems = [
    socialProfile.displayName,
    normalizeHandle(socialProfile.handle),
    socialProfile.bio,
    identityGames.length >= 3,
    currentHardware.length > 0,
    (socialProfile.setupPhotos || []).length > 0 ||
      (socialProfile.collectionPhotos || []).length > 0,
    ["public","friends"].includes(socialProfile.visibility),
  ];
  const profileCompletion = Math.round(
    (profileCompletionItems.filter(Boolean).length / profileCompletionItems.length) * 100
  );
  const featuredBadge = getFeaturedBadgeFromSelection(
    badges,
    socialProfile.featuredBadgeId
  );
  const unlockedBadges = badges.filter((badge) => badge.unlocked);
  const selectedBadgeIds = Array.isArray(socialProfile.selectedBadgeIds)
    ? socialProfile.selectedBadgeIds
        .map(String)
        .filter((id, index, list) => id && list.indexOf(id) === index)
        .filter((id) => id !== String(featuredBadge?.id || ""))
        .slice(0, 4)
    : [];
  const selectedBadgeList = selectedBadgeIds
    .map((id) => unlockedBadges.find((badge) => String(badge.id) === id))
    .filter(Boolean);
  const badgeChoices = [...unlockedBadges].sort((a, b) => {
    const rarityOrder = {
      creator: 0,
      mythic: 1,
      legendary: 2,
      epic: 3,
      rare: 4,
      common: 5,
    };
    const aRank = a.special === "creator" ? rarityOrder.creator : rarityOrder[a.rarity] ?? 9;
    const bRank = b.special === "creator" ? rarityOrder.creator : rarityOrder[b.rarity] ?? 9;

    if (aRank !== bRank) return aRank - bRank;

    return (a.name || "").localeCompare(b.name || "", "fr", {
      numeric: true,
      sensitivity: "base",
    });
  });
  const secondaryBadgeChoices = badgeChoices.filter(
    (badge) => String(badge.id) !== String(featuredBadge?.id || "")
  );
  const finishedCount = games.filter(isGameFinishedStatus).length;
  const shareUrl = getProfileShareUrl(socialProfile.handle);
  const showShowcasePreview = socialProfile.showShowcasePreview !== false;
  const ownPublicPreview = {
    photoURL: socialProfile.photoURL || "",
    displayName: socialProfile.displayName || DEFAULT_SOCIAL_PROFILE.displayName,
    handle: normalizeHandle(socialProfile.handle),
    bio: socialProfile.bio || "",
    platform: socialProfile.platform || "",
    visibility: socialProfile.visibility || "prive",
    publicSections: {
      ...DEFAULT_PUBLIC_SECTIONS,
      ...(socialProfile.publicSections || {}),
    },
    featuredBadge,
    selectedBadges: selectedBadgeList
      .map(toPublicBadge),
    level,
    totalGames: games.length,
    finishedGames: finishedCount,
    hardwareCount: currentHardware.length,
    averageRating: stats.avgRating ? Math.round(stats.avgRating * 10) / 10 : 0,
    platformStrengths: stats.topPlatforms,
    setupPhotos: socialProfile.setupPhotos || [],
    collectionPhotos: socialProfile.collectionPhotos || [],
    identityTitle,
    playerAnalysis,
    gamingTimeline,
    showcase: profileShowcase,
    identityGames: identityGames.map((game) => ({
      ...toPublicFounderGame(game),
      rating: getGameRating(game),
    })),
    essentialTops: essentialTopSections.map((section) => {
      const game = section.games[0];

      return {
        key: section.key,
        label: section.label,
        game: {
          id: game.id,
          name: game.name,
          image: game.image || "",
          sourceIds:toPublicFounderGame(game).sourceIds,
          released:game.released||"",
          score: getGameScore(game, section.key),
        },
      };
    }),
    favoriteGames: publicFavoriteGames(games,getGameRating,socialProfile.publicSections?.essential!==false),
    currentHardware: currentHardware.slice(0, 6).map((item) => ({
      id: item.id,
      name: item.name,
      image: item.image || "",
      type: item.type || "",
      status: item.status || "",
      rating: clampRating(item.rating),
      ratings: item.ratings || {},
      gameCount: getHardwareConsoleGameStats(item, games).games,
    })),
    sanctuaryGames: sanctuaryGames.slice(0, 4).map((game) => ({
      id: game.id,
      name: game.name,
      image: game.image || "",
      rating: getGameRating(game),
      platforms: game.platforms || [],
    })),
    sanctuaryHardware: sanctuaryHardware.slice(0, 3).map((item) => ({
      id: item.id,
      name: item.name,
      image: item.image || "",
      type: item.type || "",
      brand: item.brand || "",
      rating: clampRating(item.rating),
    })),
    recentActivity: allSocialFeedItems.slice(0, 8).map((activity) => {
      const likeState = getActivityLikeState(activityLikes, activity.id);

      return {
        ...activity,
        likes: likeState.count,
        liked: likeState.liked,
        comments: activityComments[activity.id] || [],
      };
    }),
  };

  const handleVisibilityChange = async (visibility) => {
    const result = await onSetProfileVisibility(visibility);
    setSocialMessage(result.ok?result.message:'');
    return result;
  };

  const handleCopy = async () => {
    const result = await onCopyProfileLink();
    setSocialMessage(result.message);
  };

  const handleAddFriend = async () => {
    const result = await onAddFriend(friendHandle);
    setSocialMessage(result.message);

    if (result.ok) {
      setFriendHandle("");
    }
  };

  const handleToggleActivityLike = (activityId) => {
    const current = getActivityLikeState(activityLikes, activityId);
    const nextLikes = {
      ...activityLikes,
      [activityId]: {
        liked: !current.liked,
        count: Math.max(0, current.count + (current.liked ? -1 : 1)),
        updatedAt: new Date().toISOString(),
      },
    };

    onProfileChange("activityLikes", nextLikes);
  };

  const handleFeaturedBadgeSelect = (badgeId) => {
    onProfileChange("featuredBadgeId", String(badgeId));
  };

  const handlePublicBadgeToggle = (badgeId) => {
    const id = String(badgeId);
    const exists = selectedBadgeIds.includes(id);
    const nextIds = exists
      ? selectedBadgeIds.filter((item) => item !== id)
      : [...selectedBadgeIds, id].slice(0, 4);

    onProfileChange("selectedBadgeIds", nextIds);
  };

  const handleShowcasePreviewToggle = () => {
    setShowPublicPreview(value => !value);
  };

  const handleAddComment = (activityId, text) => {
    const nextComments = {
      ...activityComments,
      [activityId]: [
        ...(activityComments[activityId] || []),
        {
          id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
          text,
          author: socialProfile.displayName || DEFAULT_SOCIAL_PROFILE.displayName,
          createdAt: new Date().toISOString(),
        },
      ].slice(-12),
    };

    onProfileChange("activityComments", nextComments);
  };

  const handlePublishPost = () => {
    const text = postDraft.trim();

    if (!text) return;

    const nextPosts = [
      {
        id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
        text,
        createdAt: new Date().toISOString(),
      },
      ...socialPosts,
    ].slice(0, 24);

    onProfileChange("posts", nextPosts);
    setPostDraft("");
    setSocialView("feed");
    setSocialFeedFilter("posts");
    setSocialFeedSource("mine");
    setComposerOpen(false);
  };

  const handleDeletePost = (postId) => {
    if (!postId) return;

    const nextPosts = socialPosts.filter((post) => post.id !== postId);
    onProfileChange("posts", nextPosts);
  };

  const handlePhotoUpload = async (field, event) => {
    const result = await onAddProfilePhotos(field, event.target.files);
    event.target.value = "";
    setSocialMessage(result.message);
  };

  const handleIdentityGameChange = (index, value) => {
    const nextIds = [...identityGameIds];
    nextIds[index] = value;
    onProfileChange(
      "identityGameIds",
      nextIds.filter(Boolean).filter((id, itemIndex, list) => list.indexOf(id) === itemIndex)
    );
  };

  const handleUseSanctuaryAsFounders = () => {
    if (!sanctuaryFounderSuggestions.length) return;

    const nextIds = [
      ...identityGameIds,
      ...sanctuaryFounderSuggestions.map((game) => String(game.id)),
    ]
      .filter(Boolean)
      .filter((id, index, list) => list.indexOf(id) === index)
      .slice(0, 3);

    onProfileChange("identityGameIds", nextIds);
    setSocialView("showcase");
  };

  return (
    <div className={`progression-stack social-tab social-clean ${sharedProfile ? "social-visiting" : ""}`}>
      <SocialNavigation view={socialView} profile={socialProfile} friendCount={socialFriends.length} requestCount={friendInvitations?.connections.filter(item=>item.status==='pending'&&item.direction==='received').length||0} onChange={view => {
        setSocialView(view);
        setShowPublicPreview(false);
      }} />
      {socialView === "polls" && !sharedProfile && <FriendPolls games={games} friends={socialFriends} targetId={targetPollId}/>}
      {socialView === "challenges" && !sharedProfile && <FriendChallenges key={auth.currentUser?.uid||'local'} targetId={targetChallengeId} games={games} friends={socialFriends}/>}
      {socialView === "recommendations" && !sharedProfile && <GameRecommendations key={auth.currentUser?.uid||'local'} targetId={targetRecommendationId} games={games} friends={socialFriends} onDiscoverGame={onDiscoverGame}/>}
      {socialMessage && <p role="status" className="social-hub-notice">{socialMessage}</p>}
      {socialView === "feed" && <div className="social-hub-feed-tabs" role="group" aria-label="Choisir le fil">
        <button type="button" className={socialFeedSource === "friends" ? "active" : ""} aria-pressed={socialFeedSource === "friends"} onClick={() => setSocialFeedSource("friends")}>Mes amis</button>
        <button type="button" className={socialFeedSource === "mine" ? "active" : ""} aria-pressed={socialFeedSource === "mine"} onClick={() => setSocialFeedSource("mine")}>Mon activité</button>
      </div>}
      {sharedProfile && <FriendComparison games={games} profile={sharedProfile} onOpenGame={onOpenComparisonGame} onDiscover={onDiscoverGame} ratingGetter={getGameRating}/>}
      {sharedProfile && (
        <PublicProfilePreview
          profile={sharedProfile}
          title="Profil du joueur"
          closeLabel="Retour au Social"
          onClose={onCloseSharedProfile}
          onBlock={sharedProfile.handle!==socialProfile.handle?onBlockFriend:undefined}
          onReport={sharedProfile.handle!==socialProfile.handle?onReportSocial:undefined}
          actionLabel={socialFriends.some(friend => friend.handle === sharedProfile.handle) ? undefined : "Envoyer une invitation"}
          onAction={async () => {
            const result = await onAddFriend(sharedProfile.handle);
            setSocialMessage(result.message);
          }}
        />
      )}

      {socialView === "showcase" && (
        <div className="search-panel social-showcase-control">
          <div>
            <h2 className="panel-title">Aperçu public</h2>
            <div className="option-value">
              Ouvre ta vitrine comme tes amis la verront.
            </div>
          </div>
          <button
            type="button"
            className={`social-preview-toggle ${showPublicPreview ? "active" : ""}`}
            onClick={handleShowcasePreviewToggle}
          >
            {showPublicPreview ? "Fermer l’aperçu" : "Voir mon profil"}
          </button>
        </div>
      )}

      {showPublicPreview && (
        <PublicProfilePreview
          profile={ownPublicPreview}
          title="Aperçu de ton profil public"
          onClose={
            showPublicPreview
              ? () => setShowPublicPreview(false)
              : undefined
          }
        />
      )}

      {socialView === "showcase"&&<div className="search-panel"><FavoriteReasonPicker games={games} onSave={onSetFavoriteReason}/></div>}

      {socialView === "showcase" && (
        <div className="search-panel social-badge-picker-panel">
          <div className="home-section-head">
            <div>
              <h2 className="panel-title">Badges affichés</h2>
              <div className="option-value">
                Le badge principal apparaît près de ton pseudo. Les secondaires complètent ta vitrine publique.
              </div>
            </div>
            <span className="social-section-count">{selectedBadgeList.length}/4 secondaires</span>
          </div>

          {badgeChoices.length > 0 ? (
            <>
              <div className="social-badge-subsection">
                <div className="social-badge-subhead">
                  <span>Badge principal</span>
                  <small>{featuredBadge ? "Visible à côté du pseudo" : "Aucun badge choisi"}</small>
                </div>

                {featuredBadge && (
                  <div className="social-selected-badge">
                    <span>Sélection actuelle</span>
                    <FeaturedBadgePill badge={featuredBadge} />
                  </div>
                )}

                <div className="social-badge-scroll-row" aria-label="Choisir le badge principal">
                  {badgeChoices.map((badge) => {
                    const isFeatured = String(featuredBadge?.id || "") === String(badge.id);
                    const rarityLabel = getBadgeRarityLabel(badge);

                    return (
                      <button
                        key={badge.id}
                        type="button"
                        className={`social-badge-primary-card ${badge.rarity || ""} ${badge.special ? `badge-special-${badge.special}` : ""} ${isFeatured ? "featured" : ""}`}
                        onClick={() => handleFeaturedBadgeSelect(badge.id)}
                        aria-pressed={isFeatured}
                      >
                        <span className={`social-badge-choice-icon ${badge.rarity} ${badge.special ? `badge-special-${badge.special}` : ""}`}>
                          <BadgeVisualIcon badge={badge} />
                        </span>
                        <strong>{badge.name}</strong>
                        <small>{isFeatured ? `Principal • ${rarityLabel}` : rarityLabel}</small>
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="social-badge-subsection">
                <div className="social-badge-subhead">
                  <span>Badges secondaires</span>
                  <small>Jusqu'à 4 badges dans la vitrine</small>
                </div>

                <div className="social-badge-secondary-list" aria-label="Choisir les badges secondaires">
                  {secondaryBadgeChoices.map((badge) => {
                    const isSelected = selectedBadgeIds.includes(String(badge.id));
                    const rarityLabel = getBadgeRarityLabel(badge);

                    return (
                      <button
                        key={badge.id}
                        type="button"
                        className={`social-badge-choice-card social-badge-secondary-card ${badge.rarity || ""} ${badge.special ? `badge-special-${badge.special}` : ""} ${isSelected ? "selected" : ""}`}
                        onClick={() => handlePublicBadgeToggle(badge.id)}
                        aria-pressed={isSelected}
                      >
                        <span className={`social-badge-choice-icon ${badge.rarity} ${badge.special ? `badge-special-${badge.special}` : ""}`}>
                          <BadgeVisualIcon badge={badge} />
                        </span>
                        <span className="social-badge-secondary-copy">
                          <strong>{badge.name}</strong>
                          <small>{isSelected ? `Affiché • ${rarityLabel}` : rarityLabel}</small>
                        </span>
                        <span className="social-badge-toggle-choice">
                          {isSelected ? "Retirer" : "Ajouter"}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            </>
          ) : (
            <EmptyState
              title="Aucun badge débloqué"
              subtitle="Les badges que tu débloques pourront être affichés ici."
            />
          )}
        </div>
      )}

      {socialView === "feed" && (
        <div className="search-panel social-composer-panel">
          <button type="button" className="social-hub-compose-trigger" aria-expanded={composerOpen} onClick={() => setComposerOpen(value => !value)}>
            {composerOpen ? "Fermer la rédaction" : "Partager un moment"}<span aria-hidden="true">{composerOpen ? "−" : "+"}</span>
          </button>
          {composerOpen && <>
          <textarea
            value={postDraft}
            onChange={(event) => setPostDraft(event.target.value)}
            placeholder="À quoi tu joues en ce moment ?"
            maxLength={240}
            rows={3}
          />
          <div className="social-composer-actions">
            <span>{postDraft.trim().length}/240</span>
            <button
              type="button"
              onClick={handlePublishPost}
              disabled={!postDraft.trim()}
            >
              Publier
            </button>
          </div>
          </>}
        </div>
      )}

      <div className={`search-panel social-share-panel ${socialView === "profile" ? "" : "social-section-hidden"}`}>
        <div className="social-profile-guidance">
          <span>Profil</span>
          <strong>Ton identité joueur</strong>
          <p>
            Choisis ton pseudo, ajoute une photo ou une bio, puis décide qui peut voir ton profil.
          </p>
        </div>

        <div>
          <h2 className="panel-title">Visibilité du profil</h2>
          <div className="option-value">
            Choisis si ton profil peut être trouvé et partagé par tes amis.
          </div>
        </div>

        <ProfileVisibilityChoices value={socialProfile.visibility} onChange={handleVisibilityChange}/><p className="option-value">Amis uniquement : seuls tes amis confirmés voient le profil. Ton identifiant et ton nom restent visibles dans les invitations que tu envoies.</p>

        <div className="social-share-row">
          <div className="social-share-link">{shareUrl}</div>
          <button type="button" onClick={handleCopy}>
            Copier
          </button>
          <button type="button" onClick={() => setShowPublicPreview(true)}>
            Aperçu
          </button>
        </div>


      </div>

      <div className={`search-panel social-editor ${socialView === "profile" ? "" : "social-section-hidden"}`}>
        <h2 className="panel-title">Identité publique</h2>
        <ProfilePhotoEditor name={socialProfile.displayName} value={socialProfile.photoURL || ""} onChange={photo => onProfileChange("photoURL", photo)} />
        <div className="social-editor-grid">
          <label>
            Pseudo
            <input
              value={socialProfile.displayName}
              aria-label="Pseudo"
              onChange={(e) => onProfileChange("displayName", e.target.value)}
              placeholder="Le nom visible sur ton profil"
            />
            <span className="field-hint">C'est le nom que les autres voient dans le fil et sur ton profil.</span>
          </label>

          <p className="field-hint">Tes amis peuvent te chercher avec ton pseudo. Ton lien de profil est créé automatiquement à l’activation du profil.{socialProfile.handle ? ` Adresse actuelle : @${socialProfile.handle}.` : ''}</p>

          <FavoritePlatforms
            value={socialProfile.platform}
            onChange={value => onProfileChange("platform", value)}
          />

        </div>

        <label className="social-bio-field">
          Bio
          <textarea
            value={socialProfile.bio}
            onChange={(e) => onProfileChange("bio", e.target.value)}
            rows={3}
            placeholder="Ta petite présentation gaming"
          />
        </label>
      </div>

      <div className={`search-panel social-identity-panel ${socialView === "showcase" ? "" : "social-section-hidden"}`}>
        <div>
          <h2 className="panel-title">Jeux fondateurs</h2>
          <div className="option-value">
            Choisis jusqu’à trois jeux qui comptent pour toi. Un souvenir, un favori ou simplement un jeu que tu adores.
          </div>
        </div>

        <div className="social-identity-selectors">
          {[0, 1, 2].map((slot) => (
            <label key={slot}>
              Jeu {slot + 1}
              <select
                value={identityGameIds[slot] || ""}
                onChange={(event) =>
                  handleIdentityGameChange(slot, event.target.value)
                }
              >
                <option value="">Choisir un jeu</option>
                {alphabeticGames.map((game) => {
                  const gameId = String(game.id);
                  const alreadySelected =
                    identityGameIds.includes(gameId) &&
                    identityGameIds[slot] !== gameId;

                  return (
                    <option
                      key={game.id}
                      value={gameId}
                      disabled={alreadySelected}
                    >
                      {game.name}
                    </option>
                  );
                })}
              </select>
            </label>
          ))}
        </div>

        {identityGames.length ? (
          <div className="social-identity-selection-summary">
            {identityGames.length}/3 jeux selectionnes - affiches dans l'essentiel du profil.
          </div>
        ) : (
          <div className="empty-small">Aucun jeu fondateur choisi.</div>
        )}

        {sanctuaryFounderSuggestions.length > 0 && (
          <div className="social-founder-suggestions">
            <div>
              <strong>Depuis ton Sanctuaire</strong>
              <span>
                Tu peux aussi choisir parmi tes jeux du Sanctuaire.
              </span>
            </div>
            <div>
              {sanctuaryFounderSuggestions.map((game) => (
                <button
                  key={game.id}
                  type="button"
                  onClick={handleUseSanctuaryAsFounders}
                >
                  {game.image ? <img src={game.image} alt={game.name} /> : null}
                  <span>{game.name}</span>
                </button>
              ))}
            </div>
          </div>
        )}
      </div>

      <div className={`search-panel social-photo-panel ${socialView === "showcase" ? "" : "social-section-hidden"}`}>
        <div className="home-section-head">
          <div>
            <h2 className="panel-title">Photos publiques</h2>
            <div className="option-value">
              Ajoute ton setup et ta collection physique au profil public.
            </div>
          </div>
        </div>

        <div className="social-photo-sections">
          {[
            { field: "setupPhotos", title: "Setup" },
            { field: "collectionPhotos", title: "Collection physique" },
          ].map((section) => (
            <div key={section.field} className="social-photo-section">
              <div className="social-photo-section-head">
                <strong>{section.title}</strong>
                <label className="social-photo-add">
                  Ajouter
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(event) =>
                      handlePhotoUpload(section.field, event)
                    }
                  />
                </label>
              </div>

              {(socialProfile[section.field] || []).length ? (
                <div className="social-photo-gallery editable">
                  {socialProfile[section.field].map((photo, index) => (
                    <div key={photo.id || photo.src} className="social-photo-item">
                      <img src={photo.src} alt={section.title} />
                      <button
                        type="button"
                        onClick={() => onRemoveProfilePhoto(section.field, index)}
                      >
                        Retirer
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-small">Aucune photo ajoutée.</div>
              )}
            </div>
          ))}
        </div>
      </div>

      <div className={`search-panel social-friends-panel ${socialView === "friends" ? "" : "social-section-hidden"}`}>
        <div className="home-section-head">
          <div>
            <h2 className="panel-title">Amis</h2>
            <div className="option-value">
              Retrouve un joueur par son pseudo. L’accès à son profil et les invitations dépendent de ses réglages.
            </div>
          </div>
          <span className="social-section-count">{socialFriends.length}</span>
        </div>

        <ProfileSearch onOpen={onOpenFriend} onInvite={onAddFriend} connections={friendInvitations?.connections||[]} canInvite={['public','friends'].includes(socialProfile.visibility)}/>
        <details><summary>Inviter directement avec un identifiant</summary><p className="option-value">Tu connais déjà son identifiant ? Envoie une demande sans passer par la recherche.</p>
        <div className="social-add-friend">
          <input
            value={friendHandle}
            onChange={(e) => setFriendHandle(normalizeHandle(e.target.value))}
            placeholder="identifiant ami"
            aria-label="Identifiant de l’ami à ajouter"
          />
          <button type="button" disabled={friendInvitations?.busy} onClick={handleAddFriend}>
            Inviter
          </button>
        </div>

        </details>
        {!['public','friends'].includes(socialProfile.visibility) && <p className="social-hub-notice">Ton pseudo reste visible dans la recherche, mais ton profil privé ne peut pas être consulté et ne reçoit pas d’invitations. Pour envoyer une invitation, choisis « Public » ou « Amis uniquement » dans « Profil public → Identité et partage ». Tu peux toujours répondre aux demandes reçues.</p>}
        {socialView === "friends" && !sharedProfile && friendInvitations && <FriendRequests {...friendInvitations} onOpen={onOpenFriend} onBlock={onBlockFriend}/>}
        {socialView === "friends" && !sharedProfile && <SocialFriendsDirectory friends={socialFriends} onOpen={onOpenFriend} onRemove={onRemoveFriend} loadProfile={loadFriendProfile} onBlock={onBlockFriend} onCommon={onCommonGames} onReport={onReportSocial} onMute={onMuteFriend} mutedHandles={socialControls?.data?.muted?.map(player=>player.handle)||[]} onInvite={async handle=>{const result=await onAddFriend(handle);setSocialMessage(result.message);}} confirmedHandles={friendInvitations?.connections.filter(item=>item.status==='accepted').map(item=>item.profile.handle)||[]} />}
        {socialView==='friends'&&!sharedProfile&&socialControls&&<SocialContactSettings controls={socialControls}/>}

      </div>

      {socialView === "feed" && socialFeedSource === "friends" && !sharedProfile && <FriendsActivityFeed friends={socialFriends} loadProfile={loadFriendProfile} onOpen={onOpenFriend} mutedHandles={socialControls?.data?.muted?.map(player=>player.handle)||[]} onReport={onReportSocial}/>}
      <div className={`search-panel ${socialView === "feed" && socialFeedSource === "mine" ? "" : "social-section-hidden"}`}>
        <div className="home-section-head">
          <h2 className="panel-title">Mon activité</h2>
          <span className="social-section-count">{socialFeedItems.length}</span>
        </div>
        <div className="social-feed-filters">
          {socialFeedFilters.map((filter) => (
            <button
              key={filter.key}
              type="button"
              className={socialFeedFilter === filter.key ? "active" : ""}
              onClick={() => setSocialFeedFilter(filter.key)}
            >
              <span>{filter.label}</span>
              <strong>{filter.count}</strong>
            </button>
          ))}
        </div>
        <ActivityFeed
          activities={socialFeedItems.slice(0, 8)}
          reactionResource={ownReactions}
          reactionHandle={socialProfile.handle}
          compact
          activityLikes={activityLikes}
          onToggleLike={handleToggleActivityLike}
          activityComments={activityComments}
          onAddComment={handleAddComment}
          onDeletePost={handleDeletePost}
        />
      </div>

    </div>
  );
}

export function HomeTab({
  releaseBanner,
  onEngagementChange,
  onOpenChallenges,
  socialProfile={},
  socialFriends=[],
  onExperiencesChange,
  theme,
  games,
  hardware = [],
  badges,
  level,
  totalXP,
  progress,
  dealPreferences = DEFAULT_APP_OPTIONS,
  setActiveTab,
  onHomeLayoutChange,
  onRediscoverPin,
  detectedSeries,
  onOpenDetail,
  gamingEvents = [],
  socialActivities = [],
  weeklyQuiz,
  weeklyQuizProgress = DEFAULT_WEEKLY_QUIZ_PROGRESS,
  onAnswerWeeklyQuiz,
  checkpointGoalProgress = DEFAULT_CHECKPOINT_GOAL_PROGRESS,
  onClaimCheckpointGoal,
}) {
  const dueCapsuleCount=dueCapsules(dealPreferences.experiences).length;
  const [recommendations, setRecommendations] = useState([]);
  const [suggestionOffset, setSuggestionOffset] = useState(0);
  const [loadingRecommendations, setLoadingRecommendations] = useState(false);
  const homeSeries=useMemo(()=>detectedSeries||getDetectedGameSeries(games),[games,detectedSeries]);

  const profile = getPlayerProfile(games);
  const playerAnalysis = getPlayerAnalysis(games, hardware, badges);
  const quizCompleted = (weeklyQuiz?.questions || []).filter(question => getWeeklyQuizAnswer(weeklyQuizProgress, weeklyQuiz.weekKey, question.id)).length;
  const quizLocked = quizCompleted === (weeklyQuiz?.questions?.length || WEEKLY_QUIZ_SIZE);

  const isFinishedGame = isGameFinishedStatus;
  const isOwnedHardware = (item) =>
    String(item.status || "").toLowerCase().includes("poss");

  const total = games.length;
  const inProgressCount = games.filter((g) => g.status === "en cours").length;
  const wishlistCount = games.filter((g) => g.status === "wishlist").length;

  const dashboardFinished = games.filter(isFinishedGame).length;
  const ownedHardwareCount = hardware.filter(isOwnedHardware).length;
  const ratedGames = games.filter((game) => getGameRating(game) > 0);
  const averageRating =
    ratedGames.length > 0
      ? ratedGames.reduce((sum, game) => sum + getGameRating(game), 0) / ratedGames.length
      : 0;

  const inProgressGames = games
    .filter((g) => g.status === "en cours")
    .slice(0, 3);

  const backlogCandidates = games
    .filter((game) => game.status === "collection" || game.status === "wishlist")
    .filter((game) => game.progressStatus !== "completed"&&!isStoppedGame(game))
    .sort((a, b) => {
      const ratingGap = getGameRating(b) - getGameRating(a);
      if (ratingGap !== 0) return ratingGap;
      return String(b.released || "").localeCompare(String(a.released || ""));
    });

  const nextPlayCandidate = inProgressGames[0] || backlogCandidates[0] || null;
  const availableLevelActions = [
    ...games.filter((game) => game.status !== "wishlist" && getGameExperience(game) !== "tried" && !(game.review?.trim().length > 20)).map((game) => ({
      id: `review-${game.id}`, title: `Ton avis sur ${game.name}`, detail: "Écris un avis de plus de 20 caractères sur ce que tu as joué.", xp: 20, game,
    })),
    ...games.filter((game) => game.status !== "wishlist" && (!game.playtimeRange || game.playtimeRange === "none")).map((game) => ({
      id: `time-${game.id}`, title: `Ton temps sur ${game.name}`, detail: "Ouvre « Ma partie → Ta session de jeu » pour renseigner le temps passé.", xp: null, game,
    })),
    ...hardware.filter((item) => isOwnedHardware(item) && !(Number(item.rating) > 0)).map((item) => ({
      id: `hardware-${item.id}`, title: `Noter ${item.name}`, detail: "Ouvre sa fiche dans Matériel pour donner ton avis.", xp: 20, tab: "hardware",
    })),
  ];
  const actionGroups = ["review-", "hardware-", "time-"].map((prefix) => availableLevelActions.filter((action) => action.id.startsWith(prefix)));
  const levelActions = Array.from({length: Math.max(0, ...actionGroups.map((group) => group.length))}, (_, index) => actionGroups.map((group) => group[index]).filter(Boolean)).flat();
  const proposedActions = levelActions.length ? Array.from({length: Math.min(3, levelActions.length)}, (_, index) => levelActions[(suggestionOffset + index) % levelActions.length]) : [];
  const homeGreeting =
    inProgressGames.length > 0
      ? "En ce moment"
      : total > 0
      ? "Ta collection"
      : "Bienvenue sur Checkpoint";
  const homeHeroCopy = nextPlayCandidate
    ? (nextPlayCandidate.status === "en cours" ? "Ta partie en cours, à retrouver quand tu veux." : "Un jeu de ta collection à découvrir.")
    : total > 0
      ? "Retrouve tes jeux, tes notes et tes envies dans la bibliothèque."
      : "Commence par ajouter un jeu que tu aimes.";
  const homeHeroTags = [
    inProgressCount ? `${inProgressCount} jeu${inProgressCount > 1 ? "x" : ""} en cours` : `${total} jeu${total > 1 ? "x" : ""} dans ta collection`,
  ];

  const dashboardAction = nextPlayCandidate
    ? {
        label: nextPlayCandidate.status === "en cours" ? "Reprendre" : "Voir la fiche",
        title: nextPlayCandidate.status === "en cours" ? "Continuer à jouer" : "À découvrir",
        detail: `${nextPlayCandidate.name} · ${formatRating10(getGameRating(nextPlayCandidate), "pas encore noté")}`,
        game: nextPlayCandidate,
      }
    : {
        label: "Ajouter",
        title: "Ton premier jeu",
        detail: "Cherche un titre pour l’ajouter à ta bibliothèque.",
        tab: "search",
      };

  const hardwareByType = hardware.reduce((acc, item) => {
    if (!isOwnedHardware(item)) return acc;
    acc[item.type] = (acc[item.type] || 0) + 1;
    return acc;
  }, {});

  const dashboardStats = [
    { label: "Jeux", value: total, detail: `${dashboardFinished} terminés`, icon: Library },
    { label: "En cours", value: inProgressCount, detail: `${wishlistCount} en wishlist`, icon: Play },
    { label: "Note moyenne", value: averageRating ? formatRating10(averageRating, "-") : "—", detail: `${ratedGames.length} notes`, icon: StarIcon },
    { label: "Matériel", value: ownedHardwareCount, detail: `${hardwareByType.console || 0} plateformes`, icon: Joystick },
  ];
  const sessionQueue = [
    ...inProgressGames,
    ...backlogCandidates.filter(
      (candidate) => !inProgressGames.some((game) => game.id === candidate.id)
    ),
  ].slice(0, 3);
  const libraryPreviewGames = sessionQueue.filter(game => game.id !== nextPlayCandidate?.id);
  const homeQuickActions = [
    { label: "Ajouter", tab: "search", icon: BookmarkPlus },
    { label: "Promos", tab: "deals", icon: Sparkles },
    { label: "Top 5", tab: "top5", icon: Trophy },
  ];
  const favoriteGamesCount = games.filter((game) => game.favorite).length;
  const homeLayout=normalizeHomeLayout(dealPreferences.homeLayout);
  const visibleRecommendations=recommendations.filter(game=>!homeLayout.dismissedGames.includes(String(game.id)));
  const primaryRecommendation = visibleRecommendations[0] || null;
  const secondaryRecommendations = visibleRecommendations.slice(1, 4);
  const dismissRecommendation=game=>onHomeLayoutChange?.({...homeLayout,dismissedGames:[String(game.id),...homeLayout.dismissedGames]});


  const homeActivityItems = socialActivities.slice(0, 4);
  const featuredActivity = homeActivityItems[0] || null;
  const secondaryActivities = homeActivityItems.slice(1, 4);
  const homePulseStats = [
    {
      label: "Activités",
      value: socialActivities.length,
    },
    {
      label: "Notes",
      value: socialActivities.filter((activity) =>
        normalizeIdentityText(activity.type || "").includes("note")
      ).length,
    },
    {
      label: "Badges",
      value: socialActivities.filter((activity) => activity.source === "badges").length,
    },
  ];

  const badgeStats = calculateBadgeStats(games, level, hardware);

  const nextBadge = badges
    .filter((b) => !b.unlocked)
    .map((b) => ({
      ...b,
      progressData: getBadgeProgress(b, badgeStats),
    }))
    .sort((a, b) => {
      const aPercent = a.progressData
        ? a.progressData.current / a.progressData.target
        : 0;

      const bPercent = b.progressData
        ? b.progressData.current / b.progressData.target
        : 0;

      return bPercent - aPercent;
    })[0];

  const unratedGames = games.filter(
    (game) => game.status !== "wishlist" && getGameRating(game) <= 0
  );

  const unratedOwnedHardware = hardware.filter((item) => {
    if (!isOwnedHardware(item)) return false;

    const mainRating = Number(item.rating || 0);
    const detailedRatings = Object.values(item.ratings || {}).map((value) =>
      Number(value || 0)
    );

    return mainRating <= 0 && !detailedRatings.some((value) => value > 0);
  });

  const claimedGoalIds = checkpointGoalProgress.claimed || {};
  const ratedHardwareCount = Math.max(0, ownedHardwareCount - unratedOwnedHardware.length);
  const makeCheckpointGoal = (goal) => {
    const current = Math.min(Number(goal.current || 0), Number(goal.target || 1));
    const target = Math.max(Number(goal.target || 1), 1);
    const progress = Math.min((current / target) * 100, 100);
    const claimed = Boolean(claimedGoalIds[goal.id]);

    return {
      ...goal,
      current,
      target,
      progress,
      claimed,
      claimable: progress >= 100 && Number(goal.xp || 0) > 0 && !claimed,
      reward: goal.xp ? `+${goal.xp} XP` : goal.reward,
    };
  };

  const buildMilestoneGoal = ({
    baseId,
    title,
    detail,
    actionLabel,
    tab,
    current,
    targets,
    xpByTarget,
  }) => {
    const target =
      targets.find((value) => !claimedGoalIds[`${baseId}-${value}`]) ||
      targets[targets.length - 1];

    return makeCheckpointGoal({
      id: `${baseId}-${target}`,
      title,
      detail: typeof detail === "function" ? detail(target) : detail,
      actionLabel,
      tab,
      current,
      target,
      xp: xpByTarget?.[target] || Math.round(target * 4),
    });
  };

  const measurableGoals = [
    makeCheckpointGoal({
      id: `quiz-${weeklyQuiz?.weekKey || "free"}`,
      title: "Checkpoint quiz",
      detail: `Termine les ${weeklyQuiz?.questions?.length || WEEKLY_QUIZ_SIZE} questions de la semaine à ton rythme.`,
      actionLabel: quizLocked ? "Valider" : "Repondre",
      tab: "home",
      current: quizCompleted,
      target: weeklyQuiz?.questions?.length || WEEKLY_QUIZ_SIZE,
      xp: 60,
    }),
    buildMilestoneGoal({
      baseId: "quiz-streak",
      title: "Serie quiz",
      detail: (target) => `Atteindre une serie de ${target} bonnes reponses.`,
      actionLabel: "Quiz",
      tab: "home",
      current: Math.max(
        weeklyQuizProgress.streak || 0,
        weeklyQuizProgress.bestStreak || 0
      ),
      targets: [3, 7, 14, 30],
      xpByTarget: { 3: 90, 7: 160, 14: 260, 30: 500 },
    }),
    buildMilestoneGoal({
      baseId: "rated-games",
      title: "Bibliotheque fiable",
      detail: (target) => `Atteindre ${target} jeux notes pour rendre les tops plus precis.`,
      actionLabel: "Bibliotheque",
      tab: "library",
      current: ratedGames.length,
      targets: [25, 50, 100, 200, 400],
      xpByTarget: { 25: 120, 50: 220, 100: 420, 200: 800, 400: 1400 },
    }),
    buildMilestoneGoal({
      baseId: "rated-hardware",
      title: "Materiel calibre",
      detail: (target) => `Noter ${target} materiels possedes pour fiabiliser le classement.`,
      actionLabel: "Materiel",
      tab: "hardware",
      current: ratedHardwareCount,
      targets: [10, 25, 50, 100],
      xpByTarget: { 10: 100, 25: 220, 50: 420, 100: 760 },
    }),
    buildMilestoneGoal({
      baseId: "finished-games",
      title: "Archives de joueur",
      detail: (target) => `Valider ${target} jeux termines dans ta collection.`,
      actionLabel: "Bibliotheque",
      tab: "library",
      current: dashboardFinished,
      targets: [25, 50, 100, 250, 500],
      xpByTarget: { 25: 150, 50: 280, 100: 520, 250: 1100, 500: 1900 },
    }),
  ].filter((goal) => !goal.claimed);

  const shortcutGoals = [
    inProgressGames[0] && {
      id: "resume-game",
      title: "Reprendre une partie",
      detail: inProgressGames[0].name,
      actionLabel: "Ouvrir",
      game: inProgressGames[0],
      progress: Math.min((dashboardFinished / Math.max(total, 1)) * 100, 100),
      reward: "Raccourci",
    },
    unratedGames.length > 0 && {
      id: "rate-games",
      title: "Notes a completer",
      detail: `${unratedGames.length} jeux sans note dans la collection.`,
      actionLabel: "Bibliotheque",
      tab: "library",
      progress: Math.min((ratedGames.length / Math.max(total, 1)) * 100, 100),
      reward: "Action utile",
    },
    nextBadge?.progressData && {
      id: "badge",
      title: "Badge a portee",
      detail: `${nextBadge.name} - ${nextBadge.progressData.current}/${nextBadge.progressData.target}`,
      actionLabel: "Badges",
      tab: "profile",
      progress: Math.min(
        (nextBadge.progressData.current / Math.max(nextBadge.progressData.target, 1)) * 100,
        100
      ),
      reward: nextBadge.rarity,
    },
  ].filter(Boolean);

  const checkpointGoals = [
    ...measurableGoals.sort((a, b) => Number(b.claimable) - Number(a.claimable) || b.progress - a.progress),
    ...shortcutGoals,
  ].slice(0, 4);

  const claimedGoals = Object.values(claimedGoalIds);
  const claimedGoalsXP = checkpointGoalProgress.totalXP || 0;
  const unlockedBadgeCount = badges.filter((badge) => badge.unlocked).length;
  const badgeCompletionPercent = badges.length
    ? Math.round((unlockedBadgeCount / badges.length) * 100)
    : 0;
  const nextRank = RANKS.find((rank) => rank.min > level);
  const progressionMilestones = [
    { level: 10, label: "Theme", reward: "Harry Potter" },
    { level: 15, label: "Theme", reward: "Star Wars" },
    { level: 20, label: "Theme", reward: "Marvel (MCU)" },
    { level: 25, label: "Theme", reward: "Matrix" },
    { level: 30, label: "Theme", reward: "Batman" },
    { level: 40, label: "Profil", reward: "Badge mis en avant" },
    { level: 60, label: "Theme", reward: "One Piece" },
    { level: 70, label: "Theme", reward: "Fallout" },
    { level: 80, label: "Theme", reward: "The Legend of Zelda" },
    { level: 90, label: "Theme", reward: "Pokémon" },
    { level: 100, label: "Statut", reward: "Supreme" },
  ];
  const unlockedMilestones = progressionMilestones.filter((milestone) => level >= milestone.level);
  const nextMilestone = progressionMilestones.find((milestone) => level < milestone.level);
  const currentMilestone = unlockedMilestones[unlockedMilestones.length - 1] || progressionMilestones[0];
  const nextMilestonePercent = nextMilestone
    ? Math.min(100, Math.round((level / Math.max(nextMilestone.level, 1)) * 100))
    : 100;
  const nextRankPercent = nextRank
    ? Math.min(100, Math.round((level / Math.max(nextRank.min, 1)) * 100))
    : 100;
  const weeklyMomentum = Math.min(
    100,
    Math.round(
      (quizLocked ? 24 : 0) +
        Math.min((weeklyQuizProgress.streak || 0) * 8, 32) +
        Math.min(checkpointGoals.filter((goal) => goal.claimable).length * 18, 36) +
        Math.min((ratedGames.length / Math.max(total, 1)) * 8, 8)
    )
  );
  const progressionCards = [
    {
      label: nextMilestone ? "Prochaine recompense" : "Palier max",
      value: nextMilestone ? `Niv. ${nextMilestone.level}` : "Complet",
      detail: nextMilestone ? nextMilestone.reward : currentMilestone.reward,
      progress: nextMilestonePercent,
    },
    {
      label: "Rythme actuel",
      value: `${weeklyMomentum}%`,
      detail: quizLocked ? "Quiz valide" : "Quiz disponible",
      progress: weeklyMomentum,
    },
    {
      label: "Badges",
      value: `${unlockedBadgeCount}/${badges.length || 0}`,
      detail: nextBadge ? `Prochain : ${nextBadge.name}` : "Collection complete",
      progress: badgeCompletionPercent,
    },
    {
      label: "Prochain rang",
      value: nextRank ? nextRank.title : "Supreme",
      detail: nextRank ? `Niveau ${nextRank.min}` : "Rang maximum",
      progress: nextRankPercent,
    },
  ];

  const upcomingEvents = getUpcomingEvents(gamingEvents);
  const nextEvent = upcomingEvents[0];
  const homeAgendaEvents = upcomingEvents.slice(0, 3);
  const liveAgendaCount = upcomingEvents.filter(
    (event) => getEventState(event) === "live"
  ).length;
  const homeActionCards = [
    nextPlayCandidate && {
      id: "resume",
      kicker: nextPlayCandidate.status === "en cours" ? "Partie active" : "A lancer",
      title: nextPlayCandidate.name,
      detail:
        nextPlayCandidate.status === "en cours"
          ? "Reprendre sans repasser par la bibliotheque."
          : "Une bonne candidate pour la prochaine session.",
      action: "Ouvrir",
      onClick: () => onOpenDetail(nextPlayCandidate),
    },
    unratedGames.length > 0 && {
      id: "ratings",
      kicker: "Bibliotheque",
      title: `${unratedGames.length} jeux sans note`,
      detail: "Quelques notes en plus rendent les tops et recommandations plus fiables.",
      action: "Noter",
      onClick: () => setActiveTab("library"),
    },
    !quizLocked && {
      id: "quiz",
      kicker: "XP rapide",
      title: "Quiz disponible",
      detail: `${(weeklyQuiz?.questions?.length || WEEKLY_QUIZ_SIZE) - quizCompleted} questions cette semaine, +${WEEKLY_QUIZ_XP.correct} XP par bonne réponse.`,
      action: "Jouer",
      onClick: () =>
        document
          .querySelector(".weekly-quiz-card")
          ?.scrollIntoView({ behavior: "smooth", block: "center" }),
    },
    nextBadge?.progressData && {
      id: "badge",
      kicker: "Badge proche",
      title: nextBadge.name,
      detail: `${nextBadge.progressData.current}/${nextBadge.progressData.target} avant le prochain palier.`,
      action: "Voir",
      onClick: () => setActiveTab("profile"),
    },
    nextEvent && {
      id: "event",
      kicker: "A suivre",
      title: nextEvent.name,
      detail: formatEventDate(nextEvent.date),
      action: "Live",
      onClick: () => setActiveTab("live"),
    },
  ]
    .filter(Boolean)
    .slice(0, 3);
  const featuredCheckpointGoal = checkpointGoals[0] || null;
  const secondaryCheckpointGoals = checkpointGoals.slice(1, 3);
  const rewardTrackCards = progressionCards.slice(0, 3);
  const handleCheckpointGoalAction = (goal) => {
    if (!goal) return;

    if (goal.claimable) {
      onClaimCheckpointGoal?.(goal);
      return;
    }

    if (goal.game) {
      onOpenDetail(goal.game);
      return;
    }

    if (goal.tab === "home") {
      document
        .querySelector(".weekly-quiz-card")
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }

    if (goal.tab) {
      setActiveTab(goal.tab);
    }
  };

  useEffect(() => {
    const fetchRecommendations = async () => {
      const likedGames = games.filter(
        (game) => game.status !== 'wishlist' && (game.favorite || getGameRating(game) >= 8)
      );

      const sourceGames = likedGames.length > 0 ? likedGames : games.filter(game=>game.status!=='wishlist');

      if (sourceGames.length === 0) {
        setRecommendations([]);
        return;
      }

      try {
        setLoadingRecommendations(true);

        const genreScores = {};
        const platformScores = {};

        sourceGames.forEach((game) => {
          (game.genreNames || []).forEach((genre) => {
            genreScores[genre] = (genreScores[genre] || 0) + 1;
          });

          (game.platformNames || []).forEach((platform) => {
            platformScores[platform] = (platformScores[platform] || 0) + 1;
          });
        });

        const favoriteGenres = Object.entries(genreScores)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([genre]) => genre);

        const favoritePlatforms = Object.entries(platformScores)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 3)
          .map(([platform]) => platform);

        const genreQuery = favoriteGenres
          .map((genre) => genre.toLowerCase().replaceAll(" ", "-"))
          .join(",");

        const data = await rawgApiRequest(
          "/games",
          {
            genres: genreQuery,
            ordering: "-rating",
            page_size: "40",
          }
        );

        const ownedNames = games.map((game) => game.name.toLowerCase());

        const scored = (data.results || [])
          .filter((rawgGame) => {
            const name = rawgGame.name.toLowerCase();

            return !ownedNames.some(
              (owned) =>
                owned === name ||
                owned.includes(name) ||
                name.includes(owned)
            );
          })
          .map((rawgGame) => {
            let score = 0;
            const reasons = [];

            const rawgGenres = rawgGame.genres?.map((g) => g.name) || [];
            const rawgPlatforms =
              rawgGame.platforms?.map((p) => p.platform.name) || [];

            score += (rawgGame.rating || 0) * 25;
            score += Math.min(rawgGame.ratings_count || 0, 8000) / 120;

            favoriteGenres.forEach((genre) => {
              if (rawgGenres.includes(genre)) {
                score += 40;
                reasons.push(genre);
              }
            });

            favoritePlatforms.forEach((platform) => {
              if (rawgPlatforms.includes(platform)) {
                score += 25;
                reasons.push(platform);
              }
            });

            const year = Number(rawgGame.released?.split("-")[0]);
            if (year >= 2015) score += 8;
            if (year >= 2020) score += 10;

            if ((rawgGame.rating || 0) < 3.5) score -= 30;
            if ((rawgGame.ratings_count || 0) < 50) score -= 20;

            return {
              ...rawgGame,
              recommendationScore: score,
              recommendationReasons: [...new Set(reasons)].slice(0, 2),
              recommendationExplanation: recommendationReason(rawgGame, games),
            };
          })
          .sort((a, b) => b.recommendationScore - a.recommendationScore);

        setRecommendations(scored.slice(0, 20));
      } catch (e) {
        if (isAbortError(e)) return;
        console.warn("Erreur recommandations ignorée :", e);
      } finally {
        setLoadingRecommendations(false);
      }
    };

    fetchRecommendations();
  }, [games]);

  const levelSection = <XPCard
        totalXP={totalXP}
        level={level}
        title={getRankTitle(level)}
        progress={progress}
      />;
  const nextLevelSection = (<section className="search-panel home-next-level">
        <div className="home-section-head">
          <div><h2 className="panel-title">{level >= 100 ? "Ton parcours" : "Ton prochain niveau"}</h2><p className="option-value">{level >= 100 ? "Niveau maximum atteint. Ton profil continue d’évoluer avec tes découvertes." : `Encore ${Math.max(0, progress.xpToNext - progress.currentXP)} XP pour le niveau ${level + 1}.`}</p></div>
          <button type="button" className="profile-toggle-btn" disabled={levelActions.length <= 3} onClick={() => setSuggestionOffset((offset) => (offset + 3) % levelActions.length)}>D’autres idées</button>
        </div>
        <div className="home-level-actions">
          {proposedActions.map((action) => <button type="button" className="home-level-action" key={action.id} onClick={() => action.game ? onOpenDetail(action.game) : setActiveTab(action.tab)}><strong>{action.title}</strong><span>{action.detail}</span><em>{action.xp ? `+${action.xp} XP` : "XP selon la durée"}</em></button>)}
          {!proposedActions.length && <p>Tout est à jour ici. Tu peux découvrir un autre jeu ou tenter le quiz.</p>}
        </div>
        <p className="option-value">Ton profil se dessine avec tes jeux et ton matériel. L’XP dépend des informations enregistrées : les modifier plusieurs fois ne cumule pas les récompenses.</p>
      </section>);
  const sessionSection = (<div className="home-hero">
        <div>
          <div className="home-kicker">{isCheckpointTheme(theme) ? "Ta prochaine session" : homeGreeting}</div>
          <h2>{nextPlayCandidate?.name || (total > 0 ? "Tes jeux, tout simplement" : "On commence ?")}</h2>
          <p>{homeHeroCopy}</p>
          <div className="home-analysis-tags">
            {homeHeroTags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
        </div>

        {nextPlayCandidate?.image && <img className="home-current-cover" src={nextPlayCandidate.image} alt="" />}
        <button type="button" className="save-review-btn home-resume-action" onClick={() => dashboardAction.game ? onOpenDetail(dashboardAction.game) : setActiveTab(dashboardAction.tab)}>{dashboardAction.label}</button>
      </div>);
  const collectionSection = (<section className="home-collection-overview home-overview-card" aria-labelledby="home-overview-title">
    <div className="home-overview-heading"><h2 id="home-overview-title">Ta collection en bref</h2><Library size={20} aria-hidden="true"/></div>
    <dl className="home-overview-stats">
      {dashboardStats.map(stat=><div key={stat.label} className="home-overview-stat">
        <dt><stat.icon size={15} aria-hidden="true"/>{stat.label}</dt>
        <dd>{stat.value}<small>{stat.detail}</small></dd>
      </div>)}
    </dl>
    <div className="home-overview-actions">{homeQuickActions.map(action=><button key={action.tab} type="button" onClick={()=>setActiveTab(action.tab)}><action.icon size={16} aria-hidden="true"/><span>{action.label}</span></button>)}</div>
  </section>);

  return (
    <HomeLayout value={dealPreferences.homeLayout} onChange={onHomeLayoutChange}>
      {levelSection}
      <HomeBlock id="rendezvous" notice="Vote de la semaine"><RendezvousHub key={auth.currentUser?.uid||'local'} games={games} value={dealPreferences.engagement} onChange={onEngagementChange} onOpenGame={onOpenDetail} onChallenges={onOpenChallenges}/></HomeBlock>
      <FirstSteps games={games} onSearch={() => setActiveTab("search")} onOpenGame={onOpenDetail} onHardware={() => setActiveTab("hardware")}/>
      {releaseBanner}
      {games.some(game=>game.status!=='wishlist') && <><HomeBlock id="collection">{collectionSection}</HomeBlock><HomeBlock id="nextLevel">{nextLevelSection}</HomeBlock><HomeBlock id="session">{sessionSection}</HomeBlock><HomeBlock id="picker"><SessionPicker games={games} onOpen={onOpenDetail}/></HomeBlock></>}
      {games.some(game=>isGameFinishedStatus(game))&&<HomeBlock id="rediscover"><Rediscover games={games} ratingGetter={getGameRating} onOpen={onOpenDetail} onPin={onRediscoverPin}/></HomeBlock>}
      {games.filter(game=>game.status!=='wishlist').length>=2&&<HomeBlock id="duel"><ArenaShortcut onOpen={()=>setActiveTab("arena")}/></HomeBlock>}
      {games.some(game=>game.status!=='wishlist')&&<HomeBlock id="monthly"><MonthlyRecap games={games}/></HomeBlock>}
      <HomeBlock id="experiences" notice={dueCapsuleCount?`${dueCapsuleCount} capsule${dueCapsuleCount>1?'s':''} à ouvrir`:null}><CheckpointExperiences games={games} profile={socialProfile} friends={socialFriends} lists={dealPreferences.personalLists} level={level} value={dealPreferences.experiences} onChange={onExperiencesChange} loadProfile={loadCheckpointSocialProfile} onOpenGame={onOpenDetail}/></HomeBlock>
      <HomeBlock id="quiz"><WeeklyQuiz quiz={weeklyQuiz} progress={weeklyQuizProgress} onAnswer={onAnswerWeeklyQuiz}/></HomeBlock>


      {libraryPreviewGames.length > 0 && <HomeBlock id="library"><section className="home-library-preview home-library-card" aria-labelledby="home-library-preview-title">
        <div className="home-library-heading">
          <div><p className="home-section-eyebrow">Ta bibliothèque</p><h2 id="home-library-preview-title">À retrouver</h2></div>
          <button type="button" className="home-library-open" onClick={()=>setActiveTab("library")}>Tout voir<ArrowLeft className="home-forward-arrow" size={16} aria-hidden="true"/></button>
        </div>
        <div className="home-library-list">{libraryPreviewGames.map(game=><button key={game.id} type="button" className="home-library-game" onClick={()=>onOpenDetail(game)}>
          {game.image?<img src={game.image} alt="" loading="lazy"/>:<span className="home-library-placeholder" aria-hidden="true"><Gamepad2 size={24}/></span>}
          <span className="home-library-game-copy"><strong>{game.name}</strong><span className="home-library-game-meta"><span className={`home-library-status${game.status === "en cours" ? " is-playing" : ""}`}>{game.status === "en cours" ? "En cours" : "À découvrir"}</span><span aria-hidden="true">·</span><span>{formatRating10(getGameRating(game), "Pas encore noté")}</span></span></span>
          <ArrowLeft className="home-forward-arrow" size={16} aria-hidden="true"/>
        </button>)}</div>
      </section></HomeBlock>}


      <HomeBlock id="deals"><HomeDealsPreview
        dealPreferences={dealPreferences}
        games={games}
        onOpenDeals={() => setActiveTab("deals")}
      /></HomeBlock>

      {homeActionCards.length > 0 && (
        <HomeBlock id="actions"><div className="home-card home-command-center">
          <div className="home-section-head">
            <div>
              <div className="home-card-title">A faire maintenant</div>
              <p className="home-section-subtitle">
                Trois raccourcis utiles, pas une liste de missions qui traine.
              </p>
            </div>

            <div className="home-command-score">
              <strong>{weeklyMomentum}%</strong>
              <span>rythme</span>
            </div>
          </div>

          <div className="home-command-grid">
            {homeActionCards.map((card) => (
              <button
                key={card.id}
                type="button"
                className="home-command-card"
                onClick={card.onClick}
              >
                <span>{card.kicker}</span>
                <strong>{card.title}</strong>
                <small>{card.detail}</small>
                <em>{card.action}</em>
              </button>
            ))}
          </div>
        </div></HomeBlock>
      )}

      {socialActivities.length>0&&<HomeBlock id="activity"><div className="home-card home-activity-card home-pulse-panel">
        <div className="home-section-head">
          <div>
            <div className="home-card-title">Activité récente</div>
            <p className="home-section-subtitle">
              Les derniers ajouts, notes et moments importants.
            </p>
          </div>

          <button type="button" onClick={() => setActiveTab("social")}>
            Voir le social
          </button>
        </div>

        <div className="home-pulse-stats">
          {homePulseStats.map((stat) => (
            <div key={stat.label} className="home-pulse-stat">
              <strong>{stat.value}</strong>
              <span>{stat.label}</span>
            </div>
          ))}
        </div>

        {featuredActivity ? (
          <div className="home-pulse-layout">
            <button
              type="button"
              className="home-pulse-featured"
              onClick={() => setActiveTab("social")}
            >
              {featuredActivity.image ? (
                <img src={hardwarePhotoUrl(featuredActivity.image)} alt={featuredActivity.title} loading="lazy" />
              ) : (
                <div className={`social-feed-placeholder source-${featuredActivity.source || "generic"}`}>
                  {featuredActivity.type === "Badge" ? "B" : "C"}
                </div>
              )}

              <div>
                <span>{featuredActivity.type}</span>
                <strong>{featuredActivity.text}</strong>
                <small>
                  {spoilerSummary(featuredActivity.detail)} · {formatActivityTime(featuredActivity.date)}
                </small>
              </div>
            </button>

            <div className="home-pulse-list">
              {secondaryActivities.map((activity) => (
                <button
                  key={activity.id}
                  type="button"
                  className="home-pulse-row"
                  onClick={() => setActiveTab("social")}
                >
                  {activity.image ? (
                    <img src={hardwarePhotoUrl(activity.image)} alt={activity.title} loading="lazy" />
                  ) : (
                    <div className={`social-feed-placeholder source-${activity.source || "generic"}`}>
                      {activity.type === "Badge" ? "B" : "C"}
                    </div>
                  )}
                  <div>
                    <span>{activity.type}</span>
                    <strong>{activity.text}</strong>
                    <small>{formatActivityTime(activity.date)}</small>
                  </div>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <EmptyState
            title="Pulse en attente"
            subtitle="Ajoute des jeux, des notes ou du matériel pour alimenter ton activité."
          />
        )}
      </div></HomeBlock>}


      {games.some(game=>game.status!=='wishlist')&&<HomeBlock id="radar"><div className="search-panel home-recommendations-panel home-radar-panel">
        <div className="home-section-head home-radar-head">
          <div>
            <div className="home-card-title">Des idées pour toi</div>
            <p className="home-section-subtitle">
              Des jeux en lien avec tes goûts. Chaque suggestion explique pourquoi elle apparaît.
            </p>
          </div>

          <button type="button" onClick={() => setActiveTab("search")}>
            Explorer
          </button>
        </div>

        {homeLayout.dismissedGames.length>0&&onHomeLayoutChange&&<button type="button" className="home-dismiss-action" onClick={()=>onHomeLayoutChange({...homeLayout,dismissedGames:[]})}>Rétablir les suggestions masquées ({homeLayout.dismissedGames.length})</button>}
        {loadingRecommendations ? (
          <div className="home-radar-loading">
            <span />
            Recherche de jeux à découvrir…
          </div>
        ) : !primaryRecommendation ? (
          <div className="home-radar-empty">
            <strong>Pas de suggestion pour l’instant</strong>
            <span>{homeLayout.dismissedGames.length?'Tu as écarté les suggestions disponibles. Tu peux les rétablir avec le bouton ci-dessus ou revenir plus tard pour de nouvelles découvertes.':'Ajoute un jeu à ta collection, puis indique ceux que tu apprécies. Les suggestions expliqueront leur lien avec tes jeux.'}</span>
          </div>
        ) : (
          <div className="home-radar-layout">
            <div className="home-radar-featured"><button
              type="button"
              className="home-radar-hero"
              onClick={() => setActiveTab("search")}
            >
              {primaryRecommendation.background_image ? (
                <img src={primaryRecommendation.background_image} alt={primaryRecommendation.name} />
              ) : (
                <div className="home-game-placeholder">CP</div>
              )}

              <div className="home-radar-hero-overlay">
                <span className="home-recommendation-kicker">À découvrir</span>
                <strong>{primaryRecommendation.name}</strong>
                <small>
                  {primaryRecommendation.recommendationExplanation}
                </small>
                <em>
                  {primaryRecommendation.released?.split("-")[0] || "Année inconnue"} ·{" "}
                  {formatRating10(primaryRecommendation.rating || 0, "Non noté")}
                </em>
              </div>
            </button>{onHomeLayoutChange&&<button type="button" className="home-dismiss-action" onClick={()=>dismissRecommendation(primaryRecommendation)}>Ça ne m’intéresse pas · {primaryRecommendation.name}</button>}</div>
            <div className="home-radar-side-list">
              {secondaryRecommendations.map((game, index) => (
                <div key={game.id} className="home-radar-mini-choice"><button
                  type="button"
                  className="home-radar-mini-card"
                  onClick={() => setActiveTab("search")}
                >
                  {game.background_image ? (
                    <img src={game.background_image} alt={game.name} />
                  ) : (
                    <div className="home-game-placeholder">CP</div>
                  )}
                  <div>
                    <span>Autre idée</span>
                    <strong>{game.name}</strong>
                    <small className="recommendation-explanation">{game.recommendationExplanation}</small>
                    <small>{formatRating10(game.rating || 0, "Non noté")}</small>
                  </div>
                </button>{onHomeLayoutChange&&<button type="button" className="home-dismiss-action" onClick={()=>dismissRecommendation(game)}>Ça ne m’intéresse pas · {game.name}</button>}</div>
              ))}

              <button
                type="button"
                className="home-radar-action-card"
                onClick={() => setActiveTab("deals")}
              >
                <span>Opportunités</span>
                <strong>Voir les promos liées</strong>
              </button>
            </div>
          </div>
        )}
      </div></HomeBlock>}
      <HomeBlock id="agenda"><div className="search-panel live-event-card home-agenda-card">
        <div className="home-section-head home-agenda-head">
          <div>
            <div className="home-card-title">Agenda Checkpoint</div>
            <p className="home-section-subtitle">
              Les lives et rendez-vous gaming à garder dans le viseur.
            </p>
          </div>

          <button type="button" onClick={() => setActiveTab("live")}>
            Voir le live
          </button>
        </div>

        {nextEvent ? (
          <div className="home-agenda-layout">
            <button
              type="button"
              className={`home-agenda-featured ${getEventState(nextEvent)}`}
              onClick={() => setActiveTab("live")}
            >
              <span className="home-agenda-status">
                {getEventState(nextEvent) === "live" ? "Live maintenant" : "Prochain signal"}
              </span>
              <strong>{nextEvent.name}</strong>
              <small>{formatEventDate(nextEvent.date)}</small>
              {nextEvent.description && <p>{nextEvent.description}</p>}
            </button>

            <div className="home-agenda-side">
              <div className="home-agenda-meter">
                <span>Surveillance</span>
                <strong>{homeAgendaEvents.length}</strong>
                <small>
                  {liveAgendaCount ? `${liveAgendaCount} live actif` : "Aucun live en cours"}
                </small>
              </div>

              {homeAgendaEvents.slice(1).map((event) => (
                <button
                  key={event.id}
                  type="button"
                  className="home-agenda-row"
                  onClick={() => setActiveTab("live")}
                >
                  <span>{event.type || "Événement"}</span>
                  <strong>{event.name}</strong>
                  <small>{formatEventDate(event.date)}</small>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="home-agenda-empty">
            <strong>Aucun événement prévu</strong>
            <span>Les prochains lives seront ajoutés ici.</span>
          </div>
        )}
      </div></HomeBlock>

      <HomeBlock id="rewards"><div className="home-card home-reward-panel">
        <div className="home-section-head home-reward-head">
          <div>
            <div className="home-card-title">Récompenses à portée</div>
            <p className="home-section-subtitle">
              Les prochains gains utiles, sans transformer l'accueil en liste de tâches.
            </p>
          </div>

          <div className="home-reward-xp">
            <strong>{claimedGoalsXP}</strong>
            <span>XP bonus</span>
          </div>
        </div>

        <div className="home-reward-track">
          {rewardTrackCards.map((card) => (
            <div key={card.label} className="home-reward-track-card">
              <span>{card.label}</span>
              <strong>{card.value}</strong>
              <small>{card.detail}</small>
              <div className="home-reward-meter">
                <div style={{ width: `${Math.max(4, Math.min(card.progress, 100))}%` }} />
              </div>
            </div>
          ))}
        </div>

        {featuredCheckpointGoal ? (
          <div className="home-reward-layout">
            <button
              type="button"
              className={`home-reward-featured ${featuredCheckpointGoal.claimable ? "claimable" : ""}`}
              onClick={() => handleCheckpointGoalAction(featuredCheckpointGoal)}
            >
              <span>{featuredCheckpointGoal.claimable ? "Prêt à réclamer" : "Mission prioritaire"}</span>
              <strong>{featuredCheckpointGoal.title}</strong>
              <small>{featuredCheckpointGoal.detail}</small>
              <div className="home-reward-progress-line">
                <div style={{ width: `${Math.max(4, Math.min(featuredCheckpointGoal.progress, 100))}%` }} />
              </div>
              <em>{featuredCheckpointGoal.claimable ? featuredCheckpointGoal.reward : featuredCheckpointGoal.actionLabel}</em>
            </button>

            <div className="home-reward-mini-list">
              {secondaryCheckpointGoals.map((goal) => (
                <button
                  key={goal.id}
                  type="button"
                  className={`home-reward-mini ${goal.claimable ? "claimable" : ""}`}
                  onClick={() => handleCheckpointGoalAction(goal)}
                >
                  <div>
                    <span>{goal.reward}</span>
                    <strong>{goal.title}</strong>
                  </div>
                  <small>{Math.round(goal.progress)}%</small>
                </button>
              ))}
            </div>
          </div>
        ) : (
          <div className="home-reward-empty">
            <strong>Tout est propre</strong>
            <span>Continue à jouer, noter et compléter ton profil pour ouvrir de nouveaux gains.</span>
          </div>
        )}
      </div></HomeBlock>
    </HomeLayout>
  );
}

function LiveTab({
  gamingEvents, setMiniPlayerLive, setMiniPlayerCollapsed }) {

  const events = getUpcomingEvents(gamingEvents);
  const liveEvents = events.filter((event) => getEventState(event) === "live");
  const upcomingEvents = events.filter((event) => getEventState(event) === "upcoming");

  const renderEventCard = (event) => {
    const state = getEventState(event);

    return (
      <div key={event.id} className={`live-card ${state}`}>
        <div className="live-card-top">
          <div>
            <div className="live-type">{event.type || "Événement"}</div>
            <div className="live-title">{event.name}</div>
          </div>

          <span className={`live-status ${state}`}>
            {state === "live" ? "LIVE" : "À venir"}
          </span>
        </div>

        <div className="event-date">📅 {formatEventDate(event.date)}</div>

        {event.description && (
          <div className="live-description">{event.description}</div>
        )}

        <button
          type="button"
          className="save-review-btn live-open-btn"
          onClick={() => {
            setMiniPlayerLive(event);
            setMiniPlayerCollapsed(false);
          }}
          disabled={!event.youtubeId || event.youtubeId === "VIDEO_ID_ICI"}
        >
          {event.youtubeId && event.youtubeId !== "VIDEO_ID_ICI"
            ? "Ouvrir le live"
            : "Lien bientôt disponible"}
        </button>
      </div>
    );
  };

  return (
    <div className="live-page">
      <div className="section-header">
        <h2>Lives & événements</h2>
        <span className="section-count">{events.length}</span>
      </div>

      {liveEvents.length > 0 && (
        <div className="live-section">
          <h3 className="live-section-title">🔴 En direct maintenant</h3>
          <div className="live-list">
            {liveEvents.map(renderEventCard)}
          </div>
        </div>
      )}

      <div className="live-section">
        <h3 className="live-section-title">📅 À venir</h3>

        {upcomingEvents.length > 0 ? (
          <div className="live-list">
            {upcomingEvents.map(renderEventCard)}
          </div>
        ) : (
          <EmptyState
            title="Aucun événement à venir"
            subtitle="Les prochains lives gaming apparaîtront ici."
          />
        )}
      </div>
    </div>
  );
}

function AddEventForm({ editingEvent, onCancelEdit }) {
  const [name, setName] = useState("");
  const [date, setDate] = useState("");
  const [duration, setDuration] = useState(2);
  const [youtubeId, setYoutubeId] = useState("");
  const [type, setType] = useState("");
  const [description, setDescription] = useState("");

  useEffect(() => {
    if (editingEvent) {
      setName(editingEvent.name || "");
      setDate(editingEvent.date || "");
      setDuration(editingEvent.durationHours || 2);
      setYoutubeId(editingEvent.youtubeId || "");
      setType(editingEvent.type || "");
      setDescription(editingEvent.description || "");
    }
  }, [editingEvent]);

  const resetForm = () => {
    setName("");
    setDate("");
    setDuration(2);
    setYoutubeId("");
    setType("");
    setDescription("");
  };

  const handleSubmit = async () => {
    if (!name || !date) {
      alert("Nom et date obligatoires");
      return;
    }

    const eventData = {
      name,
      date,
      durationHours: Number(duration),
      youtubeId,
      type,
      description,
    };

    try {
      if (editingEvent?.firebaseId) {
        await updateDoc(doc(db, "events", editingEvent.firebaseId), eventData);
        alert("Événement modifié ✅");
        onCancelEdit();
      } else {
        await addDoc(collection(db, "events"), eventData);
        alert("Événement ajouté ✅");
      }

      resetForm();
    } catch (e) {
      console.error(e);
      alert("Erreur lors de l’enregistrement");
    }
  };

  return (
    <div className="search-panel">
      <h2 className="panel-title">
        {editingEvent ? "Modifier l’événement" : "Ajouter un événement"}
      </h2>

      <div className="input-container">
        <input
          placeholder="Nom"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>

      <div className="input-container">
        <input
          type="datetime-local"
          value={date}
          onChange={(e) => setDate(e.target.value)}
        />
      </div>

      <div className="input-container">
        <input
          type="number"
          placeholder="Durée (heures)"
          value={duration}
          onChange={(e) => setDuration(e.target.value)}
        />
      </div>

      <div className="input-container">
        <input
          placeholder="YouTube ID"
          value={youtubeId}
          onChange={(e) => setYoutubeId(e.target.value)}
        />
      </div>

      <div className="input-container">
        <input
          placeholder="Type (Showcase, Awards...)"
          value={type}
          onChange={(e) => setType(e.target.value)}
        />
      </div>

      <div className="input-container">
        <input
          placeholder="Description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </div>

      <button onClick={handleSubmit}>
        {editingEvent ? "Mettre à jour" : "Ajouter l’événement"}
      </button>

      {editingEvent && (
        <button
          type="button"
          className="filter-toggle-btn"
          style={{ marginTop: "10px" }}
          onClick={() => {
            resetForm();
            onCancelEdit();
          }}
        >
          Annuler la modification
        </button>
      )}
    </div>
  );
}

function ConsoleXPSection({ games, compact = false }) {
  const consoleStats = getConsoleXPStats(games);
  const visibleConsoleStats = compact ? consoleStats.slice(0, 4) : consoleStats;

  if (!consoleStats.length) {
    return (
      <div className={`search-panel console-xp-panel ${compact ? "compact" : ""}`}>
        <h2 className="panel-title">XP plateformes</h2>

        <EmptyState
          title="Aucune donnée console"
          subtitle="Ajoute des plateformes jouées pour débloquer les stats."
        />
      </div>
    );
  }

  return (
    <div className={`search-panel console-xp-panel ${compact ? "compact" : ""}`}>
      <div className="profile-section-header">
        <div>
          <h2 className="panel-title">XP plateformes</h2>
          <div className="option-value">
            Les plateformes où ton profil a le plus de vécu.
          </div>
        </div>
      </div>

      <div className="console-xp-list">
        {visibleConsoleStats.map((item) => {
          const level = Math.max(1, Math.floor(item.xp / 500) + 1);

          let rank = "Débutant";

          if (level >= 20) rank = "Légende";
          else if (level >= 15) rank = "Expert";
          else if (level >= 10) rank = "Confirmé";
          else if (level >= 5) rank = "Passionné";

          return (
            <div key={item.platform} className="console-xp-card">
              <div className="console-xp-top">
                <div>
                  <div className="console-xp-name">
                    {item.platform}
                  </div>

                  <div className="console-xp-rank">
                    {rank}
                  </div>
                </div>

                <div className="console-xp-level">
                  Nv {level}
                </div>
              </div>

              <div className="console-xp-bar">
                <div
                  className="console-xp-fill"
                  style={{
                    width: `${(item.xp % 500) / 5}%`,
                  }}
                />
              </div>

              <div className="console-xp-bottom">
                <span>{item.games} jeux</span>
                <strong>{item.xp} XP</strong>
              </div>

              {!compact && (
                <div className="console-badges">
                  {item.games >= 5 && (
                    <span className="console-badge">
                      📚 Collectionneur
                    </span>
                  )}

                  {item.games >= 15 && (
                    <span className="console-badge">
                      🏆 Vétéran
                    </span>
                  )}

                  {item.xp >= 3000 && (
                    <span className="console-badge legendary">
                      👑 Maître
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export function ProfileTab({
  badges,
  level,
  totalXP,
  rawTotalXP,
  progress,
  games,
  hardware = [],
  socialProfile = {},
  featuredBadgeId,
  onSelectFeaturedBadge,
  checkpointTrialProgress = DEFAULT_CHECKPOINT_TRIAL_PROGRESS,
  pendingCheckpointLevel = null,
  onOpenTrial,
  onOpenPublicProfile,
}) {
  const unlockedBadges = badges.filter((b) => b.unlocked);
  const lockedBadges = badges.filter((b) => !b.unlocked && !b.hiddenWhenLocked);
  const nextBadges = lockedBadges.slice(0, 3);
  const [showAllBadges, setShowAllBadges] = useState(false);

  const nextRank = RANKS.find((rank) => rank.min > level);
  const stats = getAdvancedStats(games);
  const playerAnalysis = getPlayerAnalysis(games, hardware);
  const badgeStats = calculateBadgeStats(games, level, hardware);
  const featuredBadge = getFeaturedBadgeFromSelection(badges, featuredBadgeId);
  const founderIds = (socialProfile.identityGameIds || []).slice(0, 3).map(String);
  const founderGames = founderIds.map(id => games.find(game => String(game.id) === id)).filter(Boolean);
  const founderSignature = getIdentityPlayerTitle(founderGames);
  const completionRate = playerAnalysis.facts.collectionCount
    ? Math.round(playerAnalysis.facts.finishedCount / playerAnalysis.facts.collectionCount * 100)
    : 0;
  const profileShowcase = getProfileShowcase(games, hardware);
  const gamingTimeline = getGamingTimeline(games, hardware, socialProfile);
  const hasProfileShowcase =
    profileShowcase.topScores.length > 0 ||
    profileShowcase.specializedTops.length > 0 ||
    profileShowcase.gotyHighlights.length > 0 ||
    profileShowcase.hardwareHighlights.length > 0;
  const currentRank =
    [...RANKS].reverse().find((rank) => level >= rank.min) || RANKS[0];
  const checkpointRoadmap = getCheckpointRoadmap(
    level,
    rawTotalXP ?? totalXP,
    checkpointTrialProgress
  );
  const completedCheckpoints = checkpointRoadmap.filter((item) => item.isCompleted);
  const trialRewardBadges = getCheckpointRewardBadges(checkpointTrialProgress);
  const trialHistory = Array.isArray(checkpointTrialProgress.history)
    ? checkpointTrialProgress.history.slice(0, 5)
    : [];
  const pendingCheckpoint =
    checkpointRoadmap.find((item) => item.level === pendingCheckpointLevel) ||
    checkpointRoadmap.find((item) => item.isPending);
  const nextCheckpoint =
    pendingCheckpoint || checkpointRoadmap.find((item) => !item.isCompleted);
  const completedCount = games.filter(isGameFinishedStatus).length;
  const ratedGames = games
    .map((game) => ({
      ...game,
      checkpointRating: getGameRating(game),
    }))
    .filter((game) => game.checkpointRating > 0)
    .sort((a, b) => b.checkpointRating - a.checkpointRating);
  const favoriteGames = games.filter((game) => game.favorite);
  const identityGame = favoriteGames[0] || ratedGames[0];
  const currentGame = games.find(game => game.status === "en cours");
  const currentHardware = hardware.filter((item) => {
    const status = normalizeIdentityText(item.status || "");
    return status.includes("poss") || status.includes("reparer");
  });
  const ratedHardware = currentHardware
    .map((item) => ({
      ...item,
      average: getHardwareAverageRating(item),
    }))
    .filter((item) => item.average > 0)
    .sort((a, b) => b.average - a.average);
  const profileLivingCards = [
    {
      label: favoriteGames.length || !identityGame ? "Un de tes coups de cœur" : "Ta meilleure note",
      value: identityGame?.name || "Pas encore de favori",
      detail: identityGame
        ? formatRating10(getGameRating(identityGame), "Pas encore noté")
        : "Le cœur sur une fiche permet de garder un jeu en favori.",
    },
    {
      label: "Ta partie en cours",
      value: currentGame?.name || "Aucune pour le moment",
      detail: currentGame ? "À retrouver quand tu en as envie." : "Pas besoin de jouer tous les jours.",
    },
    {
      label: "Dans ton setup",
      value: ratedHardware[0]?.name || currentHardware[0]?.name || "Pas encore renseigné",
      detail: ratedHardware[0]
        ? `${formatRating10(ratedHardware[0].average, "-")} d’après tes notes`
        : `${currentHardware.length} équipement${currentHardware.length > 1 ? "s" : ""} actuellement en ta possession`,
    },
  ];

  const profileMission = nextBadges[0]
    ? {
        label: "Mission active",
        title: nextBadges[0].name,
        detail: nextBadges[0].desc,
      }
    : nextRank
      ? {
          label: "Mission active",
          title: nextRank.title,
          detail: `Atteindre le niveau ${nextRank.min} pour passer au rang suivant.`,
        }
      : {
          label: "Mission active",
          title: "Profil complet",
          detail: "Ton profil a deja atteint les principaux paliers visibles.",
        };
  const activeRankIndex = RANKS.findIndex(
    (rank) => level >= rank.min && level <= rank.max
  );
  const visibleRanks = RANKS.filter((rank, index) => {
    if (activeRankIndex < 0) return index < 4;
    return Math.abs(index - activeRankIndex) <= 2 || rank.title === nextRank?.title;
  }).slice(0, 5);

  return (
    <div className="progression-stack profile-tab">
      <div className="search-panel profile-hero">
        <div className="profile-hero-main">
          {socialProfile.photoURL && <SocialAvatar name={socialProfile.displayName} src={socialProfile.photoURL} className="profile-personal-avatar" zoomable />}
          <div className="profile-kicker">Profil joueur</div>
          <div className="social-profile-name-row">
            <h2 className="profile-title">{socialProfile.displayName || "Ton profil"}</h2>
            <FeaturedBadgePill badge={featuredBadge} />
          </div>
          <div className="profile-subtitle">{`${playerAnalysis.facts.collectionCount} ${playerAnalysis.facts.collectionCount === 1 ? "jeu" : "jeux"} dans ta bibliothèque`}</div>

          <div className="profile-hero-tags">
            <span>Niveau {level}</span>
            <span>{currentRank.title}</span>
            <span>{playerAnalysis.maturity}</span>
          </div>
          <FavoritePlatformTags value={socialProfile.platform} />
        </div>

        <div className="profile-hero-side">
          <div className="profile-badge-count">
            <strong>{unlockedBadges.length}</strong>
            <span>/ {badges.length} badges</span>
          </div>

          <div className="profile-hero-xp">
            <div className="profile-hero-xp-top">
              <span>{totalXP} XP</span>
              <strong>{progress.percent}%</strong>
            </div>
            <div className="xp-bar compact">
              <div className="xp-fill" style={{ width: `${progress.percent}%` }} />
            </div>
            <small>
              {level >= 100
                ? "Rang maximal atteint"
                : `${progress.currentXP} / ${progress.xpToNext} XP avant le niveau suivant`}
            </small>
          </div>
        </div>
      </div>

      {onOpenPublicProfile && <section className="search-panel profile-public-link"><div><strong>Mon profil public</strong><p>Photo, présentation et vitrine visibles par les autres joueurs.</p></div><button type="button" className="experience-secondary" onClick={onOpenPublicProfile}>Modifier mon profil public</button></section>}
      <div className="search-panel profile-living-panel">
        <div className="profile-living-main">
          <span>Ta signature de joueur</span>
          <strong>{founderSignature.title}</strong>
          <p>{founderSignature.subtitle}</p>
          {!founderGames.length && <small>Tu peux les choisir dans Social → Profil public → Ma vitrine.</small>}


        </div>

        <div className="profile-living-side">
          <div className="profile-living-mission">
            <span>{profileMission.label}</span>
            <strong>{profileMission.title}</strong>
            <small>{profileMission.detail}</small>
          </div>

          <div className="profile-living-cards">
            {profileLivingCards.map((card) => (
              <div key={card.label} className="profile-living-card">
                <span>{card.label}</span>
                <strong>{card.value}</strong>
                <small>{card.detail}</small>
              </div>
            ))}
          </div>
        </div>
      </div>

      <ProfileSection title="Tes habitudes de jeu" summary={playerAnalysis.title}>
<section className="search-panel player-notes-panel" aria-label="Tes habitudes de jeu">
        <div className="profile-section-header">

        </div>
        <div className="player-notes-summary">
          <strong>{playerAnalysis.title}</strong>
          <p>{playerAnalysis.subtitle}</p>
        </div>
        <div className="player-analysis-meta">
          <small>{playerAnalysis.maturity}</small>
          {playerAnalysis.vibe && <small>{playerAnalysis.vibe}</small>}
        </div>
        {playerAnalysis.nextSteps.length > 0 && (
          <p className="player-notes-suggestion">{playerAnalysis.nextSteps[0]}</p>
        )}
      </section>

            </ProfileSection>

      <ProfileSection defaultOpen={Boolean(pendingCheckpoint)} title="Salle des Epreuves" summary={`${completedCheckpoints.length} paliers validés · ${pendingCheckpoint ? "Une épreuve disponible" : "Tes prochains paliers"}`}>
<div className="search-panel profile-trials-panel">
        <div className="profile-section-header">
          <div>

            <div className="option-value">
              Les grands rangs se valident par checkpoint, pas seulement par XP.
            </div>
          </div>

          <div className="profile-trials-counter">
            {completedCheckpoints.length}/{CHECKPOINT_LEVELS.length}
          </div>
        </div>

        <div className="profile-trials-status">
          <div>
            <span>Prochain cap</span>
            <strong>
              {nextCheckpoint
                ? `Checkpoint ${nextCheckpoint.level}`
                : "Tous les checkpoints valides"}
            </strong>
            <small>
              {pendingCheckpoint
                ? `${pendingCheckpoint.guardian} attend dans la Salle.`
                : nextCheckpoint
                  ? `${nextCheckpoint.xpRemaining} XP avant l'epreuve.`
                  : "Progression libre jusqu'au prochain chapitre."}
            </small>
          </div>

          {pendingCheckpoint && (
            <button
              type="button"
              className="profile-trials-enter"
              onClick={() => onOpenTrial?.(pendingCheckpoint.level)}
            >
              Entrer
            </button>
          )}
        </div>

        <div className="profile-trials-rewards">
          <div className="profile-trials-rewards-head">
            <div>
              <span>Recompenses exclusives</span>
              <strong>
                {trialRewardBadges.length
                  ? `${trialRewardBadges.length} sceau${trialRewardBadges.length > 1 ? "x" : ""} obtenu${trialRewardBadges.length > 1 ? "s" : ""}`
                  : "Aucun sceau debloque"}
              </strong>
            </div>
            <small>Ces badges viennent uniquement des Epreuves.</small>
          </div>

          <div className="profile-trials-badge-row">
            {checkpointRoadmap.map((checkpoint) => {
              const reward = checkpoint.reward;
              const unlocked = checkpoint.isCompleted && reward;

              return (
                <div
                  key={`reward-${checkpoint.level}`}
                  className={`profile-trial-reward ${unlocked ? "unlocked" : "locked"}`}
                  style={reward?.accent ? { "--trial-reward-accent": reward.accent } : undefined}
                >
                  <span>{reward?.badgeIcon || checkpoint.level}</span>
                  <div>
                    <strong>{reward?.badgeName || `Checkpoint ${checkpoint.level}`}</strong>
                    <small>
                      {unlocked
                        ? checkpoint.rewardRank
                        : checkpoint.isPending
                          ? "Epreuve disponible"
                          : `Niveau ${checkpoint.level}`}
                    </small>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {trialHistory.length > 0 && (
          <div className="profile-trials-history">
            <div className="profile-trials-history-head">
              <span>Historique des Epreuves</span>
              <strong>Dernieres validations</strong>
            </div>

            {trialHistory.map((entry) => (
              <div key={entry.id || `${entry.level}-${entry.completedAt}`} className="profile-trial-history-row">
                <div>
                  <span>Checkpoint {entry.level}</span>
                  <strong>{entry.rewardRank}</strong>
                  <small>{entry.badgeName || entry.title}</small>
                </div>
                <div>
                  <strong>{entry.score}/{entry.totalQuestions}</strong>
                  <small>{formatFullDate(entry.completedAt)}</small>
                </div>
              </div>
            ))}
          </div>
        )}

        <div className="profile-trials-roadmap">
          {checkpointRoadmap.map((checkpoint) => (
            <div
              key={checkpoint.level}
              className={`profile-trial-node ${
                checkpoint.isCompleted
                  ? "completed"
                  : checkpoint.isPending
                    ? "pending"
                    : checkpoint.isReached
                      ? "reached"
                      : "locked"
              }`}
            >
              <div className="profile-trial-head">
                <span>Lvl {checkpoint.level}</span>
                <strong>{checkpoint.title}</strong>
              </div>
              <div className="profile-trial-rank">{checkpoint.rewardRank}</div>
              <div className="profile-trial-meta">
                <span>
                  {checkpoint.isCompleted
                    ? "Valide"
                    : checkpoint.isPending
                      ? "Epreuve disponible"
                      : `${checkpoint.xpRemaining} XP restants`}
                </span>
                {checkpoint.attempts > 0 && <span>{checkpoint.attempts} essai(s)</span>}
                {checkpoint.totalQuestions > 0 && (
                  <span>
                    {checkpoint.passScore}/{checkpoint.totalQuestions} requis
                  </span>
                )}
              </div>
              <div className="profile-trial-progress">
                <div style={{ width: `${checkpoint.segmentProgress}%` }} />
              </div>
            </div>
          ))}
        </div>
      </div>

            </ProfileSection>

      <ProfileSection title="Mes statistiques" summary={`${completedCount} jeux terminés · ${completionRate}% de complétion`}>
<div className="profile-stats-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.totalPlaytime}h</div>
          <div className="stat-label">Temps total</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">
            {stats.avgRating ? stats.avgRating.toFixed(1) : "-"}
          </div>
          <div className="stat-label">Note moyenne</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{completedCount}</div>
          <div className="stat-label">Terminés</div>
        </div>

        <div className="stat-card">
          <div className="stat-value">{completionRate}%</div>
          <div className="stat-label">Complétion</div>
        </div>
      </div>

      <ConsoleXPSection games={games} compact />
      <GenreStatsChart games={games} />

            </ProfileSection>

      {hasProfileShowcase && (
        <ProfileSection title="Mes sélections" summary="Tops, jeux de l’année et matériel préféré"><div className="search-panel profile-showcase-panel">
          <div className="profile-section-header">
            <div>
              <h2 className="panel-title">Vitrine Checkpoint</h2>
              <div className="option-value">
                Tes meilleurs Tops, GOTY et matériels préférés au même endroit.
              </div>
            </div>
          </div>

          <ProfileShowcase showcase={profileShowcase} />
        </div></ProfileSection>
      )}

      <ProfileSection title="Ton histoire gaming" summary={"Consoles, jeux fondateurs et jeux de l’année"}>
<div className="search-panel profile-timeline-panel">
        <div className="profile-section-header">
          <div>

            <div className="option-value">
              Tes consoles, jeux fondateurs, GOTY perso et moments marquants.
            </div>
          </div>
        </div>

        <GamingTimeline timeline={gamingTimeline} />
      </div>

            </ProfileSection>

      <ProfileSection title="Progression" summary={nextRank ? `Prochain rang : ${nextRank.title} au niveau ${nextRank.min}` : "Tes rangs et objectifs"}>
<div className="search-panel profile-growth-panel">
        <div className="profile-section-header">
          <div>

            <div className="option-value">
              Rang actuel, prochain cap et objectifs utiles à court terme.
            </div>
          </div>
        </div>

        <div className="profile-growth-grid">
          {nextBadges[0] ? (
            <div className="next-goal-card profile-next-goal">
              <div className="badge-icon">
                <BadgeVisualIcon badge={nextBadges[0]} />
              </div>
              <div>
                <span>Prochain badge</span>
                <div className="badge-name">{nextBadges[0].name}</div>
                <div className="badge-desc">{nextBadges[0].desc}</div>
              </div>
            </div>
          ) : nextRank ? (
            <div className="next-goal-card profile-next-goal">
              <div className="badge-icon">⬆️</div>
              <div>
                <span>Prochain rang</span>
                <div className="badge-name">{nextRank.title}</div>
                <div className="badge-desc">Atteindre le niveau {nextRank.min}</div>
              </div>
            </div>
          ) : (
            <div className="option-value">Tous les objectifs principaux sont débloqués.</div>
          )}

          <div className="profile-rank-summary">
            <span>Rang actuel</span>
            <strong>{currentRank.title}</strong>
            <small>
              {nextRank
                ? `Prochain cap : ${nextRank.title} au niveau ${nextRank.min}`
                : "Tu es sur le dernier palier de rang."}
            </small>
          </div>
        </div>

        <div className="rank-list compact-ranks">
          {visibleRanks.map((rank) => {
            const unlocked = level >= rank.min;
            const active = level >= rank.min && level <= rank.max;

            return (
              <div
                key={rank.title}
                className={`rank-item ${unlocked ? "unlocked" : "locked"} ${active ? "active" : ""}`}
              >
                <div>
                  <div className="rank-name">{rank.title}</div>
                  <div className="rank-range">
                    {rank.min === rank.max
                      ? `Niveau ${rank.min}`
                      : `Niveaux ${rank.min} à ${rank.max}`}
                  </div>
                </div>

                <div className="rank-state">
                  {active ? "Actuel" : unlocked ? "Débloqué" : "Verrouillé"}
                </div>
              </div>
            );
          })}
        </div>
      </div>



      </ProfileSection>

            <ProfileSection title="Badges" summary={`${unlockedBadges.length} débloqués sur ${badges.length}`}>      <div className="search-panel">
        <div className="profile-section-header">
          <div>

            <div className="option-value">
              {unlockedBadges.length} débloqués / {badges.length}
              {featuredBadge ? ` • Mis en avant : ${featuredBadge.name}` : ""}
            </div>
          </div>

          <button
            type="button"
            className="profile-toggle-btn"
            onClick={() => setShowAllBadges((prev) => !prev)}
          >
            {showAllBadges ? "Réduire" : "Voir tous"}
          </button>
        </div>

        <div className="profile-badges-section">
          {(showAllBadges ? unlockedBadges : unlockedBadges.slice(0, 6)).map((b) => (
            <button
              key={b.id}
              type="button"
              className={`badge-card unlocked ${b.rarity} ${
                featuredBadgeId === b.id ? "featured" : ""
              }${b.platformFamily ? ` platform-${b.platformFamily}` : ""}${
                b.platformKey ? " platform-specific" : " brand-family"
              }${isBadgeNumericSeal(b) ? " badge-numeric-seal" : ""}${
                b.special ? ` badge-special-${b.special}` : ""
              }`}
              onClick={() => onSelectFeaturedBadge?.(b.id)}
            >
              <span className="badge-sheen" aria-hidden="true" />
              <span className="badge-sparkles" aria-hidden="true" />
              <div className="badge-topline">
                <div className="badge-emblem">
                  <span className="badge-icon">
                    <BadgeVisualIcon badge={b} />
                  </span>
                </div>
                <span className="badge-rarity-chip">{getBadgeRarityLabel(b)}</span>
              </div>
              <div className="badge-name">{b.name}</div>
              <div className="badge-desc">{b.desc}</div>
              <div className="badge-status">
                {featuredBadgeId === b.id ? "Badge affiché" : "Choisir ce badge"}
              </div>
            </button>
          ))}

          {(showAllBadges ? lockedBadges : nextBadges).map((b) => {
              const progress = getBadgeProgress(b, badgeStats);

              return (
                <div
                  key={b.id}
                  className={`badge-card locked ${b.rarity}${
                    b.platformFamily ? ` platform-${b.platformFamily}` : ""
                  }${b.platformKey ? " platform-specific" : " brand-family"}${
                    isBadgeNumericSeal(b) ? " badge-numeric-seal" : ""
                  }${b.special ? ` badge-special-${b.special}` : ""
                  }`}
                >
                  <span className="badge-sheen" aria-hidden="true" />
                  <span className="badge-sparkles" aria-hidden="true" />
                  <div className="badge-topline">
                    <div className="badge-emblem">
                      <span className="badge-icon">
                        <BadgeVisualIcon badge={b} />
                      </span>
                    </div>
                    <span className="badge-rarity-chip">{getBadgeRarityLabel(b)}</span>
                  </div>
                  <div className="badge-name">{b.name}</div>
                  <div className="badge-desc">{b.desc}</div>

                  {progress && (
                    <div className="badge-progress">
                      {progress.current} / {progress.target}
                      <div className="badge-progress-bar">
                        <div
                          className="badge-progress-fill"
                          style={{
                            width: `${Math.min(
                              (progress.current / progress.target) * 100,
                              100
                            )}%`,
                          }}
                        />
                      </div>
                    </div>
                  )}

                  <div className="badge-status">
                    {showAllBadges ? "🔒 Verrouillé" : "🎯 Prochain objectif"}
                  </div>
                </div>
              );
            })}
        </div>
      </div>
      </ProfileSection>
    </div>
  );
}

function normalizeGameName(name) {
  return name
    .toLowerCase()
    .replace(/[™®]/g, "")
    .replace(/\b(remastered|remake|definitive edition|complete edition|deluxe edition|game of the year|goty)\b/g, "")
    .replace(/[0-9]+/g, "")
    .replace(/[:–—-].*$/g, "")
    .trim();
}

function normalizeSearchText(value = "") {
  return value
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^\w\s]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function getSteamGameKey(game) {
  return game?.steamAppId ? String(game.steamAppId) : "";
}

function getXboxGameKey(game) {
  return game?.xboxTitleId ? String(game.xboxTitleId) : "";
}

function flattenPlatformHints(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.flatMap(flattenPlatformHints);
  if (typeof value === "string") return [value];
  if (typeof value === "object") {
    return [
      value.name,
      value.slug,
      value.value,
      value.id,
      value.platform?.name,
      value.deviceName,
      value.family,
    ]
      .filter(Boolean)
      .map(String);
  }
  return [String(value)];
}

function inferMicrosoftImportPlatforms(game = {}) {
  const hints = [
    ...flattenPlatformHints(game.playedPlatforms),
    ...flattenPlatformHints(game.platformNames),
    ...flattenPlatformHints(game.platforms),
    ...flattenPlatformHints(game.platform),
    ...flattenPlatformHints(game.devices),
    ...flattenPlatformHints(game.deviceFamilies),
    ...flattenPlatformHints(game.sourcePlatform),
    ...flattenPlatformHints(game.sourceStore),
  ];
  const hintText = normalizeSearchText(hints.join(" "));

  if (/\b(pc|windows|win32|desktop)\b/.test(hintText) || hintText.includes("game pass pc")) {
    return ["PC"];
  }
  if (hintText.includes("series")) return ["Xbox Series X/S"];
  if (hintText.includes("xbox one")) return ["Xbox One"];
  if (hintText.includes("360")) return ["Xbox 360"];
  if (hintText.includes("xbox") && hintText !== "xbox") return ["Xbox"];

  return ["PC"];
}

function mergeMicrosoftImportPlatforms(existingGame = {}, importedPlatforms = []) {
  const existingPlatforms = (existingGame.platformNames || []).filter(Boolean);
  const isOnlyGenericXbox =
    existingPlatforms.length === 0 ||
    (existingPlatforms.length === 1 && normalizeSearchText(existingPlatforms[0]) === "xbox");

  if (isOnlyGenericXbox) return importedPlatforms;

  return Array.from(new Set([...existingPlatforms, ...importedPlatforms]));
}

function mergeMicrosoftPlayedPlatforms(existingGame = {}, importedPlatforms = []) {
  const existingPlatforms = (existingGame.playedPlatforms || []).filter(Boolean);
  const isOnlyGenericXbox =
    existingPlatforms.length === 0 ||
    (existingPlatforms.length === 1 && normalizeSearchText(existingPlatforms[0]) === "xbox");

  return isOnlyGenericXbox ? importedPlatforms : existingPlatforms;
}

function formatSteamPlaytime(minutes = 0) {
  const safeMinutes = Number(minutes) || 0;
  if (safeMinutes < 60) return `${safeMinutes} min`;

  const hours = Math.round((safeMinutes / 60) * 10) / 10;
  return `${hours} h`;
}

function getRawgSlugCandidates(query = "") {
  const base = normalizeSearchText(query)
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
  const withoutArticles = normalizeSearchText(query)
    .replace(/\b(the|a|an)\b/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");

  return Array.from(new Set([base, withoutArticles].filter(Boolean)));
}

function getSearchQueryAliases(query = "") {
  const normalized = normalizeSearchText(query);
  if (!normalized) return [];

  const aliases = [];

  if (/\bgta\b/.test(normalized)) {
    aliases.push(
      "grand theft auto",
      "grand theft auto v",
      "grand theft auto vi",
      "grand theft auto san andreas",
      "grand theft auto iv"
    );
  }

  aliases.push(query.trim());

  return Array.from(new Set(aliases.filter(Boolean)));
}

const KNOWN_SEARCH_FALLBACKS = [
  {
    id: "known-gta-v",
    slug: "grand-theft-auto-v",
    name: "Grand Theft Auto V",
    aliases: ["gta", "gta 5", "gta v", "grand theft auto"],
    playtime: 0,
    released: "2013-09-17",
    tba: false,
    background_image: "",
    rating: 4.5,
    ratings_count: 10000,
    platforms: [
      { platform: { id: 4, name: "PC", slug: "pc" } },
      { platform: { id: 18, name: "PlayStation 4", slug: "playstation4" } },
      { platform: { id: 1, name: "Xbox One", slug: "xbox-one" } },
    ],
    parent_platforms: [
      { platform: { id: 1, name: "PC", slug: "pc" } },
      { platform: { id: 2, name: "PlayStation", slug: "playstation" } },
      { platform: { id: 3, name: "Xbox", slug: "xbox" } },
    ],
    genres: [
      { id: 4, name: "Action", slug: "action" },
      { id: 3, name: "Adventure", slug: "adventure" },
    ],
  },
  {
    id: "known-gta-iv",
    slug: "grand-theft-auto-iv",
    name: "Grand Theft Auto IV",
    aliases: ["gta", "gta 4", "gta iv", "grand theft auto"],
    playtime: 0,
    released: "2008-04-29",
    tba: false,
    background_image: "",
    rating: 4.3,
    ratings_count: 7000,
    platforms: [
      { platform: { id: 4, name: "PC", slug: "pc" } },
      { platform: { id: 16, name: "PlayStation 3", slug: "playstation3" } },
      { platform: { id: 14, name: "Xbox 360", slug: "xbox360" } },
    ],
    parent_platforms: [
      { platform: { id: 1, name: "PC", slug: "pc" } },
      { platform: { id: 2, name: "PlayStation", slug: "playstation" } },
      { platform: { id: 3, name: "Xbox", slug: "xbox" } },
    ],
    genres: [
      { id: 4, name: "Action", slug: "action" },
      { id: 3, name: "Adventure", slug: "adventure" },
    ],
  },
  {
    id: "known-gta-san-andreas",
    slug: "grand-theft-auto-san-andreas",
    name: "Grand Theft Auto: San Andreas",
    aliases: ["gta", "gta san andreas", "grand theft auto"],
    playtime: 0,
    released: "2004-10-26",
    tba: false,
    background_image: "",
    rating: 4.4,
    ratings_count: 8000,
    platforms: [
      { platform: { id: 4, name: "PC", slug: "pc" } },
      { platform: { id: 15, name: "PlayStation 2", slug: "playstation2" } },
      { platform: { id: 80, name: "Xbox", slug: "xbox-old" } },
    ],
    parent_platforms: [
      { platform: { id: 1, name: "PC", slug: "pc" } },
      { platform: { id: 2, name: "PlayStation", slug: "playstation" } },
      { platform: { id: 3, name: "Xbox", slug: "xbox" } },
    ],
    genres: [
      { id: 4, name: "Action", slug: "action" },
      { id: 3, name: "Adventure", slug: "adventure" },
    ],
  },
  {
    id: 989329,
    slug: "ghost-of-yotei",
    name: "Ghost of Yotei",
    playtime: 0,
    released: "2025-10-02",
    tba: false,
    background_image:
      "https://media.rawg.io/media/games/30b/30b195c2321d763f807366967ffad793.jpg",
    rating: 4.18,
    ratings_count: 41,
    platforms: [
      {
        platform: {
          id: 187,
          name: "PlayStation 5",
          slug: "playstation5",
        },
      },
    ],
    parent_platforms: [
      {
        platform: {
          id: 2,
          name: "PlayStation",
          slug: "playstation",
        },
      },
    ],
    genres: [
      { id: 4, name: "Action", slug: "action" },
      { id: 5, name: "RPG", slug: "role-playing-games-rpg" },
    ],
  },
];

function getKnownSearchFallbacks(query = "") {
  const normalizedQuery = normalizeSearchText(query);
  if (!normalizedQuery) return [];

  return KNOWN_SEARCH_FALLBACKS.filter((game) => {
    const normalizedName = normalizeSearchText(game.name);
    const normalizedAliases = (game.aliases || []).map(normalizeSearchText);
    const queryWords = normalizedQuery.split(" ").filter(Boolean);

    return (
      normalizedName.includes(normalizedQuery) ||
      normalizedAliases.some((alias) => alias.includes(normalizedQuery)) ||
      queryWords.every((word) => normalizedName.includes(word))
    );
  });
}

function createFallbackPoster(title, colorA = "#60a5fa", colorB = "#d6b54a") {
  const safeTitle = String(title || "Checkpoint").replace(/[<>&"]/g, "");
  const shortTitle = safeTitle
    .split(/\s+/)
    .slice(0, 3)
    .join(" ");
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="720" height="960" viewBox="0 0 720 960">
      <defs>
        <linearGradient id="bg" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#07111f"/>
          <stop offset="0.52" stop-color="${colorA}"/>
          <stop offset="1" stop-color="${colorB}"/>
        </linearGradient>
        <radialGradient id="glow" cx="50%" cy="38%" r="56%">
          <stop offset="0" stop-color="#ffffff" stop-opacity=".22"/>
          <stop offset="1" stop-color="#000000" stop-opacity="0"/>
        </radialGradient>
      </defs>
      <rect width="720" height="960" fill="url(#bg)"/>
      <rect width="720" height="960" fill="#020813" opacity=".58"/>
      <circle cx="360" cy="315" r="285" fill="url(#glow)"/>
      <path d="M-80 690 C120 590 235 760 430 635 C565 548 635 560 780 452" fill="none" stroke="#fff" stroke-opacity=".16" stroke-width="22"/>
      <path d="M-50 730 C155 645 270 790 455 680 C585 602 650 626 770 538" fill="none" stroke="#fff" stroke-opacity=".08" stroke-width="10"/>
      <circle cx="145" cy="175" r="46" fill="#fff" opacity=".13"/>
      <circle cx="602" cy="155" r="78" fill="#fff" opacity=".07"/>
      <rect x="58" y="720" width="604" height="2" fill="#fff" opacity=".18"/>
      <text x="58" y="650" fill="#ffffff" font-family="Arial Black, Arial, sans-serif" font-size="54" font-weight="900">${shortTitle}</text>
      <text x="58" y="698" fill="#dbeafe" font-family="Arial, sans-serif" font-size="25" font-weight="700" opacity=".78">Sortie a suivre</text>
    </svg>`;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
}



function getGameVisual(game = {}) {
  return (
    game.cover_image ||
    game.image ||
    game.background_image ||
    game.short_screenshots?.[0]?.image ||
    ""
  );
}

function getLibraryFallbackVisual(game = {}) {
  return getGameVisual(game) || createFallbackPoster(game.name || "Jeu", "#1d4ed8", "#22c55e");
}

function hasRealGameVisual(game = {}) {
  const visual = getGameVisual(game);
  return Boolean(visual && !String(visual).startsWith("data:image/svg+xml"));
}





function gameMatchesSearchFilters(game, filters = {}) {
  const { yearFilter, platformFilter, genreFilter } = filters;

  if (yearFilter && !String(game.released || "").startsWith(String(yearFilter))) {
    return false;
  }

  if (platformFilter) {
    const platformIds = (game.platforms || []).map((entry) =>
      String(entry?.platform?.id || "")
    );

    if (!platformIds.includes(String(platformFilter))) {
      return false;
    }
  }

  if (genreFilter) {
    const genreIds = (game.genres || []).map((genre) => String(genre?.id || ""));

    if (!genreIds.includes(String(genreFilter))) {
      return false;
    }
  }

  return true;
}


function getDetectedGameSeries(games) {
  const groups = {};

  games.forEach((game) => {
    if (!isSeriesLibraryEpisode(game)) return;
    const seriesName = detectSeriesName(game);
    if (!seriesName) return;

    if (!groups[seriesName]) groups[seriesName] = [];

    groups[seriesName].push(game);
  });

  return Object.entries(groups)
    .filter(([, items]) => items.length >= 2)
    .map(([name, rawItems]) => {
      const items = dedupeSeriesGames(rawItems, name, isGameFinishedStatus)
        .sort((a,b) => String(a.released || '').localeCompare(String(b.released || '')) || a.name.localeCompare(b.name));
      const finished = items.filter(isGameFinishedStatus).length;

      const total = items.length;

      return {
        name,
        games: items,
        finished,
        total,
        percent: Math.round((finished / total) * 100),
      };
    })
    .filter(series => series.total >= 2);
}

export function GameSeriesTab({ games, onAddGameToLibrary, initialSeriesName }) {
  const [seriesDisplayMode, setSeriesDisplayMode] = useState("compact");
  const [enrichedSeries, setEnrichedSeries] = useState([]);
  const [selectedSeries, setSelectedSeries] = useState(null);
  const [seriesView, setSeriesView] = useState("owned");
  const [seriesSuggestions, setSeriesSuggestions] = useState([]);
  const [suggestionsStatus, setSuggestionsStatus] = useState('idle');
  const [suggestionsComplete, setSuggestionsComplete] = useState(false);
  const suggestionsRequest = useRef(null);
  useEffect(() => () => suggestionsRequest.current?.abort(), []);
  const activeSeries = selectedSeries ? enrichedSeries.find(series => series.name === selectedSeries.name) || selectedSeries : null;
  const [seriesScrollY, setSeriesScrollY] = useState(0);

  const openSeries = (series) => {
    suggestionsRequest.current?.abort();
    setSeriesScrollY(window.scrollY);
    setSelectedSeries(series);
    setSeriesView("owned");
    setSeriesSuggestions([]);
  };

  useEffect(()=>{if(initialSeriesName){const match=getDetectedGameSeries(games).find(item=>item.name===initialSeriesName);if(match)openSeries(match);}},[initialSeriesName]);

  const closeSeries = () => {
    suggestionsRequest.current?.abort();
    setSelectedSeries(null);
    setSeriesSuggestions([]);
    setSeriesView("owned");

    requestAnimationFrame(() => {
      window.scrollTo(0, seriesScrollY);
    });
  };

  const loadSeriesSuggestions = async (seriesName) => {
    suggestionsRequest.current?.abort();
    const controller = new AbortController();
    suggestionsRequest.current = controller;
    setSeriesView("suggestions");
    setSuggestionsStatus("loading");
    setSeriesSuggestions([]);
    try {
      const catalog = await loadSeriesCatalog(seriesName, {signal:controller.signal});
      if (controller.signal.aborted) return;
      setSeriesSuggestions(catalog.games);
      setSuggestionsComplete(catalog.complete);
      setSuggestionsStatus("ready");
    } catch (error) {
      if (!controller.signal.aborted) setSuggestionsStatus("error");
    }
  };

  useEffect(() => {
    const baseSeries = getDetectedGameSeries(games);

    setEnrichedSeries(
      baseSeries
        .map((series) => {
          const ratedGames = series.games.filter((game) => getGameRating(game) > 0);
          const averageRating = ratedGames.length
            ? ratedGames.reduce((sum, game) => sum + getGameRating(game), 0) / ratedGames.length
            : 0;
          const unfinished = series.games.filter((game) => !isGameFinishedStatus(game));
          const wishlist = series.games.filter((game) => game.status === "wishlist");
          const latestGame = [...series.games].sort((a, b) =>
            String(b.released || "").localeCompare(String(a.released || ""))
          )[0];

          return {
            ...series,
            apiTotal: series.total,
            missing: 0,
            averageRating,
            unfinished,
            wishlist,
            nextGame: unfinished[0] || series.games[0],
            cover: latestGame?.image || series.games.find((game) => game.image)?.image || "",
            mood:
              series.percent >= 100
                ? "À jour dans ta biblio"
                : series.percent >= 70
                ? "Presque au bout"
                : series.percent >= 35
                ? "Belle lancée"
                : "À explorer",
          };
        })
        .sort((a, b) => b.percent - a.percent || b.total - a.total || a.name.localeCompare(b.name))
    );
  }, [games]);

  const seriesStats = useMemo(() => {
    const totalSeries = enrichedSeries.length;
    const completedSeries = enrichedSeries.filter((series) => series.percent >= 100).length;
    const nearComplete = enrichedSeries.filter(
      (series) => series.percent >= 60 && series.percent < 100
    ).length;
    const totalGamesInSeries = enrichedSeries.reduce((sum, series) => sum + series.total, 0);
    const nextSeriesToContinue =
      enrichedSeries.find((series) => series.percent < 100 && series.unfinished.length > 0) ||
      enrichedSeries.find((series) => series.percent < 100) ||
      null;

    return {
      totalSeries,
      completedSeries,
      nearComplete,
      totalGamesInSeries,
      nextSeriesToContinue,
    };
  }, [enrichedSeries]);

  if (selectedSeries) {
    return (
      <div className="progression-stack series-detail-page">
        <div className="search-panel">
          <button
            type="button"
            className="back-btn series-back-btn"
            onClick={() => {
              if (seriesView === "suggestions") {
                suggestionsRequest.current?.abort();
                setSeriesView("owned");
                return;
              }

              closeSeries();
            }}
          >
            <ArrowLeft size={18} />
            Retour
          </button>

          <h2 className="panel-title">{activeSeries.name}</h2>
          <SeriesCatalogProgress name={activeSeries.name} games={activeSeries.games} isFinished={isGameFinishedStatus}/>

          <div className="series-detail-summary">
            <div>
              <strong>{activeSeries.finished}/{activeSeries.total}</strong>
              <span>terminés biblio</span>
            </div>
            <div>
              <strong>{activeSeries.unfinished.length}</strong>
              <span>à suivre</span>
            </div>
            <div>
              <strong>{formatRating10(activeSeries.averageRating, "—")}</strong>
              <span>note moy.</span>
            </div>
          </div>

          {seriesView === "owned" && (
            <>
              <h3 className="hardware-group-title">Ma série</h3>

              <div className="series-list">
                {activeSeries.games.map((game) => (
                  <div key={game.id} className="series-game-row">
                    <span>{game.name}</span>
                    <span>
                      {isGameFinishedStatus(game)
                        ? "✅ Terminé"
                        : game.status === "collection"
                        ? "Collection"
                        : game.status}
                    </span>
                  </div>
                ))}
              </div>

              <button
                type="button"
                className="save-review-btn series-suggestions-btn"
                onClick={() => loadSeriesSuggestions(activeSeries.name)}
              >
                Dans la même série...
              </button>
            </>
          )}

          {seriesView === "suggestions" && (
            <>
              <h3 className="hardware-group-title">Les épisodes de la série</h3>
              <p className="series-scope-note">Les épisodes sortis, sans compilations ni rééditions.</p>
              {suggestionsStatus === 'loading' && <p role="status">Chargement des épisodes…</p>}
              {suggestionsStatus === 'error' && <div role="status">
                <p>Le catalogue est indisponible pour le moment.</p>
                <button type="button" className="back-btn" onClick={() => loadSeriesSuggestions(activeSeries.name)}>Réessayer</button>
              </div>}
              {suggestionsStatus === 'ready' && !suggestionsComplete && <p className="series-scope-note">Catalogue partiel : d’autres épisodes peuvent manquer.</p>}
              {suggestionsStatus === 'ready' && !seriesSuggestions.length && <p>Aucun autre épisode confirmé dans le catalogue.</p>}

              <div className="series-list">
                {seriesSuggestions.map((rawgGame) => {
                  const libraryMatch = findSeriesLibraryMatch(rawgGame, activeSeries.games, activeSeries.name, seriesSuggestions);
                  const alreadyInLibrary = Boolean(libraryMatch);

                  return (
                    <div key={rawgGame.id} className="series-game-row">
                      <span>{seriesCatalogTitle(rawgGame, seriesSuggestions)}</span>

                      {alreadyInLibrary ? (
                        <span className="already-added">{isGameFinishedStatus(libraryMatch) ? "Terminé" : libraryMatch.status === "wishlist" ? "Dans ta wishlist" : "Dans ta bibliothèque"}</span>
                      ) : (
                        <div className="quick-status-actions">
                          <button
                            type="button"
                            className="quick-status-btn"
                            title="Wishlist"
                            onClick={() =>
                              onAddGameToLibrary({
                                discoveryMetadata: getDiscoveryMetadata(rawgGame),
                                name: rawgGame.name,
                                rating: 0,
                                favorite: false,
                                sanctuary: false,
                                image: rawgGame.background_image || "",
                                status: "wishlist",
                                progressStatus: "not_started",
                                playtimeRange: "none",
                                released: rawgGame.released || "",
                                platformNames:
                                  rawgGame.platforms?.map((p) => p.platform.name) || [],
                                genreNames: rawgGame.genres?.map((g) => g.name) || [],
                                playtime: rawgGame.playtime || null,
                                difficulty: getSuggestedDifficulty(rawgGame),
                                review: "",
                                ratingGraphics: 0,
                                ratingGameplay: 0,
                                ratingStory: 0,
                                ratingSound: 0,
                                ostRating: 0,
                                ratingLongevity: 0,
                                rawgId: rawgGame.id,
                              })
                            }
                          >
                            <BookmarkPlus size={18} />
                          </button>

                          <button
                            type="button"
                            className="quick-status-btn"
                            title="Collection"
                            onClick={() =>
                              onAddGameToLibrary({
                                discoveryMetadata: getDiscoveryMetadata(rawgGame),
                                name: rawgGame.name,
                                rating: 0,
                                favorite: false,
                                sanctuary: false,
                                image: rawgGame.background_image || "",
                                status: "collection",
                                progressStatus: "not_started",
                                playtimeRange: "none",
                                released: rawgGame.released || "",
                                platformNames:
                                  rawgGame.platforms?.map((p) => p.platform.name) || [],
                                genreNames: rawgGame.genres?.map((g) => g.name) || [],
                                playtime: rawgGame.playtime || null,
                                difficulty: getSuggestedDifficulty(rawgGame),
                                review: "",
                                ratingGraphics: 0,
                                ratingGameplay: 0,
                                ratingStory: 0,
                                ratingSound: 0,
                                ostRating: 0,
                                ratingLongevity: 0,
                                rawgId: rawgGame.id,
                              })
                            }
                          >
                            <Library size={18} />
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  return (
  <div className="progression-stack series-page">
    <div className="series-hub-hero">
      <div>
        <span className="home-kicker">Sagas et licences</span>
        <h2>Tes séries de jeux</h2>
        <p>
          Retrouve les licences que tu suis, celles presque terminées et les
          prochains épisodes à ajouter.
        </p>
      </div>

      <div className="series-hero-orb">
        <Puzzle size={28} />
      </div>
    </div>

    <div className="series-stats-grid">
      <div>
        <strong>{seriesStats.totalSeries}</strong>
        <span>séries</span>
      </div>
      <div>
        <strong>{seriesStats.completedSeries}</strong>
        <span>biblio à jour</span>
      </div>
      <div>
        <strong>{seriesStats.nearComplete}</strong>
        <span>à finir</span>
      </div>
      <div>
        <strong>{seriesStats.totalGamesInSeries}</strong>
        <span>jeux liés</span>
      </div>
    </div>

    {seriesStats.nextSeriesToContinue && (
      <button
        type="button"
        className="series-next-card"
        onClick={() => openSeries(seriesStats.nextSeriesToContinue)}
      >
        <span className="home-kicker">À reprendre</span>
        <strong>{seriesStats.nextSeriesToContinue.name}</strong>
        <small>
          {seriesStats.nextSeriesToContinue.nextGame?.name || "Un épisode t’attend"} ·{" "}
          {seriesStats.nextSeriesToContinue.finished}/{seriesStats.nextSeriesToContinue.total} terminés dans ta biblio
        </small>
      </button>
    )}
    <div className="search-panel series-main-panel">
      <div className="section-header">
        <h2>Séries détectées</h2>
        <span className="section-count">{enrichedSeries.length}</span>
      </div>

      <p className="series-scope-note">
        Chaque épisode compte une fois dans ta biblio, même avec un remaster. Les autres jeux sortis sont indiqués à part, hors DLC et compilations. Le catalogue peut être incomplet.
      </p>

      <div className="library-view-toggle">
        <button
          className={seriesDisplayMode === "compact" ? "active" : ""}
          onClick={() => setSeriesDisplayMode("compact")}
          type="button"
        >
          Compact
        </button>

        <button
          className={seriesDisplayMode === "detailed" ? "active" : ""}
          onClick={() => setSeriesDisplayMode("detailed")}
          type="button"
        >
          Détaillé
        </button>
      </div>

      {enrichedSeries.length === 0 ? (
        <EmptyState
          title="Aucune série détectée"
          subtitle="Ajoute plusieurs jeux d’une même licence pour voir la complétion."
        />
      ) : seriesDisplayMode === "compact" ? (
        <div className="series-grid compact-series-grid">
          {enrichedSeries.map((series) => (
            <button
              key={series.name}
              type="button"
              className="series-card compact-series-card"
              onClick={() => openSeries(series)}
            >
              <div
                className="series-showcase-cover"
                style={{ "--series-cover": `url(${series.cover || ""})` }}
              >
                {!series.cover && <Puzzle size={24} />}
              </div>

              <div className="series-card-top">
                <div>
                  <span className="series-card-mood">{series.mood}</span>
                  <strong>{series.name}</strong>
                </div>
                <span
                  className="series-ring"
                  style={{ "--series-percent": `${series.percent}%` }}
                >
                  <em>{series.finished}/{series.total}</em>
                </span>
              </div>

              <div className="progress-bar">
                <div className="progress-fill" style={{ width: `${series.percent}%` }} />
              </div>

              <div className="series-card-meta">
                {series.finished} / {series.total} terminés dans ta biblio
              </div>
              <SeriesCatalogProgress name={series.name} games={series.games} isFinished={isGameFinishedStatus}/>
            </button>
          ))}
        </div>
      ) : (
        <div className="series-list">
          {enrichedSeries.map((series) => (
            <div
              key={series.name}
              className="series-card detailed-series-card"
              onClick={() => openSeries(series)}
            >
              <div className="series-top">
                <div>
                  <div className="series-title">{series.name}</div>
                  <div className="hardware-meta">
                    {series.finished} / {series.total} terminés dans ta biblio
                  </div>
                </div>

                <div className="series-percent">{series.finished}/{series.total}</div>
              </div>

              <div className="xp-bar">
                <div className="xp-fill" style={{ width: `${series.percent}%` }} />
              </div>

              <SeriesCatalogProgress name={series.name} games={series.games} isFinished={isGameFinishedStatus}/>
              <div className="series-games">
                {series.games.map((game) => (
                  <div key={game.id} className="series-game-row">
                    <span>{game.name}</span>
                    <span>
                      {isGameFinishedStatus(game)
                        ? "✅"
                        : "◌"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  </div>
);
}

function HardwareDropdown({
  id,
  label,
  value,
  options,
  onChange,
  openedDropdown,
  setOpenedDropdown,
}) {
  const open = openedDropdown === id;

  const selectedOption =
    options.find((option) => option.id === value) || options[0];

  return (
    <div className={`hardware-dropdown ${open ? "open" : ""}`}>
      <span className="hardware-dropdown-label">{label}</span>

      <div className="hardware-dropdown-wrap">
        <button
          type="button"
          className={`hardware-dropdown-trigger ${open ? "open" : ""}`}
          onClick={() =>
            setOpenedDropdown(open ? null : id)
          }
        >
          {selectedOption?.label}
          <span>⌄</span>
        </button>

        {open && (
          <div className="hardware-dropdown-menu">
            {options.map((option) => (
              <button
                key={option.id}
                type="button"
                className={`hardware-dropdown-option ${
                  option.id === value ? "active" : ""
                }`}
                onClick={() => {
                  onChange(option.id);
                  setOpenedDropdown(null);
                }}
              >
                {option.label}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

const HARDWARE_RATING_FIELDS = {
  console: [
    { key: "catalog", label: "Catalogue", icon: "📚" },
    { key: "controller", label: "Manette", icon: "🕹️" },
    { key: "design", label: "Design", icon: "🎨" },
    { key: "interface", label: "Interface / fonctionnalités", icon: "⚙️" },
    { key: "reliability", label: "Fiabilité", icon: "🔧" },
    { key: "nostalgia", label: "Nostalgie / attachement", icon: "❤️" },
  ],
  controller: [
    { key: "ergonomics", label: "Ergonomie", icon: "✋" },
    { key: "precision", label: "Précision", icon: "🎯" },
    { key: "buttons", label: "Boutons / gâchettes", icon: "🔘" },
    { key: "vibrations", label: "Vibrations / fonctionnalités", icon: "📳" },
    { key: "battery", label: "Autonomie", icon: "🔋" },
    { key: "comfort", label: "Confort longue session", icon: "🏆" },
  ],
  audio: [
    { key: "sound", label: "Qualité sonore", icon: "🎧" },
    { key: "comfort", label: "Confort", icon: "☁️" },
    { key: "microphone", label: "Micro", icon: "🎤" },
    { key: "isolation", label: "Isolation", icon: "🔇" },
    { key: "battery", label: "Autonomie", icon: "🔋" },
    { key: "value", label: "Rapport qualité / prix", icon: "💰" },
  ],
  speaker: [
    { key: "sound", label: "Qualite sonore", icon: "SND" },
    { key: "spatial", label: "Scene sonore / spatialisation", icon: "3D" },
    { key: "bass", label: "Basses / impact", icon: "BASS" },
    { key: "latency", label: "Latence / synchro", icon: "MS" },
    { key: "connectivity", label: "Connectique", icon: "I/O" },
    { key: "setup", label: "Integration setup", icon: "SET" },
    { key: "value", label: "Rapport qualite / prix", icon: "EUR" },
  ],
  vr: [
    { key: "image", label: "Nettete / effet grille", icon: "IMG" },
    { key: "comfort", label: "Confort / poids", icon: "ERG" },
    { key: "tracking", label: "Tracking / manettes", icon: "TRK" },
    { key: "immersion", label: "Immersion / FOV", icon: "FOV" },
    { key: "performance", label: "Fluidite / latence", icon: "HZ" },
    { key: "ecosystem", label: "Catalogue / ecosysteme", icon: "APP" },
    { key: "setup", label: "Installation / autonomie", icon: "SET" },
    { key: "value", label: "Rapport qualite / prix", icon: "EUR" },
  ],
  display: [
    { key: "image", label: "Qualite d'image", icon: "IMG" },
    { key: "hdr", label: "HDR / luminosite", icon: "HDR" },
    { key: "motion", label: "Fluidite", icon: "HZ" },
    { key: "latency", label: "Latence / reactivite", icon: "MS" },
    { key: "features", label: "HDMI 2.1 / VRR / ALLM", icon: "VRR" },
    { key: "comfort", label: "Confort / ergonomie", icon: "ERG" },
  ],
  mouse: [
    { key: "shape", label: "Forme / prise en main", icon: "ERG" },
    { key: "sensor", label: "Capteur / precision", icon: "DPI" },
    { key: "clicks", label: "Clics / molette", icon: "BTN" },
    { key: "weight", label: "Poids / equilibre", icon: "G" },
    { key: "wireless", label: "Connexion / latence", icon: "MS" },
    { key: "battery", label: "Autonomie", icon: "BAT" },
  ],
  keyboard: [
    { key: "switches", label: "Switches / sensation", icon: "SW" },
    { key: "latency", label: "Latence / rapid trigger", icon: "MS" },
    { key: "build", label: "Construction", icon: "BLD" },
    { key: "layout", label: "Layout / ergonomie", icon: "KEY" },
    { key: "software", label: "Logiciel / profils", icon: "APP" },
    { key: "sound", label: "Son / stabilisateurs", icon: "SND" },
  ],
};

function getHardwareRatingFields(type) {
  return HARDWARE_RATING_FIELDS[type] || HARDWARE_RATING_FIELDS.console;
}

function getHardwareTypeIcon(type) {
  return "";
}

function handleHardwareImageError(event) {
  const image = event.currentTarget;
  const holder = image.closest(
    ".hardware-image-zoom-btn, .hardware-image-wrapper, .hardware-card-image-zone"
  );

  image.style.display = "none";
  holder?.classList.add("hardware-image-missing");
}

function getHardwareAverageRating(item) {
  const fields = getHardwareRatingFields(item.type);
  const ratings = item.ratings || {};

  const values = fields
    .map((field) => clampRating(ratings[field.key]))
    .filter((value) => value > 0);

  if (!values.length) return 0;

  const average = values.reduce((sum, value) => sum + value, 0) / values.length;
  return Math.round(average * 10) / 10;
}

function normalizeHardwareRatings(item = {}) {
  const ratings = Object.fromEntries(
    Object.entries(item.ratings || {}).map(([key, value]) => [
      key,
      clampRating(value),
    ])
  );
  const normalizedItem = {
    ...item,
    ratings,
    rating: clampRating(item.rating),
  };

  return {
    ...normalizedItem,
    name: normalizedItem.name || "Materiel sans nom",
    brand: normalizedItem.brand || "",
    type: normalizedItem.type || "other",
    status: normalizedItem.status || "",
    condition: normalizedItem.condition || "",
    sanctuary: Boolean(normalizedItem.sanctuary),
    image: normalizedItem.image || "",
    displaySizes: normalizeArray(normalizedItem.displaySizes).map(String),
    components: normalizeObject(normalizedItem.components),
    rating: normalizedItem.rating || getHardwareAverageRating(normalizedItem),
  };
}

function getHardwareCatalogVersionSizes(item) {
  if (item.displaySizes?.length) return item.displaySizes;

  for (const catalogItem of HARDWARE_CATALOG) {
    for (const variant of catalogItem.variants || []) {
      const version = variant.versions?.find(
        (candidate) => candidate.id === item.versionId
      );

      if (version?.sizes?.length) return version.sizes;
    }
  }

  return [];
}

function isConfigurablePcHardware(item = {}) {
  const text = [
    item.name,
    item.parentName,
    item.variantName,
    item.brand,
    item.category,
    item.versionId,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    item.versionId === "desktop-pc" ||
    item.versionId === "gaming-laptop" ||
    text.includes("pc fixe") ||
    text.includes("pc portable") ||
    text.includes("gaming pc") ||
    text.includes("pc gaming")
  );
}

function HardwareDetailModal({
  item,
  detailRef,
  onClose,
  onDeleteHardware,
  openedDropdown,
  setOpenedDropdown,
  statusOptions,
  conditionOptions,
  displaySizeOptions = [],
  onUpdateHardwareStatus,
  onUpdateHardwareSanctuary,
  onUpdateHardwareCondition,
  onUpdateHardwareAcquiredYear,
  onUpdateHardwareDisplaySize,
  onUpdateHardwareComponent,
  onEditHardwareEdition,
  onAddHardwareEdition,
  onUpdateHardwareRating,
  onUpdateHardwareReview,
  onToggleHardwareFavorite,
  consoleGameStats,
  getHardwareStatusLabel = (status) => status || "Possédé",
}) {
  const [localReview, setLocalReview] = useState(item?.review || "");

  useEffect(() => {
    setLocalReview(item?.review || "");
  }, [item?.id]);

  useDetailReturn(Boolean(item));
  if (!item) return null;

  const fields = getHardwareRatingFields(item.type);
  const averageRating = getHardwareAverageRating(item);
  const catalogDisplaySizes = getHardwareCatalogVersionSizes(item);
  const showPcConfiguration = isConfigurablePcHardware(item);
  const currentDisplaySizeOptions =
    item.type === "display" && catalogDisplaySizes.length
      ? [
          { id: "", label: "Taille non précisée" },
          ...catalogDisplaySizes.map((size) => {
            const option = displaySizeOptions.find((entry) => entry.id === size);
            return option || { id: size, label: `${size} pouces` };
          }),
        ]
      : displaySizeOptions;
  const hardwareTypeLabel = getHardwareTypeLabel(item.type);
  const displaySizeLabel =
    item.type === "display" && item.displaySize
      ? currentDisplaySizeOptions.find((option) => option.id === item.displaySize)?.label ||
        `${item.displaySize} pouces`
      : "";
  const hardwareFacts = [
    { label: "Type", value: hardwareTypeLabel },
    { label: "Marque", value: item.brand || "Marque inconnue" },
    item.variantName && { label: "Gamme", value: item.variantName },
    item.storage && { label: "Stockage", value: item.storage },
    hardwareEditionLabel(item) && {label:"Édition / coloris",value:hardwareEditionLabel(item)},
    displaySizeLabel && { label: "Taille", value: displaySizeLabel },
    { label: "Statut", value: getHardwareStatusLabel(item.status) },
  ].filter(Boolean);
  const highlightedFields = fields.slice(0, 3);
  const ratedFields = fields
    .map((field) => ({
      ...field,
      value: clampRating(item.ratings?.[field.key]),
    }))
    .filter((field) => field.value > 0)
    .sort((a, b) => b.value - a.value);
  const bestRatedField = ratedFields[0];
  const ratingCompletion = fields.length
    ? Math.round((ratedFields.length / fields.length) * 100)
    : 0;

  return (
    <div className="hardware-inline-detail" ref={detailRef}>
      <div className="hardware-inline-detail-panel">
        <button className="hardware-inline-close" type="button" aria-label="Fermer la fiche du matériel" onClick={onClose}>
          ✕
        </button>

        {hardwareDisplayImage(item) ? (
          <img
            src={hardwareDisplayImage(item)}
            alt={item.name}
            className="modal-image hardware-detail-image"
            onError={handleHardwareImageError}
          />
        ) : (
          <div className="modal-image placeholder">
            {item.type === "audio" ? "🎧" : item.type === "controller" ? "🕹️" : "🎮"}
          </div>
        )}

        <div className="modal-content">
          <div className="modal-title-row">
            <h2>{item.name}</h2>

            <button
              className={`heart-btn ${item.favorite ? "active" : ""}`}
              type="button"
              onClick={() => onToggleHardwareFavorite(item.id, item.favorite)}
            >
              <Heart size={18} fill={item.favorite ? "currentColor" : "none"} />
            </button>
          </div>

          {hardwareEditionLabel(item)&&<p className="hardware-edition-badge">{hardwareEditionLabel(item)}</p>}
          <div className="hardware-edition-tools">{(onEditHardwareEdition||onAddHardwareEdition)&&<button type="button" className="option-pill" onClick={()=>onEditHardwareEdition?onEditHardwareEdition(item):onAddHardwareEdition(item)}>Couleur et éditions</button>}</div>
          <div className="hardware-detail-score">
            <div className="hardware-detail-score-main">
              <span>⭐ {formatRating10(averageRating, "Non noté")}</span>
              <small>Moyenne critères</small>
            </div>

            <div className="hardware-detail-score-progress">
              <strong>
                {ratedFields.length}/{fields.length}
              </strong>
              <small>critères notés</small>
              <div className="hardware-detail-score-bar">
                <span style={{ width: `${ratingCompletion}%` }} />
              </div>
            </div>
          </div>

          {ratedFields.length > 0 && (
            <div className="hardware-detail-insights">
              {bestRatedField && (
                <div className="hardware-detail-insight strong">
                  <span>Point fort</span>
                  <strong>
                    {bestRatedField.icon} {bestRatedField.label}
                  </strong>
                  <small>{formatRating10(bestRatedField.value, "-")}</small>
                </div>
              )}
            </div>
          )}

          <div className="hardware-detail-profile">
            <div className="hardware-detail-profile-head">
              <span>Profil {hardwareTypeLabel.toLowerCase()}</span>
              <strong>
                {item.storage ||
                  displaySizeLabel ||
                  item.variantName ||
                  getHardwareStatusLabel(item.status)}
              </strong>
            </div>

            <div className="hardware-detail-facts">
              {hardwareFacts.map((fact) => (
                <div key={`${fact.label}-${fact.value}`} className="hardware-detail-fact">
                  <span>{fact.label}</span>
                  <strong>{fact.value}</strong>
                </div>
              ))}
            </div>

            {highlightedFields.length > 0 && (
              <div className="hardware-detail-focus">
                <span>Critères de notation</span>
                <div>
                  {highlightedFields.map((field) => (
                    <small key={field.key}>
                      {field.icon} {field.label}
                    </small>
                  ))}
                </div>
              </div>
            )}
          </div>

          {item.type === "console" && consoleGameStats && (
            <div className="hardware-detail-console-stats">
              <div>
                <strong>{consoleGameStats.games}</strong>
                <span>jeux joués</span>
              </div>
              <div>
                <strong>{consoleGameStats.level}</strong>
                <span>niveau console</span>
              </div>
              <div>
                <strong>{consoleGameStats.xp}</strong>
                <span>XP plateforme</span>
              </div>
            </div>
          )}

          <div className="modal-block hardware-detail-controls">
            <div className="modal-block-title">Mon matériel</div>

            <div className="hardware-card-dropdowns">
              <label className="hardware-acquired-year-field">
                <span>Année d’obtention</span>
                <select value={item.acquiredYear || ""} onChange={(event) => onUpdateHardwareAcquiredYear?.(item.id, event.target.value)}>
                  <option value="">Non précisée</option>
                  {Array.from({ length: new Date().getFullYear() - 1949 }, (_, index) => new Date().getFullYear() - index).map((year) => <option key={year} value={year}>{year}</option>)}
                </select>
              </label>
              {item.type === "console" && String(item.status || "").toLowerCase().includes("poss") && (
                <HardwareDropdown
                  id={`detail-condition-${item.id}`}
                  label="État"
                  value={item.condition || "bon"}
                  options={conditionOptions}
                  openedDropdown={openedDropdown}
                  setOpenedDropdown={setOpenedDropdown}
                  onChange={(value) => onUpdateHardwareCondition?.(item.id, value)}
                />
              )}

              {item.type === "display" && (
                <HardwareDropdown
                  id={`detail-display-size-${item.id}`}
                  label="Taille"
                  value={item.displaySize || ""}
                  options={currentDisplaySizeOptions}
                  openedDropdown={openedDropdown}
                  setOpenedDropdown={setOpenedDropdown}
                  onChange={(value) => onUpdateHardwareDisplaySize?.(item.id, value)}
                />
              )}

              <HardwareDropdown
                id={`detail-status-${item.id}`}
                label="Statut"
                value={item.status || "possédé"}
                options={statusOptions}
                openedDropdown={openedDropdown}
                setOpenedDropdown={setOpenedDropdown}
                onChange={(value) => onUpdateHardwareStatus?.(item.id, value)}
              />

              <HardwareDropdown
                id={`detail-sanctuary-${item.id}`}
                label="Sanctuaire"
                value={item.sanctuary ? "in" : "out"}
                options={[
                  { id: "out", label: "Ne pas afficher" },
                  { id: "in", label: "Dans le Sanctuaire" },
                ]}
                openedDropdown={openedDropdown}
                setOpenedDropdown={setOpenedDropdown}
                onChange={(value) => onUpdateHardwareSanctuary?.(item.id, value === "in")}
              />
            </div>
          </div>

          {showPcConfiguration && (
            <div className="modal-block hardware-pc-config-block">
              <div className="modal-block-title">Configuration PC</div>

              <div className="hardware-pc-config-grid">
                {PC_COMPONENT_FIELDS.map((field) => (
                  <HardwareDropdown
                    key={field.key}
                    id={`detail-pc-${field.key}-${item.id}`}
                    label={field.label}
                    value={item.pcConfig?.[field.key] || ""}
                    options={getPcComponentOptions(field.key)}
                    openedDropdown={openedDropdown}
                    setOpenedDropdown={setOpenedDropdown}
                    onChange={(value) =>
                      onUpdateHardwareComponent?.(item.id, field.key, value)
                    }
                  />
                ))}
              </div>
            </div>
          )}

          <div className="modal-block">
            <div className="modal-block-title">Notes détaillées</div>

            <div className="hardware-rating-list">
              {fields.map((field) => {
                const fieldRating = clampRating(item.ratings?.[field.key]);

                return (
                  <div
                    key={field.key}
                    className={`hardware-rating-row ${fieldRating > 0 ? "rated" : ""}`}
                  >
                    <div className="hardware-rating-label">
                      <span className="hardware-rating-icon">{field.icon}</span>
                      <strong>{field.label}</strong>
                      <em>{formatRating10(fieldRating, "À noter")}</em>
                    </div>

                    <RatingSlider
                      rating={fieldRating}
                      onRate={(value) =>
                        onUpdateHardwareRating(item.id, field.key, value, item.type)
                      }
                    />
                  </div>
                );
              })}
            </div>
          </div>

          <div className="modal-block">
            <div className="modal-block-title">Mon avis</div>

            <textarea
              className="review-textarea"
              value={localReview}
              placeholder="Note ce que tu penses de ce matériel..."
              onChange={(e) => setLocalReview(e.target.value)}
              onBlur={() => onUpdateHardwareReview(item.id, localReview)}
            />

            <button
              type="button"
              className="save-review-btn"
              onClick={() => onUpdateHardwareReview(item.id, localReview)}
            >
              Enregistrer l’avis
            </button>
          </div>

          <div className="hardware-detail-actions">
            <button
              type="button"
              className="hardware-detail-delete"
              onClick={() => {
                onDeleteHardware(item.id);
                onClose();
              }}
            >
              Supprimer ce matériel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function HardwareTab({
  hardware,
  initialHardwareRequest,
  games,
  onAddHardware,
  onDeleteHardware,
  onToggleHardwareFavorite,
  onUpdateHardwareStatus,
  onUpdateHardwareSanctuary,
  onUpdateHardwareCondition,
  onUpdateHardwareAcquiredYear,
  onUpdateHardwareDisplaySize,
  onUpdateHardwareRank,
  onUpdateHardwareRating,
  onUpdateHardwareReview,
  onUpdateHardwareComponent,
  onUpdateHardwareEdition,
}) {
  const [hardwareSearch, setHardwareSearch] = useState("");
  const [hardwareEditionDraft,setHardwareEditionDraft]=useState(null);
  const editionAdds=useRef(new Set());
  const [selectedHardwareId, setSelectedHardwareId] = useState(null);
  const [selectedHardwareBrandView, setSelectedHardwareBrandView] = useState(null);
  const [showAllHardwareByBrand, setShowAllHardwareByBrand] = useState(false);
  const [returnToAllHardware, setReturnToAllHardware] = useState(false);
  const [selectedCatalogVersionId, setSelectedCatalogVersionId] = useState(null);
  const [openedDropdown, setOpenedDropdown] = useState(null);
  const [hardwareCategory, setHardwareCategory] = useState("console");
  const [zoomedHardwareImage, setZoomedHardwareImage] = useState(null);
  const [selectedHardwareDetail, setSelectedHardwareDetail] = useState(null);
  useEffect(()=>{if(initialHardwareRequest){const item=hardware.find(item=>item.id===initialHardwareRequest.id);if(item){setHardwareCategory(item.type||"console");setSelectedHardwareDetail({...item,instanceKey:`search-${item.id}`});}}},[initialHardwareRequest]);
  const [showHardwareCatalog, setShowHardwareCatalog] = useState(false);
  const [hardwareCollectionFilter, setHardwareCollectionFilter] = useState("all");
  const [collectionFilters,setCollectionFilters]=useState({...EMPTY_HARDWARE_FILTERS});
  useEffect(()=>setCollectionFilters({...EMPTY_HARDWARE_FILTERS}),[hardwareCategory]);

  const autoControllerSyncRef = useRef(new Set());
  const hardwareTopRef = useRef(null);
  const hardwareCatalogPanelRef = useRef(null);
  const hardwareDetailRef = useRef(null);

  const scrollToHardwareArea = (ref, offset = 96) => {
    window.requestAnimationFrame(() => {
      if (!ref.current) return;

      const mobileOffset = window.matchMedia("(max-width: 700px)").matches
        ? Math.max(offset, 132)
        : offset;

      const top =
        ref.current.getBoundingClientRect().top + window.scrollY - mobileOffset;

      window.scrollTo({
        top: Math.max(0, top),
        behavior: "smooth",
      });
    });
  };

  useEffect(() => {
    if (!selectedHardwareDetail) return;
    scrollToHardwareArea(hardwareDetailRef, 116);
  }, [selectedHardwareDetail?.instanceKey]);

  useEffect(() => {
    if (!selectedHardwareId || !selectedCatalogVersionId) return;

    const scrollTimer = window.setTimeout(() => {
      const selectedVersionCard = document.querySelector(
        `[data-catalog-version-id="${selectedCatalogVersionId}"]`
      );

      selectedVersionCard?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }, 80);

    return () => window.clearTimeout(scrollTimer);
  }, [selectedHardwareId, selectedCatalogVersionId]);

  const brandLogos = {
    Sony: "/images/brands/sony-corporate.png",
    PlayStation: "/images/brands/sony.png",
    Microsoft: "/images/brands/xbox.png",
    Nintendo: "/images/brands/nintendo.png",
    SEGA: "/images/brands/sega.png",
    PC: "/images/brands/pc.png",
    Valve: "/images/brands/steam.png",
    Atari: "/images/brands/atari.png",
    SNK: "/images/brands/snk.png",
    NEC: "/images/brands/nec.png",
    "3DO": "/images/brands/3do.png",
    Retro: "/images/brands/retro.png",
    Alternative: "/images/brands/retro.png",
    Xbox: "/images/brands/xbox.png",
    Bose: "/images/brands/bose.png",
    SteelSeries: "/images/brands/steelseries.png",
    HyperX: "/images/brands/hyperx.png",
    Logitech: "/images/brands/logitech.png",
    Beyerdynamic: "/images/brands/beyerdynamic.png",
    Sennheiser: "/images/brands/sennheiser.png",
    JBL: "/images/brands/jbl.png",
    Creative: "/images/brands/creative.png",
    Edifier: "/images/brands/edifier.png",
    Klipsch: "/images/brands/klipsch.png",
    Panasonic: "/images/brands/panasonic.png",
    EPOS: "/images/brands/epos.png",
    Audeze: "/images/brands/audeze.png",
    "Audio-Technica": "/images/brands/audio-technica.png",
    Apple: "/images/brands/apple.png",
    Meta: "/images/brands/meta.png",
    HTC: "/images/brands/htc.png",
    Pimax: "/images/brands/pimax.png",
    Bigscreen: "/images/brands/bigscreen.png",
    Pico: "/images/brands/pico.png",
    HP: "/images/brands/hp.png",
    Razer: "/images/brands/razer.png",
    Astro: "/images/brands/astro.png",
    Corsair: "/images/brands/corsair.png",
    "Turtle Beach": "/images/brands/turtlebeach.png",
    LG: "/images/brands/lg.png",
    Samsung: "/images/brands/samsung.png",
    TCL: "/images/brands/tcl.png",
    Hisense: "/images/brands/hisense.png",
    ASUS: "/images/brands/asus.png",
    Alienware: "/images/brands/alienware.png",
    MSI: "/images/brands/msi.png",
    Gigabyte: "/images/brands/gigabyte.png",
    Zowie: "/images/brands/zowie.png",
    Pulsar: "/images/brands/pulsar.png",
    VAXEE: "/images/brands/vaxee.png",
    "Endgame Gear": "/images/brands/endgame-gear.png",
    Lamzu: "/images/brands/lamzu.png",
    ROCCAT: "/images/brands/roccat.png",
    "Cooler Master": "/images/brands/cooler-master.png",
    Glorious: "/images/brands/glorious.png",
    Finalmouse: "/images/brands/finalmouse.png",
    Xtrfy: "/images/brands/xtrfy.png",
    Fnatic: "/images/brands/fnatic.png",
    Wooting: "/images/brands/wooting.png",
    Tryhard: "/images/brands/tryhard.png",
    Keychron: "/images/brands/keychron.png",
    Ducky: "/images/brands/ducky.png",
    Mountain: "/images/brands/mountain.png",
    Akko: "/images/brands/akko.png",
    DrunkDeer: "/images/brands/drunkdear.png",
    "8BitDo": "/images/brands/8bitdo.png",
    SCUF: "/images/brands/scuf.png",
    Victrix: "/images/brands/victrix.png",
    Nacon: "/images/brands/nacon.png",
    Thrustmaster: "/images/brands/thrustmaster.png",
    HORI: "/images/brands/hori.png",
    PowerA: "/images/brands/powera.png",
    Backbone: "/images/brands/backbone.png",
    Gulikit: "/images/brands/gulikit.png",
  };

  const getHardwareBrandLogo = (brand, type = hardwareCategory) => {
    if (brand === "Sony" && (type === "console" || type === "controller")) {
      return brandLogos.PlayStation;
    }

    return brandLogos[brand];
  };

  const HARDWARE_STATUS_OPTIONS = [
    { id: "possédé", label: "Possédé" },
    { id: "historique", label: "Historique" },
    { id: "wishlist", label: "Wishlist" },
    { id: "vendu", label: "Vendue" },
    { id: "à réparer", label: "À réparer" },
  ];

  const CONDITION_OPTIONS = [
    { id: "neuf", label: "Neuf" },
    { id: "très bon", label: "Très bon" },
    { id: "bon", label: "Bon" },
    { id: "usé", label: "Usé" },
    { id: "à réparer", label: "À réparer" },
  ];

  const DISPLAY_SIZE_OPTIONS = [
    { id: "", label: "Taille non précisée" },
    { id: "24", label: "24 pouces" },
    { id: "25", label: "25 pouces" },
    { id: "27", label: "27 pouces" },
    { id: "32", label: "32 pouces" },
    { id: "34", label: "34 pouces ultrawide" },
    { id: "38", label: "38 pouces ultrawide" },
    { id: "42", label: "42 pouces" },
    { id: "45", label: "45 pouces ultrawide" },
    { id: "48", label: "48 pouces" },
    { id: "49", label: "49 pouces super ultrawide" },
    { id: "55", label: "55 pouces" },
    { id: "57", label: "57 pouces super ultrawide" },
    { id: "65", label: "65 pouces" },
    { id: "75", label: "75 pouces" },
    { id: "77", label: "77 pouces" },
    { id: "83", label: "83 pouces" },
    { id: "85", label: "85 pouces" },
    { id: "98", label: "98 pouces" },
  ];

  const DEFAULT_CONTROLLER_BY_CONSOLE = {
    ps5: "dualsense",
    ps4: "dualshock-4",
    ps3: "dualshock-3",
    ps2: "dualshock-2",
    ps1: "dualshock",
    "xbox-series": "xbox-series-controller",
    "xbox-one": "xbox-one-controller",
    "xbox-360": "xbox-360-controller",
    "xbox-original": "xbox-controller-s",
    switch: "joy-con",
    wii: "wii-remote",
    "wii-u": "wii-u-pro-controller",
    gamecube: "gamecube-controller",
    "nintendo-64": "n64-controller",
    snes: "snes-controller",
    nes: "nes-controller",
    dreamcast: "dreamcast-controller",
    saturn: "saturn-controller",
    "mega-drive": "mega-drive-3b",
    "master-system": "master-system-controller",
  };

  const getHardwareStatusLabel = (status) => {
    const statusKey = getHardwareStatusKey(status);
    return (
      HARDWARE_STATUS_OPTIONS.find(
        (option) => getHardwareStatusKey(option.id) === statusKey
      )?.label || "Possédé"
    );
  };

  const getHardwareStatusKey = (status = "") => {
    const raw = String(status || "").toLowerCase();
    const normalized = getNormalizedStatus(raw);

    if (normalized.includes("wishlist")) return "wishlist";
    if (normalized.includes("historique")) return "historique";
    if (normalized.includes("vend")) return "vendu";
    if (normalized.includes("reparer")) return "reparer";
    if (normalized.includes("poss") || raw.includes("poss")) return "possede";

    return normalized;
  };

  const hasHardwareStatus = (item, statusKey) =>
    getHardwareStatusKey(item?.status) === statusKey;

  const isCurrentHardware = (item) =>
    hasHardwareStatus(item, "possede") || hasHardwareStatus(item, "reparer");

  const isRankableHardware = (item) =>
    ["possede", "historique", "vendu", "reparer"].includes(
      getHardwareStatusKey(item?.status)
    );
  const findControllerByVersionId = (versionId) => {
    for (const item of HARDWARE_CATALOG) {
      if (item.type !== "controller") continue;

      for (const variant of item.variants || []) {
        for (const version of variant.versions || []) {
          if (version.id === versionId) return { item, variant, version };
        }
      }
    }

    return null;
  };

  const hardwareRankingStatuses = [
    "possédé",
    "historique",
    "vendu",
    "à réparer",
  ];
  const hardwareStatusPriority = {
    possede: 3,
    reparer: 3,
    historique: 2,
    vendu: 1,
  };
  const getRankedHardware = (type) =>
    Object.values(
    hardware
      .filter(
        (item) =>
          item.type === type && isRankableHardware(item)
      )
      .reduce((rankedItems, item) => {
        const hardwareKey =
          item.versionId || `${item.catalogId}-${item.variantId}-${item.name}`;
        const currentItem = rankedItems[hardwareKey];

        if (
          !currentItem ||
          (hardwareStatusPriority[getHardwareStatusKey(item.status)] || 0) >
            (hardwareStatusPriority[getHardwareStatusKey(currentItem.status)] || 0) ||
          getHardwareAverageRating(item) > getHardwareAverageRating(currentItem)
        ) {
          rankedItems[hardwareKey] = item;
        }

        return rankedItems;
      }, {})
  ).sort((a, b) => {
      const ratingDiff =
        getHardwareAverageRating(b) - getHardwareAverageRating(a);
      if (ratingDiff !== 0) return ratingDiff;
      return (a.name || "").localeCompare(b.name || "");
    });

  const rankedControllers = getRankedHardware("controller");
  const rankedConsoles = getRankedHardware("console");
  const rankedAudio = getRankedHardware("audio");
  const rankedSpeakers = getRankedHardware("speaker");
  const rankedVr = getRankedHardware("vr");
  const rankedDisplays = getRankedHardware("display");
  const rankedMice = getRankedHardware("mouse");
  const rankedKeyboards = getRankedHardware("keyboard");

  useEffect(() => {
    const syncDefaultControllers = async () => {
      const currentOrPastConsoles = hardware.filter(
        (item) =>
          item.type === "console" &&
          ["possede", "historique", "vendu"].includes(getHardwareStatusKey(item.status))
      );

      for (const consoleItem of currentOrPastConsoles) {
        const controllerVersionId =
          DEFAULT_CONTROLLER_BY_CONSOLE[consoleItem.catalogId];

        if (!controllerVersionId) continue;

        const controllerData = findControllerByVersionId(controllerVersionId);
        if (!controllerData) continue;

        const ownedStatusId = HARDWARE_STATUS_OPTIONS[0].id;
        const controllerStatus = hasHardwareStatus(consoleItem, "possede")
          ? ownedStatusId
          : "historique";
        const syncKey = `controller-${controllerVersionId}-${controllerStatus}`;

        const alreadySynced = hardware.some((item) => {
          if (item.type !== "controller" || item.versionId !== controllerVersionId) {
            return false;
          }

          return controllerStatus === "historique"
            ? isRankableHardware(item)
            : hasHardwareStatus(item, "possede");
        });

        if (!alreadySynced && !autoControllerSyncRef.current.has(syncKey)) {
          autoControllerSyncRef.current.add(syncKey);

          await onAddHardware({
            catalogId: controllerData.item.id,
            variantId: controllerData.variant.id,
            versionId: controllerData.version.id,
            name: controllerData.version.name,
            parentName: controllerData.item.name,
            variantName: controllerData.variant.name,
            brand: controllerData.item.brand,
            type: "controller",
            image: controllerData.version.image || "",
            status: controllerStatus,
            rating: 0,
            ratings: {},
            review: "",
            favorite: false,
            sanctuary: false,
            source:
              controllerStatus === ownedStatusId
                ? "auto-controller"
                : "auto-controller-history",
            createdAt: Date.now(),
          });
        }
      }
    };

    syncDefaultControllers();
  }, [hardware]);
  const catalogByType = HARDWARE_CATALOG.filter(
    (item) => item.type === hardwareCategory
  );

  const hardwareBrands = [...new Set(catalogByType.map((item) => item.brand))];

  const preferredOrder = [
    "Sony",
    "Microsoft",
    "Nintendo",
    "SEGA",
    "PC",
    "Valve",
    "Atari",
    "SNK",
    "NEC",
    "3DO",
    "Retro",
    "Alternative",
    "Xbox",
    "Bose",
    "SteelSeries",
    "HyperX",
    "Logitech",
    "Beyerdynamic",
    "Sennheiser",
    "JBL",
    "Creative",
    "Edifier",
    "Klipsch",
    "Panasonic",
    "EPOS",
    "Audeze",
    "Audio-Technica",
    "Apple",
    "Meta",
    "PlayStation",
    "HTC",
    "Pimax",
    "Bigscreen",
    "Pico",
    "HP",
    "Razer",
    "Astro",
    "Corsair",
    "Turtle Beach",
    "Tritton",
    "LG",
    "Samsung",
    "TCL",
    "Hisense",
    "ASUS",
    "Alienware",
    "MSI",
    "Gigabyte",
    "Zowie",
    "Pulsar",
    "VAXEE",
    "Endgame Gear",
    "Lamzu",
    "ROCCAT",
    "Cooler Master",
    "Glorious",
    "Finalmouse",
    "Xtrfy",
    "Fnatic",
    "Wooting",
    "Tryhard",
    "Keychron",
    "Ducky",
    "Mountain",
    "Akko",
    "DrunkDeer",
    "8BitDo",
    "SCUF",
    "Victrix",
    "Nacon",
    "Thrustmaster",
    "HORI",
    "PowerA",
    "Backbone",
    "PDP",
    "Gulikit",
  ];

  const brandList = [
    ...new Set([
      ...preferredOrder.filter((brand) => hardwareBrands.includes(brand)),
      ...hardwareBrands.filter((brand) => !preferredOrder.includes(brand)).sort(),
    ]),
  ];

  const itemsBySelectedBrand = selectedHardwareBrandView
    ? catalogByType.filter((item) => item.brand === selectedHardwareBrandView)
    : [];

  const getCatalogModelCount = (catalogItem) =>
    catalogItem.variants?.reduce(
      (sum, variant) => sum + (variant.versions?.length || 0),
      0
    ) || 0;

  const catalogGroupsByBrand = brandList
    .map((brand) => ({
      brand,
      items: catalogByType.filter((item) => item.brand === brand),
    }))
    .filter((group) => group.items.length > 0);

  const catalogModelTotal = catalogByType.reduce(
    (sum, catalogItem) => sum + getCatalogModelCount(catalogItem),
    0
  );

  const findExistingCatalogHardware = (catalogItem, variant, version) =>
    hardware.find(
      (item) =>
        item.catalogId === catalogItem.id &&
        item.variantId === variant.id &&
        item.versionId === version.id &&
        item.type === catalogItem.type &&
        item.status !== "ranked" && !hasHardwareEdition(item)
    ) ||
    hardware.find(
      (item) =>
        item.versionId === version.id &&
        item.type === catalogItem.type &&
        item.status !== "ranked" && !hasHardwareEdition(item)
    );

  const selectedHardware = catalogByType.find(
    (item) => item.id === selectedHardwareId
  );

  const filteredCatalog = catalogByType.filter((item) => {
    const query = hardwareSearch.trim().toLowerCase();
    if (!query) return true;

    return (
      item.name.toLowerCase().includes(query) ||
      item.brand.toLowerCase().includes(query) ||
      item.variants?.some((variant) =>
        variant.versions?.some((version) =>
          version.name.toLowerCase().includes(query)
        )
      )
    );
  });

  const openCatalogItem = (catalogItem, options = {}) => {
    setSelectedHardwareBrandView(catalogItem.brand);
    setSelectedHardwareId(catalogItem.id);
    setReturnToAllHardware(Boolean(options.fromAll));
    setSelectedCatalogVersionId(options.versionId || null);
    setShowAllHardwareByBrand(false);
    setHardwareSearch("");
    if (!options.fromAll) {
      scrollToHardwareArea(hardwareTopRef);
    }
  };

  const addFromCatalog = async (
    catalogItem,
    variant,
    version,
    status = "possédé",
    edition
  ) => {
    const candidate={catalogId:catalogItem.id,variantId:variant.id,versionId:version.id,name:version.name,brand:catalogItem.brand,type:catalogItem.type,image:version.image||'',status,edition:normalizeHardwareEdition(edition)};
    if(duplicateHardwareEdition(hardware,candidate)){
      if(edition!==undefined)throw new Error('Cette édition avec ce coloris et ce statut est déjà dans ta collection.');
      return false;
    }
    const pendingKey=JSON.stringify(candidate);
    if(editionAdds.current.has(pendingKey))return false;
    editionAdds.current.add(pendingKey);
    try{return await onAddHardware({
      catalogId: catalogItem.id,
      variantId: variant.id,
      versionId: version.id,
      name: version.name,
      parentName: catalogItem.name,
      variantName: variant.name,
      brand: catalogItem.brand,
      type: catalogItem.type,
      storage: version.storage || "",
      disc: version.disc ?? null,
      backwardCompatible: version.backwardCompatible ?? null,
      image: hardwareImageForEdition(candidate),
      catalogImage: version.image || "",
      status,
      condition: catalogItem.type === "console" ? "bon" : "",
      displaySizes: catalogItem.type === "display" ? version.sizes || [] : [],
      displaySize:
        catalogItem.type === "display" && version.sizes?.length === 1
          ? version.sizes[0]
          : "",
      rating: 0,
      ratings: {},
      review: "",
      favorite: false,
      sanctuary: false,
      source: "local-catalog",
      edition: candidate.edition,
      createdAt: Date.now(),
    });}finally{editionAdds.current.delete(pendingKey);}
  };

  const editionContextFromItem=item=>{
    const model=HARDWARE_CATALOG.find(model=>model.id===item.catalogId)||{id:item.catalogId||'',name:item.parentName||item.name,brand:item.brand,type:item.type};
    const variant=model.variants?.find(variant=>variant.id===item.variantId)||{id:item.variantId||'',name:item.variantName||item.name};
    const version=variant.versions?.find(version=>version.id===item.versionId)||{...item,id:item.versionId||'',name:item.name};
    return {catalogItem:model,variant,version};
  };
  const openEditionFromItem=item=>setHardwareEditionDraft({mode:'add',...editionContextFromItem(item)});
  const editionButton=(catalogItem,variant,version)=>{
    const count=hardwareEditionChoices({type:catalogItem.type,catalogId:catalogItem.id,versionId:version.id}).length;
    return <button type="button" className="option-pill hardware-edition-add" aria-label={`Ajouter une couleur ou une édition de ${version.name}`} onClick={event=>{event.stopPropagation();setHardwareEditionDraft({mode:'add',catalogItem,variant,version});}}>{count?`Couleurs et éditions · ${count}`:'Préciser la couleur / l’édition'}</button>;
  };
  const saveHardwareEdition=async(edition,status,mode=hardwareEditionDraft.mode)=>{
    if(mode==='edit'){
      const item=hardware.find(item=>item.id===hardwareEditionDraft.item.id);
      if(!item)throw new Error('Ce matériel n’est plus disponible.');
      if(duplicateHardwareEdition(hardware,{...item,edition},item.id))throw new Error('Cette édition avec ce coloris et ce statut existe déjà. Les fiches restent séparées.');
      return onUpdateHardwareEdition?.(item.id,edition)??false;
    }
    const context=hardwareEditionDraft.mode==='edit'?editionContextFromItem(hardwareEditionDraft.item):hardwareEditionDraft;
    return addFromCatalog(context.catalogItem,context.variant,context.version,status,edition);
  };

  const getHardwareCompletion = () => {
    const completionCatalog = HARDWARE_CATALOG.filter(
      (item) => item.type === hardwareCategory
    );

    const totalVersions = completionCatalog.reduce((sum, hardwareItem) => {
      return (
        sum +
        (hardwareItem.variants || []).reduce((variantSum, variant) => {
          return variantSum + (variant.versions || []).length;
        }, 0)
      );
    }, 0);

    const ownedOrHistoryVersionIds = new Set(
      hardware
        .filter(
          (item) =>
            item.type === hardwareCategory &&
            isRankableHardware(item)
        )
        .map((item) => item.versionId)
        .filter(Boolean)
    );

    return {
      owned: ownedOrHistoryVersionIds.size,
      total: totalVersions,
      percent: totalVersions
        ? Math.round((ownedOrHistoryVersionIds.size / totalVersions) * 100)
        : 0,
    };
  };

  const hardwareCompletion = getHardwareCompletion();

  const collectionGroups =
    hardwareCategory === "controller"
      ? [
          {
            id: "controllers",
            title: "Manettes utilisées actuellement",
            items: hardware.filter(
              (item) => item.type === "controller" && isCurrentHardware(item)
            ),
          },
          {
            id: "controller-ranking",
            title: "Classement automatique par note",
            items: rankedControllers,
          },
          {
            id: "controller-history",
            title: "Mes anciennes manettes",
            items: hardware.filter((item) => item.type === "controller" && ["historique", "vendu"].includes(getHardwareStatusKey(item.status))),
          },
          {
            id: "controller-wishlist",
            title: "Wishlist manettes",
            items: hardware.filter((item) => item.type === "controller" && hasHardwareStatus(item, "wishlist")),
          },
        ]
      : hardwareCategory === "audio"
      ? [
          {
            id: "audio-owned",
            title: "Casques utilisés actuellement",
            items: hardware.filter(
              (item) => item.type === "audio" && hasHardwareStatus(item, "possede")
            ),
          },
          {
            id: "audio-ranking",
            title: "Classement des casques par note",
            items: rankedAudio,
          },
          {
            id: "audio-wishlist",
            title: "Wishlist audio",
            items: hardware.filter(
              (item) => item.type === "audio" && hasHardwareStatus(item, "wishlist")
            ),
          },
        ]
      : hardwareCategory === "speaker"
      ? [
          {
            id: "speaker-owned",
            title: "Enceintes utilisées actuellement",
            items: hardware.filter(
              (item) => item.type === "speaker" && hasHardwareStatus(item, "possede")
            ),
          },
          {
            id: "speaker-ranking",
            title: "Classement des enceintes par note",
            items: rankedSpeakers,
          },
          {
            id: "speaker-wishlist",
            title: "Wishlist enceintes",
            items: hardware.filter(
              (item) => item.type === "speaker" && hasHardwareStatus(item, "wishlist")
            ),
          },
        ]
      : hardwareCategory === "vr"
      ? [
          {
            id: "vr-owned",
            title: "Casques VR utilisés actuellement",
            items: hardware.filter(
              (item) =>
                item.type === "vr" &&
                hasHardwareStatus(item, "possede")
            ),
          },
          {
            id: "vr-ranking",
            title: "Classement des casques VR par note",
            items: rankedVr,
          },
          {
            id: "vr-wishlist",
            title: "Wishlist VR",
            items: hardware.filter(
              (item) => item.type === "vr" && hasHardwareStatus(item, "wishlist")
            ),
          },
        ]
      : hardwareCategory === "mouse"
      ? [
          {
            id: "mouse-owned",
            title: "Souris utilisées actuellement",
            items: hardware.filter(
              (item) => item.type === "mouse" && hasHardwareStatus(item, "possede")
            ),
          },
          {
            id: "mouse-ranking",
            title: "Classement des souris par note",
            items: rankedMice,
          },
          {
            id: "mouse-wishlist",
            title: "Wishlist souris",
            items: hardware.filter(
              (item) => item.type === "mouse" && hasHardwareStatus(item, "wishlist")
            ),
          },
        ]
      : hardwareCategory === "keyboard"
      ? [
          {
            id: "keyboard-owned",
            title: "Claviers utilisés actuellement",
            items: hardware.filter(
              (item) => item.type === "keyboard" && hasHardwareStatus(item, "possede")
            ),
          },
          {
            id: "keyboard-ranking",
            title: "Classement des claviers par note",
            items: rankedKeyboards,
          },
          {
            id: "keyboard-wishlist",
            title: "Wishlist claviers",
            items: hardware.filter(
              (item) => item.type === "keyboard" && hasHardwareStatus(item, "wishlist")
            ),
          },
        ]
      : hardwareCategory === "display"
      ? [
          {
            id: "display-owned",
            title: "Écrans et TV utilisés actuellement",
            items: hardware.filter(
              (item) => item.type === "display" && hasHardwareStatus(item, "possede")
            ),
          },
          {
            id: "display-ranking",
            title: "Classement des écrans et TV par note",
            items: rankedDisplays,
          },
          {
            id: "display-wishlist",
            title: "Wishlist écrans & TV",
            items: hardware.filter(
              (item) => item.type === "display" && hasHardwareStatus(item, "wishlist")
            ),
          },
        ]
      : [
          {
            id: "possédé",
            title: "Ma collection actuelle",
            items: hardware.filter(
              (item) =>
                item.type === "console" &&
                isCurrentHardware(item)
            ),
          },
          {
            id: "console-ranking",
            title: "Classement des consoles par note",
            items: rankedConsoles,
          },
          {
            id: "wishlist",
            title: "Wishlist matériel",
            items: hardware.filter(
              (item) => item.type === "console" && hasHardwareStatus(item, "wishlist")
            ),
          },
        ];

  const hardwareCollectionFilters = [
    { id: "all", label: "Tout" },
    { id: "current", label: "Actuel" },
    { id: "history", label: "Historique" },
    { id: "wishlist", label: "Wishlist" },
  ];

  const matchesHardwareCollectionFilter = (item) => {
    if (hardwareCollectionFilter === "current") return isCurrentHardware(item);
    if (hardwareCollectionFilter === "wishlist") return hasHardwareStatus(item, "wishlist");
    if (hardwareCollectionFilter === "history") {
      return ["historique", "vendu"].includes(getHardwareStatusKey(item.status));
    }

    return true;
  };

  const visibleCollectionGroups = collectionGroups
    .map((group) => ({
      ...group,
      items: group.items.filter(matchesHardwareCollectionFilter).filter(item=>matchesHardwareFilters(item,collectionFilters,HARDWARE_CATALOG)),
    }))
    .filter((group) => group.items.length > 0);

  const hasVisibleHardware = visibleCollectionGroups.some(
    (group) => group.items.length > 0
  );

  const getConsoleGameStats = (hardwareItem) => {
    return getHardwareConsoleGameStats(hardwareItem, games);
  };

  const hardwareTitle =
    hardwareCategory === "controller"
      ? "Mes manettes"
      : hardwareCategory === "audio"
      ? "Mon audio"
      : hardwareCategory === "speaker"
      ? "Mes enceintes"
      : hardwareCategory === "vr"
      ? "Mes casques VR"
      : hardwareCategory === "display"
      ? "Mes écrans & TV"
      : hardwareCategory === "mouse"
      ? "Mes souris"
      : hardwareCategory === "keyboard"
      ? "Mes claviers"
      : "Mon matériel";

  const allHardwareCatalogLabel =
    hardwareCategory === "controller"
      ? "Toutes les manettes"
      : hardwareCategory === "audio"
      ? "Tous les casques audio"
      : hardwareCategory === "speaker"
      ? "Toutes les enceintes"
      : hardwareCategory === "vr"
      ? "Tous les casques VR"
      : hardwareCategory === "display"
      ? "Tous les écrans & TV"
      : hardwareCategory === "mouse"
      ? "Toutes les souris"
      : hardwareCategory === "keyboard"
      ? "Tous les claviers"
      : "Toutes les consoles";

  const emptyTitle =
    hardwareCategory === "controller"
      ? "Aucune manette dans ton setup"
      : hardwareCategory === "audio"
      ? "Aucun casque dans ton setup"
      : hardwareCategory === "speaker"
      ? "Aucune enceinte dans ton setup"
      : hardwareCategory === "vr"
      ? "Aucun casque VR dans ton setup"
      : hardwareCategory === "mouse"
      ? "Aucune souris dans ton setup"
      : hardwareCategory === "keyboard"
      ? "Aucun clavier dans ton setup"
      : "Ton setup attend sa première pièce";

  const emptySubtitle =
    hardwareCategory === "controller"
      ? "Pioche dans le catalogue pour retrouver tes manettes préférées."
      : hardwareCategory === "audio"
      ? "Ajoute le casque qui accompagne tes sessions."
      : hardwareCategory === "speaker"
      ? "Ajoute les enceintes qui donnent du coffre à ton coin gaming."
      : hardwareCategory === "vr"
      ? "Ajoute ton casque VR pour compléter l’espace immersif."
      : hardwareCategory === "mouse"
      ? "Ajoute ta souris principale et garde une trace de ton ressenti."
      : hardwareCategory === "keyboard"
      ? "Ajoute ton clavier et commence à noter ton confort de jeu."
      : "Ajoute ta première console depuis le catalogue pour construire ton historique.";

  return (
    <div className="progression-stack">
      {hardwareEditionDraft&&<HardwareEditionEditor item={hardwareEditionDraft.mode==='edit'?{...hardwareEditionDraft.item,edition:hardwareEditionDraft.edition||hardwareEditionDraft.item.edition}:{name:hardwareEditionDraft.version.name,versionId:hardwareEditionDraft.version.id,catalogId:hardwareEditionDraft.catalogItem.id,type:hardwareEditionDraft.catalogItem.type,image:hardwareEditionDraft.version.image,status:'possédé',edition:hardwareEditionDraft.edition}} editing={hardwareEditionDraft.mode==='edit'} allowCreate={hardwareEditionDraft.mode==='edit'&&Boolean(onAddHardware)} onSave={saveHardwareEdition} onClose={()=>setHardwareEditionDraft(null)}/>}
      {selectedHardwareDetail?.instanceKey?.startsWith('search-')&&hardware.some(item=>item.id===selectedHardwareDetail.id)&&<HardwareDetailModal
        item={{...hardware.find(item=>item.id===selectedHardwareDetail.id),instanceKey:selectedHardwareDetail.instanceKey}}
        detailRef={hardwareDetailRef} onClose={()=>setSelectedHardwareDetail(null)} onDeleteHardware={onDeleteHardware}
        openedDropdown={openedDropdown} setOpenedDropdown={setOpenedDropdown} statusOptions={HARDWARE_STATUS_OPTIONS}
        conditionOptions={CONDITION_OPTIONS} displaySizeOptions={DISPLAY_SIZE_OPTIONS}
        onUpdateHardwareStatus={onUpdateHardwareStatus} onUpdateHardwareSanctuary={onUpdateHardwareSanctuary}
        onUpdateHardwareCondition={onUpdateHardwareCondition} onUpdateHardwareAcquiredYear={onUpdateHardwareAcquiredYear}
        onUpdateHardwareDisplaySize={onUpdateHardwareDisplaySize} onUpdateHardwareComponent={onUpdateHardwareComponent}
        onEditHardwareEdition={onUpdateHardwareEdition?(item,edition)=>setHardwareEditionDraft({mode:'edit',item,edition}):undefined}
        onAddHardwareEdition={openEditionFromItem}
        onUpdateHardwareRating={onUpdateHardwareRating} onUpdateHardwareReview={onUpdateHardwareReview}
        onToggleHardwareFavorite={onToggleHardwareFavorite} consoleGameStats={getConsoleGameStats(selectedHardwareDetail)} getHardwareStatusLabel={getHardwareStatusLabel}/>}
      {hardwareCategory === "console" && (
        <div className="search-panel">
          <h2 className="panel-title">Complétion matériel</h2>

          <div className="completion-top">
            <strong>{hardwareCompletion.percent}%</strong>
            <span>
              {hardwareCompletion.owned} / {hardwareCompletion.total} versions
            </span>
          </div>

          <div className="xp-bar">
            <div
              className="xp-fill"
              style={{ width: `${hardwareCompletion.percent}%` }}
            />
          </div>
        </div>
      )}

      <div
        className={`search-panel hardware-catalog-panel ${showHardwareCatalog ? "open" : "collapsed"}`}
        ref={hardwareCatalogPanelRef}
      >
        <h2 className="panel-title">Catalogue matériel</h2>

        <div className="hardware-catalog-header">
          <h2 className="panel-title">Matériel</h2>

          <button
            type="button"
            className={`hardware-catalog-toggle ${showHardwareCatalog ? "active" : ""}`}
            onClick={() => {
              setShowHardwareCatalog((open) => !open);
              setSelectedHardwareId(null);
              setSelectedHardwareBrandView(null);
              setSelectedHardwareDetail(null);
              setShowAllHardwareByBrand(false);
              setReturnToAllHardware(false);
              setSelectedCatalogVersionId(null);
              setHardwareSearch("");
              scrollToHardwareArea(hardwareCatalogPanelRef);
            }}
          >
            {showHardwareCatalog ? "Fermer" : "Ajouter / rechercher"}
          </button>
        </div>

        <div className="hardware-type-switch">
          {[
            { id: "console", label: "Consoles" },
            { id: "controller", label: "Manettes" },
            { id: "audio", label: "Audio" },
            { id: "speaker", label: "Enceintes" },
            { id: "vr", label: "VR" },
            { id: "display", label: "Écrans & TV" },
            { id: "mouse", label: "Souris" },
            { id: "keyboard", label: "Claviers" },
          ].map((category) => (
            <button
              key={category.id}
              type="button"
              className={hardwareCategory === category.id ? "active" : ""}
              onClick={() => {
                setHardwareCategory(category.id);
                setSelectedHardwareId(null);
                setSelectedHardwareBrandView(null);
                setSelectedHardwareDetail(null);
                setShowAllHardwareByBrand(false);
                setReturnToAllHardware(false);
                setSelectedCatalogVersionId(null);
                setHardwareSearch("");
                setHardwareCollectionFilter("all");
                scrollToHardwareArea(hardwareCatalogPanelRef);
              }}
            >
              {category.label}
            </button>
          ))}
        </div>

        <div className="input-container">
          <input
            value={hardwareSearch}
            onChange={(e) => {
              setHardwareSearch(e.target.value);
              setSelectedHardwareId(null);
              setSelectedHardwareBrandView(null);
              setShowAllHardwareByBrand(false);
              setReturnToAllHardware(false);
              setSelectedCatalogVersionId(null);
              setSelectedHardwareDetail(null);
            }}
            placeholder={
              hardwareCategory === "controller"
                ? "Rechercher une manette..."
                : hardwareCategory === "audio"
                ? "Rechercher un casque..."
                : hardwareCategory === "speaker"
                ? "Rechercher des enceintes gaming..."
                : hardwareCategory === "vr"
                ? "Rechercher un casque VR gaming..."
                : hardwareCategory === "display"
                ? "Rechercher un écran ou une TV gaming..."
                : hardwareCategory === "mouse"
                ? "Rechercher une souris gaming..."
                : hardwareCategory === "keyboard"
                ? "Rechercher un clavier gaming..."
                : "Rechercher une console..."
            }
          />
        </div>

        {hardwareSearch.trim() ? (
          <div className="hardware-console-list">
            {filteredCatalog.map((catalogItem) => (
              <button
                key={catalogItem.id}
                type="button"
                className="hardware-console-card"
                data-brand={catalogItem.brand}
                data-type={catalogItem.type}
                onClick={() => openCatalogItem(catalogItem)}
              >
                <div className="hardware-image-wrapper">
                  {catalogItem.variants?.[0]?.versions?.[0]?.image ? (
                    <img
                      src={catalogItem.variants?.[0]?.versions?.[0]?.image}
                      alt={catalogItem.name}
                      className="hardware-catalog-image"
                      onError={handleHardwareImageError}
                    />
                  ) : (
                    <div className="hardware-collection-placeholder">
                      {getHardwareTypeIcon(catalogItem.type)}
                    </div>
                  )}
                </div>

                <div className="hardware-console-info">
                  <div className="hardware-name">{catalogItem.name}</div>
                  <div className="hardware-meta">{catalogItem.brand}</div>
                </div>

                <div className="hardware-console-arrow">›</div>
              </button>
            ))}
          </div>
        ) : (
          <div className="hardware-view-container" ref={hardwareTopRef}>
            {showAllHardwareByBrand ? (
              <div className="hardware-view hardware-all-view">
                <button
                  type="button"
                  className="back-btn hardware-catalog-back"
                  onClick={() => {
                    setShowAllHardwareByBrand(false);
                    setSelectedHardwareBrandView(null);
                    setSelectedHardwareId(null);
                    setReturnToAllHardware(false);
                    setSelectedCatalogVersionId(null);
                    scrollToHardwareArea(hardwareTopRef);
                  }}
                >
                  <ArrowLeft size={16} /> Marques
                </button>

                <div className="hardware-all-heading">
                  <h3 className="hardware-group-title">
                    {allHardwareCatalogLabel}
                  </h3>
                  <span>
                    {catalogByType.reduce(
                      (sum, catalogItem) => sum + getCatalogModelCount(catalogItem),
                      0
                    )}{" "}
                    modèles
                  </span>
                </div>

                <div className="hardware-all-groups">
                  {catalogGroupsByBrand.map((group) => (
                    <section key={group.brand} className="hardware-all-group">
                      <div className="hardware-all-brand-head">
                        <div className="hardware-brand-line">
                          {getHardwareBrandLogo(group.brand) && (
                            <img
                              src={getHardwareBrandLogo(group.brand)}
                              alt={group.brand}
                              className="brand-logo"
                              onError={(event) => {
                                event.currentTarget.style.display = "none";
                              }}
                            />
                          )}
                          <span className="hardware-brand-name">
                            {group.brand}
                          </span>
                        </div>
                      </div>

                      {group.items.map((catalogItem) => (
                        <div key={catalogItem.id} className="hardware-all-model">
                          <div className="hardware-all-model-title">
                            <span>{catalogItem.name}</span>
                            <small>{getCatalogModelCount(catalogItem)} modèles</small>
                          </div>

                          <div className="hardware-console-list">
                            {catalogItem.variants?.flatMap((variant) =>
                              (variant.versions || []).map((version) => {
                                const existingItem = findExistingCatalogHardware(
                                  catalogItem,
                                  variant,
                                  version
                                );
                                const metaParts = [
                                  version.storage || "",
                                  variant.name &&
                                  variant.name !== catalogItem.name &&
                                  variant.name !== version.name
                                    ? variant.name
                                    : "",
                                ].filter(Boolean);
                                const detailInstanceKey = `all-${catalogItem.id}-${version.id}`;
                                const detailItem =
                                  hardware.find((item) => item.id === existingItem?.id) ||
                                  existingItem;

                                return (
                                  <Fragment key={version.id}>
                                  <div
                                    className={`hardware-console-card hardware-all-version-card ${existingItem ? "is-openable" : ""} ${
                                      selectedHardwareDetail?.instanceKey === detailInstanceKey
                                        ? "active"
                                        : ""
                                    }`}
                                    data-brand={catalogItem.brand}
                                    data-type={catalogItem.type}
                                    onClick={() => {
                                      if (!existingItem) return;
                                      setSelectedHardwareDetail((current) =>
                                        current?.instanceKey === detailInstanceKey
                                          ? null
                                          : { ...existingItem, instanceKey: detailInstanceKey }
                                      );
                                    }}
                                  >
                                    <div className="hardware-image-wrapper">
                                      {version.image ? (
                                        <img
                                          src={version.image}
                                          alt={version.name}
                                          className="hardware-catalog-image"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            setZoomedHardwareImage({
                                              image: version.image,
                                              name: version.name,
                                              x: event.clientX,
                                              y: event.clientY,
                                            });
                                          }}
                                          onError={handleHardwareImageError}
                                        />
                                      ) : (
                                        <div className="hardware-collection-placeholder">
                                          {getHardwareTypeIcon(catalogItem.type)}
                                        </div>
                                      )}
                                    </div>

                                    <div className="hardware-console-info">
                                      <div className="hardware-name">
                                        {version.name}
                                      </div>
                                      {metaParts.length > 0 && (
                                        <div className="hardware-meta">
                                          {metaParts.join(" - ")}
                                        </div>
                                      )}

                                      {existingItem ? (
                                        <div className="hardware-status-badge">
                                          {getHardwareStatusLabel(existingItem.status)}
                                        </div>
                                      ) : (
                                        <div className="hardware-status-actions hardware-all-status-actions">
                                          {[
                                            ["possédé", "Je l'ai"],
                                            ["historique", "Historique"],
                                            ["wishlist", "Wishlist"],
                                          ].map(([status, label]) => (
                                            <button
                                              key={status}
                                              type="button"
                                              onClick={(event) => {
                                                event.stopPropagation();
                                                addFromCatalog(catalogItem, variant, version, status);
                                              }}
                                            >
                                              {label}
                                            </button>
                                          ))}
                                        </div>
                                      )}
                                      {editionButton(catalogItem,variant,version)}
                                    </div>

                                  </div>
                                  {selectedHardwareDetail?.instanceKey === detailInstanceKey &&
                                    detailItem && (
                                      <HardwareDetailModal
                                        item={{
                                          ...detailItem,
                                          instanceKey: detailInstanceKey,
                                        }}
                                        detailRef={hardwareDetailRef}
                                        onClose={() => setSelectedHardwareDetail(null)}
                                        onDeleteHardware={onDeleteHardware}
                                        openedDropdown={openedDropdown}
                                        setOpenedDropdown={setOpenedDropdown}
                                        statusOptions={HARDWARE_STATUS_OPTIONS}
                                        conditionOptions={CONDITION_OPTIONS}
                                        displaySizeOptions={DISPLAY_SIZE_OPTIONS}
                                        onUpdateHardwareStatus={onUpdateHardwareStatus}
                                        onUpdateHardwareSanctuary={onUpdateHardwareSanctuary}
                                        onUpdateHardwareCondition={onUpdateHardwareCondition}
                          onUpdateHardwareAcquiredYear={onUpdateHardwareAcquiredYear}
                                        onUpdateHardwareDisplaySize={onUpdateHardwareDisplaySize}
                                        onUpdateHardwareComponent={onUpdateHardwareComponent}
        onEditHardwareEdition={onUpdateHardwareEdition?(item,edition)=>setHardwareEditionDraft({mode:'edit',item,edition}):undefined}
        onAddHardwareEdition={openEditionFromItem}
                                        onUpdateHardwareRating={onUpdateHardwareRating}
                                        onUpdateHardwareReview={onUpdateHardwareReview}
                                        onToggleHardwareFavorite={onToggleHardwareFavorite}
                                        consoleGameStats={getConsoleGameStats(detailItem)}
                                        getHardwareStatusLabel={getHardwareStatusLabel}
                                      />
                                    )}
                                  </Fragment>
                                );
                              })
                            )}
                          </div>
                        </div>
                      ))}
                    </section>
                  ))}
                </div>
              </div>
            ) : !selectedHardwareBrandView ? (
              <div className="hardware-brand-grid">
                <button
                  type="button"
                  className="hardware-brand-card hardware-all-card"
                  onClick={() => {
                    setShowAllHardwareByBrand(true);
                    setSelectedHardwareBrandView(null);
                    setSelectedHardwareId(null);
                    setSelectedHardwareDetail(null);
                    setReturnToAllHardware(false);
                    setSelectedCatalogVersionId(null);
                    scrollToHardwareArea(hardwareTopRef);
                  }}
                >
                  <span>{allHardwareCatalogLabel}</span>
                  <small>{catalogModelTotal} modèles</small>
                </button>

                {brandList.map((brand) => (
                  <button
                    key={brand}
                    type="button"
                    className="hardware-brand-card"
                    onClick={() => {
                      setSelectedHardwareBrandView(brand);
                      setSelectedHardwareId(null);
                      setSelectedHardwareDetail(null);
                      setSelectedCatalogVersionId(null);
                      scrollToHardwareArea(hardwareTopRef);
                    }}
                  >
                    {getHardwareBrandLogo(brand) && (
                      <img
                        src={getHardwareBrandLogo(brand)}
                        alt={brand}
                        className="hardware-brand-card-logo"
                        onError={(event) => {
                          event.currentTarget.style.display = "none";
                        }}
                      />
                    )}
                    <span>{brand}</span>
                  </button>
                ))}
              </div>
            ) : !selectedHardware ? (
              <div className="hardware-view">
                <button
                  type="button"
                  className="back-btn hardware-catalog-back"
                  onClick={() => {
                    setSelectedHardwareBrandView(null);
                    setSelectedHardwareId(null);
                    scrollToHardwareArea(hardwareTopRef);
                  }}
                >
                  <ArrowLeft size={16} aria-hidden="true" /> Marques
                </button>

                <h3 className="hardware-group-title">
                  {selectedHardwareBrandView}
                </h3>

                <div className="hardware-console-list">
                  {itemsBySelectedBrand.map((catalogItem) => (
                    <button
                      key={catalogItem.id}
                      type="button"
                      className="hardware-console-card"
                      data-brand={catalogItem.brand}
                      data-type={catalogItem.type}
                      onClick={() => openCatalogItem(catalogItem)}
                    >
                      <div className="hardware-image-wrapper">
                        {catalogItem.variants?.[0]?.versions?.[0]?.image ? (
                            <img
                              src={catalogItem.variants?.[0]?.versions?.[0]?.image}
                              alt={catalogItem.name}
                              className="hardware-catalog-image"
                              onError={handleHardwareImageError}
                            />
                        ) : (
                          <div className="hardware-collection-placeholder">
                            {getHardwareTypeIcon(catalogItem.type)}
                          </div>
                        )}
                      </div>

                      <div className="hardware-console-info">
                        <div className="hardware-name">{catalogItem.name}</div>
                        <div className="hardware-meta">
                          {catalogItem.variants?.reduce(
                            (sum, v) => sum + (v.versions?.length || 0),
                            0
                          )} modèles
                        </div>
                      </div>

                      <div className="hardware-console-arrow">›</div>
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              <div className="hardware-view hardware-detail-view">
                <button
                  type="button"
                  className="back-btn hardware-catalog-back"
                  onClick={() => {
                    setSelectedHardwareId(null);
                    if (returnToAllHardware) {
                      setSelectedHardwareBrandView(null);
                      setShowAllHardwareByBrand(true);
                    }
                    scrollToHardwareArea(hardwareTopRef);
                  }}
                >
                  <ArrowLeft size={16} aria-hidden="true" />{" "}
                  {returnToAllHardware ? allHardwareCatalogLabel : selectedHardwareBrandView}
                </button>

                <h3 className="hardware-group-title">
                  {selectedHardware.name}
                </h3>

                {selectedHardware.variants?.map((variant) => (
                  <div key={variant.id} className="hardware-variant-block">
                    <div className="hardware-variant-title">{variant.name}</div>

                    <div className="hardware-catalog-grid">
                      {variant.versions?.map((version) => {
                        const existingItem = findExistingCatalogHardware(
                          selectedHardware,
                          variant,
                          version
                        );

                        const ownedItem =
                          existingItem && hasHardwareStatus(existingItem, "possede")
                            ? existingItem
                            : null;

                        return (
                          <div
                            key={version.id}
                            className={`hardware-catalog-card ${
                              selectedCatalogVersionId === version.id
                                ? "selected-catalog-version"
                                : ""
                            }`}
                            data-type={selectedHardware.type}
                            data-catalog-version-id={version.id}
                          >
                            <div className="hardware-image-wrapper">
                              {version.image ? (
                                <button
                                  type="button"
                                  className="hardware-image-zoom-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setZoomedHardwareImage({
                                      image: version.image,
                                      name: version.name,
                                      x: e.clientX,
                                      y: e.clientY,
                                    });
                                  }}
                                >
                                  <img
                                    src={version.image}
                                    alt={version.name}
                                    className="hardware-catalog-image"
                                    onError={handleHardwareImageError}
                                  />
                                </button>
                              ) : (
                                <div className="hardware-collection-placeholder">
                                  {getHardwareTypeIcon(selectedHardware.type)}
                                </div>
                              )}
                            </div>

                            <div className="hardware-catalog-content">
                              <div className="hardware-name">
                                {version.name}
                              </div>

                              <div className="hardware-meta">
                                {selectedHardware.brand}
                                {version.storage ? ` • ${version.storage}` : ""}
                              </div>

                              {selectedHardware.type === "controller" ? (
                                <div className="hardware-status-actions">
                                  {!ownedItem && (
                                    <button
                                      type="button"
                                      onClick={() =>
                                        addFromCatalog(
                                          selectedHardware,
                                          variant,
                                          version,
                                          "possédé"
                                        )
                                      }
                                    >
                                      J’utilise cette manette
                                    </button>
                                  )}

                                  {ownedItem && (
                                    <div className="hardware-status-badge">
                                      Utilisée
                                    </div>
                                  )}

                                </div>
                              ) : existingItem ? (
                                <div className="hardware-status-badge">
                                  {getHardwareStatusLabel(existingItem.status)}
                                </div>
                              ) : (
                                <div className="hardware-status-actions">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      addFromCatalog(
                                        selectedHardware,
                                        variant,
                                        version,
                                        "possédé"
                                      )
                                    }
                                  >
                                    Je l’ai
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      addFromCatalog(
                                        selectedHardware,
                                        variant,
                                        version,
                                        "historique"
                                      )
                                    }
                                  >
                                    Historique
                                  </button>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      addFromCatalog(
                                        selectedHardware,
                                        variant,
                                        version,
                                        "wishlist"
                                      )
                                    }
                                  >
                                    Wishlist
                                  </button>
                                </div>
                              )}
                              {editionButton(selectedHardware,variant,version)}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>



      <div className="search-panel hardware-collection-panel">
        <div className="hardware-collection-head">
          <h2 className="panel-title">{hardwareTitle}</h2>

          <div className="hardware-collection-filters">
            {hardwareCollectionFilters.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={hardwareCollectionFilter === filter.id ? "active" : ""}
                onClick={() => setHardwareCollectionFilter(filter.id)}
              >
                {filter.label}
              </button>
            ))}
          </div>
        </div>

        <HardwareFilters items={hardware.filter(item=>item.type===hardwareCategory)} catalog={HARDWARE_CATALOG} value={collectionFilters} onChange={setCollectionFilters} count={new Set(visibleCollectionGroups.flatMap(group=>group.items.map(item=>item.id))).size}/>
        {!hasVisibleHardware ? (
          <EmptyState title={Object.keys(EMPTY_HARDWARE_FILTERS).some(key=>collectionFilters[key]!==EMPTY_HARDWARE_FILTERS[key])?'Aucun matériel ne correspond aux filtres':emptyTitle} subtitle={emptySubtitle} />
        ) : (
          <div className="hardware-collection-sections">
            {visibleCollectionGroups.map((group) => (
                <div key={group.id} className="hardware-collection-section">
                  <h3 className="hardware-group-title">{group.title}</h3>

                  <div className="hardware-collection-grid">
                    {group.items.map((item, index) => {
                      const detailInstanceKey = `${group.id}-${item.id}`;

                      return (
                      <Fragment key={detailInstanceKey}>
                      {selectedHardwareDetail?.instanceKey === detailInstanceKey ? (
                        <HardwareDetailModal
                          item={hardware.find((h) => h.id === item.id) || item}
                          detailRef={hardwareDetailRef}
                          onClose={() => setSelectedHardwareDetail(null)}
                          onDeleteHardware={onDeleteHardware}
                          openedDropdown={openedDropdown}
                          setOpenedDropdown={setOpenedDropdown}
                          statusOptions={HARDWARE_STATUS_OPTIONS}
                          conditionOptions={CONDITION_OPTIONS}
                          displaySizeOptions={DISPLAY_SIZE_OPTIONS}
                          onUpdateHardwareStatus={onUpdateHardwareStatus}
                          onUpdateHardwareSanctuary={onUpdateHardwareSanctuary}
                          onUpdateHardwareCondition={onUpdateHardwareCondition}
                          onUpdateHardwareAcquiredYear={onUpdateHardwareAcquiredYear}
                          onUpdateHardwareDisplaySize={onUpdateHardwareDisplaySize}
                          onUpdateHardwareComponent={onUpdateHardwareComponent}
        onEditHardwareEdition={onUpdateHardwareEdition?(item,edition)=>setHardwareEditionDraft({mode:'edit',item,edition}):undefined}
        onAddHardwareEdition={openEditionFromItem}
                          onUpdateHardwareRating={onUpdateHardwareRating}
                          onUpdateHardwareReview={onUpdateHardwareReview}
                          onToggleHardwareFavorite={onToggleHardwareFavorite}
                          consoleGameStats={getConsoleGameStats(
                            hardware.find((h) => h.id === item.id) || item
                          )}
                          getHardwareStatusLabel={getHardwareStatusLabel}
                        />
                      ) : (
                      <div
                        className="hardware-collection-card hardware-card-v3"
                        data-type={item.type}
                        onClick={() => {
                          setSelectedHardwareDetail((current) =>
                            current?.instanceKey === detailInstanceKey
                              ? null
                              : { ...item, instanceKey: detailInstanceKey }
                          );
                        }}
                      >
                        <div className="hardware-card-image-zone">
                          {hardwareDisplayImage(item) ? (
                            <button
                              type="button"
                              className="hardware-image-zoom-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                setZoomedHardwareImage({
                                  image: hardwareDisplayImage(item),
                                  name: item.name,
                                  x: e.clientX,
                                  y: e.clientY,
                                });
                              }}
                            >
                              <img
                                src={hardwareDisplayImage(item)}
                                alt={item.name}
                                className="hardware-collection-image"
                                onError={handleHardwareImageError}
                              />
                            </button>
                          ) : (
                            <div className="hardware-collection-placeholder">
                              {getHardwareTypeIcon(item.type)}
                            </div>
                          )}
                        </div>

                        <div className="hardware-card-main">
                          <div className="hardware-card-head">
                            <div className="hardware-card-title-zone">
                              <div className="hardware-name">
                                {group.id === "controller-ranking" ? `#${index + 1} ${item.name}` : item.name}
                              </div>

                              {hardwareEditionLabel(item)&&<span className="hardware-edition-badge">{hardwareEditionLabel(item)}</span>}
                              <div className="hardware-brand-rating">
                                <span>{item.brand || "Marque inconnue"}</span>
                                <span className="hardware-card-rating">
                                  ⭐{" "}
                                  {formatRating10(
                                    getHardwareAverageRating(item),
                                    "Non noté"
                                  )}
                                </span>
                              </div>
                            </div>

                            <div className="hardware-card-actions">

                              <button
                                type="button"
                                className="delete-btn hardware-delete-floating"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  onDeleteHardware(item.id);
                                }}
                              >
                                ✕
                              </button>
                            </div>
                          </div>

                          <div className="hardware-card-bottom">
                            <div className="hardware-card-badges">
                              {item.acquiredYear && <span className="hardware-card-badge">Obtenu en {item.acquiredYear}</span>}
                              {item.type === "console" && String(item.status || "").toLowerCase().includes("poss") && (
                                <span className="hardware-card-badge">
                                  {CONDITION_OPTIONS.find((option) => option.id === (item.condition || "bon"))?.label || "Bon"}
                                </span>
                              )}

                              <span className="hardware-card-badge strong">
                                {getHardwareStatusLabel(item.status)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                      )}
                      </Fragment>
                      );
                    })}
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>





      {zoomedHardwareImage && createPortal(
        <div
          className="hardware-zoom-backdrop"
          onClick={() => setZoomedHardwareImage(null)}
        >
          <div
            className="hardware-zoom-modal"
            style={{
              "--hardware-zoom-x": `${zoomedHardwareImage.x || window.innerWidth / 2}px`,
              "--hardware-zoom-y": `${zoomedHardwareImage.y || window.innerHeight / 2}px`,
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="hardware-zoom-close"
              onClick={() => setZoomedHardwareImage(null)}
            >
              ✕
            </button>

            <img
              src={zoomedHardwareImage.image}
              alt={zoomedHardwareImage.name}
              className="hardware-zoom-image"
            />

            <div className="hardware-zoom-title">
              {zoomedHardwareImage.name}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

/* ==================== OPTIONS ==================== */

function OptionGroup({
    id,
    title,
    description,
    summary,
    children, isOpen, onToggle, }) {

    return (
      <section className={`options-group options-accordion ${isOpen ? "open" : ""}`}>
        <button
          type="button"
          className="options-group-head"
          onClick={onToggle}
          aria-expanded={isOpen}
        >
          <div>
            <span>{title}</span>
            <p>{description}</p>
          </div>
          <div className="options-group-summary">
            <span>{summary}</span>
            <b aria-hidden="true">v</b>
          </div>
        </button>
        {isOpen && <div className="options-group-body">{children}</div>}
      </section>
    );
  }

function OptionsTab({
  theme,
  level = 1,
  unlockedThemes = [],
  setTheme,
  uiMode,
  setUiMode,
  appOptions,
  onOptionChange,
  socialProfile,
  onProfileChange,
  onProfileVisibilityChange,
  onExportBackup,
  onImportBackup,
  onCaptureBackup,
  onRestoreVersion,
  onResetOptions,
  games = [],
  onRepairGameData,
  onOpenTrial,
  checkpointTrialProgress = DEFAULT_CHECKPOINT_TRIAL_PROGRESS,
  onImportSteamGames,
  onImportXboxGames,
  developerEvents = [],
  onImportNews,
}) {
  const [helpTopic, setHelpTopic] = useState(null);
  const [importReview,setImportReview]=useState(null);
  const [isRepairingGameData, setIsRepairingGameData] = useState(false);
  const [showIntegrityDetails, setShowIntegrityDetails] = useState(false);
  const [steamInput, setSteamInput] = useState(
    appOptions.steamProfile?.profileInput || ""
  );
  const [steamLibrary, setSteamLibrary] = useState(null);
  const [steamWishlist, setSteamWishlist] = useState(null);
  const [steamError, setSteamError] = useState("");
  const [steamWishlistError, setSteamWishlistError] = useState("");
  const [isSteamLoading, setIsSteamLoading] = useState(false);
  const [isSteamImporting, setIsSteamImporting] = useState(false);
  const [isSteamWishlistLoading, setIsSteamWishlistLoading] = useState(false);
  const [isSteamWishlistImporting, setIsSteamWishlistImporting] = useState(false);
  const [xboxSession, setXboxSession] = useState(null);
  const [xboxLibrary, setXboxLibrary] = useState(null);
  const [xboxError, setXboxError] = useState("");
  const [isXboxLoading, setIsXboxLoading] = useState(false);
  const [isXboxSyncing, setIsXboxSyncing] = useState(false);
  const [isXboxImporting, setIsXboxImporting] = useState(false);
  const [connectedServicesExpanded, setConnectedServicesExpanded] = useState(false);
  const [openOptionGroups, setOpenOptionGroups] = useState({
    help: false,
    interface: false,
    behavior: false,
    rating: false,
    services: false,
    development: false,
    data: false,
  });
  const themes = THEME_CATALOG;
  const startTabs = [
    { id: "home", label: "Accueil" },
    { id: "library", label: "Bibliothèque" },
    { id: "arena", label: "L’Arène" },
    { id: "social", label: "Social" },
    { id: "top5", label: "Top 5" },
    { id: "hardware", label: "Matériel" },
  ];
  const afterAddActions = [
    { id: "stay", label: "Rester" },
    { id: "library", label: "Bibliothèque" },
    { id: "detail", label: "Fiche" },
  ];
  const publicSections = {
    ...DEFAULT_PUBLIC_SECTIONS,
    ...(socialProfile.publicSections || {}),
  };
  const dealSources = {
    ...DEFAULT_APP_OPTIONS.dealSources,
    ...(appOptions.dealSources || {}),
  };
  const steamProfile = {
    ...DEFAULT_APP_OPTIONS.steamProfile,
    ...(appOptions.steamProfile || {}),
  };
  const xboxProfile = {
    ...DEFAULT_APP_OPTIONS.xboxProfile,
    ...(appOptions.xboxProfile || {}),
  };
  const steamExistingStats = useMemo(() => {
    const plan=planGameImport(games,steamLibrary?.games||[],"steam");
    return {alreadyPresent:plan.filter(row=>row.type==="update").length,missing:plan.filter(row=>row.type==="add").length};
  }, [games, steamLibrary]);
  const steamWishlistExistingStats = useMemo(() => {
    const plan=planGameImport(games,steamWishlist?.games||[],"steam");
    return {alreadyPresent:plan.filter(row=>row.type==="update").length,missing:plan.filter(row=>row.type==="add").length};
  }, [games, steamWishlist]);
  const xboxExistingStats = useMemo(() => {
    const plan=planGameImport(games,xboxLibrary?.games||[],"xbox");
    return {alreadyPresent:plan.filter(row=>row.type==="update").length,missing:plan.filter(row=>row.type==="add").length};
  }, [games, xboxLibrary]);
  const gameIntegrityIssues = useMemo(
    () => getGameDataIntegrityIssues(games),
    [games]
  );
  const gameIntegrityPreview = gameIntegrityIssues.slice(
    0,
    showIntegrityDetails ? 8 : 3
  );
  const handleRepairGameData = async () => {
    if (!gameIntegrityIssues.length || !onRepairGameData) return;

    setIsRepairingGameData(true);
    try {
      await onRepairGameData(gameIntegrityIssues);
      setShowIntegrityDetails(false);
    } finally {
      setIsRepairingGameData(false);
    }
  };
  const updateDealSource = (source, enabled) => {
    onOptionChange("dealSources", {
      ...dealSources,
      [source]: enabled,
    });
  };
  const updateSteamProfile = (patch) => {
    onOptionChange("steamProfile", {
      ...steamProfile,
      ...patch,
    });
  };
  const updateXboxProfile = (patch) => {
    onOptionChange("xboxProfile", {
      ...xboxProfile,
      ...patch,
    });
  };
  const fetchSteamLibrary = async () => {
    const profileInput = steamInput.trim();

    if (!profileInput) {
      setSteamError("Ajoute ton SteamID64 ou ton URL de profil Steam.");
      return;
    }

    setSteamError("");
    setIsSteamLoading(true);

    try {
      const response = await fetch(
        `/api/steam/owned-games?profile=${encodeURIComponent(profileInput)}`
      );
      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/json")) {
        throw new Error(
          "API Steam non disponible sur ce serveur. Teste via le deploy Cloudflare."
        );
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Connexion Steam indisponible.");
      }

      setSteamLibrary(data);
      updateSteamProfile({
        profileInput,
        steamId: data.steamId || "",
        lastSyncedAt: data.updatedAt || new Date().toISOString(),
        lastGameCount: data.total || data.games?.length || 0,
      });
    } catch (error) {
      setSteamLibrary(null);
      setSteamError(String(error?.message || error));
    } finally {
      setIsSteamLoading(false);
    }
  };
  const importSteamLibrary = async (selectedGames=steamLibrary?.games) => {
    if (!steamLibrary?.games?.length || !onImportSteamGames) return;

    setIsSteamImporting(true);
    setSteamError("");

    try {
      const result = await onImportSteamGames(selectedGames, {
        steamId: steamLibrary.steamId,
      });
      updateSteamProfile({
        profileInput: steamInput.trim(),
        steamId: steamLibrary.steamId || steamProfile.steamId,
        lastSyncedAt: new Date().toISOString(),
        lastGameCount: steamLibrary.total || steamLibrary.games.length,
        lastImportedCount: result?.imported || 0,
      });
    } catch (error) {
      setSteamError(String(error?.message || error));
      return false;
    } finally {
      setIsSteamImporting(false);
    }
  };
  const fetchSteamWishlist = async () => {
    const profileInput = steamInput.trim();

    if (!profileInput) {
      setSteamWishlistError("Ajoute ton SteamID64 ou ton URL de profil Steam.");
      return;
    }

    setSteamWishlistError("");
    setIsSteamWishlistLoading(true);

    try {
      const response = await fetch(
        `/api/steam/wishlist?profile=${encodeURIComponent(profileInput)}`
      );
      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/json")) {
        throw new Error(
          "API Steam non disponible sur ce serveur. Teste via le deploy Cloudflare."
        );
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Wishlist Steam indisponible.");
      }

      setSteamWishlist(data);
      updateSteamProfile({
        profileInput,
        steamId: data.steamId || steamProfile.steamId,
        lastWishlistSyncedAt: data.updatedAt || new Date().toISOString(),
        lastWishlistCount: data.total || data.games?.length || 0,
      });
    } catch (error) {
      setSteamWishlist(null);
      setSteamWishlistError(String(error?.message || error));
    } finally {
      setIsSteamWishlistLoading(false);
    }
  };
  const importSteamWishlist = async (selectedGames=steamWishlist?.games) => {
    if (!steamWishlist?.games?.length || !onImportSteamGames) return;

    setIsSteamWishlistImporting(true);
    setSteamWishlistError("");

    try {
      const result = await onImportSteamGames(selectedGames, {
        steamId: steamWishlist.steamId,
        status: "wishlist",
        source: "steam-wishlist",
      });
      updateSteamProfile({
        profileInput: steamInput.trim(),
        steamId: steamWishlist.steamId || steamProfile.steamId,
        lastWishlistSyncedAt: new Date().toISOString(),
        lastWishlistCount: steamWishlist.total || steamWishlist.games.length,
        lastWishlistImportedCount: result?.imported || 0,
      });
    } catch (error) {
      setSteamWishlistError(String(error?.message || error));
      return false;
    } finally {
      setIsSteamWishlistImporting(false);
    }
  };
  const refreshXboxSession = async () => {
    setIsXboxLoading(true);
    setXboxError("");

    try {
      const response = await fetch("/api/xbox/session");
      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/json")) {
        throw new Error("API Xbox non disponible sur ce serveur. Teste via le deploy Cloudflare.");
      }

      const data = await response.json();
      setXboxSession(data);

      if (data.connected && data.profile) {
        updateXboxProfile({
          connected: true,
          gamertag: data.profile.gamertag || "",
          xuid: data.profile.xuid || "",
          avatar: data.profile.avatar || "",
          connectedAt: data.connectedAt || xboxProfile.connectedAt || new Date().toISOString(),
          lastCheckedAt: new Date().toISOString(),
        });
      } else if (!data.connected && xboxProfile.connected) {
        updateXboxProfile({
          connected: false,
          gamertag: "",
          xuid: "",
          avatar: "",
          connectedAt: "",
          lastCheckedAt: new Date().toISOString(),
        });
      }
    } catch (error) {
      setXboxError(String(error?.message || error));
    } finally {
      setIsXboxLoading(false);
    }
  };
  const connectXbox = () => {
    window.location.href = "/api/xbox/auth/start";
  };
  const disconnectXbox = async () => {
    setIsXboxLoading(true);
    setXboxError("");

    try {
      await fetch("/api/xbox/logout", { method: "POST" });
      setXboxSession({ connected: false });
      updateXboxProfile({
        connected: false,
        gamertag: "",
        xuid: "",
        avatar: "",
        connectedAt: "",
        lastCheckedAt: new Date().toISOString(),
      });
    } catch (error) {
      setXboxError(String(error?.message || error));
    } finally {
      setIsXboxLoading(false);
    }
  };
  const fetchXboxLibrary = async ({append=false}={}) => {
    setIsXboxSyncing(true);
    setXboxError("");

    try {
      const response = await fetch(`/api/xbox/library${append&&xboxLibrary?.continuationToken?`?continuation=${encodeURIComponent(xboxLibrary.continuationToken)}`:""}`);
      const contentType = response.headers.get("content-type") || "";

      if (!contentType.includes("application/json")) {
        throw new Error("API Xbox non disponible sur ce serveur. Teste via le deploy Cloudflare.");
      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.error || "Synchronisation Xbox indisponible.");
      }

      const combined=append?mergeXboxPages(xboxLibrary,data):data;
      setXboxLibrary(combined);
      updateXboxProfile({
        connected: true,
        gamertag: data.profile?.gamertag || xboxProfile.gamertag,
        xuid: data.profile?.xuid || xboxProfile.xuid,
        avatar: data.profile?.avatar || xboxProfile.avatar,
        lastSyncedAt: data.updatedAt || new Date().toISOString(),
        lastGameCount: data.total || data.games?.length || 0,
      });
    } catch (error) {
      if(!append)setXboxLibrary(null);
      setXboxError(String(error?.message || error));
    } finally {
      setIsXboxSyncing(false);
    }
  };
  const importXboxLibrary = async (selectedGames=xboxLibrary?.games) => {
    if (!xboxLibrary?.games?.length || !onImportXboxGames) return;

    setIsXboxImporting(true);
    setXboxError("");

    try {
      const result = await onImportXboxGames(selectedGames, {
        xuid: xboxLibrary.profile?.xuid || xboxProfile.xuid,
      });
      updateXboxProfile({
        connected: true,
        gamertag: xboxLibrary.profile?.gamertag || xboxProfile.gamertag,
        xuid: xboxLibrary.profile?.xuid || xboxProfile.xuid,
        avatar: xboxLibrary.profile?.avatar || xboxProfile.avatar,
        lastSyncedAt: new Date().toISOString(),
        lastGameCount: xboxLibrary.total || xboxLibrary.games.length,
        lastImportedCount: result?.imported || 0,
      });
    } catch (error) {
      setXboxError(String(error?.message || error));
      return false;
    } finally {
      setIsXboxImporting(false);
    }
  };
  const updatePublicSection = (section, enabled) => {
    onProfileChange("publicSections", {
      ...publicSections,
      [section]: enabled,
    });
  };
  useEffect(() => {
    refreshXboxSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    const handleFocus = () => {
      refreshXboxSession();
    };

    window.addEventListener("focus", handleFocus);
    return () => window.removeEventListener("focus", handleFocus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!helpTopic) return undefined;

    document.body.classList.add("modal-open");

    return () => {
      document.body.classList.remove("modal-open");
    };
  }, [helpTopic]);
  const helpTopics = {
    icon: {
      title: "Icône de l'app",
      lead: "Change l'icône utilisée par le navigateur et la PWA.",
      bullets: [
        "Sur iPhone, l'icône déjà ajoutée à l'écran d'accueil ne se met pas toujours à jour seule.",
        "Si elle ne change pas, supprime le raccourci puis ajoute à nouveau l'app à l'écran d'accueil.",
      ],
    },
    animations: {
      title: "Transition d'onglet",
      lead: "Ce réglage concerne uniquement l'animation pixel quand tu changes d'onglet.",
      bullets: [
        "Activée garde l'effet pixel entre deux onglets.",
        "Désactivée change d'onglet plus directement, utile si l'animation paraît lente.",
      ],
    },
    navigation: {
      title: "Démarrage",
      lead: "Décide où l'app s'ouvre quand tu la relances.",
      bullets: [
        "Dernier onglet reprend automatiquement l'endroit où tu t'étais arrêté.",
        "Onglet fixe ouvre toujours la section choisie.",
        "Accueil reste le choix le plus neutre si tu veux un point de départ stable.",
      ],
    },
    ergonomics: {
      title: "Ergonomie",
      lead: "Règle les comportements qui changent vraiment le confort d'utilisation au quotidien.",
      bullets: [
        "Confirmation protège les suppressions de jeux et de matériel.",
        "Après ajout décide si la recherche reste ouverte, si tu vas à la bibliothèque ou si la fiche du jeu s'ouvre directement.",
      ],
    },
    publicProfile: {
      title: "Profil public",
      lead: "Contrôle ce que les autres peuvent voir de ton profil.",
      bullets: [
        "Privé garde le profil uniquement dans ton app.",
        "Public permet de partager ton profil avec ton identifiant.",
        "Les blocs visibles masquent ou affichent photos, matériel, activité et jeux fondateurs.",
      ],
    },
    rating: {
      title: "Notation",
      lead: "Change l'affichage des notes et le comportement des fiches après notation.",
      bullets: [
        "Le calcul interne reste toujours sur 10 pour éviter les erreurs.",
        "Les étoiles et le mode compact sont uniquement des affichages.",
        "Après une note permet de rester dans la fiche ou de revenir directement à la liste.",
      ],
    },
    deals: {
      title: "Promos",
      lead: "Filtre les boutiques et la région utilisées pour les promotions.",
      bullets: [
        "Steam et Epic peuvent être chargés automatiquement quand leurs sources répondent.",
        "PlayStation et Xbox ouvrent des vitrines officielles propres en attendant une API fiable.",
        "La région change les prix ou les pages quand la boutique le permet.",
      ],
    },
    steam: {
      title: "Synchro Steam",
      lead: "Importe tes jeux Steam dans Checkpoint a partir de ton profil public.",
      bullets: [
        "Colle l'URL complete de ton profil Steam ou ton SteamID64.",
        "Ton profil et les details de jeux doivent etre visibles publiquement dans Steam.",
        "Checkpoint ajoute les jeux manquants et synchronise ceux deja presents sans demander ton mot de passe.",
      ],
    },
    xbox: {
      title: "Connexion Xbox",
      lead: "Relie ton compte Microsoft/Xbox sans confier ton mot de passe a Checkpoint.",
      bullets: [
        "La connexion passe par la page Microsoft officielle et revient ensuite dans l'app.",
        "Checkpoint recupere uniquement ton profil Xbox utile : gamertag, avatar et identifiant Xbox.",
        "La bibliotheque Xbox complete n'a pas de flux web public fiable comme Steam. On prepare donc la connexion proprement avant de brancher l'import quand la source est sure.",
      ],
    },
    psn: {
      title: "Connexion PSN",
      lead: "PlayStation ne propose pas d'equivalent public simple et officiel pour importer une bibliotheque PSN.",
      bullets: [
        "Les solutions visibles en ligne utilisent souvent des API non officielles ou des cookies de session PSN.",
        "Checkpoint garde donc PSN en attente plutot que de te demander une manipulation fragile.",
        "On pourra brancher PlayStation proprement si Sony ouvre une source autorisee ou si on trouve une integration partenaire stable.",
      ],
    },
    data: {
      title: "Données",
      lead: "Garde une porte de secours pour tes réglages et vérifie les anciennes données.",
      bullets: [
        "Exporter crée une sauvegarde JSON locale.",
        "Importer restaure les options et le profil social depuis une sauvegarde.",
        "Reset options remet uniquement les réglages d'options par défaut.",
        "Le diagnostic aligne statut, progression et jeu terminé pour éviter les compteurs faux.",
      ],
    },
  };
  const SectionTitle = ({ title, help }) => (
    <div className="option-section-headline">
      <h3>{title}</h3>
      {help && (
        <button
          type="button"
          className="option-help-btn"
          onClick={() => setHelpTopic(helpTopics[help])}
          aria-label={`Aide ${title}`}
        >
          ?
        </button>
      )}
    </div>
  );
  const activeThemeLabel =
    theme === CHECKPOINT_ULTIMATE_THEME ? "Checkpoint Ultime" : theme === CHECKPOINT_PREMIUM_THEME ? "Checkpoint Premium" : themes.find((item) => item.id === theme)?.label || "Theme actif";
  const helpModal = helpTopic
    ? createPortal(
        <div className="option-help-backdrop" onClick={() => setHelpTopic(null)}>
          <div className="option-help-modal" onClick={(event) => event.stopPropagation()}>
            <div className="option-help-modal-head">
              <h3>{helpTopic.title}</h3>
              <button type="button" onClick={() => setHelpTopic(null)}>
                Fermer
              </button>
            </div>
            <p className="option-help-lead">{helpTopic.lead}</p>
            {helpTopic.bullets?.length > 0 && (
              <div className="option-help-points">
                {helpTopic.bullets.map((item) => (
                  <div key={item} className="option-help-point">
                    <span />
                    <p>{item}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>,
        document.body
      )
    : null;

  return (
    <div className="progression-stack options-tab">
      <TesterFeedback onSubmit={sendTesterFeedback} />
      <div className="search-panel options-panel">
        <OptionGroup id="help" isOpen={!!openOptionGroups.help} onToggle={() => setOpenOptionGroups(current => ({...current, help:!current.help}))}
          title="Aide et tutoriels" description="Un guide pour chaque onglet, à revoir quand tu veux." summary={`${Object.keys(TAB_TUTORIALS).length} guides`}>
          <TutorialLibrary/>
        </OptionGroup>
        <OptionGroup id="releaseNotes" isOpen={!!openOptionGroups.releaseNotes} onToggle={()=>setOpenOptionGroups(current=>({...current,releaseNotes:!current.releaseNotes}))} title="Nouveautés" description="Les derniers ajouts, les améliorations et leurs guides." summary="Historique des mises à jour"><ReleaseNotes/></OptionGroup>
        <OptionGroup id="notifications" isOpen={!!openOptionGroups.notifications} onToggle={()=>setOpenOptionGroups(current=>({...current,notifications:!current.notifications}))} title="Notifications" description="Choisis les informations affichées dans la cloche." summary="Dans l’appli">
          <NotificationPreferences value={appOptions.notifications} onChange={value=>onOptionChange("notifications",value)}/>
        </OptionGroup>
        <OptionGroup id="interface" isOpen={!!openOptionGroups.interface} onToggle={() => setOpenOptionGroups((current) => ({ ...current, interface: !current.interface }))}
          title="Interface"
          description="Ce qui change l'identité visuelle et les retours de l'app."
          summary={`${activeThemeLabel} · ${uiMode === "modern" ? "Transitions" : "Sans transition"}`}
        >

          <div className="option-section">
            <SectionTitle title="Thème" />

            <p className="theme-progression-note">Un thème à débloquer tous les 2 niveaux. Checkpoint est disponible dès le départ ; Premium au niveau 50 et Ultime au niveau 100.</p>
            <div className="option-pill-grid themes">
              <button type="button" className={`option-pill checkpoint-signature-choice${theme === "theme-checkpoint" ? " active" : ""}`} onClick={() => setTheme("theme-checkpoint")} aria-pressed={theme === "theme-checkpoint"}>
                <CheckpointMark/><span>Checkpoint</span>
              </button>
              <CheckpointPremiumChoice theme={theme} level={level} unlockedThemes={unlockedThemes} onSelect={setTheme}/>
              <CheckpointPremiumChoice rewardTheme={CHECKPOINT_ULTIMATE_THEME} theme={theme} level={level} unlockedThemes={unlockedThemes} onSelect={setTheme}/>
              {themes.filter(item => item.id !== "theme-checkpoint").map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`option-pill theme-${item.id} ${
                    theme === item.id ? "active" : ""
                  }`}
                  disabled={!canUseTheme(item.id, unlockedThemes)}
                  aria-pressed={theme === item.id}
                  aria-label={`${item.label} — ${canUseTheme(item.id,unlockedThemes)?"débloqué":`disponible au niveau ${getThemeUnlockLevel(item.id)}`}`}
                  onClick={() => {if(canUseTheme(item.id,unlockedThemes))setTheme(item.id);}}
                >
                  <span>{item.label}</span>{!canUseTheme(item.id,unlockedThemes)&&<small className="theme-unlock-level"><Lock size={12} aria-hidden="true"/>Niv. {getThemeUnlockLevel(item.id)}</small>}
                </button>
              ))}
            </div>
          </div>

          <div className="option-section option-section-split">
            <div className="option-setting-card">
              <div>
                <SectionTitle title="Transition d'onglet" help="animations" />
                <span>Choisis les effets qui conviennent à ton appareil.</span>
              </div>
              <TransitionSettings mode={uiMode === "reduced" ? "off" : appOptions.transitionMotion} onChange={mode => {
                setUiMode(mode === "off" ? "reduced" : "modern");
                if (mode !== "off") onOptionChange("transitionMotion", mode);
              }} />
            </div>

            <div className="option-setting-card option-header-setting">
              <div>
                <strong>En-tête des onglets</strong>
                <span>Choisis les informations affichées sous le logo. L’accueil et le profil gardent leur présentation dédiée.</span>
              </div>
              <div className="option-header-choices" role="group" aria-label="En-tête des onglets">
                {[
                  { id: "standard", label: "Complet", detail: "Niveau, XP et statistiques de ta collection." },
                  { id: "compact", label: "Progression", detail: "Ton niveau et une barre d’XP discrète." },
                  { id: "minimal", label: "Essentiel", detail: "Le logo, les notifications, puis le contenu." },
                ].map((choice) => <button key={choice.id} type="button" className={"option-header-choice " + (appOptions.headerMode === choice.id ? "active" : "")} aria-pressed={appOptions.headerMode === choice.id} onClick={() => onOptionChange("headerMode", choice.id)}>
                  <span className={"option-header-preview preview-" + choice.id} aria-hidden="true"><i /><i /><i /></span>
                  <strong className="option-header-choice-label">{choice.label}<span className="option-choice-indicator" aria-hidden="true">{appOptions.headerMode === choice.id && <Check size={14} />}</span></strong><small>{choice.detail}</small>
                </button>)}
              </div>
              <span className="option-header-note">Les statistiques restent masquées dans Actu, Sorties, Promos, Live, Sanctuaire et Top 5.</span>
            </div>

            <SoundSettings value={appOptions.sound} onChange={(value) => onOptionChange("sound", value)} />
          </div>
        </OptionGroup>

        <ContentPreferences value={appOptions} onChange={onOptionChange}/>

        <OptionGroup id="behavior" isOpen={!!openOptionGroups.behavior} onToggle={() => setOpenOptionGroups((current) => ({ ...current, behavior: !current.behavior }))}
          title="Comportement"
          description="Les choix qui évitent des gestes inutiles ou des erreurs."
          summary={appOptions.rememberLastTab ? "Dernier onglet" : "Onglet fixe"}
        >

          <div className="option-section">
            <SectionTitle title="Démarrage" help="navigation" />

            <div className="option-setting-card">
              <div>
                <strong>Ouverture de l'app</strong>
                <span>Reprendre où tu t'étais arrêté ou ouvrir toujours le même onglet.</span>
              </div>
              <div className="option-pill-grid two startup-primary">
                <button
                  type="button"
                  className={`option-pill ${appOptions.rememberLastTab ? "active" : ""}`}
                  onClick={() => onOptionChange("rememberLastTab", true)}
                >
                  Dernier onglet
                </button>
                <button
                  type="button"
                  className={`option-pill ${!appOptions.rememberLastTab ? "active" : ""}`}
                  onClick={() => onOptionChange("rememberLastTab", false)}
                >
                  Onglet fixe
                </button>
              </div>
            </div>

            {!appOptions.rememberLastTab && (
              <div className="option-pill-grid compact option-subgrid startup-target-grid">
                {startTabs.filter(tab=>!appOptions.hiddenTabs?.includes(tab.id)).map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`option-pill ${appOptions.startTab === tab.id ? "active" : ""}`}
                    onClick={() => onOptionChange("startTab", tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="option-section">
            <SectionTitle title="Ergonomie" help="ergonomics" />

            <div className="option-setting-card">
              <div>
                <strong>Navigation mobile</strong>
                <span>Confort agrandit la barre du bas et les zones tactiles sur iPhone.</span>
              </div>
              <div className="option-pill-grid two compact">
                <button
                  type="button"
                  className={`option-pill ${appOptions.mobileNavMode !== "comfort" ? "active" : ""}`}
                  onClick={() => onOptionChange("mobileNavMode", "standard")}
                >
                  Standard
                </button>
                <button
                  type="button"
                  className={`option-pill ${appOptions.mobileNavMode === "comfort" ? "active" : ""}`}
                  onClick={() => onOptionChange("mobileNavMode", "comfort")}
                >
                  Confort
                </button>
              </div>
            </div>

            <div className="option-setting-card">
              <div>
                <strong>Suppressions</strong>
                <span>Demander une validation avant de supprimer un jeu ou un matériel.</span>
              </div>
              <div className="option-pill-grid two compact">
                <button
                  type="button"
                  className={`option-pill ${appOptions.confirmDangerActions ? "active" : ""}`}
                  onClick={() => onOptionChange("confirmDangerActions", true)}
                >
                  Confirmer
                </button>
                <button
                  type="button"
                  className={`option-pill ${!appOptions.confirmDangerActions ? "active" : ""}`}
                  onClick={() => onOptionChange("confirmDangerActions", false)}
                >
                  Direct
                </button>
              </div>
            </div>

            <div className="option-setting-card">
              <div>
                <strong>Après ajout d'un jeu</strong>
                <span>Choisir ce que fait l'app quand tu ajoutes un résultat de recherche.</span>
              </div>
              <div className="option-pill-grid three compact">
                {afterAddActions.map((action) => (
                  <button
                    key={action.id}
                    type="button"
                    className={`option-pill ${
                      appOptions.afterAddAction === action.id ? "active" : ""
                    }`}
                    onClick={() => onOptionChange("afterAddAction", action.id)}
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </OptionGroup>

        <OptionGroup id="rating" isOpen={!!openOptionGroups.rating} onToggle={() => setOpenOptionGroups((current) => ({ ...current, rating: !current.rating }))}
          title="Notation"
          description="Les règles d'affichage et le comportement des fiches quand tu notes."
          summary={appOptions.afterRatingAction === "list" ? "Retour liste" : "Rester sur fiche"}
        >
          <div className="option-section option-section-split">
            <div className="option-setting-card">
              <div>
                <SectionTitle title="Affichage des notes" help="rating" />
                <span>Le calcul reste sur 10 partout. Ici tu choisis seulement la façon de l'afficher.</span>
              </div>
              <div className="option-pill-grid three compact rating-display-grid">
                <button
                  type="button"
                  className={`option-pill ${appOptions.ratingDisplay === "number" ? "active" : ""}`}
                  onClick={() => onOptionChange("ratingDisplay", "number")}
                >
                  8.5/10
                </button>
                <button
                  type="button"
                  className={`option-pill ${appOptions.ratingDisplay === "stars" ? "active" : ""}`}
                  onClick={() => onOptionChange("ratingDisplay", "stars")}
                >
                  Étoiles
                </button>
                <button
                  type="button"
                  className={`option-pill ${appOptions.ratingDisplay === "compact" ? "active" : ""}`}
                  onClick={() => onOptionChange("ratingDisplay", "compact")}
                >
                  Compact
                </button>
              </div>
            </div>

            <div className="option-setting-card">
              <div>
                <strong>Après une note</strong>
                <span>Décide si la fiche reste ouverte ou si elle revient à la liste après validation.</span>
              </div>
              <div className="option-pill-grid two compact rating-flow-grid">
                <button
                  type="button"
                  className={`option-pill ${appOptions.afterRatingAction !== "list" ? "active" : ""}`}
                  onClick={() => onOptionChange("afterRatingAction", "stay")}
                >
                  Rester sur la fiche
                </button>
                <button
                  type="button"
                  className={`option-pill ${appOptions.afterRatingAction === "list" ? "active" : ""}`}
                  onClick={() => onOptionChange("afterRatingAction", "list")}
                >
                  Retour liste
                </button>
              </div>
            </div>
          </div>
        </OptionGroup>

        <OptionGroup id="services" isOpen={!!openOptionGroups.services} onToggle={() => setOpenOptionGroups((current) => ({ ...current, services: !current.services }))}
          title="Profil & services"
          description="Profil public, comptes liés et sources externes."
          summary={`${socialProfile.visibility === "public" ? "Public" : socialProfile.visibility === "friends" ? "Amis uniquement" : "Privé"} · Steam ${steamProfile.steamId ? "lié" : "off"}`}
        >

          <AccountOptions />

          <div className="option-section option-section-split">
            <div className="option-setting-card">
              <div>
                <SectionTitle title="Profil public" help="publicProfile" />
                <span>Choisis si ton profil peut être partagé ou reste seulement visible par toi.</span>
              </div>
              <ProfileVisibilityChoices value={socialProfile.visibility} onChange={onProfileVisibilityChange}/>
            </div>

            <div className="option-setting-card">
              <div>
                <strong>Sections visibles</strong>
                <span>Active uniquement les blocs que tu veux montrer dans l'aperçu public.</span>
              </div>
              <div className="option-pill-grid compact public-section-grid">
                {[
                  ["photos", "Photos"],
                  ["essential", "Essentiel"],
                  ["identityGames", "Jeux fondateurs"],
                  ["hardware", "Matériel"],
                  ["activity", "Activité"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`option-pill ${publicSections[id] ? "active" : ""}`}
                    onClick={() => updatePublicSection(id, !publicSections[id])}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="option-section option-section-split">
            <div className="option-setting-card">
              <div>
                <SectionTitle title="Promos" help="deals" />
                <span>Choisis la région utilisée pour récupérer les prix et offres affichées.</span>
              </div>
              <div className="option-pill-grid three compact deal-region-grid">
                {["FR", "EU", "US"].map((region) => (
                  <button
                    key={region}
                    type="button"
                    className={`option-pill ${appOptions.dealRegion === region ? "active" : ""}`}
                    onClick={() => onOptionChange("dealRegion", region)}
                  >
                    {region}
                  </button>
                ))}
              </div>
            </div>

            <div className="option-setting-card">
              <div>
                <strong>Sources des promos</strong>
                <span>Garde uniquement les boutiques que tu veux voir dans l'onglet Promos.</span>
              </div>
              <div className="option-pill-grid three compact deal-source-grid">
                {[
                  ["steam", "Steam"],
                  ["epic", "Epic"],
                  ["psn", "PlayStation"],
                  ["xbox", "Xbox"],
                ].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    className={`option-pill ${dealSources[id] ? "active" : ""}`}
                    onClick={() => updateDealSource(id, !dealSources[id])}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="option-section">
            <div className="option-setting-card connected-services-summary">
              <div>
                <SectionTitle title="Services connectes" />
                <span>
                  Steam, Xbox et les futures connexions restent accessibles sans encombrer les Options.
                </span>
              </div>
              <div className="connected-services-grid">
                <div className="connected-service-pill">
                  <strong>Steam</strong>
                  <span>
                    {steamProfile.steamId
                      ? `${steamProfile.lastGameCount || 0} jeux suivis`
                      : "Non configure"}
                  </span>
                </div>
                <div className="connected-service-pill">
                  <strong>Xbox</strong>
                  <span>
                    {xboxProfile.connected
                      ? xboxProfile.gamertag || "Connecte"
                      : "Non connecte"}
                  </span>
                </div>
                <div className="connected-service-pill disabled">
                  <strong>PSN</strong>
                  <span>A l'etude</span>
                </div>
              </div>
              <button
                type="button"
                className="option-pill connected-services-toggle"
                onClick={() => setConnectedServicesExpanded((value) => !value)}
              >
                {connectedServicesExpanded ? "Masquer les services" : "Gerer les services"}
              </button>
            </div>

            {connectedServicesExpanded && (
              <>
            <div className="option-setting-card option-setting-card-featured steam-sync-card">
              <div>
                <SectionTitle title="Synchronisation Steam" help="steam" />
                <span>
                  Relie ton profil Steam public pour importer tes jeux PC dans la bibliotheque.
                </span>
              </div>

              <div className="steam-sync-hints">
                <span>Profil public requis</span>
                <span>Mot de passe jamais demande</span>
                <span>Anti-doublons automatique</span>
              </div>

              <div className="steam-sync-form">
                <input
                  type="text"
                  value={steamInput}
                  onChange={(event) => setSteamInput(event.target.value)}
                  placeholder="URL Steam complete ou SteamID64"
                />
                <button
                  type="button"
                  className="option-pill steam-sync-primary"
                  onClick={fetchSteamLibrary}
                  disabled={isSteamLoading}
                >
                  {isSteamLoading ? "Analyse..." : "Analyser"}
                </button>
              </div>

              {steamError && (
                <div className="steam-sync-error">
                  {steamError}
                  <small>
                    Astuce : le pseudo Steam visible ne suffit pas toujours. Le plus fiable est
                    l'URL de ton profil Steam ou le SteamID64.
                  </small>
                </div>
              )}

              {(steamLibrary || steamProfile.steamId) && (
                <div className="steam-sync-status">
                  <div>
                    <strong>{steamLibrary?.total || steamProfile.lastGameCount || 0}</strong>
                    <span>jeux Steam trouves</span>
                  </div>
                  <div>
                    <strong>{steamExistingStats.alreadyPresent}</strong>
                    <span>deja dans Checkpoint</span>
                  </div>
                  <div>
                    <strong>{steamExistingStats.missing}</strong>
                    <span>a importer</span>
                  </div>
                </div>
              )}

              {steamLibrary?.games?.length > 0 && (
                <>
                  <div className="steam-preview-list">
                    {planGameImport(games,steamLibrary.games,"steam").filter(row=>row.type!=="skip").slice(0,5).map(({item:game,index}) => (
                      <div key={`steamLibrary-${game.steamAppId}-${index}`} className="steam-preview-row">
                        <img src={game.image} alt="" loading="lazy" />
                        <div>
                          <strong>{game.name}</strong>
                          <span>{game.playtimeForever==null?"Temps non communiqué":`${formatSteamPlaytime(game.playtimeForever)} sur Steam`}</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="option-pill steam-import-action"
                    onClick={()=>setImportReview({kind:"steam",provider:"steam",items:steamLibrary.games,title:"Vérifier l’import Steam"})}
                    disabled={isSteamImporting}
                  >
                    {isSteamImporting
                      ? "Import en cours..."
                      : steamExistingStats.missing
                        ? `Importer ${steamExistingStats.missing} jeu${steamExistingStats.missing > 1 ? "x" : ""}`
                        : "Vérifier et synchroniser"}
                  </button>
                </>
              )}

              <div className="steam-sync-subsection">
                <div>
                  <strong>Wishlist Steam</strong>
                  <span>
                    Importe les jeux de ta wishlist Steam directement en statut Wishlist.
                  </span>
                </div>
                <button
                  type="button"
                  className="option-pill steam-sync-secondary"
                  onClick={fetchSteamWishlist}
                  disabled={isSteamWishlistLoading}
                >
                  {isSteamWishlistLoading ? "Analyse..." : "Analyser la wishlist"}
                </button>
              </div>

              {steamWishlistError && (
                <div className="steam-sync-error">
                  {steamWishlistError}
                  <small>
                    Steam ne renvoie la wishlist que si elle est publique. Tu peux garder ta
                    bibliotheque publique et laisser le reste prive si tu preferes.
                  </small>
                </div>
              )}

              {(steamWishlist || steamProfile.lastWishlistCount > 0) && (
                <div className="steam-sync-status">
                  <div>
                    <strong>{steamWishlist?.total || steamProfile.lastWishlistCount || 0}</strong>
                    <span>jeux en wishlist</span>
                  </div>
                  <div>
                    <strong>{steamWishlistExistingStats.alreadyPresent}</strong>
                    <span>deja dans Checkpoint</span>
                  </div>
                  <div>
                    <strong>{steamWishlistExistingStats.missing}</strong>
                    <span>a ajouter</span>
                  </div>
                </div>
              )}

              {steamWishlist?.games?.length > 0 && (
                <>
                  <div className="steam-preview-list">
                    {planGameImport(games,steamWishlist.games,"steam").filter(row=>row.type!=="skip").slice(0,5).map(({item:game,index}) => (
                      <div key={`steamWishlist-${game.steamAppId}-${index}`} className="steam-preview-row">
                        <img src={game.image} alt="" loading="lazy" />
                        <div>
                          <strong>{game.name}</strong>
                          <span>
                            {game.steamDiscountPercent
                              ? `-${game.steamDiscountPercent}% sur Steam`
                              : "Wishlist Steam"}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>

                  <button
                    type="button"
                    className="option-pill steam-import-action"
                    onClick={()=>setImportReview({kind:"wishlist",provider:"steam",items:steamWishlist.games,title:"Vérifier la wishlist Steam"})}
                    disabled={isSteamWishlistImporting}
                  >
                    {isSteamWishlistImporting
                      ? "Import en cours..."
                      : steamWishlistExistingStats.missing
                        ? `Importer ${steamWishlistExistingStats.missing} souhait${steamWishlistExistingStats.missing > 1 ? "s" : ""}`
                        : "Vérifier et synchroniser"}
                  </button>
                </>
              )}
            </div>

            <div className="option-setting-card option-setting-card-featured xbox-connect-card">
              <div>
                <SectionTitle title="Connexion Xbox" help="xbox" />
                <span>
                  Relie ton profil Xbox pour preparer une synchro propre avec l'ecosysteme Microsoft.
                </span>
              </div>

              <div className="steam-sync-hints xbox-sync-hints">
                <span>Connexion Microsoft officielle</span>
                <span>Profil Xbox detecte</span>
                <span>Bibliotheque en attente d'une source fiable</span>
              </div>

              {(xboxSession?.connected || xboxProfile.connected) ? (
                <div className="xbox-profile-card">
                  {xboxSession?.profile?.avatar || xboxProfile.avatar ? (
                    <img
                      src={xboxSession?.profile?.avatar || xboxProfile.avatar}
                      alt=""
                      className="xbox-avatar"
                      loading="lazy"
                    />
                  ) : (
                    <div className="xbox-avatar xbox-avatar-fallback">XB</div>
                  )}
                  <div>
                    <strong>{xboxSession?.profile?.gamertag || xboxProfile.gamertag || "Profil Xbox"}</strong>
                    <span>{xboxSession?.profile?.xuid || xboxProfile.xuid || "XUID connecte"}</span>
                  </div>
                </div>
              ) : (
                <div className="xbox-status-note">
                  <strong>Aucun compte Xbox connecte</strong>
                  <span>Connecte Microsoft pour autoriser Checkpoint a reconnaitre ton profil Xbox.</span>
                </div>
              )}

              {xboxSession?.message && (
                <div className="xbox-status-note subtle">
                  <strong>Etat de la synchro</strong>
                  <span>{xboxSession.message}</span>
                </div>
              )}

              {(xboxLibrary || xboxProfile.lastGameCount > 0) && (
                <div className="steam-sync-status">
                  <div>
                    <strong>{xboxLibrary?.total || xboxProfile.lastGameCount || 0}</strong>
                    <span>titres Xbox detectes</span>
                  </div>
                  <div>
                    <strong>{xboxExistingStats.alreadyPresent}</strong>
                    <span>deja dans Checkpoint</span>
                  </div>
                  <div>
                    <strong>{xboxExistingStats.missing}</strong>
                    <span>a importer</span>
                  </div>
                </div>
              )}

              {xboxLibrary?.continuationToken&&<div className="steam-sync-note"><p>{xboxLibrary.games.length} titres chargés. Il reste une partie de l’historique à consulter.</p><button type="button" className="option-pill" disabled={isXboxSyncing||isXboxImporting} onClick={()=>fetchXboxLibrary({append:true})}>{isXboxSyncing?'Chargement…':'Charger la suite des titres Xbox'}</button></div>}
              {xboxLibrary?.note && (
                <div className="xbox-status-note subtle">
                  <strong>Source Xbox</strong>
                  <span>{xboxLibrary.note}</span>
                </div>
              )}

              {xboxLibrary?.games?.length > 0 && (
                <div className="steam-preview-list">
                  {planGameImport(games,xboxLibrary.games,"xbox").filter(row=>row.type!=="skip").slice(0,5).map(({item:game,index}) => (
                    <div key={`xboxLibrary-${game.xboxTitleId}-${index}`} className="steam-preview-row">
                      {game.image ? (
                        <img src={game.image} alt="" loading="lazy" />
                      ) : (
                        <div className="xbox-preview-fallback">XB</div>
                      )}
                      <div>
                        <strong>{game.name}</strong>
                        <span>
                          {game.xboxCurrentGamerscore || game.xboxMaxGamerscore
                            ? `${game.xboxCurrentGamerscore}/${game.xboxMaxGamerscore} G`
                            : "Titre detecte par Xbox"}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {xboxError && (
                <div className="steam-sync-error">
                  {xboxError}
                  <small>
                    Si tu testes en local, utilise le deploy Cloudflare : le callback Microsoft doit
                    pointer vers une URL publique.
                  </small>
                </div>
              )}

              <div className="xbox-connect-actions">
                {(xboxSession?.connected || xboxProfile.connected) ? (
                  <>
                    <button
                      type="button"
                      className="option-pill"
                      onClick={refreshXboxSession}
                      disabled={isXboxLoading}
                    >
                      {isXboxLoading ? "Verification..." : "Actualiser"}
                    </button>
                    <button
                      type="button"
                      className="option-pill xbox-disconnect-action"
                      onClick={disconnectXbox}
                      disabled={isXboxLoading}
                    >
                      Deconnecter
                    </button>
                    <button
                      type="button"
                      className="option-pill steam-sync-primary xbox-connect-action"
                      onClick={fetchXboxLibrary}
                      disabled={isXboxSyncing}
                    >
                      {isXboxSyncing ? "Analyse Xbox..." : "Analyser les titres"}
                    </button>
                    {xboxLibrary?.games?.length > 0 && (
                      <button
                        type="button"
                        className="option-pill steam-import-action"
                        onClick={()=>setImportReview({kind:"xbox",provider:"xbox",items:xboxLibrary.games,title:"Vérifier l’import Xbox"})}
                        disabled={isXboxImporting}
                      >
                        {isXboxImporting
                          ? "Import en cours..."
                          : xboxExistingStats.missing
                            ? `Importer ${xboxExistingStats.missing} titre${xboxExistingStats.missing > 1 ? "s" : ""}`
                            : "Vérifier et synchroniser"}
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    type="button"
                    className="option-pill steam-sync-primary xbox-connect-action"
                    onClick={connectXbox}
                    disabled={isXboxLoading}
                  >
                    {isXboxLoading ? "Verification..." : "Connecter Xbox"}
                  </button>
                )}
              </div>
            </div>

            <div className="option-setting-card psn-connect-card">
              <div>
                <SectionTitle title="Connexion PSN" help="psn" />
                <span>
                  PlayStation reste volontairement en attente tant qu'on n'a pas de source officielle
                  fiable pour la bibliotheque.
                </span>
              </div>
              <div className="xbox-status-note subtle">
                <strong>Pas de connexion fragile</strong>
                <span>
                  Les methodes actuelles demandent souvent un cookie PSN ou une API non documentee.
                  On garde l'emplacement pret, mais on ne met pas ton compte dans un flux douteux.
                </span>
              </div>
            </div>
              </>
            )}
          </div>

        </OptionGroup>

        {auth.currentUser?.uid === ownerUid && <OptionGroup id="development" isOpen={!!openOptionGroups.development} onToggle={() => setOpenOptionGroups((current) => ({ ...current, development: !current.development }))}
          title="Accès développeur"
          description="Tes outils de test, les prochains lives et les retours des testeurs."
          summary="Tests · Lives · Retours"
        >
          <DeveloperTools accountUid={auth.currentUser?.uid} tests={
          <div className="option-section">
            <div className="option-setting-card option-setting-card-featured">
              <div>
                <strong>Salle des Épreuves (Test)</strong>
                <span>
                  Lance directement un checkpoint. Les questions utilisent une base séparée du Quiz.
                </span>
              </div>
              <div className="option-pill-grid trial-dev-grid">
                {CHECKPOINT_LEVELS.map((checkpointLevel) => (
                  <button
                    key={checkpointLevel}
                    type="button"
                    className={`option-pill ${
                      checkpointTrialProgress.completed?.[String(checkpointLevel)] ? "active" : ""
                    }`}
                    onClick={() => onOpenTrial?.(checkpointLevel)}
                  >
                    Checkpoint {checkpointLevel}
                  </button>
                ))}
              </div>
            </div>
          </div>} events={<AdminPanel events={developerEvents} onImportNews={onImportNews}/>} feedback={<FeedbackInbox/>} reports={<SocialReportsInbox/>} predictions={<GamingPredictions manage/>}/>
        </OptionGroup>}

        <OptionGroup id="data" isOpen={!!openOptionGroups.data} onToggle={() => setOpenOptionGroups((current) => ({ ...current, data: !current.data }))}
          title="Données et sauvegardes"
          description="Corbeille, copies de sécurité, mode hors ligne et import."
          summary={gameIntegrityIssues.length ? `${gameIntegrityIssues.length} à vérifier` : "OK"}
        >

          <div className="option-section">
            <DataProtection onCapture={onCaptureBackup} onRestore={onRestoreVersion}/>
            <div className="option-setting-card option-setting-card-featured data-options-card">
              <div>
                <SectionTitle title="Données" help="data" />
                <span>Contrôle la cohérence de la bibliothèque et prépare les sauvegardes.</span>
              </div>

          <div
            className={`data-health-card ${
              gameIntegrityIssues.length ? "needs-action" : "clean"
            }`}
          >
            <div className="data-health-main">
              <div>
                <span className="data-health-kicker">Diagnostic bibliothèque</span>
                <strong>
                  {gameIntegrityIssues.length
                    ? `${gameIntegrityIssues.length} fiche${gameIntegrityIssues.length > 1 ? "s" : ""} à aligner`
                    : "Bibliothèque cohérente"}
                </strong>
                <p>
                  {gameIntegrityIssues.length
                    ? "L'app peut synchroniser statut, progression et compteur terminé."
                    : "Les compteurs utilisent déjà la même logique partout."}
                </p>
              </div>

              <button
                type="button"
                className="option-pill data-health-action"
                onClick={handleRepairGameData}
                disabled={!gameIntegrityIssues.length || isRepairingGameData}
              >
                {isRepairingGameData ? "Réparation..." : "Réparer"}
              </button>
            </div>

            {gameIntegrityPreview.length > 0 && (
              <div className="data-health-preview">
                {gameIntegrityPreview.map(({ game, patch, reasons }) => (
                  <div key={game.id || game.name} className="data-health-row">
                    <div className="data-health-game">
                      <span>{game.name}</span>
                      <em>{reasons[0]}</em>
                    </div>
                    <div className="data-health-flow">
                      <b>{getGameProgressStateLabel(getGameProgressState(game))}</b>
                      <i>-></i>
                      <b>{getGameIntegrityTargetLabel(game, patch)}</b>
                    </div>
                    {showIntegrityDetails && (
                      <small>{getGameIntegrityChangeSummary(game, patch)}</small>
                    )}
                  </div>
                ))}
                {gameIntegrityIssues.length > gameIntegrityPreview.length && (
                  <div className="data-health-row muted">
                    <div className="data-health-game">
                      <span>
                        +{gameIntegrityIssues.length - gameIntegrityPreview.length} autre
                      {gameIntegrityIssues.length - gameIntegrityPreview.length > 1 ? "s" : ""}
                      </span>
                      <em>corrigé avec le même bouton</em>
                    </div>
                  </div>
                )}
                {gameIntegrityIssues.length > 3 && (
                  <button
                    type="button"
                    className="data-health-toggle"
                    onClick={() => setShowIntegrityDetails((value) => !value)}
                  >
                    {showIntegrityDetails ? "Masquer le détail" : "Voir le détail"}
                  </button>
                )}
              </div>
            )}
          </div>
            </div>

            <div className="option-section-split">
              <div className="option-setting-card">
                <div>
                  <strong>Sauvegarde locale</strong>
                  <span>Exporte un fichier JSON avec tes données pour le garder de côté.</span>
                </div>
                <button type="button" className="option-pill option-wide-action" onClick={onExportBackup}>
                  Exporter
                </button>
              </div>

              <div className="option-setting-card">
                <div>
                  <strong>Restaurer</strong>
                  <span>Importe une sauvegarde JSON existante dans l'application.</span>
                </div>
                <label className="option-pill option-file-pill option-wide-action">
                  Importer
                  <input
                    type="file"
                    accept="application/json"
                    onChange={onImportBackup}
                  />
                </label>
              </div>

              <div className="option-setting-card option-setting-card-danger">
                <div>
                  <strong>Réinitialiser les options</strong>
                  <span>Remet uniquement les préférences de l'app à leur état par défaut.</span>
                </div>
                <button type="button" className="option-pill option-wide-action" onClick={onResetOptions}>
                  Reset options
                </button>
              </div>
            </div>
          </div>
        </OptionGroup>
      </div>

      {importReview&&<ImportReview games={games} incoming={importReview.items} provider={importReview.provider} title={importReview.title} onClose={()=>setImportReview(null)} onConfirm={items=>importReview.kind==="steam"?importSteamLibrary(items):importReview.kind==="wishlist"?importSteamWishlist(items):importXboxLibrary(items)}/>}
      {helpModal}
    </div>
  );
}

function LiveMiniPlayer({ live, collapsed, setCollapsed, onClose }) {
  if (!live || !live.youtubeId || live.youtubeId === "VIDEO_ID_ICI") return null;

  return (
    <div className={`live-mini-player ${collapsed ? "collapsed" : ""}`}>
      <div className="live-mini-header">
        <div>
          <div className="live-mini-kicker">🔴 Live en cours</div>
          <div className="live-mini-title">{live.name}</div>
        </div>

        <div className="live-mini-actions">
          <button type="button" onClick={() => setCollapsed(!collapsed)}>
            {collapsed ? "▴" : "▾"}
          </button>

          <button type="button" onClick={onClose}>
            ✕
          </button>
        </div>
      </div>

      {!collapsed && (
        <div className="live-mini-video">
          <iframe
            src={`https://www.youtube.com/embed/${live.youtubeId}`}
            title={live.name}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
          />
        </div>
      )}
    </div>
  );
}

function EventsAdminList({ events, onEdit }) {
  const handleDelete = async (event) => {
    if (!event.isFirebaseEvent || !event.firebaseId) {
      alert("Impossible de supprimer un événement de secours.");
      return;
    }

    const confirmDelete = window.confirm(
      `Supprimer l’événement "${event.name}" ?`
    );

    if (!confirmDelete) return;

    try {
      await deleteDoc(doc(db, "events", event.firebaseId));
      alert("Événement supprimé ✅");
    } catch (e) {
      console.error(e);
      alert("Erreur lors de la suppression");
    }
  };

  if (!events.length) {
    return (
      <EmptyState
        title="Aucun événement"
        subtitle="Ajoute un premier événement depuis le formulaire."
      />
    );
  }

  return (
    <div className="admin-events-list">
      <h3 className="admin-section-title">Événements enregistrés</h3>

      {events.map((event) => (
        <div key={event.id} className="admin-event-item">
          <div>
            <div className="admin-event-name">{event.name}</div>
            <div className="admin-event-date">
              {formatEventDate(event.date)}
            </div>
          </div>

          <div className="admin-event-actions">
            <button
              type="button"
              className="filter-toggle-btn"
              onClick={() => onEdit(event)}
            >
              Modifier
            </button>

            <button
              type="button"
              className="delete-btn"
              onClick={() => handleDelete(event)}
            >
              Supprimer
            </button>
          </div>

        </div>
      ))}
    </div>
  );
}

function AdminPanel({ events, onImportNews }) {
  const [editingEvent, setEditingEvent] = useState(null);
  return <div className="search-panel">
    <h2 className="panel-title">Lives et actualités</h2>
    <button type="button" className="filter-toggle-btn" onClick={() => onImportNews({ force: true })}>Importer les actus RSS</button>
    <div style={{marginTop:14}}>
      <AddEventForm editingEvent={editingEvent} onCancelEdit={() => setEditingEvent(null)}/>
      <EventsAdminList events={events} onEdit={setEditingEvent}/>
    </div>
  </div>;
}

function NewsTab({ newsItems }) {
  const sortedNews = [...newsItems].sort(
    (a, b) => new Date(b.date) - new Date(a.date)
  );

  return (
    <div className="news-page">
      <div className="section-header">
        <h2>Actualités gaming</h2>
        <span className="section-count">{sortedNews.length}</span>
      </div>

      {sortedNews.length === 0 ? (
        <EmptyState
          title="Aucune actu pour le moment"
          subtitle="Importe les actus depuis le panneau administrateur."
        />
      ) : (
        <div className="news-list">
          {sortedNews.map((news) => (
            <article key={news.id} className="news-card">
              {news.image && (
                <img src={news.image} alt={news.title} className="news-image" />
              )}

              <div className="news-content">
                <div className="news-meta">
                  {news.category || "Gaming"} • {news.source || "Source inconnue"}
                </div>

                <h3>{news.title}</h3>

                <div className="news-date">{formatFullDate(news.date)}</div>

                <p>{news.summary}</p>

                {news.url && (
                  <a href={news.url} target="_blank" rel="noreferrer">
                    Lire l’article
                  </a>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}

const DEAL_SOURCES = [
  { id: "all", label: "Tout" },
  { id: "steam", label: "Steam" },
  { id: "epic", label: "Epic" },
  { id: "psn", label: "PlayStation" },
  { id: "xbox", label: "Xbox" },
];

const DEAL_REGION_CONFIG = {
  FR: { country: "FR", locale: "fr-FR", steamLang: "french", currency: "EUR", psn: "fr-fr", xbox: "fr-fr" },
  EU: { country: "DE", locale: "fr-FR", steamLang: "french", currency: "EUR", psn: "fr-fr", xbox: "fr-fr" },
  US: { country: "US", locale: "en-US", steamLang: "english", currency: "USD", psn: "en-us", xbox: "en-us" },
};

const DEAL_SORT_OPTIONS = [
  { id: "smart", label: "Pertinence" },
  { id: "discount", label: "Remise" },
  { id: "price", label: "Prix" },
  { id: "ending", label: "Fin proche" },
];

const DEAL_LIBRARY_FILTERS = [
  { id: "all", label: "Toutes" },
  { id: "smart-wishlist", label: "Alertes prix Steam / Epic" },
  { id: "wishlist", label: "Wishlist Checkpoint" },
  { id: "missing", label: "Pas dans ma bibliothèque" },
  { id: "owned", label: "Déjà possédées" },
];

const DEAL_RESULT_LIMIT = 60;

function formatSteamPrice(value, currency = "EUR") {
  if (typeof value !== "number") return "";

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
  }).format(value / 100);
}

function formatDealPrice(value, currency = "USD", usdRate = 1) {
  const price = Number.parseFloat(value);
  if (!Number.isFinite(price)) return "";
  if (price <= 0) return "Gratuit";
  const convertedPrice = currency === "USD" ? price : price * usdRate;

  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency,
  }).format(convertedPrice);
}

function getEpicImage(images = []) {
  const preferred =
    images.find((image) => image.type === "OfferImageWide") ||
    images.find((image) => image.type === "featuredMedia") ||
    images.find((image) => image.type === "Thumbnail") ||
    images[0];

  return preferred?.url || "";
}

function getEpicDealUrl(item) {
  const mappingSlug = item.offerMappings?.[0]?.pageSlug;
  const productSlug = item.productSlug?.split("/")[0];
  const slug = productSlug || mappingSlug || item.urlSlug;

  return slug ? `https://store.epicgames.com/fr/p/${slug}` : "https://store.epicgames.com/fr/free-games";
}

function uniqueDealItems(items = [], getKey = (item) => item?.id) {
  const seen = new Set();

  return items.filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function normalizeDealTitle(value = "") {
  return String(value)
    .toLowerCase()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function normalizeSmartWishlistTitle(value = "") {
  return normalizeDealTitle(value)
    .replace(
      /\b(remaster(ed)?|remake|definitive|complete|deluxe|ultimate|standard|collector|edition|bundle|pack|goty|game of the year)\b/g,
      " "
    )
    .replace(/\s+/g, " ")
    .trim();
}

function getSteamAppIdFromDeal(deal = {}) {
  if (deal.steamAppId) return String(deal.steamAppId);

  const idMatch = String(deal.id || "").match(/^steam-(\d+)/);
  if (idMatch?.[1]) return idMatch[1];

  const urlMatch = String(deal.url || "").match(/store\.steampowered\.com\/app\/(\d+)/i);
  return urlMatch?.[1] || "";
}

function getDealWishlistMatch(deal = {}, games = []) {
  if (deal.isStoreHub) return null;

  const wishlistGames = games.filter((game) => game.status === "wishlist");
  if (!wishlistGames.length) return null;

  const dealSteamAppId = getSteamAppIdFromDeal(deal);
  if (dealSteamAppId) {
    const steamMatch = wishlistGames.find(
      (game) => getSteamGameKey(game) && getSteamGameKey(game) === dealSteamAppId
    );

    if (steamMatch) {
      return {
        game: steamMatch,
        confidence: 100,
        origin: getWishlistOriginLabel(steamMatch),
      };
    }
  }

  const dealKey = normalizeSmartWishlistTitle(deal.title);
  if (!dealKey) return null;

  const dealTokens = new Set(dealKey.split(" ").filter((token) => token.length > 1));
  let bestMatch = null;

  wishlistGames.forEach((game) => {
    const gameKey = normalizeSmartWishlistTitle(game.name);
    if (!gameKey) return;

    let confidence = 0;
    if (dealKey === gameKey) {
      confidence = 96;
    } else if (
      dealKey.length >= 7 &&
      gameKey.length >= 7 &&
      (dealKey.includes(gameKey) || gameKey.includes(dealKey))
    ) {
      confidence = 86;
    } else {
      const gameTokens = gameKey.split(" ").filter((token) => token.length > 1);
      const sharedTokens = gameTokens.filter((token) => dealTokens.has(token));
      const ratio = sharedTokens.length / Math.max(gameTokens.length, 1);

      if (sharedTokens.length >= 2 && ratio >= 0.66) {
        confidence = 74 + Math.round(ratio * 10);
      }
    }

    if (confidence > (bestMatch?.confidence || 0)) {
      bestMatch = {
        game,
        confidence,
        origin: getWishlistOriginLabel(game),
      };
    }
  });

  return bestMatch?.confidence >= 74 ? bestMatch : null;
}

function parseDealPrice(value = "") {
  if (!value || /gratuit|free/i.test(value)) return 0;
  const normalized = String(value)
    .replace(/[^\d,.-]/g, "")
    .replace(",", ".");
  const parsed = Number.parseFloat(normalized);
  return Number.isFinite(parsed) ? parsed : Number.POSITIVE_INFINITY;
}

function getDealEndTime(deal) {
  const timestamp = deal.endsAt ? new Date(deal.endsAt).getTime() : Number.POSITIVE_INFINITY;
  return Number.isFinite(timestamp) ? timestamp : Number.POSITIVE_INFINITY;
}

function getOfficialStoreDealHubs(region) {
  return [
    {
      id: "psn-official-deals",
      store: "psn",
      storeLabel: "PlayStation",
      title: "Promos PlayStation Store",
      image: "",
      discount: 0,
      normalPrice: "",
      salePrice: "Voir les offres",
      url: `https://store.playstation.com/${region.psn}/pages/deals`,
      endsAt: "",
      isStoreHub: true,
    },
    {
      id: "xbox-official-deals",
      store: "xbox",
      storeLabel: "Xbox",
      title: "Promos Xbox Store",
      image: "",
      discount: 0,
      normalPrice: "",
      salePrice: "Voir les offres",
      url: `https://www.xbox.com/${region.xbox}/games/all-games?cat=onsale`,
      endsAt: "",
      isStoreHub: true,
    },
  ];
}

function enrichDealsWithLibrary(deals, games = []) {
  const gameIndex = games.map((game) => ({
    game,
    key: normalizeDealTitle(game.name),
  }));

  return deals.map((deal) => {
    if (deal.isStoreHub) {
      return {
        ...deal,
        libraryStatus: "missing",
        matchedGameId: null,
        priceValue: Number.POSITIVE_INFINITY,
        endsAtTime: Number.POSITIVE_INFINITY,
      };
    }

    const dealKey = normalizeDealTitle(deal.title);
    const match = gameIndex.find(({ key }) => {
      if (!key || !dealKey) return false;
      return key === dealKey || key.includes(dealKey) || dealKey.includes(key);
    });
    const wishlistMatch = getDealWishlistMatch(deal, games);
    const libraryStatus = wishlistMatch
      ? "wishlist"
      : match
      ? match.game.status === "wishlist"
        ? "wishlist"
        : "owned"
      : "missing";

    return {
      ...deal,
      libraryStatus,
      smartWishlistMatch: Boolean(wishlistMatch),
      wishlistOrigin: wishlistMatch?.origin || "",
      matchedGameId: wishlistMatch?.game?.id || match?.game?.id || null,
      matchedGameName: wishlistMatch?.game?.name || match?.game?.name || "",
      priceValue: parseDealPrice(deal.salePrice),
      endsAtTime: getDealEndTime(deal),
    };
  });
}

function normalizeSteamDeals(data) {
  const candidates = [
    ...(data?.specials?.items || []),
    ...(data?.top_sellers?.items || []),
    ...(data?.new_releases?.items || []),
    ...(data?.dailydeal?.items || []),
  ];

  return uniqueDealItems(candidates, (item) => item?.id)
    .filter((item) => item.discounted && item.discount_percent > 0)
    .sort((a, b) => (b.discount_percent || 0) - (a.discount_percent || 0))
    .slice(0, DEAL_RESULT_LIMIT)
    .map((item) => ({
      id: `steam-${item.id}`,
      store: "steam",
      storeLabel: "Steam",
      title: item.name,
      image: item.large_capsule_image || item.header_image || item.small_capsule_image,
      discount: item.discount_percent,
      normalPrice: formatSteamPrice(item.original_price, item.currency),
      salePrice: formatSteamPrice(item.final_price, item.currency),
      url: `https://store.steampowered.com/app/${item.id}`,
      endsAt: item.discount_expiration ? new Date(item.discount_expiration * 1000).toISOString() : "",
    }));
}

function normalizeEpicDeals(data, currency = "USD", usdRate = 1) {
  if (Array.isArray(data)) {
    return data
      .filter((item) => item.isOnSale === "1" && Number.parseFloat(item.savings || "0") > 0)
      .sort((a, b) => Number.parseFloat(b.savings || "0") - Number.parseFloat(a.savings || "0"))
      .slice(0, DEAL_RESULT_LIMIT)
      .map((item) => ({
        id: `epic-${item.dealID || item.gameID}`,
        store: "epic",
        storeLabel: "Epic",
        title: item.title,
        image: item.thumb || "",
        discount: Math.round(Number.parseFloat(item.savings || "0")),
        normalPrice: formatDealPrice(item.normalPrice, currency, usdRate),
        salePrice: formatDealPrice(item.salePrice, currency, usdRate),
        url: `https://www.cheapshark.com/redirect?dealID=${encodeURIComponent(item.dealID)}`,
        endsAt: "",
      }));
  }

  return (data?.data?.Catalog?.searchStore?.elements || [])
    .filter((item) => {
      const currentPromos = item.promotions?.promotionalOffers || [];
      const hasFreePromo = currentPromos.some((promoGroup) =>
        (promoGroup.promotionalOffers || []).some(
          (promo) => promo.discountSetting?.discountPercentage === 0
        )
      );
      const price = item.price?.totalPrice;
      return hasFreePromo || (price && price.discount > 0);
    })
    .slice(0, DEAL_RESULT_LIMIT)
    .map((item) => {
      const price = item.price?.totalPrice;
      const promo = item.promotions?.promotionalOffers?.[0]?.promotionalOffers?.[0];
      const isFree = promo?.discountSetting?.discountPercentage === 0;

      return {
        id: `epic-${item.id}`,
        store: "epic",
        storeLabel: "Epic",
        title: item.title,
        image: getEpicImage(item.keyImages),
        discount: isFree ? 100 : Math.round(((price?.discount || 0) / Math.max(price?.originalPrice || 1, 1)) * 100),
        normalPrice: price?.fmtPrice?.originalPrice || "",
        salePrice: isFree ? "Gratuit" : price?.fmtPrice?.discountPrice || "",
        url: getEpicDealUrl(item),
        endsAt: promo?.endDate || "",
      };
    });
}

function HomeDealsPreview({
  dealPreferences = DEFAULT_APP_OPTIONS,
  games = [],
  onOpenDeals,
}) {
  const sourcePreferences = useMemo(
    () => ({
      ...DEFAULT_APP_OPTIONS.dealSources,
      ...(dealPreferences.dealSources || {}),
    }),
    [dealPreferences.dealSources]
  );
  const region = DEAL_REGION_CONFIG[dealPreferences.dealRegion] || DEAL_REGION_CONFIG.FR;
  const [deals, setDeals] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  const enabledSourceKey = useMemo(
    () =>
      Object.entries(sourcePreferences)
        .filter(([, enabled]) => enabled)
        .map(([source]) => source)
        .sort()
        .join(","),
    [sourcePreferences]
  );

  const loadHomeDeals = useCallback(async () => {
    setIsLoading(true);
    setHasError(false);

    const fetchWithTimeout = async (url, timeout = 8000) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        return await fetch(url, { signal: controller.signal });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      const apiResponse = await fetchWithTimeout(
        `/api/deals?cc=${region.country}&locale=${region.locale}&lang=${region.steamLang}&currency=${region.currency}`
      );
      const contentType = apiResponse.headers.get("content-type") || "";

      if (apiResponse.ok && contentType.includes("application/json")) {
        const payload = await apiResponse.json();
        setDeals(
          (payload.deals || []).filter((deal) => sourcePreferences[deal.store])
        );
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.warn("Apercu promos indisponible via proxy :", error);
    }

    try {
      const requests = [
        {
          id: "steam",
          url: `https://store.steampowered.com/api/featuredcategories?cc=${region.country}&l=${region.steamLang}`,
          normalize: normalizeSteamDeals,
        },
        {
          id: "epic",
          url: "https://www.cheapshark.com/api/1.0/deals?storeID=25&onSale=1&pageSize=60&sortBy=Savings&desc=1",
          normalize: (data) =>
            normalizeEpicDeals(
              data,
              region.currency,
              region.currency === "USD" ? 1 : 0.92
            ),
        },
      ].filter((source) => sourcePreferences[source.id]);

      const results = await Promise.allSettled(
        requests.map(async (source) => {
          const response = await fetchWithTimeout(source.url);
          if (!response.ok) throw new Error(`${source.id}: ${response.status}`);
          return source.normalize(await response.json());
        })
      );

      setDeals(
        results
          .filter((result) => result.status === "fulfilled")
          .flatMap((result) => result.value)
      );
    } catch (error) {
      setHasError(true);
      setDeals([]);
    } finally {
      setIsLoading(false);
    }
  }, [
    region.country,
    region.currency,
    region.locale,
    region.steamLang,
    sourcePreferences,
  ]);

  useEffect(() => {
    loadHomeDeals();
  }, [loadHomeDeals, enabledSourceKey]);

  const previewDeals = useMemo(() => {
    const enrichedDeals = enrichDealsWithLibrary(deals, games).filter(
      (deal) => !deal.isStoreHub
    );

    return enrichedDeals
      .sort((a, b) => {
        const smartWishlistDelta =
          Number(b.smartWishlistMatch) - Number(a.smartWishlistMatch);
        if (smartWishlistDelta !== 0) return smartWishlistDelta;
        const wishlistDelta =
          Number(b.libraryStatus === "wishlist") - Number(a.libraryStatus === "wishlist");
        if (wishlistDelta !== 0) return wishlistDelta;
        const discountDelta = (b.discount || 0) - (a.discount || 0);
        if (discountDelta !== 0) return discountDelta;
        return a.priceValue - b.priceValue;
      })
      .slice(0, 3);
  }, [deals, games]);

  const wishlistCount = previewDeals.filter(
    (deal) => deal.libraryStatus === "wishlist"
  ).length;
  const wishlistAlertCount = previewDeals.filter((deal) => deal.smartWishlistMatch).length;
  const bestDiscount = previewDeals.reduce(
    (max, deal) => Math.max(max, deal.discount || 0),
    0
  );

  return (
    <section className="home-deals-card">
      <div className="home-section-head">
        <div>
          <div className="home-card-title">
            {wishlistAlertCount ? "Alertes prix Steam / Epic" : "Bonnes affaires Steam / Epic"}
          </div>
          <p>
            {wishlistAlertCount
              ? "Une offre Steam ou Epic correspond à un jeu de ta wishlist suivie dans Checkpoint."
              : "Les promos les plus interessantes remontent ici sans ouvrir l'onglet complet."}
          </p>
        </div>

        <button type="button" onClick={onOpenDeals}>
          Tout voir
        </button>
      </div>

      <div className="home-deals-summary">
        <span>{previewDeals.length || (isLoading ? "..." : 0)} offres</span>
        <span>{wishlistAlertCount ? `${wishlistAlertCount} alerte${wishlistAlertCount > 1 ? "s" : ""}` : `${wishlistCount} suivis dans Checkpoint`}</span>
        <span>{bestDiscount ? `-${bestDiscount}% max` : "Steam / Epic / consoles"}</span>
      </div>

      {isLoading && previewDeals.length === 0 ? (
        <div className="home-deals-empty">Recherche des meilleures offres...</div>
      ) : hasError && previewDeals.length === 0 ? (
        <div className="home-deals-empty">Les promos seront rechargees depuis l'onglet dedie.</div>
      ) : previewDeals.length > 0 ? (
        <div className="home-deals-list">
          {previewDeals.map((deal) => (
            <a
              key={deal.id}
              className={`home-deal-row ${deal.smartWishlistMatch ? "wishlist-alert" : ""}`}
              href={deal.url}
              target="_blank"
              rel="noreferrer"
            >
              {deal.image ? (
                <img src={deal.image} alt={deal.title} />
              ) : (
                <div className="home-deal-placeholder">{deal.storeLabel}</div>
              )}

              <div className="home-deal-main">
                <span className={`deal-store deal-store-${deal.store}`}>
                  {deal.storeLabel}
                </span>
                <strong>{deal.title}</strong>
                <small>
                  {deal.smartWishlistMatch
                    ? `${deal.wishlistOrigin || "Wishlist Checkpoint"} - ${deal.salePrice || "Prix indisponible"}`
                    : deal.salePrice || "Prix indisponible"}
                </small>
              </div>

              <div className="home-deal-side">
                {deal.discount > 0 && <b>-{deal.discount}%</b>}
                {deal.smartWishlistMatch ? (
                  <em>{getDealAlertLabel(deal)}</em>
                ) : (
                  deal.libraryStatus === "wishlist" && <em>Wishlist Checkpoint</em>
                )}
              </div>
            </a>
          ))}
        </div>
      ) : (
        <div className="home-deals-empty">Aucune promo forte pour le moment.</div>
      )}
    </section>
  );
}

function DealsTab({ dealPreferences = DEFAULT_APP_OPTIONS, games = [] }) {
  const sourcePreferences = {
    ...DEFAULT_APP_OPTIONS.dealSources,
    ...(dealPreferences.dealSources || {}),
  };
  const region = DEAL_REGION_CONFIG[dealPreferences.dealRegion] || DEAL_REGION_CONFIG.FR;
  const enabledSources = DEAL_SOURCES.filter(
    (source) => source.id === "all" || sourcePreferences[source.id]
  );
  const [activeSource, setActiveSource] = useState("all");
  const [deals, setDeals] = useState([]);
  const [sourceStatus, setSourceStatus] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [dealSearch, setDealSearch] = useState("");
  const [sortMode, setSortMode] = useState("smart");
  const [libraryFilter, setLibraryFilter] = useState("all");
  const [lastUpdatedAt, setLastUpdatedAt] = useState("");

  const loadDeals = async () => {
    setIsLoading(true);
    setSourceStatus({});

    const fetchWithTimeout = async (url, timeout = 9000) => {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout);

      try {
        return await fetch(url, { signal: controller.signal });
      } finally {
        clearTimeout(timeoutId);
      }
    };

    try {
      const apiResponse = await fetchWithTimeout(
        `/api/deals?cc=${region.country}&locale=${region.locale}&lang=${region.steamLang}&currency=${region.currency}`
      );
      const contentType = apiResponse.headers.get("content-type") || "";

      if (apiResponse.ok && contentType.includes("application/json")) {
        const payload = await apiResponse.json();
        const storeHubs = getOfficialStoreDealHubs(region).filter(
          (hub) => sourcePreferences[hub.store]
        );
        setDeals([...(payload.deals || []), ...storeHubs]);
        setLastUpdatedAt(payload.updatedAt || new Date().toISOString());
        setSourceStatus(
          {
            steam: "Source indisponible pour le moment.",
            epic: "Source indisponible pour le moment.",
            ...(payload.status || {}),
            ...(sourcePreferences.psn ? { psn: "Lien officiel disponible." } : {}),
            ...(sourcePreferences.xbox ? { xbox: "Lien officiel disponible." } : {}),
          }
        );
        setIsLoading(false);
        return;
      }
    } catch (error) {
      console.warn("Proxy promos indisponible, tentative directe :", error);
    }

    const requests = [
      {
        id: "steam",
        label: "Steam",
        url: `https://store.steampowered.com/api/featuredcategories?cc=${region.country}&l=${region.steamLang}`,
        normalize: normalizeSteamDeals,
      },
      {
        id: "epic",
        label: "Epic",
        url: "https://www.cheapshark.com/api/1.0/deals?storeID=25&onSale=1&pageSize=60&sortBy=Savings&desc=1",
        normalize: (data) => normalizeEpicDeals(data, region.currency, region.currency === "USD" ? 1 : 0.92),
      },
    ].filter((source) => sourcePreferences[source.id]);

    const results = await Promise.allSettled(
      requests.map(async (source) => {
        const response = await fetchWithTimeout(source.url);
        if (!response.ok) throw new Error(`${source.label}: ${response.status}`);
        const data = await response.json();
        return { source, items: source.normalize(data) };
      })
    );

    const nextDeals = [];
    const nextStatus = {};

    if (sourcePreferences.psn) {
      nextStatus.psn = "Lien officiel disponible.";
    }

    if (sourcePreferences.xbox) {
      nextStatus.xbox = "Lien officiel disponible.";
    }

    results.forEach((result, index) => {
      const source = requests[index];
      if (result.status === "fulfilled") {
        nextDeals.push(...result.value.items);
        nextStatus[source.id] =
          result.value.items.length > 0 ? "OK" : "Aucune promo trouvee.";
      } else {
        nextStatus[source.id] = "Source indisponible pour le moment.";
      }
    });

    setDeals([
      ...nextDeals,
      ...getOfficialStoreDealHubs(region).filter((hub) => sourcePreferences[hub.store]),
    ]);
    setSourceStatus(nextStatus);
    setLastUpdatedAt(new Date().toISOString());
    setIsLoading(false);
  };

  useEffect(() => {
    if (!enabledSources.some((source) => source.id === activeSource)) {
      setActiveSource("all");
    }
  }, [dealPreferences.dealSources, activeSource]);

  useEffect(() => {
    loadDeals();
  }, [dealPreferences.dealRegion, dealPreferences.dealSources]);

  const enrichedDeals = useMemo(
    () => enrichDealsWithLibrary(deals, games),
    [deals, games]
  );
  const visibleDeals = useMemo(() => {
    const searchKey = normalizeDealTitle(dealSearch);

    return enrichedDeals
      .filter((deal) => activeSource === "all" || deal.store === activeSource)
      .filter((deal) => {
        if (libraryFilter === "all") return true;
        if (libraryFilter === "smart-wishlist") return deal.smartWishlistMatch;
        return deal.libraryStatus === libraryFilter;
      })
      .filter((deal) => !searchKey || normalizeDealTitle(deal.title).includes(searchKey))
      .sort((a, b) => {
        if (sortMode === "discount") return (b.discount || 0) - (a.discount || 0);
        if (sortMode === "price") return a.priceValue - b.priceValue;
        if (sortMode === "ending") return a.endsAtTime - b.endsAtTime;

        const smartWishlistDelta =
          Number(b.smartWishlistMatch) - Number(a.smartWishlistMatch);
        if (smartWishlistDelta !== 0) return smartWishlistDelta;
        const libraryScore = { wishlist: 0, missing: 1, owned: 2 };
        const statusDelta =
          (libraryScore[a.libraryStatus] ?? 3) - (libraryScore[b.libraryStatus] ?? 3);
        if (statusDelta !== 0) return statusDelta;
        return (b.discount || 0) - (a.discount || 0);
      });
  }, [activeSource, dealSearch, enrichedDeals, libraryFilter, sortMode]);
  const bestDeal =
    visibleDeals.find((deal) => deal.smartWishlistMatch) ||
    visibleDeals[0] ||
    enrichedDeals.find((deal) => deal.smartWishlistMatch) ||
    enrichedDeals.find((deal) => deal.discount >= 75) ||
    enrichedDeals[0];
  const freeCount = enrichedDeals.filter((deal) => deal.priceValue === 0).length;
  const wishlistDealCount = enrichedDeals.filter((deal) => deal.libraryStatus === "wishlist").length;
  const smartWishlistDealCount = enrichedDeals.filter((deal) => deal.smartWishlistMatch).length;
  const averageDiscount = enrichedDeals.length
    ? Math.round(
        enrichedDeals.reduce((sum, deal) => sum + (deal.discount || 0), 0) /
          enrichedDeals.length
      )
    : 0;

  return (
    <div className="deals-page">
      <div className="section-header deals-header">
        <div>
          <h2>Promos du moment</h2>
          <p>Promos Steam et Epic en direct. PlayStation et Xbox ouvrent leurs vitrines officielles.</p>
          <p>Les alertes comparent ces offres à ta wishlist Checkpoint, y compris tes imports Steam. Elles ne synchronisent pas automatiquement les wishlists Steam ou Epic.</p>
        </div>
        <button type="button" className="deals-refresh-btn" onClick={loadDeals} disabled={isLoading}>
          {isLoading ? "Actualisation..." : "Actualiser"}
        </button>
      </div>

      <div className="deals-dashboard">
        <div className={`deals-spotlight ${bestDeal?.smartWishlistMatch ? "wishlist-alert" : ""}`}>
          <span className="deals-spotlight-kicker">{bestDeal?.smartWishlistMatch ? getDealAlertLabel(bestDeal) : "Offre à surveiller"}</span>
          {bestDeal ? (
            <a href={bestDeal.url} target="_blank" rel="noreferrer">
              <div>
                <strong>{bestDeal.title}</strong>
                <span>
                  {bestDeal.smartWishlistMatch
                    ? `${bestDeal.wishlistOrigin || "Wishlist Checkpoint"} - ${bestDeal.storeLabel} - -${bestDeal.discount || 0}% - ${bestDeal.salePrice || "Prix indisponible"}`
                    : `${bestDeal.storeLabel} - -${bestDeal.discount || 0}% - ${bestDeal.salePrice || "Prix indisponible"}`}
                </span>
              </div>
              <b>Voir</b>
            </a>
          ) : (
            <p>Aucune offre chargée pour le moment.</p>
          )}
        </div>

        <div className="deals-stat-grid">
          <div>
            <strong>{enrichedDeals.length}</strong>
            <span>offres</span>
          </div>
          <div>
            <strong>{smartWishlistDealCount || wishlistDealCount}</strong>
            <span>{smartWishlistDealCount ? "alertes prix boutiques" : "suivis dans Checkpoint"}</span>
          </div>
          <div>
            <strong>{freeCount}</strong>
            <span>gratuites</span>
          </div>
          <div>
            <strong>{averageDiscount}%</strong>
            <span>remise moy.</span>
          </div>
        </div>
      </div>

      <div className="deals-tools">
        <input
          value={dealSearch}
          onChange={(event) => setDealSearch(event.target.value)}
          placeholder="Rechercher dans les promos..."
        />
        <div className="deals-tool-row">
          {DEAL_SORT_OPTIONS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={sortMode === option.id ? "active" : ""}
              onClick={() => setSortMode(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="deals-tool-row secondary">
          {DEAL_LIBRARY_FILTERS.map((option) => (
            <button
              key={option.id}
              type="button"
              className={libraryFilter === option.id ? "active" : ""}
              onClick={() => setLibraryFilter(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="deals-source-tabs">
        {enabledSources.map((source) => (
          <button
            key={source.id}
            type="button"
            className={activeSource === source.id ? "active" : ""}
            onClick={() => setActiveSource(source.id)}
          >
            {source.label}
          </button>
        ))}
      </div>

      {activeSource === "psn" && (
        <div className="deals-source-note">
          <strong>PlayStation Store</strong>
          <span>
            Sony ne fournit pas de flux public stable équivalent à Steam. Le bouton ouvre donc la page
            officielle des promotions PlayStation.
          </span>
          <a href={`https://store.playstation.com/${region.psn}/pages/deals`} target="_blank" rel="noreferrer">
            Voir les promos PlayStation
          </a>
        </div>
      )}

      {activeSource === "xbox" && (
        <div className="deals-source-note xbox-note">
          <strong>Xbox Store</strong>
          <span>
            Microsoft expose surtout ses offres via le Store web. Pour l'instant, Checkpoint te renvoie vers
            la liste officielle filtrée sur les jeux en promotion.
          </span>
          <a href={`https://www.xbox.com/${region.xbox}/games/all-games?cat=onsale`} target="_blank" rel="noreferrer">
            Voir les promos Xbox
          </a>
        </div>
      )}

      <div className="deals-status-row">
        {lastUpdatedAt && (
          <span className="deals-status-pill ok">Mis à jour {formatFullDate(lastUpdatedAt)}</span>
        )}
        {["steam", "epic", "psn", "xbox"].filter((source) => sourcePreferences[source]).map((source) => (
          <span
            key={source}
            className={`deals-status-pill ${
              sourceStatus[source] === "OK" || sourceStatus[source]?.startsWith("Lien officiel") ? "ok" : ""
            }`}
          >
            {source.toUpperCase()} - {sourceStatus[source] || "Chargement"}
          </span>
        ))}
      </div>

      {isLoading && visibleDeals.length === 0 ? (
        <EmptyState title="Chargement des promos" subtitle="Je contacte les boutiques disponibles." />
      ) : visibleDeals.length === 0 ? (
        <EmptyState
          title="Aucune promo a afficher"
          subtitle="Essaie une autre boutique ou relance l'actualisation."
        />
      ) : (
        <div className="deals-grid">
          {visibleDeals.map((deal) => (
            <a
              key={deal.id}
              className={`deal-card ${deal.smartWishlistMatch ? "wishlist-alert" : ""}`}
              href={deal.url}
              target="_blank"
              rel="noreferrer"
            >
              <div className="deal-image-wrap">
                {deal.image ? (
                  <img src={deal.image} alt={deal.title} />
                ) : (
                  <div className="deal-image-placeholder">{deal.storeLabel}</div>
                )}
                {deal.isStoreHub && <span className="deal-hub-badge">Officiel</span>}
                {deal.discount > 0 && <span className="deal-discount">-{deal.discount}%</span>}
                {deal.libraryStatus !== "missing" && (
                  <span className={`deal-library-badge ${deal.smartWishlistMatch ? "smart-wishlist" : deal.libraryStatus}`}>
                    {deal.smartWishlistMatch
                      ? deal.wishlistOrigin || "Wishlist Checkpoint"
                      : deal.libraryStatus === "wishlist"
                        ? "Wishlist Checkpoint"
                        : "Déjà possédé"}
                  </span>
                )}
              </div>

              <div className="deal-content">
                <span className={`deal-store deal-store-${deal.store}`}>{deal.storeLabel}</span>
                <h3>{deal.title}</h3>
                <div className="deal-prices">
                  {deal.normalPrice && <span className="deal-normal-price">{deal.normalPrice}</span>}
                  <strong>{deal.salePrice}</strong>
                </div>
                {deal.endsAt && (
                  <span className="deal-end-date">Jusqu'au {formatFullDate(deal.endsAt)}</span>
                )}
              </div>
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

function CheckpointReachedOverlay({ checkpointLevel, onEnter, onClose }) {
  const trial = getCheckpointTrial(checkpointLevel);

  if (!trial) return null;

  return (
    <div className="checkpoint-gate-overlay">
      <div className="checkpoint-gate-card">
        <div className="checkpoint-gate-orb">
          <Crown size={34} />
        </div>
        <span className="checkpoint-gate-kicker">Nouveau Checkpoint atteint.</span>
        <h2>Le Gardien du Checkpoint vous attend.</h2>
        <p>
          Ton XP est securisee, mais le prochain rang reste verrouille tant que
          l'epreuve du niveau {checkpointLevel} n'est pas reussie.
        </p>

        <div className="checkpoint-gate-meta">
          <div>
            <span>Epreuve</span>
            <strong>{trial.title}</strong>
          </div>
          <div>
            <span>Rang a reveler</span>
            <strong>{trial.rewardRank}</strong>
          </div>
        </div>

        <div className="checkpoint-gate-actions">
          <button type="button" className="trial-primary-btn" onClick={onEnter}>
            Entrer dans la Salle des Epreuves
            <Sparkles size={18} />
          </button>
          <button type="button" className="trial-secondary-btn" onClick={onClose}>
            Plus tard
          </button>
        </div>
      </div>
    </div>
  );
}

/* -------------------- MAIN APP -------------------- */

/* ==================== APP PRINCIPALE ==================== */

export default function App({ startupVisible = false, onStartupProgress, tutorialsReady = true } = {}) {
  const [selectedSearchGame, setSelectedSearchGame] = useState(null);
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [results, setResults] = useState([]);
  const [games, setGames] = useState([]);
  const [weeklyQuizProgress, setWeeklyQuizProgress] = useState(() =>
    getStoredWeeklyQuizProgress()
  );
  const [checkpointGoalProgress, setCheckpointGoalProgress] = useState(() =>
    getStoredCheckpointGoalProgress()
  );
  const [checkpointTrialProgress, setCheckpointTrialProgress] = useState(() =>
    getStoredCheckpointTrialProgress()
  );
  const progressSync = useProgressSync(weeklyQuizProgress, checkpointGoalProgress, checkpointTrialProgress, ({quiz,goals,trials}) => {
    if(quiz) {setWeeklyQuizProgress(current=>JSON.stringify(current)===JSON.stringify(quiz)?current:quiz);storeWeeklyQuizProgress(quiz);}
    if(goals) {setCheckpointGoalProgress(current=>JSON.stringify(current)===JSON.stringify(goals)?current:goals);storeCheckpointGoalProgress(goals);}
    if(trials) {setCheckpointTrialProgress(current=>JSON.stringify(current)===JSON.stringify(trials)?current:trials);storeCheckpointTrialProgress(trials);}
  });
  const [activeTrialLevel, setActiveTrialLevel] = useState(null);
  const [dismissedCheckpointLevel, setDismissedCheckpointLevel] = useState(null);
  const [appOptions, setAppOptions] = useState(() => getStoredAppOptions());
  const [activeTab, setActiveTab] = useState(() => {
    const savedOptions = getStoredAppOptions();
    const savedTab = localStorage.getItem("checkpoint-last-tab");
    if (savedOptions.rememberLastTab && APP_TAB_IDS.includes(savedTab)) {
      return savedTab;
    }
    return APP_TAB_IDS.includes(savedOptions.startTab)
      ? savedOptions.startTab
      : "home";
  })

  const [theme, setTheme] = useState(
    localStorage.getItem("checkpoint-theme") || "theme-checkpoint"
  );
  const [uiMode, setUiMode] = useState(
    localStorage.getItem("checkpoint-ui-mode") === "pixel"
      ? "reduced"
      : localStorage.getItem("checkpoint-ui-mode") || "modern"
  );
  const showSplash = startupVisible;
  const [unlockedThemes, setUnlockedThemes] = useState(() => {
    try { return normalizeThemeUnlocks(JSON.parse(localStorage.getItem("checkpoint-theme-unlocks") || "[]")); } catch { return []; }
  });
  const [trialProgressReady, setTrialProgressReady] = useState(false);
  const [bootReady, setBootReady] = useState({
    events: false, news: false, hardware: false, games: false,
  });

  const [yearFilter, setYearFilter] = useState("");
  const [platformFilter, setPlatformFilter] = useState("");
  const [genreFilter, setGenreFilter] = useState("");
  const [sortBy, setSortBy] = useState("relevance");
  const searchRequestRef = useRef(0);
  const searchContextRef = useRef({query: "", ordering: "relevance"});
  const visibleSearchResults = useMemo(() => appOptions.searchIncludeFanGames
    ? results : results.filter(game => !isFanGame(game)), [results, appOptions.searchIncludeFanGames]);
  const hiddenFanCount = results.length - visibleSearchResults.length;
  const [newsItems, setNewsItems] = useState([]);
  const [bootWave, setBootWave] = useState(true);

  const [showFilters, setShowFilters] = useState(false);
  const [platforms, setPlatforms] = useState([]);
  const [genres, setGenres] = useState([]);
  const [toast, setToast] = useState("");
  const [collectionAddDraft,setCollectionAddDraft]=useState(null);
  const [physicalScannerOpen,setPhysicalScannerOpen]=useState(false);
  const startCollectionAdd=game=>setCollectionAddDraft(game);
  const [seenBadgeIds, setSeenBadgeIds] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [librarySearch, setLibrarySearch] = useState("");
  const [hardware, setHardware] = useState([]);
  const saveFeedback = useSaveFeedback();
  const documentStateRef = useRef({games, hardware});
  documentStateRef.current = {games, hardware};
  const draftFieldsRef = useRef(new Map());
  const editVersionRef = useRef(0);
  const fieldVersionsRef = useRef({});
  const scheduledEditsRef = useRef(new Map());
  const applyDocumentPatch = (kind, id, patch) => {
    const values = documentStateRef.current[kind].map(item => item.id === id ? {...item,...patch} : item);
    documentStateRef.current = {...documentStateRef.current, [kind]:values};
    if (kind === "games") { setGames(values); setSelectedGame(previous => applySelectedGamePatch(previous,id,patch)); }
    else setHardware(values);
  };
  const overlayDrafts = (kind, items) => items.map(item => {
    const draft = draftFieldsRef.current.get(`${kind}:${item.id}`);
    return draft ? {...item,...Object.fromEntries(Object.entries(draft).map(([field,edit])=>[field,edit.value]))} : item;
  });
  const persistPatch = (kind,id,patch,{success="Enregistré",undoable=false}={}) => {
    const owner=auth.currentUser?.uid;
    const key=`${kind}:${id}`;
    const scheduled=scheduledEditsRef.current.get(key);
    if(scheduled){clearTimeout(scheduled.timer);scheduledEditsRef.current.delete(key);patch={...scheduled.patch,...patch};}
    const before=documentStateRef.current[kind].find(item=>item.id===id);
    if(!before)return Promise.resolve(false);
    if(kind==="games")patch=stampGameHistory(before,stampGameMilestones(before,patch));
    const previous=Object.fromEntries(Object.keys(patch).map(field=>[field,before[field]??null]));
    const version=++editVersionRef.current;
    const currentDraft=draftFieldsRef.current.get(key)||{};
    Object.entries(patch).forEach(([field,value])=>{currentDraft[field]={value,version};});
    draftFieldsRef.current.set(key,currentDraft);
    applyDocumentPatch(kind,id,patch);
    const activeFields=()=>Object.keys(patch).filter(field=>draftFieldsRef.current.get(key)?.[field]?.version===version);
    // Versions survive successful writes for safe retry/undo checks.
    const versionKey = field => `${key}:${field}`;
    // Keep this separate from documentStateRef, which is refreshed every render.
    const registry=fieldVersionsRef.current;
    Object.keys(patch).forEach(field=>{registry[versionKey(field)]=version;});
    const isCurrent=()=>auth.currentUser?.uid===owner && Object.keys(patch).some(field=>registry[versionKey(field)]===version);
    return saveFeedback.run({key,isCurrent,success,
      write:async()=>{
        const payload=Object.fromEntries(Object.entries(patch).filter(([field])=>registry[versionKey(field)]===version));
        if(!Object.keys(payload).length)return;
        const acknowledgement=await updateDoc(doc(db,kind,id),payload);
        const draft=draftFieldsRef.current.get(key);
        if(draft){activeFields().forEach(field=>delete draft[field]);if(!Object.keys(draft).length)draftFieldsRef.current.delete(key);}
        return acknowledgement;
      },
      undo:undoable?()=>{
        const item=documentStateRef.current[kind].find(value=>value.id===id);
        const unchanged=item&&Object.keys(patch).every(field=>registry[versionKey(field)]===version&&JSON.stringify(item[field]??null)===JSON.stringify(patch[field]??null));
        if(!unchanged){setToast("Cette fiche a changé depuis. L’annulation n’a pas été appliquée.");return Promise.resolve(false);}
        return persistPatch(kind,id,previous,{success:"Modification annulée"});
      }:null,
    });
  };
  const [socialEntryView, setSocialEntryView] = useState("feed");
  const [rendezvousNotice,setRendezvousNotice]=useState(null);
  const [selectedGame, setSelectedGame] = useState(null);
  const [detailGameList, setDetailGameList] = useState([]);
  const detailCloseTimerRef = useRef(null);
  const [socialProfile, setSocialProfile] = useState(() => {
    try {
      const storedProfile = JSON.parse(
        localStorage.getItem(SOCIAL_PROFILE_STORAGE_KEY) || JSON.stringify({ displayName: auth.currentUser?.displayName || "Player One", visibility: "private" })
      );

      return storeSocialProfile(storedProfile);
    } catch (error) {
      return storeSocialProfile(DEFAULT_SOCIAL_PROFILE);
    }
  });
  const [socialFriends, setSocialFriends] = useState(() => {
    try {
      return storeSocialFriends(
        JSON.parse(localStorage.getItem(SOCIAL_FRIENDS_STORAGE_KEY) || "[]")
      );
    } catch (error) {
      return storeSocialFriends([]);
    }
  });
  const [privateSettingsReady, setPrivateSettingsReady] = useState(false);
  const socialControls=useSocialControls({accountUid:auth.currentUser?.uid,enabled:privateSettingsReady&&!showSplash,onBlocked:blocked=>{
    setSocialFriends(current=>{
      const next=current.filter(friend=>!blocked.some(player=>player.handle===friend.handle));
      return next.length===current.length?current:storeSocialFriends(next);
    });
  }});
  const [blockTarget,setBlockTarget]=useState(null),[commonFriend,setCommonFriend]=useState(null);
  const [reportTarget,setReportTarget]=useState(null);
  const friendInvitations=useFriendships({accountUid:auth.currentUser?.uid,enabled:privateSettingsReady&&!showSplash,onSync:connections=>{
    setSocialFriends(current=>{
      const next=normalizeSocialFriends(mergeConfirmedFriends(current,connections)).filter(friend=>!socialControls.data?.blocked.some(player=>player.handle===friend.handle));
      return JSON.stringify(next)===JSON.stringify(current)?current:storeSocialFriends(next);
    });
  }});
  const privateSettingsBaseRef=useRef(null);
  const privateSettingsExistsRef=useRef(false);
  const privateSettingsVersionRef=useRef(0);
  const publicProfileVersionRef=useRef(0);
  const profileVisibilityBusyRef=useRef(false);
  const [sharedProfile, setSharedProfile] = useState(null);
  const [searchDestination,setSearchDestination]=useState(null);
  const personalSeries=useMemo(()=>getDetectedGameSeries(games),[games]);
  const [miniPlayerLive, setMiniPlayerLive] = useState(null);
  const [miniPlayerCollapsed, setMiniPlayerCollapsed] = useState(false);
  const upcomingReleases = useUpcomingReleases(!showSplash);
  const { games: upcomingGames, status: upcomingSourceStatus } = upcomingReleases;
  const [notificationPanelOpen, setNotificationPanelOpen] = useState(false);
  const [monthlyNotification,setMonthlyNotification]=useState(null);
  const existingExtraNotifications=useUsefulUpdates({games,preferences:appOptions.notifications,enabled:!showSplash&&privateSettingsReady});
  const rendezvousNotifications=useRendezvousUpdates({accountUid:auth.currentUser?.uid,games,value:appOptions.engagement,preferences:appOptions.notifications,enabled:!showSplash&&privateSettingsReady});
  const releaseProgress=useReleaseProgress();
  const [releaseNotesOpen,setReleaseNotesOpen]=useState(false);
  const unreadReleases=RELEASE_NOTES.filter(r=>!releaseProgress.read.includes(r.id));
  const releaseNotifications=unreadReleases.length?[{id:`release-${unreadReleases[0].id}`,category:'updates',type:'release-notes',priority:88,title:'Du nouveau dans Checkpoint',message:unreadReleases[0].summary,cta:'Voir les nouveautés',tone:'focus'}]:[];
  const extraNotifications=[...existingExtraNotifications,...rendezvousNotifications,...releaseNotifications,...friendshipNotices(friendInvitations.connections)];
  const [dismissedNotificationIds, setDismissedNotificationIds] = useState(() => {
    try {
      const ids=JSON.parse(localStorage.getItem("checkpoint-dismissed-notifications-v2") || "[]");return Array.isArray(ids)?ids.filter(id=>typeof id==="string"):[];
    } catch (error) {
      return [];
    }
  });
  const [libraryView, setLibraryView] = useState("collection");
  const [couchOpen,setCouchOpen]=useState(false);
  const [nextPage, setNextPage] = useState(null);
  const [gamingEvents, setGamingEvents] = useState(FALLBACK_GAMING_EVENTS);
  const [pixelTransition, setPixelTransition] = useState(null);
  const soundStyle = appOptions.sound.style;
  const tabSwitchTimerRef = useRef(null);
  const tabTransitionTimerRef = useRef(null);
const tabOrder = [
  "home",
  "arena",
  "news",
  "search",
  "upcoming",
  "deals",
  "live",
  "social",
  "library",
  "series",
  "hardware",
  "favorites",
  "top5",
  "profile",
  "options",
];

useEffect(() => {
  let cancelled = false;

  const loadPrivateSettings = async () => {
    try {
      const snap = await getDoc(
        doc(db, PRIVATE_SETTINGS_COLLECTION, PRIVATE_SETTINGS_DOC_ID)
      );

      if(cancelled)return;
      privateSettingsExistsRef.current=snap.exists();
      privateSettingsBaseRef.current=snap.exists()?snap.data()||{}:{};
      if(!snap.exists())return;

      const remote = snap.data() || {};
      setUnlockedThemes(current => mergeThemeUnlocks(current, remote.unlockedThemes));

      if (remote.theme) {
        setTheme(remote.theme);
        localStorage.setItem("checkpoint-theme", remote.theme);
      }

      if (remote.uiMode) {
        setUiMode(remote.uiMode);
        localStorage.setItem("checkpoint-ui-mode", remote.uiMode);
      }

      if (remote.appOptions) {
        setAppOptions(storeAppOptions(remote.appOptions));
      }

      if (remote.socialProfile) {
        setSocialProfile((currentProfile) =>
          storeSocialProfile(
            mergeCloudSocialProfile(remote.socialProfile, currentProfile)
          )
        );
      }

      if (Array.isArray(remote.socialFriends)) {
        setSocialFriends(storeSocialFriends(remote.socialFriends));
      }
    } catch (error) {
      console.error("Erreur chargement préférences privées :", error);
      setToast("Tes réglages distants n’ont pas pu être chargés. Ils ne seront pas remplacés automatiquement. Rouvre l’app avec une connexion pour les synchroniser.");
    } finally {
      if (!cancelled) {
        setPrivateSettingsReady(true);
      }
    }
  };

  loadPrivateSettings();
  const retryInitial=()=>{if(privateSettingsBaseRef.current===null)loadPrivateSettings();};
  window.addEventListener('online',retryInitial);
  window.addEventListener('checkpoint-settings-resolved',loadPrivateSettings);
  return () => {
    cancelled = true;
    window.removeEventListener('online',retryInitial);
    window.removeEventListener('checkpoint-settings-resolved',loadPrivateSettings);
  };
}, []);

useEffect(() => {
  if(!privateSettingsReady||privateSettingsBaseRef.current===null)return;
  const owner=auth.currentUser?.uid;
  const payload={theme,unlockedThemes,uiMode,appOptions:normalizeAppOptions(appOptions),socialProfile:getCloudSafeSocialProfile(socialProfile),socialFriends:normalizeSocialFriends(socialFriends)};
  const patch=diffPrivateSettings(privateSettingsBaseRef.current,payload);
  if(!Object.keys(patch).length)return;
  const version=++privateSettingsVersionRef.current;
  const timer=window.setTimeout(()=>{
    const isCurrent=()=>auth.currentUser?.uid===owner&&privateSettingsVersionRef.current===version;
    saveFeedback.run({key:'private-settings',quiet:true,isCurrent,success:'Réglages enregistrés',write:async()=>{
      const ref=doc(db,PRIVATE_SETTINGS_COLLECTION,PRIVATE_SETTINGS_DOC_ID);
      const result=privateSettingsExistsRef.current?await updateDoc(ref,{...patch,updatedAt:serverTimestamp()}):await setDoc(ref,{...payload,updatedAt:serverTimestamp()},{merge:true});
      privateSettingsExistsRef.current=true;
      privateSettingsBaseRef.current=payload;
      return result;
    }});
  },800);
  return()=>window.clearTimeout(timer);
},[privateSettingsReady,theme,unlockedThemes,uiMode,appOptions,socialProfile,socialFriends]);

useEffect(() => {
  try{localStorage.setItem(
    "checkpoint-dismissed-notifications-v2",
    JSON.stringify(dismissedNotificationIds.slice(-150))
  );}catch{/* Device storage can be unavailable. */}
}, [dismissedNotificationIds]);

const updateAppOption = (key, value) => {
  setAppOptions((prev) => {
    const nextOptions = storeAppOptions({
      ...prev,
      [key]: value,
    });

    return nextOptions;
  });
};

useEffect(()=>{
 if(appOptions.hiddenTabs?.includes(activeTab))setActiveTab('home');
},[appOptions.hiddenTabs,activeTab]);

const resetAppOptions = () => {
  setAppOptions(storeAppOptions(DEFAULT_APP_OPTIONS));
  showToast("Options remises au propre.");
};

const backupPayload = useMemo(()=>({version:1,exportedAt:new Date().toISOString(),options:appOptions,socialProfile,socialFriends,games,hardware}),[appOptions,socialProfile,socialFriends,games,hardware]);
const captureCurrentBackup = (reason="Manuelle") => captureBackup(auth.currentUser?.uid,backupPayload,reason,true);
const restoreBackupVersion = async (payload) => {
  if(!payload || !Array.isArray(payload.games) || !Array.isArray(payload.hardware))throw new Error("Sauvegarde incomplète : jeux et matériel requis.");
  for(const item of [...payload.games,...payload.hardware])if(!item||typeof item.id!=="string"||!item.id||item.id.includes("/")||item.id.length>250)throw new Error("Une fiche de cette sauvegarde a un identifiant invalide.");
  await captureCurrentBackup("Avant restauration");
  for(const kind of ["games","hardware"])for(const item of payload[kind]){const {id,_deletedAt,...data}=item;await setDoc(doc(db,kind,id),data,{recovery:true});}
  if(payload.options){const next=storeAppOptions(payload.options);setAppOptions(next);}
  if(payload.socialProfile){const next=storeSocialProfile(payload.socialProfile);setSocialProfile(next);await syncPublicProfileIfNeeded(next);}
  if(Array.isArray(payload.socialFriends))setSocialFriends(storeSocialFriends(payload.socialFriends));
};
const exportCheckpointBackup = () => {
  const payload = {
    version: 1,
    exportedAt: new Date().toISOString(),
    options: appOptions,
    socialProfile,
    socialFriends,
    games,
    hardware,
  };
  const blob = new Blob([JSON.stringify(payload, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.download = `checkpoint-backup-${new Date().toISOString().slice(0, 10)}.json`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  showToast("Sauvegarde prête.");
};

const importCheckpointBackup = async (event) => {
  const file = event.target.files?.[0];
  event.target.value = "";

  if (!file) return;

  try {
    const text = await file.text();
    const payload = JSON.parse(text);
    if(Array.isArray(payload.games)&&Array.isArray(payload.hardware)){
      if(!window.confirm(`Restaurer ${payload.games.length} jeux et ${payload.hardware.length} matériels ? Les fiches correspondantes seront remplacées ; les ajouts plus récents seront conservés.`))return;
      await restoreBackupVersion(payload);
      showToast("Restauration préparée. Vérifie la synchronisation dans les Options.");return;
    }
    await captureCurrentBackup("Avant import des préférences");

    if (payload.options) {
      const nextOptions = storeAppOptions(payload.options);
      setAppOptions(nextOptions);
    }

    if (payload.socialProfile) {
      const nextProfile = storeSocialProfile(payload.socialProfile);
      setSocialProfile(nextProfile);
      await syncPublicProfileIfNeeded(nextProfile);
    }

    if (Array.isArray(payload.socialFriends)) {
      setSocialFriends(storeSocialFriends(payload.socialFriends));
    }

    showToast("Sauvegarde restaurée.");
  } catch (error) {
    console.error("Erreur import sauvegarde :", error);
    showToast("Impossible de lire cette sauvegarde.");
  }
};

const updateHardwareRank = (id,rank) => persistPatch("hardware",id,{controllerRank:rank},{success:"Classement du matériel enregistré"});

const newsImportInFlight = useRef(false);
const importNewsFromRSS = async ({ silent = false, force = false } = {}) => {
  if(newsImportInFlight.current)return;
  newsImportInFlight.current=true;
  const controller=new AbortController();
  const timeout=setTimeout(()=>controller.abort(),15000);
  try {
    const lastImport = localStorage.getItem("checkpoint-last-news-import");
    const extractImage = (item) => {
      if (item.thumbnail) return item.thumbnail;
      if (item.enclosure?.link) return item.enclosure.link;

      const match = item.description?.match(/<img[^>]+src="([^">]+)"/);
      if (match?.[1]) return match[1];

      return "";
    };
    const now = Date.now();
    const sixHours = 6 * 60 * 60 * 1000;

    if (!force && lastImport && now - Number(lastImport) < sixHours) {
      if (!silent) alert("Actus déjà importées aujourd’hui");
      return;
    }

    const res = await fetch(
      "https://api.rss2json.com/v1/api.json?rss_url=https://www.jeuxvideo.com/rss/rss.xml",
      {signal:controller.signal}
    );
    if(!res.ok)throw new Error('Le flux d’actualités est indisponible.');
    const data = await res.json();
    if(!Array.isArray(data.items))throw new Error('Le flux d’actualités est invalide.');
    const news = (data.items || []).slice(0, 10);

    for (const item of news) {
      const safeId = encodeURIComponent(item.link || item.guid || item.title);

      await setDoc(doc(db, "news", safeId), {
        title: item.title || "Sans titre",
        summary: item.description || "",
        url: item.link || "",
        date: item.pubDate || new Date().toISOString(),
        source: "JeuxVideo.com",
        category: "Gaming",
        image: extractImage(item),
        importedAt: new Date(),
      });
    }

    localStorage.setItem("checkpoint-last-news-import", String(now));

    if (!silent) alert("Actus importées ✅");
  } catch (error) {
    const message = String(error?.message || error).toLowerCase();

    if (
      error?.name === "AbortError" ||
      message.includes("aborted") ||
      message.includes("user aborted")
    ) {
      return;
    }

    console.error("Erreur import RSS :", error);
    if (!silent) alert("Erreur import RSS");
  } finally {
    clearTimeout(timeout);
    newsImportInFlight.current=false;
  }
};

useEffect(() => {
  if (showSplash || (accountsEnabled && auth.currentUser?.uid !== ownerUid)) return;

  importNewsFromRSS({ silent: true }).catch((error) => {
    console.error("Erreur actualisation automatique des actus :", error);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showSplash]);

const [newUnlockedBadge, setNewUnlockedBadge] = useState(null);
const badgeToastInitializedRef = useRef(false);
const newsEmptyImportAttemptedRef = useRef(false);

const addHardware = async (item) => {
  try {
    await addDoc(collection(db, "hardware"), item);
    showToast("Belle prise ajoutée à ton setup.");
    playSound("success", soundStyle);
    return true;
  } catch (error) {
    if (isAbortError(error)) return false;
    console.error("Erreur ajout matériel :", error);
    showToast("Impossible d’ajouter ce matériel pour le moment.");
    return false;
  }
};

const deleteHardware = async (id) => {
  const item = hardware.find((hardwareItem) => hardwareItem.id === id);
  if (
    appOptions.confirmDangerActions &&
    !window.confirm(`Supprimer "${item?.name || "ce matériel"}" ?`)
  ) {
    return;
  }

  try {
    await captureCurrentBackup("Avant suppression de matériel");
    await deleteDoc(doc(db, "hardware", id));
    showToast("Matériel placé dans la corbeille (30 jours après synchronisation).");
    playSound("delete", soundStyle);
  } catch (error) {
    if (isAbortError(error)) return;
    console.error("Erreur suppression matériel :", error);
    showToast("Impossible de supprimer pour le moment.");
  }
};

const toggleHardwareFavorite = (id,currentFavorite) => persistPatch("hardware",id,{favorite:!currentFavorite},{success:"Favori du matériel enregistré",undoable:true});

const showToast = (message, duration = 2000) => {
  setToast(message);
  setTimeout(() => setToast(""), duration);
};

useEffect(() => {
  const url = new URL(window.location.href);
  const xboxStatus = url.searchParams.get("xbox");

  if (!xboxStatus) return;

  if (xboxStatus === "connected") {
    showToast("Compte Xbox connecte.");
  } else if (xboxStatus === "error") {
    showToast(url.searchParams.get("message") || "Connexion Xbox annulee.");
  }

  url.searchParams.delete("xbox");
  url.searchParams.delete("message");
  window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, []);

const openTrialRoom = (checkpointLevel) => {
  setDismissedCheckpointLevel(null);
  setActiveTrialLevel(Number(checkpointLevel));
  playSound("click", soundStyle);
};

const closeTrialRoom = () => {
  setActiveTrialLevel(null);
};

const completeCheckpointTrial = async (trial, result = {}) => {
  const key = String(trial.level);
  const completedAt = new Date().toISOString();
  const reward = trial.reward || {};
  const nextAttempts = (checkpointTrialProgress.attempts?.[key] || 0) + 1;
  const historyEntry = {
    id: `trial-${key}-${Date.now()}`,
    level: trial.level,
    title: trial.title,
    guardian: trial.guardian,
    rewardRank: trial.rewardRank,
    badgeId: reward.badgeId || "",
    badgeName: reward.badgeName || "",
    score: Number(result.score) || 0,
    totalQuestions: Number(result.totalQuestions) || trial.questions?.length || 0,
    attempts: nextAttempts,
    completedAt,
  };
  const nextProgress = {
    ...checkpointTrialProgress,
    completed: {
      ...(checkpointTrialProgress.completed || {}),
      [key]: {
        completedAt,
        rewardRank: trial.rewardRank,
        badgeId: reward.badgeId || "",
        badgeName: reward.badgeName || "",
        score: historyEntry.score,
        totalQuestions: historyEntry.totalQuestions,
      },
    },
    attempts: {
      ...(checkpointTrialProgress.attempts || {}),
      [key]: nextAttempts,
    },
    exclusiveBadges: reward.badgeId
      ? {
          ...(checkpointTrialProgress.exclusiveBadges || {}),
          [reward.badgeId]: {
            id: reward.badgeId,
            name: reward.badgeName,
            icon: reward.badgeIcon,
            rarity: reward.rarity,
            accent: reward.accent,
            unlockedAt: completedAt,
            checkpointLevel: trial.level,
          },
        }
      : checkpointTrialProgress.exclusiveBadges || {},
    history: [
      historyEntry,
      ...(checkpointTrialProgress.history || []).filter(
        (entry) => String(entry.level) !== key
      ),
    ].slice(0, 24),
    lastUnlockedLevel: trial.level,
  };

  setCheckpointTrialProgress(nextProgress);
  storeCheckpointTrialProgress(nextProgress);
  setActiveTrialLevel(null);
  setDismissedCheckpointLevel(null);
  playSound("levelup", soundStyle);
  showToast(`${trial.rewardRank} debloque.`, 2800);

  try {
    await setDoc(
      doc(db, "playerProgress", "checkpointTrials"),
      {
        ...nextProgress,
        updatedAt: serverTimestamp(),
      },
      { merge: true }
    );
  } catch (error) {
    console.error("Erreur sync Salle des Epreuves :", error);
    showToast("Rang débloqué localement. Synchronisation à vérifier.", 3200);
  }
};

useEffect(() => {
  let cancelled = false;

  const loadCheckpointTrials = async () => {
    try {
      const snap = await getDoc(doc(db, "playerProgress", "checkpointTrials"));
      if (cancelled || !snap.exists()) return;

      const remote = snap.data();
      const nextProgress = {
        ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS,
        ...remote,
        completed: {
          ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS.completed,
          ...(remote.completed || {}),
        },
        attempts: {
          ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS.attempts,
          ...(remote.attempts || {}),
        },
        exclusiveBadges: {
          ...DEFAULT_CHECKPOINT_TRIAL_PROGRESS.exclusiveBadges,
          ...(remote.exclusiveBadges || {}),
        },
        history: Array.isArray(remote.history) ? remote.history : [],
      };

      setCheckpointTrialProgress(current => {
        const merged={...nextProgress,completed:{...nextProgress.completed,...current.completed},exclusiveBadges:{...nextProgress.exclusiveBadges,...current.exclusiveBadges},attempts:{...nextProgress.attempts,...current.attempts},history:[...(current.history||[]),...(nextProgress.history||[])].filter((entry,index,list)=>list.findIndex(other=>String(other.level)===String(entry.level))===index).slice(0,24)};
        storeCheckpointTrialProgress(merged);return merged;
      });
    } catch (error) {
      console.error("Erreur chargement Salle des Epreuves :", error);
    } finally {
      if (!cancelled) setTrialProgressReady(true);
    }
  };

  loadCheckpointTrials();

  return () => {
    cancelled = true;
  };
}, []);

const [quizWeekKey, setQuizWeekKey] = useState(getWeeklyQuizKey);
useEffect(() => {
  const refresh = () => setQuizWeekKey(getWeeklyQuizKey());
  const timer = window.setInterval(refresh, 60000);
  window.addEventListener("focus", refresh);
  document.addEventListener("visibilitychange", refresh);
  return () => {
    window.clearInterval(timer);
    window.removeEventListener("focus", refresh);
    document.removeEventListener("visibilitychange", refresh);
  };
}, []);
const weeklyQuiz = useMemo(() => getWeeklyQuiz(quizWeekKey), [quizWeekKey]);
const quizProgressRef = useRef(weeklyQuizProgress);
useEffect(() => { quizProgressRef.current = weeklyQuizProgress; }, [weeklyQuizProgress]);

const handleWeeklyQuizAnswer = (answerIndex, options = {}) => {
  const nextProgress = submitWeeklyQuizAnswer(quizProgressRef.current, { ...options, answerIndex });
  if (!nextProgress) {
    setQuizWeekKey(getWeeklyQuizKey());
    return;
  }
  // Claim synchronously before rendering, so rapid taps cannot grant XP twice.
  quizProgressRef.current = nextProgress;
  setWeeklyQuizProgress(nextProgress);
  storeWeeklyQuizProgress(nextProgress);
  const answer = getWeeklyQuizAnswer(nextProgress, options.weekKey, options.questionId);
  playSound(answer.correct ? "success" : "click", soundStyle);
  showToast(answer.correct ? `Bien joué ! +${answer.earnedXP} XP` : `Tentative validée. +${answer.earnedXP} XP`);
};

const handleClaimCheckpointGoal = (goal) => {
  if (!goal?.id || !goal.claimable || checkpointGoalProgress.claimed?.[goal.id]) return;

  const earnedXP = Number(goal.xp || 0);
  const nextProgress = {
    ...checkpointGoalProgress,
    totalXP: (checkpointGoalProgress.totalXP || 0) + earnedXP,
    claimed: {
      ...(checkpointGoalProgress.claimed || {}),
      [goal.id]: {
        title: goal.title,
        earnedXP,
        claimedAt: new Date().toISOString(),
      },
    },
  };

  setCheckpointGoalProgress(nextProgress);
  storeCheckpointGoalProgress(nextProgress);
  playSound("success", soundStyle);
  showToast(`${goal.title} validé. +${earnedXP} XP`);
};
const updateSocialProfile = (field, value) => {
  const nextProfile = storeSocialProfile({
    ...socialProfile,
    [field]: field === "handle" ? normalizeHandle(value) : value,
  });

  setSocialProfile(nextProfile);

  if(!profileVisibilityBusyRef.current&&['public','friends'].includes(nextProfile.visibility)&&normalizeHandle(nextProfile.handle)){
    syncPublicProfileIfNeeded(nextProfile);
  }
};

const syncPublicProfileIfNeeded = async (profile) => {
  const handle = normalizeHandle(profile.handle);

  if (!["public","friends"].includes(profile.visibility) || !handle) return;

  if(profileVisibilityBusyRef.current)return false;
  const owner=auth.currentUser?.uid,version=++publicProfileVersionRef.current;
  return saveFeedback.run({
    key:'public-profile',
    isCurrent:()=>auth.currentUser?.uid===owner&&publicProfileVersionRef.current===version,
    success:'Profil public mis à jour',
    write:()=>setDoc(doc(db,'publicProfiles',handle),{...buildPublicSocialProfile(profile),visibility:profile.visibility,...(owner?{ownerUid:owner}:{})},{merge:true})
  });
};

const addSocialProfilePhotos = async (field, fileList) => {
  const files = Array.from(fileList || []).slice(0, 6);

  if (!files.length) {
    return { ok: false, message: "Aucune photo sélectionnée." };
  }

  try {
    const photos = await Promise.all(files.map((file) => resizeSocialPhoto(file)));
    const nextProfile = storeSocialProfile({
      ...socialProfile,
      [field]: [...(socialProfile[field] || []), ...photos].slice(0, 9),
    });

    setSocialProfile(nextProfile);
    await syncPublicProfileIfNeeded(nextProfile);

    return { ok: true, message: "Photos ajoutées au profil." };
  } catch (error) {
    console.error("Erreur ajout photos :", error);
    return { ok: false, message: "Impossible d'ajouter ces photos." };
  }
};

const removeSocialProfilePhoto = async (field, index) => {
  const nextProfile = storeSocialProfile({
    ...socialProfile,
    [field]: (socialProfile[field] || []).filter(
      (_, itemIndex) => itemIndex !== index
    ),
  });

  setSocialProfile(nextProfile);

  try {
    await syncPublicProfileIfNeeded(nextProfile);
  } catch (error) {
    console.error("Erreur suppression photo publique :", error);
  }
};

const buildPublicSocialProfile = (profileOverride = socialProfile) => {
  const handle = normalizeHandle(profileOverride.handle);
  const socialPosts = Array.isArray(profileOverride.posts)
    ? profileOverride.posts
    : [];
  const postActivities = socialPosts.map((post, index) => {
    const date = parseActivityDate(post.createdAt);

    return {
      id: `post-${post.id || index}`,
      type: "Post",
      title: "Publication",
      text: post.text,
      detail: post.mood || "",
      image: post.image || "",
      author: handle,
      date: post.createdAt,
      sortTime: date?.getTime() || 0,
      priority: 90 - index,
    };
  });
  const activities = [
    ...postActivities,
    ...getSocialActivityFeed(games, hardware, badges),
  ]
    .sort((a, b) => {
      if ((b.sortTime || 0) !== (a.sortTime || 0)) {
        return (b.sortTime || 0) - (a.sortTime || 0);
      }

      return (b.priority || 0) - (a.priority || 0);
    })
    .slice(0, 8);
  const activityLikes = profileOverride.activityLikes || {};
  const activityComments = profileOverride.activityComments || {};
  const stats = getAdvancedStats(games);
  const currentHardware = getCurrentOwnedHardware(hardware);
  const identityGameIds = Array.isArray(profileOverride.identityGameIds)
    ? profileOverride.identityGameIds.slice(0, 3).map(String)
    : [];
  const identityGames = identityGameIds
    .map((id) => games.find((game) => String(game.id) === id))
    .filter(Boolean);
  const identityTitle = getIdentityPlayerTitle(identityGames);
  const playerAnalysis = getPlayerAnalysis(games, hardware, badges);
  const gamingTimeline = getGamingTimeline(games, hardware, profileOverride);
  const essentialTopSections = [
    { key: "rating", label: "Global", games: getTopGamesForScore(games, "rating", 1) },
    { key: "ratingGameplay", label: "Gameplay", games: getTopGamesForScore(games, "ratingGameplay", 1) },
    { key: "ratingGraphics", label: "Graphismes", games: getTopGamesForScore(games, "ratingGraphics", 1) },
  ].filter((section) => section.games.length > 0);
  const profileShowcase = getProfileShowcase(games, hardware);
  const featuredBadge = getFeaturedBadgeFromSelection(
    badges,
    profileOverride.featuredBadgeId
  );
  const unlockedBadges = badges.filter((badge) => badge.unlocked);
  const selectedBadgeIds = Array.isArray(profileOverride.selectedBadgeIds)
    ? profileOverride.selectedBadgeIds
        .map(String)
        .filter((id, index, list) => id && list.indexOf(id) === index)
        .filter((id) => id !== String(featuredBadge?.id || ""))
        .slice(0, 4)
    : [];
  const selectedBadges = selectedBadgeIds
    .map((id) => unlockedBadges.find((badge) => String(badge.id) === id))
    .filter(Boolean);

  return publicProfileView({
    displayName: profileOverride.displayName || DEFAULT_SOCIAL_PROFILE.displayName,
    photoURL: profileOverride.photoURL || "",
    handle,
    bio: profileOverride.bio || "",
    platform: profileOverride.platform || "",
    visibility: profileOverride.visibility || "prive",
    publicSections: {
      ...DEFAULT_PUBLIC_SECTIONS,
      ...(profileOverride.publicSections || {}),
    },
    featuredBadge: toPublicBadge(featuredBadge),
    selectedBadges: selectedBadges
      .map(toPublicBadge),
    level,
    totalGames: games.length,
    finishedGames: games.filter(isGameFinishedStatus).length,
    hardwareCount: currentHardware.length,
    averageRating: stats.avgRating ? Math.round(stats.avgRating * 10) / 10 : 0,
    platformStrengths: stats.topPlatforms,
    setupPhotos: profileOverride.setupPhotos || [],
    collectionPhotos: profileOverride.collectionPhotos || [],
    identityTitle,
    playerAnalysis,
    gamingTimeline,
    showcase: profileShowcase,
    identityGames: identityGames.map((game) => ({
      ...toPublicFounderGame(game),
      rating: getGameRating(game),
    })),
    essentialTops: essentialTopSections.map((section) => {
      const game = section.games[0];

      return {
        key: section.key,
        label: section.label,
        game: {
          id: game.id,
          name: game.name,
          image: game.image || "",
          sourceIds:toPublicFounderGame(game).sourceIds,
          released:game.released||"",
          score: getGameScore(game, section.key),
        },
      };
    }),
    favoriteGames: publicFavoriteGames(games,getGameRating,profileOverride.publicSections?.essential!==false),
    currentHardware: currentHardware.slice(0, 6).map((item) => ({
      id: item.id,
      name: item.name,
      image: item.image || "",
      type: item.type || "",
      status: item.status || "",
      rating: clampRating(item.rating),
      ratings: item.ratings || {},
      gameCount: getHardwareConsoleGameStats(item, games).games,
    })),
    recentActivity: activities.map((activity) => ({
      id: activity.id,
      type: activity.type,
      title: activity.title,
      text: activity.text,
      detail: activity.detail || "",
      source:activity.source||"",
      reviewText:activity.reviewText||"",
      reviewSpoiler:Boolean(activity.reviewSpoiler),
      image: activity.image || "",
      date: activity.date || null,
      author: activity.author || "",
      likes: getActivityLikeState(activityLikes, activity.id).count,
      comments: activityComments[activity.id] || [],
    })),
    updatedAt: serverTimestamp(),
  });
};

const setSocialProfileVisibility = async (visibility) => {
  let handle=normalizeHandle(socialProfile.handle);
  if(!['public','friends','prive'].includes(visibility))return {ok:false,message:'Choisis une visibilité valide.'};
  if(!String(socialProfile.displayName||'').trim()&&visibility!=='prive')return {ok:false,message:'Renseigne ton pseudo pour activer ton profil.'};
  if(profileVisibilityBusyRef.current)return {ok:false,message:'Le changement de visibilité est déjà en cours.'};
  profileVisibilityBusyRef.current=true;
  const owner=auth.currentUser?.uid,version=++publicProfileVersionRef.current;
  const normalizedProfile=normalizeSocialProfile({...socialProfile,handle,visibility});
  try{
    const saved=await saveFeedback.run({key:'public-profile',isCurrent:()=>auth.currentUser?.uid===owner&&publicProfileVersionRef.current===version,success:visibility==='public'?'Profil public activé':visibility==='friends'?'Profil réservé aux amis':'Profil privé appliqué',write:async()=>{
      if(['public','friends'].includes(visibility))handle=await publishProfileWithIdentity({profile:normalizedProfile,owner,write:profile=>setDoc(doc(db,'publicProfiles',profile.handle),{...buildPublicSocialProfile(profile),visibility,ownerUid:owner},{merge:true})});
      else if(handle)await deleteDoc(doc(db,'publicProfiles',handle));
      if(auth.currentUser?.uid===owner){setSocialProfile(current=>storeSocialProfile({...current,handle,visibility}));showToast(visibility==='public'?'Profil public activé.':visibility==='friends'?'Profil visible uniquement par tes amis.':'Profil repassé en privé.');}
    }});
    return saved===false?{ok:false,message:'Impossible de changer la visibilité pour le moment.'}:{ok:true,message:visibility==='public'?'Profil public. Tes amis peuvent te trouver.':visibility==='friends'?'Seuls tes amis confirmés peuvent consulter ton profil.':"Profil privé. Seul ton pseudo apparaît dans la recherche, sans accès au profil ni invitation."};
  }finally{profileVisibilityBusyRef.current=false;}
};

const copySocialProfileLink = async () => {
  const handle = normalizeHandle(socialProfile.handle);

  if (!handle) {
    return { ok: false, message: 'Active ton profil public ou réservé aux amis pour créer ton lien automatiquement.' };
  }

  if (!["public","friends"].includes(socialProfile.visibility)) {
    return {
      ok: false,
      message: "Choisis un profil public ou réservé aux amis avant de partager le lien.",
    };
  }

  try {
    if(await syncPublicProfileIfNeeded(socialProfile)===false)return {ok:false,message:'Attends la fin de l’enregistrement du profil avant de partager le lien.'};

    const shareUrl = getProfileShareUrl(handle);
    await navigator.clipboard.writeText(shareUrl);
    showToast("Lien du profil prêt à partager.");
    return { ok: true, message: "Lien copié dans le presse-papiers." };
  } catch (error) {
    console.error("Erreur copie profil :", error);
    return { ok: false, message: "Impossible de copier le lien pour le moment." };
  }
};

const addSocialFriend = async (handleValue) => {
  const handle = normalizeHandle(handleValue);
  const ownHandle = normalizeHandle(socialProfile.handle);

  if (!handle) {
    return { ok: false, message: "Entre l'identifiant d'un ami." };
  }

  if (handle === ownHandle) {
    return { ok: false, message: "Tu ne peux pas t'ajouter toi-même." };
  }

  if (friendInvitations.connections.some(item=>item.profile.handle===handle&&item.status==='accepted')) {
    return { ok: false, message: "Cet ami est déjà dans ta liste." };
  }

  try {
    if(await syncPublicProfileIfNeeded(socialProfile)===false)return {ok:false,message:'Attends la fin de l’enregistrement de ton profil.'};
    const result=await friendInvitations.act('send',{handle});
    const message=result.status==='accepted'?'Vous êtes déjà amis.':result.direction==='received'?'Ce joueur t’a déjà envoyé une invitation. Retrouve-la dans Social → Amis.':'Invitation envoyée. Ce joueur pourra l’accepter ou la refuser.';
    showToast(message);
    return {ok:true,message};
  } catch (error) {
    console.error("Erreur ajout ami :", error);
    return { ok: false, message: error.message || "Impossible d'ajouter cet ami pour le moment." };
  }
};

const removeSocialFriend = async (handle) => {
  try{
    const connection=friendInvitations.connections.find(item=>item.profile.handle===handle&&item.status==='accepted');
    if(connection)await friendInvitations.act('remove',{id:connection.id});
    setSocialFriends(prev=>storeSocialFriends(prev.filter(friend=>friend.handle!==handle)));
  }catch(error){showToast(error.message||'Impossible de retirer cet ami.');}
};

const openSocialFriendProfile = async handle => {
  const profile = await loadCheckpointSocialProfile(handle);
  setSharedProfile(profile);
  window.scrollTo({ top: 0, behavior: "auto" });
};

const closeSharedProfile = () => {
  setSharedProfile(null);
  const url = new URL(window.location.href);
  url.searchParams.delete("profile");
  window.history.replaceState({}, "", url.toString());
};

useEffect(() => {
  const handle = normalizeHandle(
    new URLSearchParams(window.location.search).get("profile") || ""
  );

  if (!handle) return;

  const fetchSharedProfile = async () => {
    try {
      const profileSnap = await getDoc(doc(db, "publicProfiles", handle));

      if (profileSnap.exists()) {
        setSharedProfile(profileSnap.data());
        setActiveTab("social");
      } else {
        showToast("Profil partagé introuvable.");
      }
    } catch (error) {
      console.error("Erreur chargement profil partagé :", error);
      showToast("Impossible de charger le profil partagé.");
    }
  };

  fetchSharedProfile();
}, []);

useEffect(() => {
  const unsubscribe = onSnapshot(collection(db, "events"), (snapshot) => {
    const eventsData = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    setGamingEvents(eventsData);
    setBootReady((ready) => ({ ...ready, events: true }));
  });

  return () => unsubscribe();
}, []);

useEffect(() => {
  const unsubscribe = onSnapshot(collection(db, "news"), (snapshot) => {
    const newsData = snapshot.docs.map((docItem) => ({
      id: docItem.id,
      ...docItem.data(),
    }));

    setNewsItems(newsData);
    setBootReady((ready) => ({ ...ready, news: true }));
  });

  return () => unsubscribe();
}, []);

useEffect(() => {
  if (showSplash || newsItems.length > 0 || newsEmptyImportAttemptedRef.current || (accountsEnabled && auth.currentUser?.uid !== ownerUid)) return;

  newsEmptyImportAttemptedRef.current = true;
  importNewsFromRSS({ silent: true, force: true }).catch((error) => {
    console.error("Erreur import actus au demarrage :", error);
  });
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [showSplash, newsItems.length]);

useEffect(() => {
  const unsubscribe = onSnapshot(collection(db, "hardware"), (snapshot) => {
    const hardwareData = snapshot.docs.map((docItem) =>
      normalizeHardwareRatings({
        id: docItem.id,
        ...docItem.data(),
      })
    );

    setHardware(overlayDrafts("hardware",hardwareData));
    setBootReady((ready) => ({ ...ready, hardware: true }));
  });

  return () => unsubscribe();
}, []);

const updateHardwareStatus = (id,status) => persistPatch("hardware",id,{status},{success:"Statut du matériel enregistré",undoable:true});
const updateHardwareSanctuary = (id,sanctuary) => persistPatch("hardware",id,{sanctuary},{success:sanctuary?"Matériel ajouté au Sanctuaire":"Matériel retiré du Sanctuaire",undoable:true});
const updateHardwareCondition = (id,condition) => persistPatch("hardware",id,{condition},{success:"État du matériel enregistré"});
const updateHardwareAcquiredYear = (id,value) => {
 const acquiredYear=value===""?null:Number(value);
 if(acquiredYear!==null&&(!Number.isInteger(acquiredYear)||acquiredYear<1950||acquiredYear>new Date().getFullYear()))return;
 return persistPatch("hardware",id,{acquiredYear},{success:"Année d’obtention enregistrée"});
};
const updateHardwareDisplaySize = (id,displaySize) => persistPatch("hardware",id,{displaySize},{success:"Taille d’écran enregistrée"});



useEffect(() => {
  applySoundSettings(appOptions.sound);
}, [appOptions.sound]);

useEffect(() => () => stopAllSounds(), []);

const [libraryCardMode, setLibraryCardMode] = useState(() => {
  const savedMode = localStorage.getItem("checkpoint-library-card-mode");
  return savedMode === "detailed" ? "compact" : savedMode || "compact";
});

useEffect(() => {
  if (libraryCardMode === "detailed") {
    setLibraryCardMode("compact");
    return;
  }

  localStorage.setItem("checkpoint-library-card-mode", libraryCardMode);
}, [libraryCardMode]);

  const years = useMemo(() => {
    const arr = [];
    for (let year = 2026; year >= 1980; year--) arr.push(String(year));
    return arr;
  }, []);


  useEffect(() => {
  const handleClick = (e) => {
    if (document.body.dataset.ui === "pixel") {
      createPixelEffect(e.clientX, e.clientY);
    }
  };

  window.addEventListener("click", handleClick);

  return () => window.removeEventListener("click", handleClick);
}, []);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    document.body.setAttribute("data-theme", theme);
    document.title = theme === CYBER2077_THEME ? "Checkpoint 2077" : "Checkpoint";
    localStorage.setItem("checkpoint-theme", theme);

    const metaTheme = document.querySelector('meta[name="theme-color"]');
    const colors = {
      "theme-checkpoint": "#090a0c",
      "theme-checkpointpremium": "#08090d",
      "theme-checkpointultimate": "#060709",
      "theme-indigo": "#07091f",
      "theme-emerald": "#121a24",
      "theme-crimson": "#111419",
      "theme-graphite": "#111a22",
      "theme-retro": "#201326",
      "theme-cyberpunk": "#0c1027",
      "theme-checkpoint2077": "#080b0e",
      "theme-playstation": "#101827",
      "theme-xbox": "#07120b",
      "theme-steamdeck": "#171717",
      "theme-arcane": "#100814",
      "theme-stellar": "#020716",
      "theme-multiverse": "#090f24",
      "theme-relic": "#07120e",
      "theme-tamer": "#07131c",
      "theme-mushroom": "#071b35",
      "theme-blueblur": "#03102f",
      "theme-blocks": "#14051d",
      "theme-cubic": "#0b1f12",
      "theme-hunter": "#08111b",
      "theme-kart": "#100b24",
      "theme-crystal": "#070c23",
      "theme-ring": "#050715",
      "theme-matrix": "#020a06",
      "theme-wasteland": "#130b05",
      "theme-treasure": "#0b2038",
      "theme-slayer": "#081b1d",
      "theme-vigilante": "#050711",
      ...Object.fromEntries([...POP_CULTURE_THEMES, ...GAME_UNIVERSE_THEMES].map(item => [item.id, item.chrome])),
          };

    if (metaTheme) {
      metaTheme.setAttribute("content", colors[theme] || "#060611");
    }
  }, [theme]);

  useEffect(() => {
    document.body.setAttribute("data-ui", uiMode);
    localStorage.setItem("checkpoint-ui-mode", uiMode);
  }, [uiMode]);

useEffect(() => {
  storeAppOptions(appOptions);
  document.body.setAttribute("data-effects", appOptions.visualEffects || "balanced");
  document.body.setAttribute(
      "data-background-motion",
      appOptions.animatedBackground ? "on" : "off"
    );
    document.body.setAttribute("data-app-icon", "theme");
    document.body.setAttribute("data-header-mode", appOptions.headerMode || "standard");
    document.body.setAttribute("data-mobile-nav", appOptions.mobileNavMode || "standard");

    let iconLink = document.querySelector('link[rel="icon"]');
    if (!iconLink) {
      iconLink = document.createElement("link");
      iconLink.setAttribute("rel", "icon");
      document.head.appendChild(iconLink);
    }

    iconLink.setAttribute("href", "/checkpoint-icon-64.png");
    iconLink.setAttribute("type", "image/png");
    iconLink.setAttribute("sizes", "64x64");
  }, [appOptions]);

useEffect(() => {
  if (!appOptions.rememberLastTab) return;
  if (!APP_TAB_IDS.includes(activeTab)) return;

  localStorage.setItem("checkpoint-last-tab", activeTab);
}, [activeTab, appOptions.rememberLastTab]);

  useEffect(() => {
    return () => {
      window.clearTimeout(tabSwitchTimerRef.current);
      window.clearTimeout(tabTransitionTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const values = Object.values(bootReady);
    onStartupProgress?.(values.filter(Boolean).length / values.length);
  }, [bootReady, onStartupProgress]);

  useEffect(() => {
    if (!showSplash) return;
    // A slow optional feed must not keep the whole app behind its introduction.
    const timer = window.setTimeout(() => onStartupProgress?.(1), 8000);
    return () => window.clearTimeout(timer);
  }, [showSplash, onStartupProgress]);

  useEffect(() => {
    saveStartupAppearance({ theme, effects: appOptions.visualEffects, light: appOptions.transitionMotion === "light" || uiMode === "reduced" });
  }, [theme, appOptions.visualEffects, appOptions.transitionMotion, uiMode]);

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "games"), (snapshot) => {
      const gamesData = snapshot.docs.map((docItem) =>
        normalizeGameRatings({
          id: docItem.id,
          ...docItem.data(),
        })
      );

      setGames(overlayDrafts("games",gamesData));
      setBootReady((ready) => ({ ...ready, games: true }));
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (showSplash) return;
    const fetchFilterData = async () => {
      try {
        const [platformsData, genresData] = await Promise.all([
          rawgApiRequest("/platforms", { page_size: "40" }, { timeout: 6500 }),
          rawgApiRequest("/genres", { page_size: "40" }, { timeout: 6500 }),
        ]);

        setPlatforms(platformsData.results || []);
        setGenres(genresData.results || []);
      } catch (e) {
        if (isAbortError(e)) return;
        console.error("Erreur chargement filtres :", e);
      }
    };

    fetchFilterData();
  }, [showSplash]);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(""), 2200);
    return () => clearTimeout(t);
  }, [toast]);



  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400);

    return () => clearTimeout(timer);
  }, [search]);

  const rawTotalXP = useMemo(() => {
    const gamesXP = games
      .filter((g) => g.status !== "wishlist")
      .reduce((sum, g) => sum + calculateXP(g), 0);

    return gamesXP + hardware.reduce((sum, item) => sum + calculateHardwareXP(item), 0) + (weeklyQuizProgress.totalXP || 0) + (checkpointGoalProgress.totalXP || 0);
  }, [games, hardware, weeklyQuizProgress.totalXP, checkpointGoalProgress.totalXP]);

  const pendingCheckpointLevel = useMemo(
    () => getPendingCheckpoint(rawTotalXP, checkpointTrialProgress.completed),
    [rawTotalXP, checkpointTrialProgress.completed]
  );
  const totalXP = useMemo(
    () => getEffectiveXP(rawTotalXP, checkpointTrialProgress.completed),
    [rawTotalXP, checkpointTrialProgress.completed]
  );
  const level = getLevel(totalXP);
  const themeRewardsReady = privateSettingsReady && bootReady.games && bootReady.hardware && trialProgressReady;
  useEffect(() => {
    if (!themeRewardsReady) return;
    const next = resolveThemeRewards({theme, unlockedThemes, level, ready:themeRewardsReady, accountUid:auth.currentUser?.uid});
    if (next.justUnlocked) {
      setUnlockedThemes(next.unlockedThemes);
      showToast(next.newlyUnlocked.length===1 ? `${getThemeLabel(next.newlyUnlocked[0])} est débloqué ! Retrouve-le dans les thèmes.` : `${next.newlyUnlocked.length} thèmes débloqués ! Retrouve-les dans Options → Interface.`, 4000);
    }
    if (next.theme !== theme) setTheme(next.theme);
  }, [themeRewardsReady, level, unlockedThemes, theme]);
  useEffect(() => {
    if (privateSettingsReady) localStorage.setItem("checkpoint-theme-unlocks", JSON.stringify(unlockedThemes));
  }, [privateSettingsReady, unlockedThemes]);
  const progress = getProgress(totalXP);
  const title = pendingCheckpointLevel
    ? `Checkpoint ${pendingCheckpointLevel}`
    : getRankTitle(level);
  const usefulNotifications = useMemo(
    () =>
      getUsefulNotifications({
        games,
        upcomingGames,
        pendingCheckpointLevel,
        upcomingSourceStatus,
      }),
    [games, upcomingGames, pendingCheckpointLevel, upcomingSourceStatus]
  );
  const visibleNotifications = useMemo(
    () =>
      [...extraNotifications,...usefulNotifications].filter(
        (notification) => notificationAllowed(notification,appOptions.notifications)&&!dismissedNotificationIds.includes(notification.id)
      ).sort((a,b)=>b.priority-a.priority).slice(0,20),
    [usefulNotifications,extraNotifications,dismissedNotificationIds,appOptions.notifications]
  );

  const [prevLevel, setPrevLevel] = useState(1);

useEffect(() => {
  if (level > prevLevel) {
    playSound("levelup", soundStyle);
    setPrevLevel(level);
  }
}, [level, prevLevel, soundStyle]);

  const badges = useMemo(
    () => [
      ...getUnlockedBadgesV2(games, level, hardware, socialProfile, weeklyQuizProgress),
      ...getCheckpointRewardBadges(checkpointTrialProgress),
    ],
    [games, level, hardware, socialProfile, weeklyQuizProgress, checkpointTrialProgress]
  );
  const socialActivities = useMemo(
    () => getSocialActivityFeed(games, hardware, badges),
    [games, hardware, badges]
  );

useEffect(() => {
  if (!badges || badges.length === 0) return;

  const unlockedIds = badges.filter((b) => b.unlocked).map((b) => String(b.id));
  const savedIds = readStoredUnlockedBadgeIds();
  const savedSet = new Set(savedIds);

  if (!badgeToastInitializedRef.current) {
    badgeToastInitializedRef.current = true;
    storeUnlockedBadgeIds([...savedIds, ...unlockedIds]);
    return;
  }

  const newlyUnlocked = badges.find(
    (badge) => badge.unlocked && !savedSet.has(String(badge.id))
  );

  if (newlyUnlocked) {
    setNewUnlockedBadge(newlyUnlocked);
    playSound("badge", soundStyle);

    if (navigator.vibrate) {
      navigator.vibrate([40, 40, 60]);
    }

    setTimeout(() => {
      setNewUnlockedBadge(null);
    }, 2800);
  }

  storeUnlockedBadgeIds([...savedIds, ...unlockedIds]);
}, [badges, soundStyle]);

useEffect(() => {
  const timer = setTimeout(() => {
    setBootWave(false);
  }, 1800);

  return () => clearTimeout(timer);
}, []);

  useEffect(() => {
    if (!selectedGame && !selectedSearchGame) {
      document.body.classList.remove("modal-open");
    }
  }, [selectedGame, selectedSearchGame]);

  useEffect(() => {
    return () => {
      window.clearTimeout(detailCloseTimerRef.current);
      document.body.classList.remove("modal-open");
    };
  }, []);

  const filteredLibraryGames = useMemo(() => {
    const q = librarySearch.trim().toLowerCase();
    if (!q) return games;

    return games.filter((game) => {
      const name = game.name?.toLowerCase() || "";
      const platforms = (game.platformNames || []).join(" ").toLowerCase();
      const genres = (game.genreNames || []).join(" ").toLowerCase();
      const review = (game.review || "").toLowerCase();

      return (
        name.includes(q) ||
        platforms.includes(q) ||
        genres.includes(q) ||
        review.includes(q)
      );
    });
  }, [games, librarySearch]);

  const wishlistGames = filteredLibraryGames
  .filter((game) => game.status === "wishlist")
  .sort((a, b) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const dateA = a.released ? new Date(a.released) : null;
    const dateB = b.released ? new Date(b.released) : null;

    const isFutureA = dateA && dateA >= today;
    const isFutureB = dateB && dateB >= today;

    // futurs en premier
    if (isFutureA && !isFutureB) return -1;
    if (!isFutureA && isFutureB) return 1;

    // deux futurs → tri par date croissante
    if (isFutureA && isFutureB) {
      return dateA - dateB;
    }

    // deux déjà sortis → plus récents en premier
    if (dateA && dateB) {
      return dateB - dateA;
    }

    return 0;
  });
  const inProgressGames = filteredLibraryGames.filter((game) => game.status === "en cours");
  const collectionGames = filteredLibraryGames.filter(isGameInCollection);
  const favoriteGames = filteredLibraryGames.filter((game) => game.favorite);
  const detailSourceGames = detailGameList.length ? detailGameList : games;
  const selectedDetailIndex = selectedGame
    ? detailSourceGames.findIndex((game) => game.id === selectedGame.id)
    : -1;
  const canGoPreviousGame = selectedDetailIndex > 0;
  const canGoNextGame =
    selectedDetailIndex >= 0 && selectedDetailIndex < detailSourceGames.length - 1;

  const getFreshGame = (game) =>
    games.find((currentGame) => currentGame.id === game?.id) || game;

  const closeGameDetail = useCallback(() => {
    window.clearTimeout(detailCloseTimerRef.current);
    detailCloseTimerRef.current = null;
    setSelectedGame(null);
    setDetailGameList([]);
    document.body.classList.remove("modal-open");
  }, []);

  const openGameDetail = (game, sourceGames = []) => {
    window.clearTimeout(detailCloseTimerRef.current);
    detailCloseTimerRef.current = null;
    const navigationList = sourceGames.length ? sourceGames : games;
    setDetailGameList(navigationList.map(getFreshGame).filter(Boolean));
    setSelectedGame(getFreshGame(game));
  };

  const navigateSelectedGame = (direction) => {
    if (selectedDetailIndex < 0) return;

    const nextIndex = selectedDetailIndex + direction;
    if (nextIndex < 0 || nextIndex >= detailSourceGames.length) return;

    setSelectedGame(getFreshGame(detailSourceGames[nextIndex]));
  };

  const handleGameRatingCommit = useCallback(() => {
    if (appOptions.afterRatingAction !== "list") return;

    window.clearTimeout(detailCloseTimerRef.current);
    detailCloseTimerRef.current = window.setTimeout(() => {
      closeGameDetail();
    }, 180);
  }, [appOptions.afterRatingAction, closeGameDetail]);

  const gameExists = (name) =>
    games.some(
      (game) =>
        game.name?.trim().toLowerCase() === name.trim().toLowerCase()
    );

  const loadMoreResults = async () => {
    if (!nextPage) {
      console.log("Pas de nextPage");
      return;
    }

    const requestId = searchRequestRef.current;
    const context = searchContextRef.current;
    try {
      let url = nextPage;
      let validResults = [];
      let nextUrl = null;
      let safety = 0;

      while (url && validResults.length === 0 && safety < 3) {
        console.log("LOAD MORE URL:", url);

        const data = await rawgApiRequest(url, {}, { timeout: 7000 });

        const pageResults = (data.results || []).filter(isMainGameResult);

        validResults = pageResults;
        nextUrl = data.next || null;
        url = nextUrl;
        safety++;
      }

      if (requestId !== searchRequestRef.current) return;
      setResults((prev) => {
        const merged = [...prev, ...validResults];

        const uniqueResults = merged.filter(
          (game, index, self) => index === self.findIndex((g) => g.id === game.id)
        );

        return sortSearchResults(uniqueResults, context.query, context.ordering);
      });

      setNextPage(nextUrl);
    } catch (e) {
      if (isAbortError(e)) return;
      console.error("Erreur pagination :", e);
    }
  };

  const addGameToFirebase = async (game) => {
    try {
      if (gameExists(game.name)) {
        setToast("Ce jeu fait déjà partie de ton univers.");
        return false;
      }

      const isFinished = isGameFinishedStatus(game);
      const newGame = {
        discoveryMetadata: getDiscoveryMetadata(game),
        rawgId: game.rawgId || game.id || null,
        name: game.name,
        completed: game.completed || isFinished || false,
        progressStatus: game.progressStatus || (isFinished ? "completed" : "not_started"),
        ratingExperience: game.ratingExperience || (isFinished ? "completed" : ""),
        rating: getGameRating(game),
        favorite: game.favorite || false,
        sanctuary: Boolean(game.sanctuary),
        image: game.image || "",
        status: isFinished ? "collection" : game.status || "wishlist",
        released: game.released || "",
        platformNames: game.platformNames || [],
        genreNames: game.genreNames || [],
        playtime: game.playtime || null,
        difficulty: game.difficulty || "normal",
        review: game.review || "",
        reviewSpoiler:Boolean(game.reviewSpoiler),
        reviewShared:Boolean(game.reviewShared),
        ostRating: clampRating(game.ostRating),
        ostTrack: game.ostTrack || "",
        ostLink: game.ostLink || "",
        ratingGraphics: clampRating(game.ratingGraphics),
        ratingGameplay: clampRating(game.ratingGameplay),
        ratingStory: clampRating(game.ratingStory),
        ratingSound: clampRating(game.ratingSound),
        ratingLongevity: clampRating(game.ratingLongevity),
        ...(game.physicalOwned?{physicalOwned:true,physicalBarcode:game.physicalBarcode||'',physicalPlatform:game.physicalPlatform||'',physicalEdition:game.physicalEdition||'',physicalUpdatedAt:game.physicalUpdatedAt||new Date().toISOString()}:{}),
        createdAt: new Date(),
      };

      const ref = await addDoc(collection(db, "games"), newGame);
      const savedGame = { ...newGame, id: ref.id };
      setToast(`${game.name} ajouté à ta collection${newGame.status==='wishlist' ? ' · Wishlist' : ''}.`);

      if (appOptions.afterAddAction === "library") {
        setSelectedSearchGame(null);
        changeTab("library");
      }

      if (appOptions.afterAddAction === "detail") {
        setSelectedSearchGame(null);
        setDetailGameList([savedGame]);
        setSelectedGame(savedGame);
      }

      return true;
    } catch (e) {
      console.error("Erreur ajout Firebase :", e);
      setToast("Impossible d’ajouter ce jeu pour le moment.");
      return false;
    }
  };

  const importSteamGamesToLibrary = async (steamGames = [], meta = {}) => {
    const importOwner=auth.currentUser?.uid;
    await captureCurrentBackup("Avant import Steam");
    const importPlan=planGameImport(documentStateRef.current.games,steamGames,"steam");
    let imported = 0;
    let updated = 0;
    let skipped = importPlan.filter(row=>row.type==="skip").length;
    const importingWishlist = meta.status === "wishlist";

    for (const row of importPlan.filter(row=>row.type!=="skip")) {
      if(auth.currentUser?.uid!==importOwner)throw new Error("Import interrompu : le compte a changé.");
      const steamGame=row.item;
      const existingGame=row.existing?documentStateRef.current.games.find(game=>game.id===row.existing.id):null;
      const steamSource = meta.source || steamGame.source || "steam";
      const steamStatus = meta.status || steamGame.status || "";

      const steamPatch = {
        ...platformSyncPatch('steam',steamGame,{profileId:meta.steamId,wishlist:importingWishlist||steamStatus==='wishlist'}),
        source: existingGame?.source || steamSource,
      };

      if (existingGame?.id) {
        await updateDoc(doc(db, "games", existingGame.id), {
          ...steamPatch,
          image: existingGame.image || steamGame.image || "",
          platformNames: Array.from(
            new Set([...(existingGame.platformNames || []), "PC"])
          ),
        });
        updated++;
        continue;
      }

      if (!steamGame.name) {
        skipped++;
        continue;
      }

      const newGame = {
        rawgId: null,
        name: steamGame.name,
        completed: false,
        rating: 0,
        favorite: false,
        sanctuary: false,
        image: steamGame.image || "",
        status: steamStatus === "wishlist" || importingWishlist ? "wishlist" : "collection",
        released: "",
        platformNames: ["PC"],
        genreNames: [],
        playtime: null,
        difficulty: "normal",
        review: "",
        ostRating: 0,
        ostTrack: "",
        ostLink: "",
        ratingGraphics: 0,
        ratingGameplay: 0,
        ratingStory: 0,
        ratingSound: 0,
        ratingLongevity: 0,
        ...steamPatch,
        createdAt: new Date(),
      };

      await addDoc(collection(db, "games"), newGame);
      imported++;
    }

    setToast(
      `${imported} jeu${imported > 1 ? "x" : ""} importes, ${updated} synchronises.`
    );

    return { imported, updated, skipped };
  };

  const importXboxGamesToLibrary = async (xboxGames = [], meta = {}) => {
    const importOwner=auth.currentUser?.uid;
    await captureCurrentBackup("Avant import Xbox");
    const importPlan=planGameImport(documentStateRef.current.games,xboxGames,"xbox");
    let imported = 0;
    let updated = 0;
    let skipped = importPlan.filter(row=>row.type==="skip").length;

    for (const row of importPlan.filter(row=>row.type!=="skip")) {
      if(auth.currentUser?.uid!==importOwner)throw new Error("Import interrompu : le compte a changé.");
      const xboxGame=row.item;
      const existingGame=row.existing?documentStateRef.current.games.find(game=>game.id===row.existing.id):null;
      const importedPlatforms = inferMicrosoftImportPlatforms(xboxGame);
      const importedImage =
        getGameVisual(existingGame || {}) ||
        getGameVisual(xboxGame) ||
        createFallbackPoster(xboxGame.name || existingGame?.name || "Jeu Microsoft", "#16a34a", "#60a5fa");

      const xboxPatch = {
        ...platformSyncPatch('xbox',xboxGame,{profileId:meta.xuid}),
        source: existingGame?.source || "xbox",
      };

      if (existingGame?.id) {
        await updateDoc(doc(db, "games", existingGame.id), {
          ...xboxPatch,
          image: importedImage,
          background_image: existingGame.background_image || xboxGame.background_image || "",
          cover_image: existingGame.cover_image || xboxGame.cover_image || "",
          platformNames: mergeMicrosoftImportPlatforms(existingGame, importedPlatforms),
          playedPlatforms: mergeMicrosoftPlayedPlatforms(existingGame, importedPlatforms),
        });
        updated++;
        continue;
      }

      if (!xboxGame.name) {
        skipped++;
        continue;
      }

      const newGame = {
        rawgId: null,
        name: xboxGame.name,
        completed: false,
        rating: 0,
        favorite: false,
        sanctuary: false,
        image: importedImage,
        background_image: xboxGame.background_image || "",
        cover_image: xboxGame.cover_image || "",
        status: "collection",
        released: "",
        platformNames: importedPlatforms,
        playedPlatforms: importedPlatforms,
        genreNames: [],
        playtime: null,
        difficulty: "normal",
        review: "",
        ostRating: 0,
        ostTrack: "",
        ostLink: "",
        ratingGraphics: 0,
        ratingGameplay: 0,
        ratingStory: 0,
        ratingSound: 0,
        ratingLongevity: 0,
        ...xboxPatch,
        createdAt: new Date(),
      };

      await addDoc(collection(db, "games"), newGame);
      imported++;
    }

    setToast(
      `${imported} titre${imported > 1 ? "s" : ""} Xbox importes, ${updated} synchronises.`
    );

    return { imported, updated, skipped };
  };

  const addGameToSeries = async (seriesName, game) => {
  try {
    const ref = doc(db, "gameCollections", seriesName);

    await setDoc(
      ref,
      {
        name: seriesName,
        games: arrayUnion({
          rawgId: game.id,
          name: game.name,
          image: game.background_image || "",
          released: game.released || "",
        }),
      },
      { merge: true }
    );

    setToast("Ajouté à la série.");
  } catch (e) {
    if (isAbortError(e)) return;
    console.error(e);
    setToast("Impossible de mettre à jour la série.");
  }
};

  const addFromSearch = async (game) => {
    const ok = await addGameToFirebase({ ...game, status: "terminé" });

    if (ok) {
      playSound("success", soundStyle);
      setToast("Ajouté à tes jeux terminés.");
    }
  };

  const addToWishlistFromSearch = async (game) => {
      const ok = await addGameToFirebase({ ...game, status: "wishlist" });

      if (ok) {
        playSound("success", soundStyle);
        setToast("Ajouté à ta wishlist.");
      }
    };

  const clearSearchResults = () => {
    searchRequestRef.current += 1;
    setIsSearching(false);
    setSearch("");
    setDebouncedSearch("");
    setResults([]);
    setNextPage(null);
  };

  const chooseSort = (value) => {
    if (value === sortBy) return;
    setSortBy(value);
    setResults(current => sortSearchResults(current, searchContextRef.current.query, value));
    if (search.trim() || yearFilter || platformFilter || genreFilter) searchGames(search, value, true);
  };

  const searchGames = async (forcedSearch = null, ordering = sortBy, keepFiltersOpen = false) => {
  const cleanSearch = (forcedSearch ?? search ?? debouncedSearch).trim();

  if (!cleanSearch && !yearFilter && !platformFilter && !genreFilter) {
    alert("Tape un jeu ou choisis au moins un filtre.");
    return;
  }

  const requestId = ++searchRequestRef.current;
  const isCurrent = () => requestId === searchRequestRef.current;
  searchContextRef.current = {query: cleanSearch, ordering};

  const buildOfflineSearchResults = () => {
    if (!cleanSearch) return [];

    const normalizedQuery = normalizeSearchText(cleanSearch);
    const queryWords = normalizedQuery.split(" ").filter(Boolean);
    const knownResults = getKnownSearchFallbacks(cleanSearch);
    const localResults = games
      .filter((game) => {
        const normalizedName = normalizeSearchText(game.name);
        return (
          normalizedName.includes(normalizedQuery) ||
          queryWords.every((word) => normalizedName.includes(word))
        );
      })
      .map((game) => ({
        discoveryMetadata: getDiscoveryMetadata(game),
        id: game.rawgId || `local-${game.id}`,
        name: game.name,
        slug: normalizeSearchText(game.name).replace(/\s+/g, "-"),
        released: game.released || "",
        background_image: game.image || "",
        rating: clampRating(game.rating) / 2,
        ratings_count: game.rating ? 1 : 0,
        playtime: game.playtime || 0,
        platforms: (game.platformNames || []).map((name) => ({
          platform: { name },
        })),
        genres: (game.genreNames || []).map((name) => ({ name })),
      }));

    return [...knownResults, ...localResults].filter(
      (game, index, list) =>
        index ===
        list.findIndex(
          (candidate) =>
            String(candidate.id) === String(game.id) ||
            normalizeSearchText(candidate.name) === normalizeSearchText(game.name)
        )
    );
  };

  try {
    setIsSearching(true);

    const offlineSeed = sortSearchResults(
      buildOfflineSearchResults(),
      cleanSearch, ordering
    );
    if (offlineSeed.length > 0) {
      setResults(offlineSeed);
      setNextPage(null);
    }

    const searchAliases = getSearchQueryAliases(cleanSearch);
    const searchTerm =
      normalizeSearchText(cleanSearch) === "gta"
        ? "grand theft auto"
        : searchAliases[0] || cleanSearch;
    const params = new URLSearchParams();
    params.append("page_size", "20");
    params.append("page", "1");

    if (searchTerm) params.append("search", searchTerm);
    if (yearFilter) params.append("dates", `${yearFilter}-01-01,${yearFilter}-12-31`);
    if (platformFilter) params.append("platforms", platformFilter);
    if (genreFilter) params.append("genres", genreFilter);

    if (ordering !== "relevance") params.append("ordering", ordering);

    const fastSearchOptions = cleanSearch
      ? { timeout: 5000, preferIgdb: true }
      : { timeout: 6500, retries: 1 };

    const data = await rawgApiRequest("/games", params, fastSearchOptions);

    if (!isCurrent()) return;
    let resultsList = (data.results || []).filter(isMainGameResult);

    if (
      cleanSearch &&
      resultsList.length === 0 &&
      (yearFilter || platformFilter || genreFilter)
    ) {
      const relaxedParams = new URLSearchParams();
      relaxedParams.append("page_size", "20");
      relaxedParams.append("page", "1");
      relaxedParams.append("search", searchTerm);
      if (ordering !== "relevance") relaxedParams.set("ordering", ordering);

      const relaxedData = await rawgApiRequest("/games", relaxedParams, fastSearchOptions);
      resultsList = (relaxedData.results || []).filter(isMainGameResult);
    }

    if (cleanSearch && resultsList.length === 0 && searchAliases.length > 1) {
      for (const alias of searchAliases.slice(1)) {
        const aliasParams = new URLSearchParams();
        aliasParams.append("page_size", "20");
        aliasParams.append("page", "1");
        aliasParams.append("search", alias);
        if (ordering !== "relevance") aliasParams.set("ordering", ordering);

        const aliasData = await rawgApiRequest("/games", aliasParams, fastSearchOptions);
        const aliasResults = (aliasData.results || []).filter(isMainGameResult);
        if (aliasResults.length > 0) {
          resultsList = aliasResults;
          break;
        }
      }
    }

    if (cleanSearch && data?.meta?.source !== "igdb") {
      const slugCandidates = searchAliases.flatMap((alias) => getRawgSlugCandidates(alias));

      for (const slug of Array.from(new Set(slugCandidates))) {
        try {
          const exactGame = await rawgApiRequest(`/games/${encodeURIComponent(slug)}`, {}, {
            timeout: 7000,
            retries: 1,
          });

          if (exactGame?.id && isMainGameResult(exactGame)) {
            resultsList = [
              exactGame,
              ...resultsList.filter((game) => game.id !== exactGame.id),
            ];
            break;
          }
        } catch (error) {
          if (!isAbortError(error)) {
            console.warn("Recherche directe RAWG ignorée :", error);
          }
        }
      }

      let fallbackResults = getKnownSearchFallbacks(cleanSearch).filter((game) =>
        gameMatchesSearchFilters(game, {
          yearFilter,
          platformFilter,
          genreFilter,
        })
      );

      if (resultsList.length === 0 && fallbackResults.length === 0) {
        fallbackResults = getKnownSearchFallbacks(cleanSearch);
      }

      if (fallbackResults.length > 0) {
        resultsList = [
          ...fallbackResults,
          ...resultsList.filter(
            (game) => !fallbackResults.some((fallback) => fallback.id === game.id)
          ),
        ];
      }
    }

    if (cleanSearch && resultsList.length === 0) {
      resultsList = buildOfflineSearchResults();
    }

    if (!isCurrent()) return;
    resultsList = sortSearchResults(resultsList, cleanSearch, ordering);

    const nextParams = new URLSearchParams(params);
    nextParams.set("page", "2");

    setResults(resultsList);

    const nextSearchUrl =
      data.next ||
      (resultsList.length === 20
        ? data?.meta?.source === "igdb"
          ? buildIgdbApiUrl("/games", nextParams)
          : buildRawgApiUrl("/games", nextParams)
        : null);

    setNextPage(nextSearchUrl);

    if (!keepFiltersOpen) setShowFilters(false);
  } catch (e) {
    if (!isCurrent() || isAbortError(e)) return;
    console.error("Erreur recherche RAWG :", e);
    const offlineResults = sortSearchResults(
      buildOfflineSearchResults(),
      cleanSearch, ordering
    );

    if (offlineResults.length > 0) {
      setResults(offlineResults);
      setNextPage(null);
      if (!keepFiltersOpen) setShowFilters(false);
      setToast("Source externe indisponible, résultats locaux affichés.");
    } else {
      setToast("La recherche n'a pas répondu correctement.");
    }
  } finally {
    if (isCurrent()) setIsSearching(false);
  }
};

  useEffect(() => {
    if (activeTab !== "search") return;
    if (debouncedSearch.trim().length < 3) return;

    searchGames(debouncedSearch);
  }, [debouncedSearch]);

  const deleteGame = async (id) => {
  const game = games.find((currentGame) => currentGame.id === id);
  if (
    appOptions.confirmDangerActions &&
    !window.confirm(`Supprimer "${game?.name || "ce jeu"}" ?`)
  ) {
    return;
  }

  try {
    await captureCurrentBackup("Avant suppression de jeu");
    await deleteDoc(doc(db, "games", id));

    playSound("delete", soundStyle); // 🔊 AJOUT

    setToast("Jeu placé dans la corbeille (30 jours après synchronisation).");

    if (selectedGame?.id === id) {
      setSelectedGame(null);
    }
  } catch (e) {
    console.error("Erreur suppression Firebase :", e);
    setToast("Impossible de supprimer ce jeu pour le moment.");
  }
};

  const schedulePatch = (kind,id,patch,success) => {
    const key=`${kind}:${id}`;
    const pending=scheduledEditsRef.current.get(key);
    if(pending)clearTimeout(pending.timer);
    const payload={...(pending?.patch||{}),...patch};
    applyDocumentPatch(kind,id,patch);
    const timer=setTimeout(()=>{scheduledEditsRef.current.delete(key);persistPatch(kind,id,payload,{success});},350);
    scheduledEditsRef.current.set(key,{timer,patch:payload});
  };
  useEffect(()=>()=>{for(const edit of scheduledEditsRef.current.values())clearTimeout(edit.timer);},[]);
  const updateHardwareRating = (id,key,value,type) => {
    const item=documentStateRef.current.hardware.find(item=>item.id===id);
    const ratings={...(item?.ratings||{}),[key]:clampRating(value)};
    const values=getHardwareRatingFields(type).map(field=>clampRating(ratings[field.key])).filter(value=>value>0);
    const rating=values.length?Math.round(values.reduce((sum,value)=>sum+value,0)/values.length*10)/10:0;
    schedulePatch("hardware",id,{ratings,rating},"Notes du matériel enregistrées");
  };
  const updateHardwareEdition=(id,edition)=>{
    const item=documentStateRef.current.hardware.find(item=>item.id===id);
    if(!item)return false;
    const normalized=normalizeHardwareEdition(edition);
    return persistPatch("hardware",id,{edition:normalized,image:hardwareImageForEdition(item,normalized),catalogImage:hardwareBaseImage(item)},{success:"Couleur, édition et photo enregistrées",undoable:true});
  };
  const updateHardwareReview = (id,review) => persistPatch("hardware",id,{review},{success:"Avis sur le matériel enregistré"});
  const updateHardwareComponent = (id,key,value) => {
    const item=documentStateRef.current.hardware.find(item=>item.id===id);
    schedulePatch("hardware",id,{pcConfig:{...(item?.pcConfig||{}),[key]:value}},"Configuration enregistrée");
  };
  const setRating = (id,value) => schedulePatch("games",id,{rating:clampRating(value)},"Note enregistrée");
  const setDetailedRating = (id,key,value) => schedulePatch("games",id,{[key]:clampRating(value)},"Notes enregistrées");
  const toggleFavorite = (id,currentValue) => persistPatch("games",id,{favorite:!currentValue},{success:currentValue?"Favori retiré":"Favori ajouté",undoable:true});
  const setGameSanctuary = (id,sanctuary) => persistPatch("games",id,{sanctuary},{success:sanctuary?"Jeu ajouté au Sanctuaire":"Jeu retiré du Sanctuaire",undoable:true});

  const setSanctuaryFigures = async (id, sanctuaryFigureIds) => {
    const nextFigureIds = Array.isArray(sanctuaryFigureIds)
      ? sanctuaryFigureIds.filter(Boolean)
      : [];

    try {
      await updateDoc(doc(db, "games", id), { sanctuaryFigureIds: nextFigureIds });
      setGames((prev) =>
        prev.map((game) =>
          game.id === id ? { ...game, sanctuaryFigureIds: nextFigureIds } : game
        )
      );
      if (selectedGame?.id === id) {
        setSelectedGame((prev) => applySelectedGamePatch(prev, id, { sanctuaryFigureIds: nextFigureIds }));
      }
      setToast(
        nextFigureIds.length
          ? "Figure culte ajoutée au Sanctuaire."
          : "Figure culte retirée du Sanctuaire."
      );
    } catch (e) {
      console.error("Erreur figures cultes :", e);
      setToast("Impossible de mettre à jour les figures cultes.");
    }
  };

  const bulkUpdateGames = async (ids,changes) => {
    await captureCurrentBackup("Avant modification groupée");
    const ownerUid=auth.currentUser?.uid;
    const selected=games.filter(game=>ids.includes(String(game.id)));
    const failedIds=[];let updated=0;
    for(let index=0;index<selected.length;index+=4){
      if(auth.currentUser?.uid!==ownerUid){failedIds.push(...selected.slice(index).map(game=>game.id));break;}
      const results=await Promise.allSettled(selected.slice(index,index+4).map(async game=>{
        const patch=stampGameHistory(game,stampGameMilestones(game,buildBulkGamePatch(game,changes)));
        await updateDoc(doc(db,"games",game.id),patch);
        setGames(current=>current.map(item=>item.id===game.id?{...item,...patch}:item));
        return game.id;
      }));
      results.forEach((result,offset)=>{if(result.status==='fulfilled')updated+=1;else failedIds.push(selected[index+offset].id);});
    }
    return {updated,failedIds};
  };

  const setStatus = async (id,status) => {
    const progressStatus=status==="en cours"?"in_progress":isGameFinishedStatus({status})?"completed":"not_started";
    const completed=progressStatus==="completed";
    const current=documentStateRef.current.games.find(game=>game.id===id);
    const ratingExperience=completed?"completed":status==="en cours"?"partial":status==="collection"?(getGameExperience(current)||""):"";
    const saved=await persistPatch("games",id,{status,progressStatus,completed,ratingExperience},{success:"Statut enregistré",undoable:true});
    if(saved&&["wishlist","en cours","collection"].includes(status))setLibraryView(status);
  };
  const setProgressStatus = (id,progressStatus,note) => {
    const current=documentStateRef.current.games.find(game=>game.id===id);
    const patch=journeyPatch(current,progressStatus,note);
    if(!patch)return false;
    return persistPatch("games",id,patch,{success:"Progression enregistrée",undoable:true});
  };

  const setGameOfYear = async (year, gameId) => {
    const numericYear = Number(year);
    if (!numericYear || !gameId) return;

    const selectedGame = games.find((game) => game.id === gameId);
    const isAlreadySelected = Number(selectedGame?.gotyYear) === numericYear;
    const affectedGames = games.filter(
      (game) => Number(game.gotyYear) === numericYear || game.id === gameId
    );

    setGames((prev) =>
      prev.map((game) => {
        if (game.id === gameId) {
          return {
            ...game,
            gotyYear: isAlreadySelected ? null : numericYear,
          };
        }

        if (Number(game.gotyYear) === numericYear) {
          return { ...game, gotyYear: null };
        }

        return game;
      })
    );

    try {
      await Promise.all(
        affectedGames.map((game) =>
          updateDoc(doc(db, "games", game.id), {
            gotyYear:
              game.id === gameId && !isAlreadySelected ? numericYear : null,
          })
        )
      );
      setToast(isAlreadySelected ? "GOTY retiré." : `Ton GOTY ${numericYear} est enregistré.`);
    } catch (e) {
      console.error("Erreur Game of the Year :", e);
      setToast("Impossible d’enregistrer ce GOTY.");
    }
  };

  const setPlaytimeRange = (id,playtimeRange) => persistPatch("games",id,{playtimeRange},{success:"Temps de jeu enregistré"});
  const setPlayedPlatforms = (id,playedPlatforms) => persistPatch("games",id,{playedPlatforms},{success:"Plateformes enregistrées"});

  const repairGameDataIntegrity = async (providedIssues = null) => {
    await captureCurrentBackup("Avant réparation des fiches");
    const issues = providedIssues || getGameDataIntegrityIssues(games);

    if (!issues.length) {
      showToast("Bibliothèque déjà bien rangée.");
      return;
    }

    try {
      await Promise.all(
        issues.map(({ game, patch }) => updateDoc(doc(db, "games", game.id), patch))
      );

      setGames((prev) =>
        prev.map((game) => {
          const issue = issues.find((item) => item.game.id === game.id);
          return issue ? { ...game, ...issue.patch } : game;
        })
      );

      setSelectedGame((prev) => {
        if (!prev) return prev;
        const issue = issues.find((item) => item.game.id === prev.id);
        return issue ? { ...prev, ...issue.patch } : prev;
      });

      showToast(`${issues.length} fiche${issues.length > 1 ? "s" : ""} remise${issues.length > 1 ? "s" : ""} au propre.`);
    } catch (error) {
      console.error("Erreur diagnostic bibliothèque :", error);
      showToast("Impossible de réparer les données pour le moment.");
    }
  };

  const toggleCompleted = (id,currentValue) => {
    const current=documentStateRef.current.games.find(game=>game.id===id);
    const completed=!currentValue;
    const status=completed||current?.status==="terminé"?"collection":current?.status||"collection";
    return persistPatch("games",id,{completed,status,progressStatus:completed?"completed":"not_started",ratingExperience:completed?"completed":""},{success:completed?"Jeu terminé":"Progression réinitialisée",undoable:true});
  };
  const setDifficulty = (id,difficulty) => persistPatch("games",id,{difficulty},{success:"Difficulté enregistrée"});
  const setExperienceInfo = (id,payload) => persistPatch("games",id,payload,{success:"Expérience enregistrée"});
  const setFavoriteReason = (id,values) => {
    const game=documentStateRef.current.games.find(g=>g.id===id);
    if(!game?.favorite||game.status==="wishlist"){setToast("Ce jeu doit être dans tes coups de cœur pour enregistrer sa phrase.");return Promise.resolve(false);}
    return persistPatch("games",id,{favoriteReason:favoriteReason(values.favoriteReason),favoriteReasonShared:values.favoriteReasonShared===true,favoriteReasonUpdatedAt:new Date().toISOString()},{success:"Phrase du coup de cœur enregistrée"});
  };
  const setRediscoverPin = (id,pinned) => {
    const gamesNow=documentStateRef.current.games,candidates=rediscoveryCandidates(gamesNow,getGameRating);
    if(!candidates.some(c=>c.game.id===id))return Promise.resolve(false);
    if(pinned&&candidates.filter(c=>c.game.rediscoverPinned&&c.game.id!==id).length>=3){setToast("Retire un jeu de ta sélection avant d’en garder un autre.");return Promise.resolve(false);}
    return persistPatch("games",id,{rediscoverPinned:pinned},{success:pinned?"Jeu gardé pour rejouer":"Jeu retiré de ta sélection",undoable:true});
  };
  const setReview = (id,review,options={}) => persistPatch("games",id,{review,reviewSpoiler:options.reviewSpoiler===true,reviewShared:options.reviewShared===true},{success:"Avis enregistré"});

  const updatePhysicalGame = async (id, patch) => {
    const payload = {
      ...patch,
      physicalUpdatedAt: new Date().toISOString(),
    };

    setGames((prev) =>
      prev.map((game) => (game.id === id ? { ...game, ...payload } : game))
    );
    if (selectedGame?.id === id) {
      setSelectedGame((prev) => applySelectedGamePatch(prev, id, { ...payload }));
    }

    try {
      await updateDoc(doc(db, "games", id), payload);
      setToast("Collection physique mise a jour.");
    } catch (e) {
      console.error("Erreur collection physique :", e);
      setToast("Impossible de mettre a jour la collection physique.");
    }
  };

  const setOstInfo = async (id, ostInfo) => {
    const payload = {
      ostRating: clampRating(ostInfo.ostRating),
    };

    try {
      await updateDoc(doc(db, "games", id), payload);
      setGames((prev) =>
        prev.map((game) => (game.id === id ? { ...game, ...payload } : game))
      );
      if (selectedGame?.id === id) {
        setSelectedGame((prev) => applySelectedGamePatch(prev, id, { ...payload }));
      }
      setToast("OST enregistrée dans ta mémoire de joueur.");
    } catch (e) {
      console.error("Erreur mise à jour OST :", e);
      setToast("Erreur lors de l'enregistrement OST.");
    }
  };

  const setDLCs = async (id, dlcs) => {
    try {
      await updateDoc(doc(db, "games", id), { dlcs });

      if (selectedGame?.id === id) {
        setSelectedGame((prev) => applySelectedGamePatch(prev, id, {
          dlcs,
        }));
      }

      setToast("DLC mis à jour.");
    } catch (e) {
      console.error("Erreur mise à jour DLC :", e);
    }
  };

  const changeTab = (nextTab) => {
    if(appOptions.hiddenTabs?.includes(nextTab)){showToast("Cet onglet est masqué. Réactive-le dans Options → Préférences de contenu.");return;}
    setSearchDestination(null);
    if (nextTab === "social") setSocialEntryView("feed");
    window.clearTimeout(tabSwitchTimerRef.current);
    window.clearTimeout(tabTransitionTimerRef.current);
    if (nextTab === activeTab) {
      setPixelTransition(null);
      return;
    }

    setNotificationPanelOpen(false);
    setSelectedGame(null);
    setSelectedSearchGame(null);
    document.body.classList.remove("modal-open");

    const currentIndex = tabOrder.indexOf(activeTab);
    const nextIndex = tabOrder.indexOf(nextTab);
    const nextDirection = nextIndex > currentIndex ? "right" : "left";

    // Transition visuelle très courte, par-dessus
    if (uiMode === "reduced") {
      setPixelTransition(null);
      setActiveTab(nextTab);
      return;
    }

    // A fresh key restarts the effect even when consecutive taps go in the
    // same direction. No deferred start can resurrect a cancelled transition.
    setPixelTransition({ direction: nextDirection, id: performance.now() });
    tabSwitchTimerRef.current = window.setTimeout(() => {
      setActiveTab(nextTab);
    }, 90);
    tabTransitionTimerRef.current = window.setTimeout(() => {
      setPixelTransition(null);
    }, getThemeTransitionDuration(theme, appOptions.transitionMotion === "light"));
  };

  const openNotification = (notification) => {
    setNotificationPanelOpen(false);
    if(notification?.type==='release-notes'){setReleaseNotesOpen(true);return;}
    if(notification?.type==='friendship'){changeTab('social');setSharedProfile(null);setSocialEntryView('friends');setSearchDestination({kind:'friendship',token:Date.now()});return;}
    if(notification?.type==='rendezvous'){setRendezvousNotice({view:notification.view,token:Date.now()});return;}
    if(notification?.type==='challenge'){changeTab('social');setSharedProfile(null);setSocialEntryView('challenges');setSearchDestination({kind:'challenge',id:notification.challengeId,token:Date.now()});return;}
    if(notification?.type==='recommendation'){changeTab('social');setSharedProfile(null);setSocialEntryView('recommendations');setSearchDestination({kind:'recommendation',id:notification.recommendationId,token:Date.now()});return;}
    if(notification?.type==="monthly"){setMonthlyNotification({month:notification.month,token:Date.now()});return;}
    if(notification?.type==="poll"){changeTab("social");setSharedProfile(null);setSocialEntryView("polls");setSearchDestination({kind:"poll",id:notification.pollId,token:Date.now()});return;}
    if (notification?.type === "trial" && pendingCheckpointLevel) {
      openTrialRoom(pendingCheckpointLevel);
      return;
    }
    if (notification?.tab) {
      changeTab(notification.tab);
    }
  };

  const dismissNotification = (id) => {
    setDismissedNotificationIds((prev) =>
      prev.includes(id) ? prev : [...prev, id]
    );
  };

  useEffect(() => {
    window.requestAnimationFrame(() => {
      window.scrollTo({
        top: 0,
        left: 0,
        behavior: "auto",
      });
    });
  }, [activeTab]);

  function getBadgeProgress(badge, stats) {
  if (badge.id.startsWith("collector_")) {
    const target = parseInt(badge.id.split("_")[1]);
    return { current: stats.total, target };
  }

  if (badge.id.startsWith("finisher_")) {
    const target = parseInt(badge.id.split("_")[1]);
    return { current: stats.finished, target };
  }

  if (badge.id.startsWith("favorites_")) {
    const target = parseInt(badge.id.split("_")[1]);
    return { current: stats.favorites, target };
  }

  if (badge.id.startsWith("reviews_")) {
    const target = parseInt(badge.id.split("_")[1]);
    return { current: stats.reviews, target };
  }

  if (badge.id.startsWith("rated_")) {
    const target = parseInt(badge.id.split("_")[1]);
    return { current: stats.ratedGames, target };
  }

  if (badge.id.startsWith("hours_")) {
    const target = parseInt(badge.id.split("_")[1]);
    return { current: stats.hours, target };
  }

  if (badge.id.startsWith("level_")) {
    const target = parseInt(badge.id.split("_")[1]);
    return { current: stats.level, target };
  }

  return null;
}

  const showFullHeader = activeTab === "library" && appOptions.headerMode === "standard";

  const showCompactHeader = appOptions.headerMode !== "minimal" && !["home", "arena", "hardware", "profile", "options"].includes(activeTab) && !showFullHeader;
  return (

     <>
<>
    </>

          <AutomaticBackup ready={bootReady.games&&bootReady.hardware} payload={backupPayload}/>
          <ConnectionStatus/>
          <SaveFeedback state={saveFeedback}/>
          <Toast message={toast} />
          <BadgeUnlockToast badge={newUnlockedBadge} />

          {pendingCheckpointLevel &&
            dismissedCheckpointLevel !== pendingCheckpointLevel &&
            !activeTrialLevel &&
            !showSplash && (
              <CheckpointReachedOverlay
                checkpointLevel={pendingCheckpointLevel}
                onEnter={() => openTrialRoom(pendingCheckpointLevel)}
                onClose={() => setDismissedCheckpointLevel(pendingCheckpointLevel)}
              />
            )}
          {activeTrialLevel && (
            <TrialRoom
              checkpointLevel={activeTrialLevel}
              rawXP={rawTotalXP}
              onClose={closeTrialRoom}
              onComplete={completeCheckpointTrial}
            />
          )}
      <div className={`app-shell ${showSplash ? "app-hidden" : "app-visible"}`}>
        <div className="container">
          <div className="app-title-row">
            <AppHeaderBrand theme={theme}/>
            {monthlyNotification&&<MonthlyRecap key={monthlyNotification.token} games={games} initialMonth={monthlyNotification.month} dialogOnly onCloseDialog={()=>setMonthlyNotification(null)}/>}
            <NotificationCenter
              notifications={visibleNotifications}
              open={notificationPanelOpen}
              onToggle={() => setNotificationPanelOpen((prev) => !prev)}
              onOpenNotification={openNotification}
              onDismiss={dismissNotification}
            />
          </div>

          <div className="personal-search-bar">
            <PersonalSearch games={games} hardware={hardware} series={personalSeries} friends={socialFriends}
              onCatalog={value=>{changeTab("search");setSearch(value);}}
              onSelect={async entry=>{
                if(entry.kind==="games"){openGameDetail(entry.item,games);return;}
                if(entry.kind==="friends"){await openSocialFriendProfile(entry.item.handle);changeTab("social");return;}
                const tab=entry.kind==="series"?"series":"hardware";changeTab(tab);setSearchDestination({kind:tab,id:entry.item.id,name:entry.item.name,token:Date.now()});
              }}/>
          </div>
          <TabIntroduction activeTab={activeTab} tutorialsReady={tutorialsReady && !showSplash}/>
          {["home","profile"].includes(activeTab) && <ProgressSync {...progressSync}/>}

          {showFullHeader && (
  <>
    <XPCard
      totalXP={totalXP}
      level={level}
      title={title}
      progress={progress}
    />


  </>
)}

{showCompactHeader && (
  <>
    <XPCardCompact
      totalXP={totalXP}
      level={level}
      title={title}
      progress={progress}
    />

    {appOptions.headerMode === "standard" && !["news", "upcoming", "deals", "live", "sanctuary", "top5"].includes(activeTab) && <StatsBarCompact
      total={games.length}
      finished={games.filter(isGameFinishedStatus).length}
      favorites={games.filter((g) => g.favorite).length}
    />}
  </>
)}

{activeTab === "home" && (
  <HomeTab
  releaseBanner={unreadReleases.length>0?<ReleaseNotes/>:null}
  socialProfile={socialProfile}
  socialFriends={socialFriends}
  onExperiencesChange={value=>updateAppOption('experiences',value)}
  onEngagementChange={value=>updateAppOption('engagement',value)}
  onOpenChallenges={()=>{changeTab('social');setSharedProfile(null);setSocialEntryView('challenges');}}
  theme={theme}
  games={games}
  hardware={hardware}
  badges={badges}
  level={level}
  totalXP={totalXP}
  progress={progress}
  dealPreferences={appOptions}
  setActiveTab={changeTab}
  onHomeLayoutChange={value=>updateAppOption("homeLayout",value)}
  onRediscoverPin={setRediscoverPin}
  detectedSeries={personalSeries}
  onOpenDetail={(game) => openGameDetail(game, games)}
  gamingEvents={gamingEvents}
  socialActivities={socialActivities}
  weeklyQuiz={weeklyQuiz}
  weeklyQuizProgress={weeklyQuizProgress}
  onAnswerWeeklyQuiz={handleWeeklyQuizAnswer}
  checkpointGoalProgress={checkpointGoalProgress}
  onClaimCheckpointGoal={handleClaimCheckpointGoal}
/>
)}

{activeTab === "arena" && <ArenaTab key={auth.currentUser?.uid||'local'} syncEnabled editionLinks={appOptions.editionLinks} onEditionLinksChange={value=>updateAppOption('editionLinks',value)} games={games} series={personalSeries} onOpenGame={game=>openGameDetail(game,games)} palmares={appOptions.duelPalmares} onPalmaresChange={value=>updateAppOption('duelPalmares',value)}/>}

{activeTab === "news" && (
  <NewsTab newsItems={newsItems} />
)}

{activeTab === "deals" && (
  <DealsTab dealPreferences={appOptions} games={games} />
)}

          {activeTab === "search" && (
            <>
    <div className="toolbar-row"><button type="button" className="option-pill" onClick={()=>setPhysicalScannerOpen(true)}>Scanner un jeu physique</button></div>
                        <div className="search-panel">
                <div className="input-container">
                  <input
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Rechercher un jeu ou utiliser les filtres"
                  />
                  <button
                    className="checkpoint-primary-action"
                    onClick={() => {
                      playClick(soundStyle);
                      searchGames(search);
                    }}
                    type="button"
                  >
                    Rechercher
                  </button>
                </div>

                <div className="toolbar-row">
                  <button
                    className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
                    onClick={() => setShowFilters(!showFilters)}
                    type="button"
                  >
                    {showFilters ? "Fermer les filtres" : "Ouvrir les filtres"}
                  </button>
                </div>

                {showFilters && (
                  <div className="filters-panel">
                    <div className="filter-block">
                      <div className="filter-label">Trier par</div>
                      <div className="chips-group">
                        <button
                          className={`chip ${sortBy === "relevance" ? "active" : ""}`}
                          aria-pressed={sortBy === "relevance"}
                          onClick={() => chooseSort("relevance")}
                          type="button"
                        >
                          Pertinence
                        </button>
                        <button
                          className={`chip ${sortBy === "-rating" ? "active" : ""}`}
                          aria-pressed={sortBy === "-rating"}
                          onClick={() => chooseSort("-rating")}
                          type="button"
                        >
                          Mieux notés
                        </button>
                        <button
                          className={`chip ${sortBy === "-released" ? "active" : ""}`}
                          aria-pressed={sortBy === "-released"}
                          onClick={() => chooseSort("-released")}
                          type="button"
                        >
                          Plus récents
                        </button>
                        <button
                          className={`chip ${sortBy === "released" ? "active" : ""}`}
                          aria-pressed={sortBy === "released"}
                          onClick={() => chooseSort("released")}
                          type="button"
                        >
                          Plus anciens
                        </button>
                        <button
                          className={`chip ${sortBy === "name" ? "active" : ""}`}
                          aria-pressed={sortBy === "name"}
                          onClick={() => chooseSort("name")}
                          type="button"
                        >
                          Nom A-Z
                        </button>
                      </div>
                    </div>

                    <div className="filter-block search-fan-filter">
                      <div className="filter-label">Jeux de fans</div>
                      <button type="button"
                        className={`chip ${appOptions.searchIncludeFanGames ? "active" : ""}`}
                        aria-pressed={appOptions.searchIncludeFanGames}
                        onClick={() => setAppOptions(current => ({ ...current, searchIncludeFanGames: !current.searchIncludeFanGames }))}
                      >Inclure les jeux de fans</button>
                      <p>Les titres au statut incertain restent visibles. Les mods sont distingués des jeux de fans.</p>
                    </div>
                    <div className="filter-block">
                      <div className="filter-label">Année</div>
                      <div className="chips-group">
                        <button
                          className={`chip ${yearFilter === "" ? "active" : ""}`}
                          onClick={() => setYearFilter("")}
                          type="button"
                        >
                          Toutes
                        </button>

                        {years.map((year) => (
                          <button
                            key={year}
                            className={`chip ${yearFilter === year ? "active" : ""}`}
                            onClick={() => setYearFilter(year)}
                            type="button"
                          >
                            {year}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="filter-block">
                      <div className="filter-label">Plateforme</div>
                      <div className="chips-group">
                        <button
                          className={`chip ${platformFilter === "" ? "active" : ""}`}
                          onClick={() => setPlatformFilter("")}
                          type="button"
                        >
                          Toutes
                        </button>

                        {platforms.map((platform) => (
                          <button
                            key={platform.id}
                            className={`chip ${
                              String(platformFilter) === String(platform.id)
                                ? "active"
                                : ""
                            }`}
                            onClick={() => setPlatformFilter(String(platform.id))}
                            type="button"
                          >
                            {platform.name}
                          </button>
                        ))}
                      </div>
                    </div>

                    <div className="filter-block">
                      <div className="filter-label">Genre</div>
                      <div className="chips-group">
                        <button
                          className={`chip ${genreFilter === "" ? "active" : ""}`}
                          onClick={() => setGenreFilter("")}
                          type="button"
                        >
                          Tous
                        </button>

                        {genres.map((genre) => (
                          <button
                            key={genre.id}
                            className={`chip ${
                              String(genreFilter) === String(genre.id) ? "active" : ""
                            }`}
                            onClick={() => setGenreFilter(String(genre.id))}
                            type="button"
                          >
                            {genre.name}
                          </button>
                        ))}
                      </div>
                    </div>
                    <div className="filter-actions">
                      <button
                        className="reset-filters-btn"
                        onClick={() => {
                          setYearFilter("");
                          setPlatformFilter("");
                          setGenreFilter("");
                          setSortBy("relevance");
                          setAppOptions(current => ({ ...current, searchIncludeFanGames: false }));
                          searchRequestRef.current += 1;
                          searchContextRef.current = {...searchContextRef.current, ordering: "relevance"};
                          setIsSearching(false);
                          setNextPage(null);
                          setResults(current => sortSearchResults(current, searchContextRef.current.query, "relevance"));
                        }}
                        type="button"
                      >
                        Réinitialiser les filtres
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {isSearching && <Loader text="Recherche des jeux..." />}

              {!isSearching && results.length > 0 && (
                <div className="search-results-block">
                  <div className="search-results-header">
                    <h2>Résultats</h2>
                    <button
                      className="close-search-btn"
                      onClick={clearSearchResults}
                      type="button"
                    >
                      Fermer
                    </button>
                  </div>

                  <div className="results-container">
                    {hiddenFanCount > 0 && <p className="search-fan-notice" role="status">
                      {hiddenFanCount} {hiddenFanCount === 1 ? "jeu de fans masqué" : "jeux de fans masqués"}. Active « Inclure les jeux de fans » dans les filtres pour les afficher.
                    </p>}
                    {visibleSearchResults.map((game) => (
                      <SearchResultCard
                        key={game.id}
                        game={game}
                        games={games}
                        onAdd={startCollectionAdd}
                        onWishlist={addGameToFirebase}
                        isOwned={games.some((g) => g.rawgId === game.id || g.name.toLowerCase() === game.name.toLowerCase())}
                        onOpenSearchDetail={setSelectedSearchGame}
                      />
                    ))}
                    {nextPage && (
                      <button className="load-more-btn" onClick={loadMoreResults}>
                        Charger plus
                      </button>
                    )}
                  </div>
                </div>
              )}

              {!isSearching &&
                results.length === 0 &&
                (search || yearFilter || platformFilter || genreFilter) && (
                  <EmptyState
                    title="Aucun résultat"
                    subtitle="Essaie un autre mot-clé ou modifie tes filtres."
                  />
                )}
            </>
          )}

          {activeTab === "upcoming" && (
            <UpcomingReleasesTab releases={upcomingReleases} renderGame={(game) => <UpcomingGameCard key={game.id} game={game} onWishlist={addToWishlistFromSearch} />} />
          )}

          {activeTab === "library" && (
  <>
  {games.length === 0 && <FirstSteps games={games} onSearch={() => changeTab("search")} onOpenGame={openGameDetail}/>}
    <div className="search-panel">
      <div className="input-container">
        <input
          value={librarySearch}
          onChange={(e) => setLibrarySearch(e.target.value)}
          placeholder="Rechercher dans ta bibliothèque"
        />
        <button
          type="button"
          onClick={() => {
            playClick(soundStyle);
            setLibrarySearch("");
          }}
        >
          Effacer
        </button>
      </div>
    </div>

    <LibrarySmartPanel
      games={filteredLibraryGames}
      collectionGames={collectionGames}
      inProgressGames={inProgressGames}
      wishlistGames={wishlistGames}
      onOpenDetail={openGameDetail}
      onSetLibraryView={setLibraryView}
      onSetLibrarySearch={setLibrarySearch}
    />

    <div className="library-navigation">
    <div className="library-switch">
  <button
    type="button"
    className={`library-switch-btn ${libraryView === "collection" ? "active" : ""}`}
    onClick={() => setLibraryView("collection")}
  >
    Collection
  </button>

  <button
    type="button"
    className={`library-switch-btn ${libraryView === "en cours" ? "active" : ""}`}
    onClick={() => setLibraryView("en cours")}
  >
    En cours
  </button>

  <button
    type="button"
    className={`library-switch-btn ${libraryView === "wishlist" ? "active" : ""}`}
    onClick={() => setLibraryView("wishlist")}
  >
    Wishlist
  </button>

  <button
    type="button"
    className={`library-switch-btn ${libraryView === "physical" ? "active" : ""}`}
    onClick={() => setLibraryView("physical")}
  >
    Physique
  </button>

  {[['paused','En pause'],['abandoned','Abandonnés'],['lists','Mes listes']].map(([id,label])=><button key={id} type="button" className={`library-switch-btn ${libraryView===id?'active':''}`} onClick={()=>setLibraryView(id)}>{label}</button>)}
</div>

<div className="library-actions"><button type="button" className="option-pill" onClick={()=>setPhysicalScannerOpen(true)}>Scanner un jeu physique</button><button type="button" className="option-pill" onClick={()=>setCouchOpen(true)}>Mode canapé</button></div>
</div>
{libraryView==='lists'&&<PersonalLists value={appOptions.personalLists} games={games} onChange={value=>updateAppOption('personalLists',value)} onOpen={game=>openGameDetail(game)}/>}
{['paused','abandoned'].includes(libraryView)&&<LibrarySection filterKey={librarySearch} title={libraryView==='paused'?'Jeux en pause':'Jeux abandonnés'} games={filteredLibraryGames.filter(game=>game.progressStatus===libraryView)} onBulkUpdate={bulkUpdateGames} onDelete={deleteGame} onSetStatus={setStatus} onSetRating={setRating} onToggleFavorite={toggleFavorite} onQuickProgress={setProgressStatus} onOpenDetail={game=>openGameDetail(game,filteredLibraryGames.filter(item=>item.progressStatus===libraryView))} libraryCardMode={libraryCardMode} setLibraryCardMode={setLibraryCardMode}/>}

{libraryView === "collection" && (
  <LibrarySection
  title="Collection"
  filterKey={librarySearch}
  games={collectionGames}
  onBulkUpdate={bulkUpdateGames}
  onDelete={deleteGame}
  onSetStatus={setStatus}
  onSetRating={setRating}
  onToggleFavorite={toggleFavorite}
  onQuickProgress={setProgressStatus}
  onOpenDetail={(game) => openGameDetail(game, collectionGames)}
  libraryCardMode={libraryCardMode}
  setLibraryCardMode={setLibraryCardMode}
/>
)}

{libraryView === "en cours" && (
  <LibrarySection
    title="Jeux en cours"
    filterKey={librarySearch}
    games={inProgressGames}
    onBulkUpdate={bulkUpdateGames}
    onDelete={deleteGame}
    onSetStatus={setStatus}
    onSetRating={setRating}
    onToggleFavorite={toggleFavorite}
  onQuickProgress={setProgressStatus}
    onOpenDetail={(game) => openGameDetail(game, inProgressGames)}
    libraryCardMode={libraryCardMode}
    setLibraryCardMode={setLibraryCardMode}
  />
)}

{libraryView === "wishlist" && (
  <LibrarySection
    title="Wishlist"
    filterKey={librarySearch}
    games={wishlistGames}
    onBulkUpdate={bulkUpdateGames}
    onDelete={deleteGame}
    onSetStatus={setStatus}
    onSetRating={setRating}
    onToggleFavorite={toggleFavorite}
  onQuickProgress={setProgressStatus}
    onOpenDetail={(game) => openGameDetail(game, wishlistGames)}
    libraryCardMode={libraryCardMode}
    setLibraryCardMode={setLibraryCardMode}
  />
)}

{libraryView === "physical" && (
  <PhysicalCollectionPanel
    games={collectionGames}
    onOpenDetail={(game) => openGameDetail(game, collectionGames)}
    onUpdatePhysicalGame={updatePhysicalGame}
  />
)}

  </>
)}

{activeTab === "series" && (
  <GameSeriesTab
  key={searchDestination?.kind==="series"?searchDestination.token:"series"}
  initialSeriesName={searchDestination?.kind==="series"?searchDestination.name:null}
  games={games}
  onAddGameToLibrary={addGameToFirebase}
  onAddGameToSeries={addGameToSeries}
/>
)}

{activeTab === "hardware" && (
  <HardwareTab
    initialHardwareRequest={searchDestination?.kind==="hardware"?searchDestination:null}
    hardware={hardware}
    onAddHardware={addHardware}
    onDeleteHardware={deleteHardware}
    onToggleHardwareFavorite={toggleHardwareFavorite}
    games={games}
    onUpdateHardwareStatus={updateHardwareStatus}
    onUpdateHardwareSanctuary={updateHardwareSanctuary}
    onUpdateHardwareCondition={updateHardwareCondition}
    onUpdateHardwareAcquiredYear={updateHardwareAcquiredYear}
    onUpdateHardwareDisplaySize={updateHardwareDisplaySize}
    onUpdateHardwareRank={updateHardwareRank}
    onUpdateHardwareRating={updateHardwareRating}
    onUpdateHardwareReview={updateHardwareReview}
    onUpdateHardwareComponent={updateHardwareComponent}
    onUpdateHardwareEdition={updateHardwareEdition}
  />
)}

          {activeTab === "favorites" && (
            <SanctuaryTab
              games={games}
              hardware={hardware}
              onOpenGameDetail={openGameDetail}
              onGoLibrary={() => setActiveTab("library")}
              onGoHardware={() => setActiveTab("hardware")}
              onSetSanctuaryFigures={setSanctuaryFigures}
            />
          )}

          {activeTab === "live" && (
            <LiveTab
              gamingEvents={gamingEvents}
              setMiniPlayerLive={setMiniPlayerLive}
              setMiniPlayerCollapsed={setMiniPlayerCollapsed}
            />
          )}

          {activeTab === "social" && (
            <SocialTab
              key={['poll','challenge','recommendation'].includes(searchDestination?.kind)?searchDestination.token:"social"}
              targetPollId={searchDestination?.kind==="poll"?searchDestination.id:null}
              targetChallengeId={searchDestination?.kind==='challenge'?searchDestination.id:null}
              targetRecommendationId={searchDestination?.kind==='recommendation'?searchDestination.id:null}
              onSetFavoriteReason={setFavoriteReason}
              onOpenComparisonGame={game=>openGameDetail(game,games)}
              onDiscoverGame={game=>{changeTab("search");setSearch(game.name);}}
              initialView={socialEntryView}
              games={games}
              hardware={hardware}
              badges={badges}
              level={level}
              socialProfile={socialProfile}
              socialFriends={socialFriends}
              sharedProfile={sharedProfile}
              onCloseSharedProfile={closeSharedProfile}
              onProfileChange={updateSocialProfile}
              onSetProfileVisibility={setSocialProfileVisibility}
              onCopyProfileLink={copySocialProfileLink}
              onAddProfilePhotos={addSocialProfilePhotos}
              onRemoveProfilePhoto={removeSocialProfilePhoto}
              friendInvitations={friendInvitations}
              socialControls={socialControls}
              onBlockFriend={setBlockTarget}
              onCommonGames={setCommonFriend}
              onReportSocial={setReportTarget}
              onMuteFriend={async friend=>{try{const muted=socialControls.data?.muted?.some(player=>player.handle===friend.handle);await socialControls.act(muted?'unmute':'mute',{handle:friend.handle});showToast(muted?'Activités réaffichées dans le fil.':'Activités masquées. Ce joueur reste ton ami.');}catch(error){showToast(error.message);}}}
              navigationToken={searchDestination?.token}
              onAddFriend={addSocialFriend}
              onRemoveFriend={removeSocialFriend}
              onOpenFriend={openSocialFriendProfile}
            />
          )}

          {activeTab === "top5" && (
            <Top5TabV2
              games={games}
              hardware={hardware}
              onSetGameOfYear={setGameOfYear}
            />
          )}

          {activeTab === "profile" && (
            <ProfileTab
              onOpenPublicProfile={() => { changeTab("social"); setSocialEntryView("profile"); }}
              badges={badges}
              level={level}
              totalXP={totalXP}
              rawTotalXP={rawTotalXP}
              progress={progress}
              games={games}
              hardware={hardware}
              socialProfile={socialProfile}
              featuredBadgeId={socialProfile.featuredBadgeId}
              onSelectFeaturedBadge={(badgeId) =>
                updateSocialProfile("featuredBadgeId", badgeId)
              }
              checkpointTrialProgress={checkpointTrialProgress}
              pendingCheckpointLevel={pendingCheckpointLevel}
              onOpenTrial={openTrialRoom}
            />
          )}

          {activeTab === "options" && (
            <>
              <OptionsTab
                theme={theme}
                level={level}
                unlockedThemes={unlockedThemes}
                setTheme={next => { if (canUseTheme(next, unlockedThemes)) setTheme(next); }}
                uiMode={uiMode}
                setUiMode={setUiMode}
                appOptions={appOptions}
                onOptionChange={updateAppOption}
                socialProfile={socialProfile}
                onProfileChange={updateSocialProfile}
                onProfileVisibilityChange={async (visibility) => {
                  const result = await setSocialProfileVisibility(visibility);
                  if(result.ok)showToast(result.message);
                  return result;
                }}
                onExportBackup={exportCheckpointBackup}
                onImportBackup={importCheckpointBackup}
                onCaptureBackup={captureCurrentBackup}
                onRestoreVersion={restoreBackupVersion}
                onResetOptions={resetAppOptions}
                games={games}
                onRepairGameData={repairGameDataIntegrity}
                onOpenTrial={openTrialRoom}
                checkpointTrialProgress={checkpointTrialProgress}
                onImportSteamGames={importSteamGamesToLibrary}
                onImportXboxGames={importXboxGamesToLibrary}
                developerEvents={gamingEvents}
                onImportNews={importNewsFromRSS}
              />

            </>
          )}


        </div>
        <SearchGameDetailModal
          game={selectedSearchGame}
          onClose={() => setSelectedSearchGame(null)}
          onWishlist={addGameToFirebase}
          onCollection={startCollectionAdd}
        />
      </div>

      <LiveMiniPlayer
          live={miniPlayerLive}
          collapsed={miniPlayerCollapsed}
          setCollapsed={setMiniPlayerCollapsed}
          onClose={() => setMiniPlayerLive(null)}
        />

        {pixelTransition && <ThemeTransition key={pixelTransition.id} theme={theme} direction={pixelTransition.direction} effects={appOptions.transitionMotion === "light" ? "calm" : appOptions.visualEffects} fullMotion light={appOptions.transitionMotion === "light"} />}

      {!showSplash && (

        <BottomTabs
        activeTab={activeTab}
        setActiveTab={changeTab}
        soundStyle={soundStyle}
        hiddenTabs={appOptions.hiddenTabs}
      />
      )}

      {couchOpen&&<CouchMode games={games} lists={appOptions.personalLists} onClose={()=>setCouchOpen(false)} onOpenGame={game=>openGameDetail(game,games)}/>}
      {releaseNotesOpen&&<ReleaseNotesDialog onClose={()=>setReleaseNotesOpen(false)}/>}
      {rendezvousNotice&&<ToolDialog title="Les rendez-vous Checkpoint" wide onClose={()=>setRendezvousNotice(null)}><RendezvousHub key={rendezvousNotice.token} initialView={rendezvousNotice.view} games={games} value={appOptions.engagement} onChange={value=>updateAppOption('engagement',value)} onOpenGame={game=>{setRendezvousNotice(null);openGameDetail(game,games);}} onChallenges={()=>{setRendezvousNotice(null);changeTab('social');setSharedProfile(null);setSocialEntryView('challenges');}}/></ToolDialog>}
      {physicalScannerOpen&&<Suspense fallback={<ToolDialog title="Scanner un jeu physique" onClose={()=>setPhysicalScannerOpen(false)}><p role="status">Chargement du scanner…</p></ToolDialog>}><PhysicalGameScanner games={games} onClose={()=>setPhysicalScannerOpen(false)} onOpenGame={game=>{setPhysicalScannerOpen(false);openGameDetail(game,games);}} onSave={async({game,physical,targetId})=>{
        if(targetId){const latest=documentStateRef.current.games.find(g=>g.id===targetId);if(!latest||latest.physicalOwned)throw new Error('Cette fiche a changé. Vérifie son édition dans ta bibliothèque.');return persistPatch('games',targetId,{...physical,...(latest.status==='wishlist'?{status:'collection'}:{})},{success:'Boîte associée à ta collection.'});}
        return addGameToFirebase({...game,...physical,status:'collection',completed:false,progressStatus:'not_started',rating:0});
      }}/></Suspense>}
      {collectionAddDraft&&<QuickCollectionAdd key={collectionAddDraft.rawgId||collectionAddDraft.name} game={collectionAddDraft} onSave={addGameToFirebase} onClose={()=>setCollectionAddDraft(null)}/>}
      <GameDetailModal
        socialFriends={socialFriends}
        personalLists={appOptions.personalLists}
        onPersonalListsChange={value=>updateAppOption('personalLists',value)}
        game={selectedGame}
        onSetExperienceInfo={setExperienceInfo}
        onSavePlatformActivity={(id,patch)=>persistPatch("games",id,patch,{success:"Relevé de plateforme enregistré."})}
        onClose={closeGameDetail}
        onDelete={deleteGame}
        onSetStatus={setStatus}
        onSetRating={setRating}
        onRatingCommit={handleGameRatingCommit}
        onToggleFavorite={toggleFavorite}
        onSetGameSanctuary={setGameSanctuary}
        onSetSanctuaryFigures={setSanctuaryFigures}
        onSetDifficulty={setDifficulty}
        onSetReview={setReview}
        onSetFavoriteReason={setFavoriteReason}
        onSetOstInfo={setOstInfo}
        onSetDetailedRating={setDetailedRating}
        onToggleCompleted={toggleCompleted}
        onSetProgressStatus={setProgressStatus}
        onSetPlaytimeRange={setPlaytimeRange}
        onSetPlayedPlatforms={setPlayedPlatforms}
        onSetDLCs={setDLCs}
        onAddGame={addGameToFirebase}
        games={games}
        onNavigateGame={navigateSelectedGame}
        canGoPrevious={canGoPreviousGame}
        canGoNext={canGoNextGame}
        closeAfterRating={appOptions.afterRatingAction === "list"}
      />
      {blockTarget&&<BlockPlayerDialog key={blockTarget.handle} player={blockTarget} controls={socialControls} onClose={()=>setBlockTarget(null)} onBlocked={()=>{setSharedProfile(null);showToast('Joueur bloqué.');}}/>}
      {reportTarget&&<SocialReportDialog key={`${reportTarget.handle}:${reportTarget.activityId||'profile'}`} target={reportTarget} onClose={()=>setReportTarget(null)}/>}
      {commonFriend&&<FriendCommonGames key={commonFriend.handle} friend={commonFriend} games={games} onClose={()=>setCommonFriend(null)} onOpenGame={game=>openGameDetail(game,games)}/>}
    </>
  );
}
